// src/components/Common/News.tsx
import { parseRSS } from "@gofranz/common";
import { Anchor, List, ListItem, Pill, Text as Text2 } from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// src/components/Common/TitleWithText.tsx
import { Box, Stack, Text, useMantineTheme as useMantineTheme2 } from "@mantine/core";

// src/components/Common/Title.tsx
import { Title, useMantineTheme } from "@mantine/core";
import { jsx } from "react/jsx-runtime";
function LargeTitle({ title, ...stackProps }) {
  const theme = useMantineTheme();
  const primaryColor = theme.colors["brand-primary"]?.[6] || theme.colors.primary[6];
  const accentColor = theme.colors["brand-accent"]?.[6] || theme.colors["brand-secondary"]?.[6] || theme.colors.primary[8];
  return /* @__PURE__ */ jsx(
    Title,
    {
      order: 1,
      style: {
        background: `linear-gradient(45deg, ${primaryColor}, ${accentColor})`,
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        marginBottom: "1rem",
        fontSize: "clamp(50px, 8vw, 60px)",
        fontWeight: 900,
        letterSpacing: "-2px"
      },
      ...stackProps,
      children: title
    }
  );
}

// src/components/Common/TitleWithText.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function LargeTitleWithText({ title, text, ...stackProps }) {
  const theme = useMantineTheme2();
  const subtitleColor = theme.colors["neutral-dark"]?.[6] || "dimmed";
  return /* @__PURE__ */ jsx2(Stack, { gap: "xl", ...stackProps, children: /* @__PURE__ */ jsxs(Box, { style: { textAlign: "center" }, children: [
    /* @__PURE__ */ jsx2(LargeTitle, { title }),
    text && /* @__PURE__ */ jsx2(Text, { size: "lg", c: subtitleColor, maw: 600, mx: "auto", children: text })
  ] }) });
}

// src/components/Common/News.tsx
import { Fragment, jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function News(props) {
  const { t } = useTranslation();
  const { blogBaseUrl, maxItems = 10, summaryLength = 180 } = props;
  const url = `${blogBaseUrl}/feed.xml`;
  const [feed, setFeed] = useState(null);
  useEffect(() => {
    (async () => {
      const res = await fetch(url);
      const resData = await res.text();
      const parsedFeed = parseRSS(resData);
      setFeed(parsedFeed.feed);
    })();
  }, [url]);
  const category = (cat, index) => /* @__PURE__ */ jsx3(Pill, { color: "gray", size: "xs", children: /* @__PURE__ */ jsx3("span", { style: { fontSize: "12px", color: "var(--mantine-color-dimmed)" }, children: cat }) }, `${cat}-${index}`);
  return /* @__PURE__ */ jsxs2(Fragment, { children: [
    /* @__PURE__ */ jsx3(LargeTitleWithText, { title: t("news.title"), mb: "md" }),
    /* @__PURE__ */ jsx3(List, { mb: "md", maw: 680, children: feed ? feed?.entry.slice(0, maxItems).map((item) => /* @__PURE__ */ jsxs2(ListItem, { mb: "xs", children: [
      /* @__PURE__ */ jsx3("div", { children: /* @__PURE__ */ jsx3(Anchor, { href: item.id, title: item.title, children: item.title }) }),
      /* @__PURE__ */ jsx3("div", { style: { fontSize: "12px", marginBottom: "8px" }, children: item.category.map(
        (cat, index) => category(cat.toLowerCase(), index)
      ) }),
      /* @__PURE__ */ jsxs2("div", { style: { fontSize: "14px", lineHeight: "1.4" }, children: [
        item.summary.replace(/<[^>]*>/g, "").slice(0, summaryLength),
        " ..."
      ] })
    ] }, `news-${item.id}`)) : /* @__PURE__ */ jsx3(Text2, { children: t("news.loading") }) }),
    /* @__PURE__ */ jsxs2(Text2, { children: [
      t("news.rssDescription"),
      " ",
      /* @__PURE__ */ jsx3(Anchor, { href: url, children: url.replace("https://", "") })
    ] })
  ] });
}

// src/components/VerifiedEmail/Email.tsx
import { ActionIcon, Badge, Button, Grid, Group as Group2, Text as Text4 } from "@mantine/core";
import { IconTrash, IconUser } from "@tabler/icons-react";
import { useState as useState2 } from "react";
import { useTranslation as useTranslation2 } from "react-i18next";

// src/components/Common/Verified.tsx
import { Group, Text as Text3 } from "@mantine/core";
import { IconCheckbox, IconSquare } from "@tabler/icons-react";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
function IsVerified(props) {
  const Icon = () => {
    return props.isVerified ? /* @__PURE__ */ jsx4(IconCheckbox, {}) : /* @__PURE__ */ jsx4(IconSquare, {});
  };
  return /* @__PURE__ */ jsxs3(Group, { children: [
    /* @__PURE__ */ jsx4(Icon, {}),
    /* @__PURE__ */ jsx4(Text3, { children: "Verified" })
  ] });
}

// src/components/VerifiedEmail/Email.tsx
import { Fragment as Fragment2, jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
function VerifiedEmailComponent(props) {
  const { t } = useTranslation2();
  const [message, setMessage] = useState2("");
  const { verifiedEmail } = props;
  const verifyCb = async (id) => {
    await props.verifyCb(id);
    setMessage(t("emails.verificationSent"));
  };
  const setAccountEmailCb = async (id) => {
    await props.setAccountEmailCb(id);
    setMessage(t("emails.accountEmailUpdated"));
  };
  return /* @__PURE__ */ jsxs4(Fragment2, { children: [
    /* @__PURE__ */ jsxs4(Grid, { children: [
      /* @__PURE__ */ jsx5(Grid.Col, { span: 5, children: /* @__PURE__ */ jsxs4(Group2, { children: [
        /* @__PURE__ */ jsx5(
          ActionIcon,
          {
            color: "red",
            onClick: () => props.deleteCb(verifiedEmail.id),
            loading: props.isBusy,
            variant: "filled",
            "aria-label": "Delete",
            children: /* @__PURE__ */ jsx5(IconTrash, { style: { width: "70%", height: "70%" }, stroke: 1.5 })
          }
        ),
        /* @__PURE__ */ jsx5(Text4, { children: verifiedEmail.email }),
        verifiedEmail.is_account_email && /* @__PURE__ */ jsx5(Badge, { color: "blue", variant: "light", leftSection: /* @__PURE__ */ jsx5(IconUser, { size: 12 }), children: t("emails.accountEmail") })
      ] }) }),
      /* @__PURE__ */ jsx5(Grid.Col, { span: "auto", children: /* @__PURE__ */ jsxs4(Group2, { children: [
        /* @__PURE__ */ jsx5(IsVerified, { isVerified: verifiedEmail.is_verified }),
        verifiedEmail.is_verified === false && /* @__PURE__ */ jsx5(
          Button,
          {
            onClick: () => verifyCb(verifiedEmail.id),
            loading: props.isBusy,
            variant: "outline",
            children: t("emails.resendVerification")
          }
        ),
        verifiedEmail.is_verified && !verifiedEmail.is_account_email && /* @__PURE__ */ jsx5(
          Button,
          {
            onClick: () => setAccountEmailCb(verifiedEmail.id),
            loading: props.isBusy,
            variant: "outline",
            size: "xs",
            children: t("emails.makePrimary")
          }
        )
      ] }) })
    ] }),
    verifiedEmail.is_account_email && /* @__PURE__ */ jsx5(Text4, { size: "xs", c: "dimmed", mt: "xs", children: t("emails.accountEmailDescription") }),
    message !== "" && /* @__PURE__ */ jsx5(Text4, { size: "sm", c: "green", mt: "xs", children: message })
  ] });
}

// src/components/I18nProvider.tsx
import { I18nextProvider } from "react-i18next";

// src/i18n/index.ts
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// src/i18n/locales/ar.json
var ar_default = {
  glob_navigation: {
    home: "\u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
    pricing: "\u0627\u0644\u0623\u0633\u0639\u0627\u0631",
    docs: "\u0627\u0644\u062A\u0648\u062B\u064A\u0642",
    news: "\u0627\u0644\u0623\u062E\u0628\u0627\u0631",
    more: "\u0627\u0644\u0645\u0632\u064A\u062F",
    login: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644",
    signup: "\u0627\u0644\u062A\u0633\u062C\u064A\u0644",
    getStarted: "\u0627\u0628\u062F\u0623 \u0627\u0644\u0622\u0646",
    forms: "\u0627\u0644\u0646\u0645\u0627\u0630\u062C",
    integrations: "\u0627\u0644\u062A\u0643\u0627\u0645\u0644\u0627\u062A",
    messages: "\u0627\u0644\u0631\u0633\u0627\u0626\u0644",
    profile: "\u0627\u0644\u0645\u0644\u0641 \u0627\u0644\u0634\u062E\u0635\u064A",
    support: "\u0627\u0644\u062F\u0639\u0645",
    logout: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062E\u0631\u0648\u062C",
    terms: "\u0627\u0644\u0634\u0631\u0648\u0637",
    billing: "\u0627\u0644\u0641\u0648\u0627\u062A\u064A\u0631",
    usageAndCredits: "\u0627\u0644\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0648\u0627\u0644\u0631\u0635\u064A\u062F",
    subscriptions: "\u0627\u0644\u0627\u0634\u062A\u0631\u0627\u0643\u0627\u062A",
    referrals: "\u0627\u0644\u0625\u062D\u0627\u0644\u0627\u062A",
    apiKeys: "\u0645\u0641\u0627\u062A\u064A\u062D API",
    securityLog: "\u0633\u062C\u0644 \u0627\u0644\u0623\u0645\u0627\u0646",
    chat: "\u0627\u0644\u062F\u0631\u062F\u0634\u0629",
    sectionWorkspace: "\u0645\u0633\u0627\u062D\u0629 \u0627\u0644\u0639\u0645\u0644",
    sectionAccount: "\u0627\u0644\u062D\u0633\u0627\u0628",
    sectionHelp: "\u0627\u0644\u0645\u0633\u0627\u0639\u062F\u0629"
  },
  auth: {
    login: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644",
    signup: "\u0627\u0644\u062A\u0633\u062C\u064A\u0644",
    logout: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062E\u0631\u0648\u062C",
    step1: "\u0627\u0644\u062E\u0637\u0648\u0629 1",
    step2: "\u0627\u0644\u062E\u0637\u0648\u0629 2",
    authenticate: "\u0627\u0644\u062A\u062D\u0642\u0642",
    enterKey: "\u0627\u0644\u0645\u0639\u0631\u0641",
    newHere: "\u062C\u062F\u064A\u062F \u0647\u0646\u0627\u061F ",
    howToSignup: "\u0643\u064A\u0641\u064A\u0629 \u0627\u0644\u062A\u0633\u062C\u064A\u0644",
    alreadyAccount: "\u0625\u0630\u0627 \u0644\u0645 \u064A\u0643\u0646 \u0644\u062F\u064A\u0643 \u062D\u0633\u0627\u0628 \u0628\u0639\u062F\u060C \u0633\u064A\u062A\u0645 \u0625\u0646\u0634\u0627\u0621 \u0648\u0627\u062D\u062F \u0644\u0643 \u0641\u064A \u0627\u0644\u0645\u0631\u0629 \u0627\u0644\u0623\u0648\u0644\u0649 \u0627\u0644\u062A\u064A \u062A\u0633\u062C\u0644 \u0641\u064A\u0647\u0627 \u0627\u0644\u062F\u062E\u0648\u0644. ",
    emailSection: "\u0631\u0627\u0628\u0637 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
    googleSection: "\u062C\u0648\u062C\u0644",
    githubSection: "GitHub",
    microsoftSection: "\u0645\u0627\u064A\u0643\u0631\u0648\u0633\u0648\u0641\u062A",
    nostrSection: "\u0625\u0636\u0627\u0641\u0629 \u0645\u062A\u0635\u0641\u062D Nostr",
    signupDescription: "\u0627\u062D\u0635\u0644 \u0639\u0644\u0649 \u0631\u0627\u0628\u0637 \u0645\u0631\u0633\u0644 \u0625\u0644\u0649 \u0628\u0631\u064A\u062F\u0643 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0644\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644. ",
    googleDescription: "\u0633\u062C\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u062D\u0633\u0627\u0628 \u062C\u0648\u062C\u0644 \u0627\u0644\u062E\u0627\u0635 \u0628\u0643. ",
    githubDescription: "\u0633\u062C\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u062D\u0633\u0627\u0628 GitHub \u0627\u0644\u062E\u0627\u0635 \u0628\u0643. ",
    microsoftDescription: "\u0633\u062C\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u062D\u0633\u0627\u0628 \u0645\u0627\u064A\u0643\u0631\u0648\u0633\u0648\u0641\u062A \u0627\u0644\u062E\u0627\u0635 \u0628\u0643. ",
    nostrDescription: "\u0633\u062C\u0644 \u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 ",
    browserPlugin: "\u0625\u0636\u0627\u0641\u0629 \u0645\u062A\u0635\u0641\u062D",
    signupLoginEmail: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0628\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A.",
    signupLoginGoogle: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u062C\u0648\u062C\u0644",
    signupLoginGithub: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 GitHub",
    signupLoginMicrosoft: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0645\u0627\u064A\u0643\u0631\u0648\u0633\u0648\u0641\u062A",
    signupLoginNostr: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 Nostr",
    privacyPolicy: "\u0633\u064A\u0627\u0633\u0629 \u0627\u0644\u062E\u0635\u0648\u0635\u064A\u0629",
    termsOfService: "\u0634\u0631\u0648\u0637 \u0627\u0644\u062E\u062F\u0645\u0629"
  },
  login: {
    welcomeBack: "\u0623\u0647\u0644\u0627\u064B \u0628\u0639\u0648\u062F\u062A\u0643",
    cardSubtitle: "\u062A\u0627\u0628\u0639 \u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0645\u0632\u0648\u0651\u062F \u0627\u062C\u062A\u0645\u0627\u0639\u064A \u0623\u0648 \u0623\u062F\u062E\u0644 \u0639\u0646\u0648\u0627\u0646 \u0628\u0631\u064A\u062F\u0643 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A.",
    orDivider: "\u0623\u0648",
    loginWithEmail: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0628\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
    magicLinkInfo: "\u0633\u0646\u0631\u0633\u0644 \u0631\u0627\u0628\u0637\u064B\u0627 \u0633\u062D\u0631\u064A\u064B\u0627 \u0625\u0644\u0649 \u0635\u0646\u062F\u0648\u0642 \u0628\u0631\u064A\u062F\u0643.",
    publicKeyRequired: "\u0627\u0644\u0645\u0641\u062A\u0627\u062D \u0627\u0644\u0639\u0627\u0645 \u0645\u0637\u0644\u0648\u0628",
    invalidEmail: "\u0628\u0631\u064A\u062F \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u063A\u064A\u0631 \u0635\u062D\u064A\u062D",
    getPublicKey: "\u0627\u062D\u0635\u0644 \u0639\u0644\u0649 \u0627\u0644\u0645\u0641\u062A\u0627\u062D \u0627\u0644\u0639\u0627\u0645 \u0645\u0646 \u0627\u0644\u0625\u0636\u0627\u0641\u0629",
    emailTab: "\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
    googleTab: "\u062C\u0648\u062C\u0644",
    githubTab: "GitHub",
    microsoftTab: "\u0645\u0627\u064A\u0643\u0631\u0648\u0633\u0648\u0641\u062A",
    nostrTab: "Nostr",
    oidcSsoTab: "GoFranz SSO",
    emailDescription: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0628\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A.",
    googleDescription: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u062C\u0648\u062C\u0644",
    nostrPluginDescription: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 Nos2x \u0623\u0648 \u0645\u0634\u0627\u0628\u0647.",
    nostrKeyDescription: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0645\u0641\u062A\u0627\u062D\u0643 \u0627\u0644\u062E\u0627\u0635.",
    nostrOtherDescription: "\u062A\u0648\u0641\u064A\u0631 \u0627\u0644\u062A\u0648\u0642\u064A\u0639 \u0628\u0648\u0633\u0627\u0626\u0644 \u0623\u062E\u0631\u0649.",
    loadedPublicKey: "\u062A\u0645 \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0645\u0641\u062A\u0627\u062D \u0627\u0644\u0639\u0627\u0645",
    noKeyLoaded: "\u0644\u0645 \u064A\u062A\u0645 \u062A\u062D\u0645\u064A\u0644 \u0623\u064A \u0645\u0641\u062A\u0627\u062D...",
    enterPublicKey: "\u0623\u062F\u062E\u0644 \u0645\u0641\u062A\u0627\u062D\u0643 \u0627\u0644\u0639\u0627\u0645",
    publicKeyPlaceholder: "32b72e6432f7282135cf124a8...",
    emailLabel: "\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
    emailPlaceholder: "your-email@provider.com",
    enterCode: "\u0623\u062F\u062E\u0644 \u0627\u0644\u0631\u0645\u0632 \u0645\u0646 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
    codePlaceholder: "6fd7e67e-6f42-4354-b8f8-775bc1e52a50",
    privateKeyLabel: "\u0623\u062F\u062E\u0644 \u0645\u0641\u062A\u0627\u062D\u0643 \u0627\u0644\u062E\u0627\u0635",
    challengeResponseLabel: "\u0623\u062F\u062E\u0644 \u0627\u0633\u062A\u062C\u0627\u0628\u062A\u0643 \u0644\u0644\u062A\u062D\u062F\u064A",
    emailInstructions: "\u0627\u0646\u0642\u0631 \u0639\u0644\u0649 \u0627\u0644\u0631\u0627\u0628\u0637 \u0641\u064A \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A\u060C \u0623\u0648 \u0627\u0646\u0633\u062E \u0627\u0644\u0631\u0645\u0632 \u0648\u0627\u0644\u0635\u0642\u0647 \u0647\u0646\u0627.",
    nostrInstructions: "\u064A\u062C\u0628 \u0623\u0646 \u062A\u0637\u0644\u0628 \u0645\u0646\u0643 \u0625\u0636\u0627\u0641\u0629 Nostr \u062A\u0648\u0642\u064A\u0639 \u062D\u062F\u062B \u0627\u0644\u062A\u062D\u0642\u0642 \u0644\u0644\u0645\u062A\u0627\u0628\u0639\u0629.",
    challenge: "\u0627\u0644\u062A\u062D\u062F\u064A: ",
    loginWithExtension: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0628\u0627\u0644\u0625\u0636\u0627\u0641\u0629",
    redirectingGoogle: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0648\u062C\u064A\u0647 \u0625\u0644\u0649 \u062C\u0648\u062C\u0644...",
    redirectingGithub: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0648\u062C\u064A\u0647 \u0625\u0644\u0649 GitHub...",
    redirectingMicrosoft: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0648\u062C\u064A\u0647 \u0625\u0644\u0649 \u0645\u0627\u064A\u0643\u0631\u0648\u0633\u0648\u0641\u062A...",
    redirectingOidc: "\u0625\u0639\u0627\u062F\u0629 \u062A\u0648\u062C\u064A\u0647 \u0625\u0644\u0649 GoFranz SSO...",
    githubNoChallenge: "\u0641\u0634\u0644 \u0641\u064A \u0627\u0644\u062D\u0635\u0648\u0644 \u0639\u0644\u0649 \u062A\u062D\u062F\u064A GitHub",
    microsoftNoChallenge: "\u0641\u0634\u0644 \u0641\u064A \u0627\u0644\u062D\u0635\u0648\u0644 \u0639\u0644\u0649 \u062A\u062D\u062F\u064A \u0645\u0627\u064A\u0643\u0631\u0648\u0633\u0648\u0641\u062A",
    oidcNoChallenge: "\u0641\u0634\u0644 \u0641\u064A \u0627\u0644\u062D\u0635\u0648\u0644 \u0639\u0644\u0649 \u062A\u062D\u062F\u064A GoFranz SSO",
    checkYourEmail: "Check your email for the login link",
    emailSent: "We've sent a login link to your email",
    clickLinkToLogin: "Click the link in the email to complete login",
    wrongEmail: "Wrong email? Try again",
    errorNotPrimaryEmail: "This email is not your primary account email.",
    errorDifferentAuthMethod: "This email is registered with a different authentication method",
    errorGoogleEmailNotVerified: "Google email not verified",
    errorInvalidAuthMethodGoogle: "This account uses Google login",
    errorInvalidAuthMethodGithub: "This account uses GitHub login",
    errorInvalidAuthMethodMicrosoft: "This account uses Microsoft login",
    errorInvalidAuthMethodNostr: "This account uses Nostr login",
    errorAuthMethodCollision: "\u0647\u0630\u0627 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0645\u0633\u062C\u064E\u0651\u0644 \u0628\u0627\u0644\u0641\u0639\u0644 \u0639\u0628\u0631 {{method}}. \u064A\u0631\u062C\u0649 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u062A\u0644\u0643 \u0627\u0644\u0637\u0631\u064A\u0642\u0629 \u0628\u062F\u0644\u0627\u064B \u0645\u0646 \u0630\u0644\u0643.",
    errorAuthMethodCollisionGeneric: "\u0647\u0630\u0627 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0645\u0633\u062C\u064E\u0651\u0644 \u0628\u0627\u0644\u0641\u0639\u0644 \u0628\u0637\u0631\u064A\u0642\u0629 \u062A\u0633\u062C\u064A\u0644 \u062F\u062E\u0648\u0644 \u0623\u062E\u0631\u0649. \u064A\u0631\u062C\u0649 \u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0637\u0631\u064A\u0642\u062A\u0643 \u0627\u0644\u0623\u0635\u0644\u064A\u0629.",
    errorInvalidNostrSignature: "Invalid Nostr signature",
    errorNostrKeyMismatch: "Nostr key mismatch",
    errorOAuthFailed: "OAuth authentication failed",
    errorTokenExchange: "\u0641\u0634\u0644 \u0625\u0643\u0645\u0627\u0644 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    errorCodeExpired: "\u0627\u0646\u062A\u0647\u062A \u0635\u0644\u0627\u062D\u064A\u0629 \u062C\u0644\u0633\u0629 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644. \u064A\u0631\u062C\u0649 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    invalidLoginResponse: "\u0627\u0633\u062A\u062C\u0627\u0628\u0629 \u062A\u0633\u062C\u064A\u0644 \u062F\u062E\u0648\u0644 \u063A\u064A\u0631 \u0635\u0627\u0644\u062D\u0629. \u064A\u0631\u062C\u0649 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    errorRateLimited: "\u0645\u062D\u0627\u0648\u0644\u0627\u062A \u062A\u0633\u062C\u064A\u0644 \u062F\u062E\u0648\u0644 \u0643\u062B\u064A\u0631\u0629 \u062C\u062F\u0627\u064B. \u064A\u0631\u062C\u0649 \u0627\u0644\u0627\u0646\u062A\u0638\u0627\u0631 {{seconds}} \u062B\u0627\u0646\u064A\u0629 \u0648\u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.",
    processing: "Processing login...",
    somethingWrong: "Something went wrong",
    invalidMagicLink: "Invalid magic link",
    noIdProvided: "No session ID provided",
    noChallengeProvided: "No challenge provided",
    back: "\u0639\u0648\u062F\u0629",
    cancel: "\u0625\u0644\u063A\u0627\u0621",
    submit: "\u0625\u0631\u0633\u0627\u0644"
  },
  glob_buttons: {
    save: "\u062D\u0641\u0638",
    cancel: "\u0625\u0644\u063A\u0627\u0621",
    submit: "\u0625\u0631\u0633\u0627\u0644",
    create: "\u0625\u0646\u0634\u0627\u0621",
    update: "\u062A\u062D\u062F\u064A\u062B",
    updateUnsaved: "\u062A\u062D\u062F\u064A\u062B (\u062A\u063A\u064A\u064A\u0631\u0627\u062A \u063A\u064A\u0631 \u0645\u062D\u0641\u0648\u0638\u0629)",
    delete: "\u062D\u0630\u0641",
    calculate: "\u062D\u0633\u0627\u0628",
    login: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644",
    back: "\u0639\u0648\u062F\u0629"
  },
  support: {
    title: "\u0627\u0644\u062F\u0639\u0645",
    getHelp: "\u0627\u062D\u0635\u0644 \u0639\u0644\u0649 \u0627\u0644\u0645\u0633\u0627\u0639\u062F\u0629 \u0645\u0639 {{serviceName}}",
    description: "\u0644\u062F\u064A\u0643 \u0633\u0624\u0627\u0644 \u0623\u0648 \u062A\u062D\u062A\u0627\u062C \u0645\u0633\u0627\u0639\u062F\u0629\u061F \u0646\u062D\u0646 \u0647\u0646\u0627 \u0644\u0644\u0645\u0633\u0627\u0639\u062F\u0629! \u0627\u0645\u0644\u0623 \u0627\u0644\u0646\u0645\u0648\u0630\u062C \u0623\u062F\u0646\u0627\u0647 \u0648\u0633\u0646\u0639\u0648\u062F \u0625\u0644\u064A\u0643 \u0641\u064A \u0623\u0642\u0631\u0628 \u0648\u0642\u062A \u0645\u0645\u0643\u0646.",
    thankYou: "\u0634\u0643\u0631\u0627\u064B \u0644\u0643!",
    successMessage: "\u062A\u0645 \u0625\u0631\u0633\u0627\u0644 \u0637\u0644\u0628 \u0627\u0644\u062F\u0639\u0645 \u0627\u0644\u062E\u0627\u0635 \u0628\u0643 \u0628\u0646\u062C\u0627\u062D. \u0633\u0646\u0639\u0648\u062F \u0625\u0644\u064A\u0643 \u0641\u064A \u0623\u0642\u0631\u0628 \u0648\u0642\u062A \u0645\u0645\u0643\u0646.",
    submitAnother: "\u0625\u0631\u0633\u0627\u0644 \u0637\u0644\u0628 \u0622\u062E\u0631",
    successAlert: "\u0646\u062C\u062D!",
    successAlertMessage: "\u062A\u0645 \u0625\u0631\u0633\u0627\u0644 \u0637\u0644\u0628 \u0627\u0644\u062F\u0639\u0645 \u0627\u0644\u062E\u0627\u0635 \u0628\u0643 \u0628\u0646\u062C\u0627\u062D!",
    nameLabel: "\u0627\u0644\u0627\u0633\u0645 (\u0627\u062E\u062A\u064A\u0627\u0631\u064A)",
    namePlaceholder: "\u0627\u0633\u0645\u0643 \u0627\u0644\u0643\u0627\u0645\u0644",
    emailLabel: "\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
    emailPlaceholder: "your.email@example.com",
    loggedInEmail: "\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0628\u0631\u064A\u062F\u0643 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0627\u0644\u0645\u0633\u062C\u0644",
    subjectLabel: "\u0627\u0644\u0645\u0648\u0636\u0648\u0639",
    subjectPlaceholder: "\u0627\u062E\u062A\u0631 \u0646\u0648\u0639 \u0637\u0644\u0628\u0643",
    messageLabel: "\u0627\u0644\u0631\u0633\u0627\u0644\u0629",
    messagePlaceholder: "\u064A\u0631\u062C\u0649 \u062A\u0642\u062F\u064A\u0645 \u062A\u0641\u0627\u0635\u064A\u0644 \u062D\u0648\u0644 \u0633\u0624\u0627\u0644\u0643 \u0623\u0648 \u0645\u0634\u0643\u0644\u062A\u0643...",
    formInfo: "\u0633\u064A\u062A\u0636\u0645\u0646 \u0647\u0630\u0627 \u0627\u0644\u0646\u0645\u0648\u0630\u062C \u0637\u0631\u064A\u0642\u0629 \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0627\u0644\u062E\u0627\u0635\u0629 \u0628\u0643 (",
    formInfoEnd: ") \u0648\u0645\u0639\u0631\u0641 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645 \u0644\u0645\u0633\u0627\u0639\u062F\u062A\u0643 \u0628\u0634\u0643\u0644 \u0623\u0641\u0636\u0644.",
    submitting: "\u062C\u0627\u0631\u064D \u0627\u0644\u0625\u0631\u0633\u0627\u0644...",
    submitButton: "\u0625\u0631\u0633\u0627\u0644 \u0637\u0644\u0628 \u0627\u0644\u062F\u0639\u0645",
    types: {
      general: "\u0633\u0624\u0627\u0644 \u0639\u0627\u0645",
      technical: "\u0633\u0624\u0627\u0644 \u062A\u0642\u0646\u064A",
      feature: "\u0637\u0644\u0628 \u0645\u064A\u0632\u0629",
      bug: "\u0627\u0644\u0625\u0628\u0644\u0627\u063A \u0639\u0646 \u062E\u0637\u0623"
    }
  },
  colorScheme: {
    lightMode: "\u0627\u0644\u0648\u0636\u0639 \u0627\u0644\u0641\u0627\u062A\u062D (\u0627\u0646\u0642\u0631 \u0644\u0644\u0648\u0636\u0639 \u0627\u0644\u0645\u0638\u0644\u0645)",
    darkMode: "\u0627\u0644\u0648\u0636\u0639 \u0627\u0644\u0645\u0638\u0644\u0645 (\u0627\u0646\u0642\u0631 \u0644\u0644\u0648\u0636\u0639 \u0627\u0644\u062A\u0644\u0642\u0627\u0626\u064A)",
    autoMode: "\u0627\u0644\u0648\u0636\u0639 \u0627\u0644\u062A\u0644\u0642\u0627\u0626\u064A (\u0627\u0646\u0642\u0631 \u0644\u0644\u0648\u0636\u0639 \u0627\u0644\u0641\u0627\u062A\u062D)",
    toggle: "\u062A\u0628\u062F\u064A\u0644 \u0646\u0638\u0627\u0645 \u0627\u0644\u0623\u0644\u0648\u0627\u0646"
  },
  privacy: {
    title: "\u0627\u0644\u062E\u0635\u0648\u0635\u064A\u0629",
    backHome: "\u0627\u0644\u0639\u0648\u062F\u0629 \u0644\u0644\u0631\u0626\u064A\u0633\u064A\u0629",
    gdprCompliant: "\u{1F1E9}\u{1F1EA} \u0645\u062A\u0648\u0627\u0641\u0642 \u0645\u0639 \u0642\u0627\u0646\u0648\u0646 \u062D\u0645\u0627\u064A\u0629 \u0627\u0644\u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0639\u0627\u0645\u0629. \u0627\u0644\u062E\u0648\u0627\u062F\u0645 \u0645\u0648\u062C\u0648\u062F\u0629 \u0641\u064A \u0623\u0644\u0645\u0627\u0646\u064A\u0627. "
  },
  terms: {
    title: "\u0634\u0631\u0648\u0637 \u0627\u0644\u062E\u062F\u0645\u0629"
  },
  news: {
    title: "\u0645\u0646 \u0645\u062F\u0648\u0646\u062A\u0646\u0627",
    loading: "\u062C\u0627\u0631\u064D \u0627\u0644\u062A\u062D\u0645\u064A\u0644 ...",
    rssDescription: "\u064A\u0645\u0643\u0646\u0643 \u0627\u0644\u0627\u0634\u062A\u0631\u0627\u0643 \u0641\u064A \u062E\u0644\u0627\u0635\u0629 Atom \u0647\u0630\u0647 \u0647\u0646\u0627 "
  },
  profile: {
    balance: "\u0627\u0644\u0631\u0635\u064A\u062F",
    addMoney: "\u0625\u0636\u0627\u0641\u0629 \u0623\u0645\u0648\u0627\u0644",
    noBalanceFound: "\u0644\u0645 \u064A\u062A\u0645 \u0627\u0644\u0639\u062B\u0648\u0631 \u0639\u0644\u0649 \u0631\u0635\u064A\u062F.",
    depositSuccess: "\u0625\u064A\u062F\u0627\u0639 \u0646\u0627\u062C\u062D",
    depositSuccessMessage: "\u0643\u0627\u0646 \u0625\u064A\u062F\u0627\u0639\u0643 \u0646\u0627\u062C\u062D\u0627\u064B. \u064A\u062C\u0628 \u062A\u0637\u0628\u064A\u0642 \u0645\u062F\u0641\u0648\u0639\u0627\u062A \u0627\u0644\u0628\u0637\u0627\u0642\u0629 \u0641\u064A \u063A\u0636\u0648\u0646 \u062B\u0648\u0627\u0646\u064D.",
    wirePaymentInfo: "\u062A\u0631\u064A\u062F \u0627\u0644\u062F\u0641\u0639 \u0639\u0646 \u0637\u0631\u064A\u0642 \u0627\u0644\u062A\u062D\u0648\u064A\u0644 \u0623\u0648 \u0627\u0644\u0628\u064A\u062A\u0643\u0648\u064A\u0646\u061F (\u20AC100+): ",
    personalInformation: "\u0627\u0644\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0634\u062E\u0635\u064A\u0629",
    personalInfoMessage: "\u0646\u062D\u0646 \u0644\u0627 \u0646\u0639\u0631\u0641 \u0627\u0644\u0643\u062B\u064A\u0631 \u0639\u0646\u0643 \u062D\u0642\u0627\u064B.",
    personalInfoContact: "\u0623\u0631\u0633\u0644 \u0628\u0631\u064A\u062F\u0627\u064B \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A\u0627\u064B \u0625\u0644\u0649 ",
    personalInfoGreeting: " \u0644\u0642\u0648\u0644 \u0645\u0631\u062D\u0628\u0627\u064B.",
    emails: "\u0631\u0633\u0627\u0626\u0644 \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
    loginInformation: "\u0645\u0639\u0644\u0648\u0645\u0627\u062A \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644",
    currentlyLoggedIn: "\u0623\u0646\u062A \u0645\u0633\u062C\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u062D\u0627\u0644\u064A\u0627\u064B \u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 ",
    saveKeysAlert: "\u0627\u062D\u0641\u0638 \u0647\u0630\u0647 \u0627\u0644\u0645\u0641\u0627\u062A\u064A\u062D \u0641\u064A \u0645\u062F\u064A\u0631 \u0643\u0644\u0645\u0627\u062A \u0627\u0644\u0645\u0631\u0648\u0631 \u0627\u0644\u062E\u0627\u0635 \u0628\u0643 \u0623\u0648 \u0641\u064A \u0645\u0643\u0627\u0646 \u0622\u0645\u0646. \u0644\u0627 \u064A\u0645\u0643\u0646\u0646\u0627 \u0627\u0633\u062A\u0631\u062F\u0627\u062F\u0647\u0627. \u064A\u0645\u0643\u0646\u0643 \u0627\u0633\u062A\u062E\u062F\u0627\u0645\u0647\u0627 \u0644\u0644\u0648\u0635\u0648\u0644 \u0625\u0644\u0649 \u0627\u0644\u062E\u062F\u0645\u0629 \u0645\u0646 \u0623\u062C\u0647\u0632\u0629 \u0623\u062E\u0631\u0649 \u0648\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0625\u0644\u0649 \u062E\u062F\u0645\u0627\u062A\u0646\u0627 \u0627\u0644\u0623\u062E\u0631\u0649.",
    downloadKeys: "\u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0645\u0641\u0627\u062A\u064A\u062D",
    publicKey: "\u0627\u0644\u0645\u0641\u062A\u0627\u062D \u0627\u0644\u0639\u0627\u0645",
    publicKeyDescription: "\u0645\u0641\u062A\u0627\u062D\u0643 \u0627\u0644\u0639\u0627\u0645 \u0647\u0648 \u0645\u0627 \u064A\u062D\u062F\u062F \u0647\u0648\u064A\u062A\u0643\u061B \u0646\u062D\u0646 \u0646\u062E\u0632\u0646\u0647 \u0639\u0644\u0649 \u062E\u0627\u062F\u0645\u0646\u0627.",
    privateKey: "\u0627\u0644\u0645\u0641\u062A\u0627\u062D \u0627\u0644\u062E\u0627\u0635",
    privateKeyDescription: "\u0645\u0641\u062A\u0627\u062D\u0643 \u0627\u0644\u062E\u0627\u0635 \u0647\u0648 \u0645\u0627 \u062A\u0633\u062A\u062E\u062F\u0645\u0647 \u0644\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0648\u0644\u0627 \u064A\u063A\u0627\u062F\u0631 \u062C\u0647\u0627\u0632 \u0627\u0644\u0643\u0645\u0628\u064A\u0648\u062A\u0631 \u0627\u0644\u062E\u0627\u0635 \u0628\u0643 \u0623\u0628\u062F\u0627\u064B.",
    security: "\u0627\u0644\u0623\u0645\u0627\u0646",
    dataPrivacy: "\u0627\u0644\u0628\u064A\u0627\u0646\u0627\u062A \u0648\u0627\u0644\u062E\u0635\u0648\u0635\u064A\u0629",
    dataPrivacyDescription: "\u0635\u062F\u0651\u0631 \u0628\u064A\u0627\u0646\u0627\u062A\u0643 \u0623\u0648 \u0627\u062D\u0630\u0641 \u062D\u0633\u0627\u0628\u0643.",
    manageMyData: "\u0625\u062F\u0627\u0631\u0629 \u0628\u064A\u0627\u0646\u0627\u062A\u064A",
    availableFunds: "\u0627\u0644\u0631\u0635\u064A\u062F \u0627\u0644\u0645\u062A\u0627\u062D",
    lowBalance: "\u0623\u0639\u062F \u0634\u062D\u0646 \u062D\u0633\u0627\u0628\u0643 \u0644\u0644\u062D\u0641\u0627\u0638 \u0639\u0644\u0649 \u0627\u0633\u062A\u0645\u0631\u0627\u0631 \u0627\u0644\u062E\u062F\u0645\u0629.",
    authProvider: "\u0645\u0632\u0648\u062F \u0627\u0644\u0645\u0635\u0627\u062F\u0642\u0629",
    publicKeyHex: "\u0627\u0644\u0645\u0641\u062A\u0627\u062D \u0627\u0644\u0639\u0627\u0645 (Hex)",
    emailsDescription: "\u0623\u062F\u0631 \u0639\u0646\u0627\u0648\u064A\u0646 \u0628\u0631\u064A\u062F\u0643 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0644\u062A\u0646\u0628\u064A\u0647\u0627\u062A \u0627\u0644\u0623\u0645\u0627\u0646 \u0648\u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A.",
    addEmail: "\u0625\u0636\u0627\u0641\u0629 \u0628\u0631\u064A\u062F \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A",
    sessionHistory: "\u0633\u062C\u0644 \u0627\u0644\u062C\u0644\u0633\u0627\u062A",
    viewLog: "\u0639\u0631\u0636",
    providerSubtitle: {
      NOSTR: "\u062A\u0633\u062C\u064A\u0644 \u062F\u062E\u0648\u0644 \u0644\u0627\u0645\u0631\u0643\u0632\u064A",
      GOOGLE: "Google OAuth",
      GITHUB: "GitHub OAuth",
      MICROSOFT: "Microsoft OAuth",
      EMAIL_MAGIC_LINK: "\u0628\u0631\u064A\u062F \u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0628\u062F\u0648\u0646 \u0643\u0644\u0645\u0629 \u0645\u0631\u0648\u0631",
      MANUAL: "\u0632\u0648\u062C \u0645\u0641\u0627\u062A\u064A\u062D \u0630\u0627\u062A\u064A \u0627\u0644\u0625\u062F\u0627\u0631\u0629",
      PRIVATE_KEY: "\u062A\u0633\u062C\u064A\u0644 \u062F\u062E\u0648\u0644 \u0628\u0645\u0641\u062A\u0627\u062D \u062E\u0627\u0635"
    }
  },
  emails: {
    accountEmail: "\u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0644\u0644\u062D\u0633\u0627\u0628",
    accountEmailDescription: "\u0647\u0630\u0627 \u0647\u0648 \u0628\u0631\u064A\u062F\u0643 \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645 \u0644\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0648\u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062A\u060C \u0628\u0645\u0627 \u0641\u064A \u0630\u0644\u0643 \u062A\u0646\u0628\u064A\u0647\u0627\u062A \u0627\u0644\u0631\u0635\u064A\u062F \u0627\u0644\u0645\u0646\u062E\u0641\u0636.",
    makePrimary: "\u062A\u0639\u064A\u064A\u0646 \u0643\u0623\u0633\u0627\u0633\u064A",
    resendVerification: "\u0625\u0639\u0627\u062F\u0629 \u0625\u0631\u0633\u0627\u0644 \u0627\u0644\u062A\u062D\u0642\u0642",
    verificationSent: "\u062A\u0645 \u0625\u0631\u0633\u0627\u0644 \u0628\u0631\u064A\u062F \u0627\u0644\u062A\u062D\u0642\u0642. \u062A\u062D\u0642\u0642 \u0645\u0646 \u0635\u0646\u062F\u0648\u0642 \u0627\u0644\u0648\u0627\u0631\u062F.",
    accountEmailUpdated: "\u062A\u0645 \u062A\u062D\u062F\u064A\u062B \u0627\u0644\u0628\u0631\u064A\u062F \u0627\u0644\u0625\u0644\u0643\u062A\u0631\u0648\u0646\u064A \u0644\u0644\u062D\u0633\u0627\u0628 \u0628\u0646\u062C\u0627\u062D."
  },
  glob_footer: {
    product: "\u0627\u0644\u0645\u0646\u062A\u062C",
    account: "\u0627\u0644\u062D\u0633\u0627\u0628"
  },
  glob_billing: {
    recentAccountMovements: "\u062D\u0631\u0643\u0627\u062A \u0627\u0644\u062D\u0633\u0627\u0628 \u0627\u0644\u0623\u062E\u064A\u0631\u0629",
    loadingAccountMovements: "\u062C\u0627\u0631\u064A \u062A\u062D\u0645\u064A\u0644 \u062D\u0631\u0643\u0627\u062A \u0627\u0644\u062D\u0633\u0627\u0628...",
    noAccountMovements: "\u0644\u0645 \u064A\u062A\u0645 \u0627\u0644\u0639\u062B\u0648\u0631 \u0639\u0644\u0649 \u062D\u0631\u0643\u0627\u062A \u062D\u0633\u0627\u0628.",
    date: "\u0627\u0644\u062A\u0627\u0631\u064A\u062E",
    amount: "\u0627\u0644\u0645\u0628\u0644\u063A",
    type: "\u0627\u0644\u0646\u0648\u0639",
    features: "\u0627\u0644\u0645\u064A\u0632\u0627\u062A",
    credit: "\u062F\u0627\u0626\u0646",
    debit: "\u0645\u062F\u064A\u0646",
    billingOverview: "\u0646\u0638\u0631\u0629 \u0639\u0627\u0645\u0629 \u0639\u0644\u0649 \u0627\u0644\u0641\u0648\u062A\u0631\u0629",
    payAsYouGoCredits: "\u0623\u0631\u0635\u062F\u0629 \u0627\u0644\u062F\u0641\u0639 \u062D\u0633\u0628 \u0627\u0644\u0627\u0633\u062A\u062E\u062F\u0627\u0645",
    noBalanceFound: "\u0644\u0645 \u064A\u062A\u0645 \u0627\u0644\u0639\u062B\u0648\u0631 \u0639\u0644\u0649 \u0631\u0635\u064A\u062F",
    addCredits: "\u0625\u0636\u0627\u0641\u0629 \u0623\u0631\u0635\u062F\u0629",
    hybridBillingModel: "\u0646\u0645\u0648\u0630\u062C \u0627\u0644\u0641\u0648\u062A\u0631\u0629 \u0627\u0644\u0645\u062E\u062A\u0644\u0637",
    hybridBillingDescription: "\u0639\u0646\u062F\u0645\u0627 \u062A\u0646\u0641\u062F \u062D\u0635\u0635 \u0627\u0644\u0627\u0634\u062A\u0631\u0627\u0643\u060C \u064A\u062A\u062D\u0648\u0644 \u0627\u0644\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u062A\u0644\u0642\u0627\u0626\u064A\u0627\u064B \u0625\u0644\u0649 \u0623\u0631\u0635\u062F\u0629 \u0627\u0644\u062F\u0641\u0639 \u062D\u0633\u0628 \u0627\u0644\u0627\u0633\u062A\u062E\u062F\u0627\u0645. \u0644\u0646 \u062A\u0641\u0642\u062F \u0627\u0644\u062E\u062F\u0645\u0629 \u0623\u0628\u062F\u0627\u064B - \u0641\u0642\u0637 \u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0625\u0636\u0627\u0641\u064A \u064A\u062A\u062C\u0627\u0648\u0632 \u062D\u062F\u0648\u062F \u062E\u0637\u062A\u0643.",
    yourReferralCode: "\u0631\u0645\u0632 \u0627\u0644\u0625\u062D\u0627\u0644\u0629 \u0627\u0644\u062E\u0627\u0635 \u0628\u0643",
    shareDirectLink: "\u0623\u0648 \u0634\u0627\u0631\u0643 \u0631\u0627\u0628\u0637 \u0627\u0644\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u0645\u0628\u0627\u0634\u0631 \u0647\u0630\u0627:",
    referralDescription: "\u0639\u0646\u062F\u0645\u0627 \u064A\u0633\u062C\u0644 \u0634\u062E\u0635 \u0645\u0627 \u0628\u0627\u0633\u062A\u062E\u062F\u0627\u0645 \u0631\u0627\u0628\u0637 \u0627\u0644\u0625\u062D\u0627\u0644\u0629 \u0627\u0644\u062E\u0627\u0635 \u0628\u0643 \u0648\u064A\u0642\u0648\u0645 \u0628\u0623\u0648\u0644 \u062F\u0641\u0639\u0629 \u0627\u0634\u062A\u0631\u0627\u0643 \u0623\u0648 \u0625\u064A\u062F\u0627\u0639\u060C \u0633\u062A\u062D\u0635\u0644 \u0639\u0644\u0649 10% \u0645\u0646 \u0647\u0630\u0627 \u0627\u0644\u0645\u0628\u0644\u063A\u060C \u0648\u0633\u064A\u062D\u0635\u0644\u0648\u0646 \u0639\u0644\u0649 \u0645\u0643\u0627\u0641\u0623\u0629 10%.",
    copy: "\u0646\u0633\u062E",
    copied: "\u062A\u0645 \u0627\u0644\u0646\u0633\u062E",
    copyLink: "\u0646\u0633\u062E \u0627\u0644\u0631\u0627\u0628\u0637",
    linkCopied: "\u062A\u0645 \u0646\u0633\u062E \u0627\u0644\u0631\u0627\u0628\u0637",
    referralStatistics: "\u0625\u062D\u0635\u0627\u0626\u064A\u0627\u062A \u0627\u0644\u0625\u062D\u0627\u0644\u0629",
    totalReferrals: "\u0625\u062C\u0645\u0627\u0644\u064A \u0627\u0644\u0625\u062D\u0627\u0644\u0627\u062A",
    totalEarnings: "\u0625\u062C\u0645\u0627\u0644\u064A \u0627\u0644\u0623\u0631\u0628\u0627\u062D",
    pendingEarnings: "\u0627\u0644\u0623\u0631\u0628\u0627\u062D \u0627\u0644\u0645\u0639\u0644\u0642\u0629",
    recentReferrals: "\u0627\u0644\u0625\u062D\u0627\u0644\u0627\u062A \u0627\u0644\u0623\u062E\u064A\u0631\u0629",
    firstPayment: "\u0627\u0644\u062F\u0641\u0639\u0629 \u0627\u0644\u0623\u0648\u0644\u0649",
    bonus: "\u0645\u0643\u0627\u0641\u0623\u0629",
    status: "\u0627\u0644\u062D\u0627\u0644\u0629",
    processed: "\u062A\u0645\u062A \u0627\u0644\u0645\u0639\u0627\u0644\u062C\u0629",
    pending: "\u0645\u0639\u0644\u0642",
    bonusPaid: "\u062A\u0645 \u062F\u0641\u0639 \u0627\u0644\u0645\u0643\u0627\u0641\u0623\u0629",
    awaitingBonus: "\u0641\u064A \u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0644\u0645\u0643\u0627\u0641\u0623\u0629",
    pendingPayment: "\u0627\u0644\u062F\u0641\u0639 \u0645\u0639\u0644\u0642",
    noReferrals: "\u0644\u0627 \u062A\u0648\u062C\u062F \u0625\u062D\u0627\u0644\u0627\u062A \u0628\u0639\u062F. \u0634\u0627\u0631\u0643 \u0631\u0645\u0632 \u0627\u0644\u0625\u062D\u0627\u0644\u0629 \u0627\u0644\u062E\u0627\u0635 \u0628\u0643 \u0644\u0628\u062F\u0621 \u0643\u0633\u0628 \u0627\u0644\u0639\u0645\u0648\u0644\u0627\u062A!",
    loadingReferralHistory: "\u062C\u0627\u0631\u064A \u062A\u062D\u0645\u064A\u0644 \u062A\u0627\u0631\u064A\u062E \u0627\u0644\u0625\u062D\u0627\u0644\u0627\u062A...",
    loadingReferralInfo: "\u062C\u0627\u0631\u064A \u062A\u062D\u0645\u064A\u0644 \u0645\u0639\u0644\u0648\u0645\u0627\u062A \u0627\u0644\u0625\u062D\u0627\u0644\u0629...",
    noActiveSubscription: "\u0644\u0627 \u064A\u0648\u062C\u062F \u0627\u0634\u062A\u0631\u0627\u0643 \u0646\u0634\u0637",
    noActiveSubscriptionDescription: "\u0644\u064A\u0633 \u0644\u062F\u064A\u0643 \u0627\u0634\u062A\u0631\u0627\u0643 \u0646\u0634\u0637. \u0623\u0646\u062A \u062A\u0633\u062A\u062E\u062F\u0645 \u0646\u0645\u0648\u0630\u062C \u0627\u0644\u0641\u0648\u062A\u0631\u0629 \u0628\u0627\u0644\u062F\u0641\u0639 \u062D\u0633\u0628 \u0627\u0644\u0627\u0633\u062A\u062E\u062F\u0627\u0645.",
    currentSubscription: "\u0627\u0644\u0627\u0634\u062A\u0631\u0627\u0643 \u0627\u0644\u062D\u0627\u0644\u064A",
    plan: "\u0627\u0644\u062E\u0637\u0629",
    periodStarted: "\u0628\u062F\u0627\u064A\u0629 \u0627\u0644\u0641\u062A\u0631\u0629",
    nextBilling: "\u0627\u0644\u0641\u0627\u062A\u0648\u0631\u0629 \u0627\u0644\u062A\u0627\u0644\u064A\u0629",
    nextCycle: "\u0627\u0644\u062F\u0648\u0631\u0629 \u0627\u0644\u062A\u0627\u0644\u064A\u0629",
    free: "\u0645\u062C\u0627\u0646\u064A",
    unlimited: "\u063A\u064A\u0631 \u0645\u062D\u062F\u0648\u062F",
    manageViaStripe: "\u0625\u062F\u0627\u0631\u0629 \u0639\u0628\u0631 Stripe",
    cancel: "\u0625\u0644\u063A\u0627\u0621"
  },
  securityLog: {
    title: "\u0633\u062C\u0644 \u0627\u0644\u0623\u0645\u0627\u0646",
    description: "\u0627\u0644\u0646\u0634\u0627\u0637 \u0627\u0644\u0623\u062E\u064A\u0631 \u0627\u0644\u0645\u062A\u0639\u0644\u0642 \u0628\u0623\u0645\u0627\u0646 \u062D\u0633\u0627\u0628\u0643. \u064A\u062A\u0645 \u0625\u0628\u0631\u0627\u0632 \u0627\u0644\u0623\u062D\u062F\u0627\u062B \u0627\u0644\u0641\u0627\u0634\u0644\u0629.",
    when: "\u0627\u0644\u0648\u0642\u062A",
    event: "\u0627\u0644\u062D\u062F\u062B",
    location: "\u0627\u0644\u0645\u0648\u0642\u0639",
    device: "\u0627\u0644\u062C\u0647\u0627\u0632",
    status: "\u0627\u0644\u062D\u0627\u0644\u0629",
    ok: "\u0646\u0627\u062C\u062D",
    failed: "\u0641\u0634\u0644",
    empty: "\u0644\u0627 \u064A\u0648\u062C\u062F \u0646\u0634\u0627\u0637 \u0645\u0633\u062C\u0644 \u0628\u0639\u062F.",
    errorTitle: "\u062A\u0639\u0630\u0631 \u062A\u062D\u0645\u064A\u0644 \u0633\u062C\u0644 \u0627\u0644\u0623\u0645\u0627\u0646"
  }
};

// src/i18n/locales/de.json
var de_default = {
  glob_navigation: {
    home: "Startseite",
    pricing: "Preise",
    docs: "Dokumentation",
    news: "Neuigkeiten",
    more: "Mehr",
    login: "Anmelden",
    signup: "Registrieren",
    getStarted: "Jetzt starten",
    forms: "Formulare",
    integrations: "Integrationen",
    messages: "Nachrichten",
    profile: "Profil",
    support: "Support",
    logout: "Abmelden",
    terms: "Nutzungsbedingungen",
    billing: "Abrechnung",
    usageAndCredits: "Nutzung & Guthaben",
    subscriptions: "Abonnements",
    referrals: "Empfehlungen",
    apiKeys: "API-Schl\xFCssel",
    securityLog: "Sicherheitsprotokoll",
    chat: "Chat",
    sectionWorkspace: "Arbeitsbereich",
    sectionAccount: "Konto",
    sectionHelp: "Hilfe"
  },
  auth: {
    login: "Anmelden",
    signup: "Registrieren",
    logout: "Abmelden",
    step1: "Schritt 1",
    step2: "Schritt 2",
    authenticate: "Authentifizieren",
    enterKey: "Kennung",
    newHere: "Neu hier? ",
    howToSignup: "Registrierungs-Anleitung",
    alreadyAccount: "Falls Sie noch kein Konto haben, wird bei Ihrer ersten Anmeldung automatisch eines erstellt. ",
    emailSection: "E-Mail-Link",
    googleSection: "Google",
    githubSection: "GitHub",
    microsoftSection: "Microsoft",
    nostrSection: "Nostr Browser-Plugin",
    signupDescription: "Lassen Sie sich einen Anmelde-Link per E-Mail senden. ",
    googleDescription: "Melden Sie sich mit Ihrem Google-Konto an. ",
    githubDescription: "Melden Sie sich mit Ihrem GitHub-Konto an. ",
    microsoftDescription: "Melden Sie sich mit Ihrem Microsoft-Konto an. ",
    nostrDescription: "Registrieren Sie sich mit einem unterst\xFCtzten ",
    browserPlugin: "Browser-Plugin",
    signupLoginEmail: "Mit E-Mail anmelden.",
    signupLoginGoogle: "Mit Google anmelden",
    signupLoginGithub: "Mit GitHub anmelden",
    signupLoginMicrosoft: "Mit Microsoft anmelden",
    signupLoginNostr: "Mit Nostr anmelden",
    privacyPolicy: "Datenschutz",
    termsOfService: "Nutzungsbedingungen"
  },
  login: {
    welcomeBack: "Willkommen zur\xFCck",
    cardSubtitle: "Fahren Sie mit einem sozialen Anbieter fort oder geben Sie Ihre E-Mail-Adresse ein.",
    orDivider: "ODER",
    loginWithEmail: "Mit E-Mail anmelden",
    magicLinkInfo: "Wir senden Ihnen einen Magic-Link an Ihren Posteingang.",
    publicKeyRequired: "\xD6ffentlicher Schl\xFCssel ist erforderlich",
    invalidEmail: "Ung\xFCltige E-Mail",
    getPublicKey: "\xD6ffentlichen Schl\xFCssel aus Erweiterung",
    emailTab: "E-Mail",
    googleTab: "Google",
    githubTab: "GitHub",
    microsoftTab: "Microsoft",
    nostrTab: "Nostr",
    oidcSsoTab: "GoFranz SSO",
    emailDescription: "Mit E-Mail anmelden.",
    googleDescription: "Mit Google anmelden",
    nostrPluginDescription: "Mit Nos2x oder \xE4hnlichem anmelden.",
    nostrKeyDescription: "Mit Ihrem privaten Schl\xFCssel anmelden.",
    nostrOtherDescription: "Signatur auf anderem Weg bereitstellen.",
    loadedPublicKey: "\xD6ffentlicher Schl\xFCssel geladen",
    noKeyLoaded: "Kein Schl\xFCssel geladen...",
    enterPublicKey: "Geben Sie Ihren \xF6ffentlichen Schl\xFCssel ein",
    publicKeyPlaceholder: "32b72e6432f7282135cf124a8...",
    emailLabel: "E-Mail-Adresse",
    emailPlaceholder: "ihre-email@anbieter.com",
    enterCode: "Geben Sie den Code aus der E-Mail ein",
    codePlaceholder: "6fd7e67e-6f42-4354-b8f8-775bc1e52a50",
    privateKeyLabel: "Geben Sie Ihren privaten Schl\xFCssel ein",
    challengeResponseLabel: "Geben Sie Ihre Challenge-Antwort ein",
    emailInstructions: "Klicken Sie den Link in der E-Mail oder kopieren Sie den Code und f\xFCgen Sie ihn hier ein.",
    nostrInstructions: "Ihre Nostr-Erweiterung sollte Sie auffordern, ein Authentifizierungs-Event zu signieren.",
    challenge: "Challenge: ",
    loginWithExtension: "Fortfahren...",
    redirectingGoogle: "Weiterleitung zu Google...",
    redirectingGithub: "Weiterleitung zu GitHub...",
    redirectingMicrosoft: "Weiterleitung zu Microsoft...",
    redirectingOidc: "Weiterleitung zu GoFranz SSO...",
    githubNoChallenge: "GitHub-Challenge konnte nicht abgerufen werden",
    microsoftNoChallenge: "Microsoft-Challenge konnte nicht abgerufen werden",
    oidcNoChallenge: "GoFranz SSO-Challenge konnte nicht abgerufen werden",
    checkYourEmail: "\xDCberpr\xFCfen Sie Ihre E-Mail f\xFCr den Login-Link",
    emailSent: "Wir haben einen Login-Link an Ihre E-Mail gesendet",
    clickLinkToLogin: "Klicken Sie auf den Link in der E-Mail, um die Anmeldung abzuschlie\xDFen",
    wrongEmail: "Falsche E-Mail? Versuchen Sie es erneut",
    errorNotPrimaryEmail: "Diese E-Mail ist nicht Ihre prim\xE4re Konto-E-Mail.",
    errorDifferentAuthMethod: "Diese E-Mail ist mit einer anderen Authentifizierungsmethode registriert",
    errorGoogleEmailNotVerified: "Google-E-Mail nicht verifiziert",
    errorInvalidAuthMethodGoogle: "Dieses Konto verwendet Google-Anmeldung",
    errorInvalidAuthMethodGithub: "Dieses Konto verwendet GitHub-Anmeldung",
    errorInvalidAuthMethodMicrosoft: "Dieses Konto verwendet Microsoft-Anmeldung",
    errorInvalidAuthMethodNostr: "Dieses Konto verwendet Nostr-Anmeldung",
    errorAuthMethodCollision: "Diese E-Mail-Adresse ist bereits \xFCber {{method}} registriert. Bitte melden Sie sich stattdessen mit dieser Methode an.",
    errorAuthMethodCollisionGeneric: "Diese E-Mail-Adresse ist bereits mit einer anderen Anmeldemethode registriert. Bitte verwenden Sie Ihre urspr\xFCngliche Methode.",
    errorInvalidNostrSignature: "Ung\xFCltige Nostr-Signatur",
    errorNostrKeyMismatch: "Nostr-Schl\xFCssel stimmt nicht \xFCberein",
    errorOAuthFailed: "OAuth-Authentifizierung fehlgeschlagen",
    errorTokenExchange: "Anmeldung fehlgeschlagen. Bitte erneut versuchen.",
    errorCodeExpired: "Anmeldesitzung abgelaufen. Bitte erneut einloggen.",
    invalidLoginResponse: "Ung\xFCltige Anmeldeantwort. Bitte erneut einloggen.",
    errorRateLimited: "Zu viele Anmeldeversuche. Bitte {{seconds}} Sekunden warten und erneut versuchen.",
    processing: "Anmeldung wird verarbeitet...",
    somethingWrong: "Etwas ist schief gelaufen",
    invalidMagicLink: "Ung\xFCltiger Magic-Link",
    noIdProvided: "Keine Sitzungs-ID angegeben",
    noChallengeProvided: "Keine Challenge angegeben",
    back: "zur\xFCck",
    cancel: "Abbrechen",
    submit: "Absenden",
    twoFactorAuth: "Zwei-Faktor-Authentifizierung",
    twoFactorRequired: "Verifizierung erforderlich",
    enterTwoFactorCode: "Geben Sie den 6-stelligen Code aus Ihrer Authentifizierungs-App oder einen Wiederherstellungscode ein.",
    verify: "\xDCberpr\xFCfen",
    useRecoveryCode: "Sie k\xF6nnen auch einen Wiederherstellungscode verwenden",
    invalid2FACode: "Ung\xFCltiger Verifizierungscode. Bitte versuchen Sie es erneut.",
    tooMany2FAAttempts: "Zu viele fehlgeschlagene Versuche. Bitte beginnen Sie erneut.",
    invalid2FASession: "Ung\xFCltige oder abgelaufene 2FA-Sitzung.",
    backToLogin: "Zur\xFCck zur Anmeldung"
  },
  glob_buttons: {
    save: "Speichern",
    cancel: "Abbrechen",
    submit: "Absenden",
    create: "Erstellen",
    update: "Aktualisieren",
    updateUnsaved: "Aktualisieren (Ungespeicherte \xC4nderungen)",
    delete: "L\xF6schen",
    calculate: "Berechnen",
    login: "Anmelden",
    back: "zur\xFCck"
  },
  support: {
    title: "Support",
    getHelp: "Hilfe bei {{serviceName}} erhalten",
    description: "Haben Sie eine Frage oder ben\xF6tigen Unterst\xFCtzung? Wir sind hier, um zu helfen! F\xFCllen Sie das folgende Formular aus und wir melden uns so schnell wie m\xF6glich bei Ihnen.",
    thankYou: "Vielen Dank!",
    successMessage: "Ihre Support-Anfrage wurde erfolgreich \xFCbermittelt. Wir melden uns so schnell wie m\xF6glich bei Ihnen.",
    submitAnother: "Weitere Anfrage stellen",
    successAlert: "Erfolg!",
    successAlertMessage: "Ihre Support-Anfrage wurde erfolgreich \xFCbermittelt!",
    nameLabel: "Name (Optional)",
    namePlaceholder: "Ihr vollst\xE4ndiger Name",
    emailLabel: "E-Mail",
    emailPlaceholder: "ihre.email@beispiel.com",
    loggedInEmail: "Ihre angemeldete E-Mail wird verwendet",
    subjectLabel: "Betreff",
    subjectPlaceholder: "W\xE4hlen Sie die Art Ihrer Anfrage",
    messageLabel: "Nachricht",
    messagePlaceholder: "Bitte geben Sie Details zu Ihrer Frage oder Ihrem Problem an...",
    formInfo: "Dieses Formular enth\xE4lt Ihre Anmeldemethode (",
    formInfoEnd: ") und Benutzerkennung, um Ihnen besser helfen zu k\xF6nnen.",
    submitting: "Wird \xFCbermittelt...",
    submitButton: "Support-Anfrage senden",
    types: {
      general: "Allgemeine Frage",
      technical: "Technische Frage",
      feature: "Feature-Anfrage",
      bug: "Fehler melden"
    }
  },
  colorScheme: {
    lightMode: "Heller Modus (f\xFCr dunkel klicken)",
    darkMode: "Dunkler Modus (f\xFCr automatisch klicken)",
    autoMode: "Automatischer Modus (f\xFCr hell klicken)",
    toggle: "Farbschema umschalten"
  },
  privacy: {
    title: "Datenschutz",
    backHome: "zur Startseite",
    gdprCompliant: "\u{1F1E9}\u{1F1EA} DSGVO-konform. Server befinden sich in Deutschland. "
  },
  terms: {
    title: "Nutzungsbedingungen"
  },
  news: {
    title: "Aus unserem Blog",
    loading: "L\xE4dt ...",
    rssDescription: "Sie k\xF6nnen diesen Atom-Feed hier abonnieren "
  },
  profile: {
    balance: "Guthaben",
    addMoney: "Geld hinzuf\xFCgen",
    noBalanceFound: "Kein Guthaben gefunden.",
    depositSuccess: "Einzahlung erfolgreich",
    depositSuccessMessage: "Ihre Einzahlung war erfolgreich. Kartenzahlungen sollten innerhalb von Sekunden verbucht werden.",
    wirePaymentInfo: "Per \xDCberweisung oder Bitcoin zahlen? (\u20AC100+): ",
    personalInformation: "Pers\xF6nliche Informationen",
    personalInfoMessage: "Wir wissen eigentlich nicht sehr viel \xFCber Sie.",
    personalInfoContact: "Senden Sie eine E-Mail an ",
    personalInfoGreeting: " um Hallo zu sagen.",
    emails: "E-Mails",
    loginInformation: "Anmelde-Informationen",
    currentlyLoggedIn: "Sie sind derzeit angemeldet mit ",
    saveKeysAlert: "Speichern Sie diese Schl\xFCssel in Ihrem Passwort-Manager oder an einem sicheren Ort. Wir k\xF6nnen sie nicht wiederherstellen. Sie k\xF6nnen sie verwenden, um von anderen Ger\xE4ten auf den Service zuzugreifen und sich bei unseren anderen Services anzumelden.",
    downloadKeys: "Schl\xFCssel herunterladen",
    publicKey: "\xD6ffentlicher Schl\xFCssel",
    publicKeyDescription: "Ihr \xF6ffentlicher Schl\xFCssel identifiziert Sie; wir speichern ihn auf unserem Server.",
    privateKey: "Privater Schl\xFCssel",
    privateKeyDescription: "Ihr privater Schl\xFCssel wird f\xFCr die Anmeldung verwendet und verl\xE4sst niemals Ihren Computer.",
    security: "Sicherheit",
    dataPrivacy: "Daten & Datenschutz",
    dataPrivacyDescription: "Exportieren Sie Ihre Daten oder l\xF6schen Sie Ihr Konto.",
    manageMyData: "Meine Daten verwalten",
    availableFunds: "Verf\xFCgbares Guthaben",
    lowBalance: "Laden Sie Ihr Konto auf, damit alles weiterl\xE4uft.",
    authProvider: "Authentifizierungsanbieter",
    publicKeyHex: "\xD6ffentlicher Schl\xFCssel (Hex)",
    emailsDescription: "Verwalten Sie Ihre E-Mail-Adressen f\xFCr Sicherheitswarnungen und Benachrichtigungen.",
    addEmail: "E-Mail hinzuf\xFCgen",
    sessionHistory: "Sitzungsverlauf",
    viewLog: "Ansehen",
    providerSubtitle: {
      NOSTR: "Dezentraler Login",
      GOOGLE: "Google OAuth",
      GITHUB: "GitHub OAuth",
      MICROSOFT: "Microsoft OAuth",
      EMAIL_MAGIC_LINK: "Passwortlose E-Mail",
      MANUAL: "Selbstverwaltetes Schl\xFCsselpaar",
      PRIVATE_KEY: "Login mit privatem Schl\xFCssel"
    }
  },
  common: {
    copy: "Kopieren",
    copied: "Kopiert!",
    done: "Fertig",
    cancel: "Abbrechen"
  },
  twoFactor: {
    loading: "2FA-Status wird geladen...",
    enabled: "Aktiviert",
    disabled: "Deaktiviert",
    description: "Erh\xF6hen Sie die Sicherheit Ihres Kontos durch einen Verifizierungscode zus\xE4tzlich zu Ihrem Passwort.",
    enable: "Zwei-Faktor-Authentifizierung aktivieren",
    setupTitle: "Zwei-Faktor-Authentifizierung einrichten",
    scanQRCode: "Scannen Sie diesen QR-Code mit Ihrer Authentifizierungs-App (z.B. Google Authenticator, Authy).",
    cantScanQR: "QR-Code nicht scannen? Geben Sie diesen geheimen Schl\xFCssel manuell ein:",
    next: "Weiter",
    verifySetup: "Einrichtung \xFCberpr\xFCfen",
    enterCodeFromApp: "Geben Sie den 6-stelligen Code aus Ihrer Authentifizierungs-App ein, um die Einrichtung zu best\xE4tigen.",
    recoveryCodes: "Wiederherstellungscodes",
    saveRecoveryCodes: "Speichern Sie diese Wiederherstellungscodes an einem sicheren Ort. Sie k\xF6nnen verwendet werden, um auf Ihr Konto zuzugreifen, wenn Sie den Zugang zu Ihrer Authentifizierungs-App verlieren.",
    copyAllCodes: "Alle Codes kopieren",
    recoveryCodesRemaining: "Verbleibende Wiederherstellungscodes",
    regenerateRecoveryCodes: "Wiederherstellungscodes neu generieren",
    regenerateWarning: "Dies macht alle bestehenden Wiederherstellungscodes ung\xFCltig und generiert neue.",
    enterCodeToRegenerate: "Geben Sie einen Code aus Ihrer Authentifizierungs-App ein, um die Wiederherstellungscodes neu zu generieren.",
    regenerate: "Neu generieren",
    disable2FA: "Zwei-Faktor-Authentifizierung deaktivieren",
    disableWarning: "Das Deaktivieren von 2FA macht Ihr Konto weniger sicher. Sie ben\xF6tigen nur noch Ihr Passwort zum Anmelden.",
    enterCodeToDisable: "Geben Sie einen Code aus Ihrer Authentifizierungs-App oder einen Wiederherstellungscode ein, um 2FA zu deaktivieren.",
    disable: "Deaktivieren"
  },
  emails: {
    accountEmail: "Konto-E-Mail",
    accountEmailDescription: "Dies ist Ihre Konto-E-Mail, die f\xFCr die Anmeldung und Benachrichtigungen verwendet wird, einschlie\xDFlich Benachrichtigungen \xFCber niedrige Guthaben.",
    makePrimary: "Als Prim\xE4r festlegen",
    resendVerification: "Best\xE4tigung erneut senden",
    verificationSent: "Best\xE4tigungs-E-Mail gesendet. \xDCberpr\xFCfen Sie Ihren Posteingang.",
    accountEmailUpdated: "Konto-E-Mail erfolgreich aktualisiert."
  },
  countries: {
    AD: "Andorra",
    AE: "Vereinigte Arabische Emirate",
    AF: "Afghanistan",
    AG: "Antigua und Barbuda",
    AI: "Anguilla",
    AL: "Albanien",
    AM: "Armenien",
    AO: "Angola",
    AQ: "Antarktis",
    AR: "Argentinien",
    AS: "Amerikanisch-Samoa",
    AT: "\xD6sterreich",
    AU: "Australien",
    AW: "Aruba",
    AX: "\xC5land-Inseln",
    AZ: "Aserbaidschan",
    BA: "Bosnien und Herzegowina",
    BB: "Barbados",
    BD: "Bangladesch",
    BE: "Belgien",
    BF: "Burkina Faso",
    BG: "Bulgarien",
    BH: "Bahrain",
    BI: "Burundi",
    BJ: "Benin",
    BL: "Saint-Barth\xE9lemy",
    BM: "Bermuda",
    BN: "Brunei",
    BO: "Bolivien",
    BQ: "Karibische Niederlande",
    BR: "Brasilien",
    BS: "Bahamas",
    BT: "Bhutan",
    BV: "Bouvetinsel",
    BW: "Botswana",
    BY: "Belarus",
    BZ: "Belize",
    CA: "Kanada",
    CC: "Kokosinseln",
    CD: "Demokratische Republik Kongo",
    CF: "Zentralafrikanische Republik",
    CG: "Republik Kongo",
    CH: "Schweiz",
    CI: "Elfenbeink\xFCste",
    CK: "Cookinseln",
    CL: "Chile",
    CM: "Kamerun",
    CN: "China",
    CO: "Kolumbien",
    CR: "Costa Rica",
    CU: "Kuba",
    CV: "Kap Verde",
    CW: "Cura\xE7ao",
    CX: "Weihnachtsinsel",
    CY: "Zypern",
    CZ: "Tschechische Republik",
    DE: "Deutschland",
    DJ: "Dschibuti",
    DK: "D\xE4nemark",
    DM: "Dominica",
    DO: "Dominikanische Republik",
    DZ: "Algerien",
    EC: "Ecuador",
    EE: "Estland",
    EG: "\xC4gypten",
    EH: "Westsahara",
    ER: "Eritrea",
    ES: "Spanien",
    ET: "\xC4thiopien",
    FI: "Finnland",
    FJ: "Fidschi",
    FK: "Falklandinseln",
    FM: "Mikronesien",
    FO: "F\xE4r\xF6er",
    FR: "Frankreich",
    GA: "Gabun",
    GB: "Vereinigtes K\xF6nigreich",
    GD: "Grenada",
    GE: "Georgien",
    GF: "Franz\xF6sisch-Guayana",
    GG: "Guernsey",
    GH: "Ghana",
    GI: "Gibraltar",
    GL: "Gr\xF6nland",
    GM: "Gambia",
    GN: "Guinea",
    GP: "Guadeloupe",
    GQ: "\xC4quatorialguinea",
    GR: "Griechenland",
    GS: "S\xFCdgeorgien und die S\xFCdlichen Sandwichinseln",
    GT: "Guatemala",
    GU: "Guam",
    GW: "Guinea-Bissau",
    GY: "Guyana",
    HK: "Hongkong",
    HM: "Heard und McDonaldinseln",
    HN: "Honduras",
    HR: "Kroatien",
    HT: "Haiti",
    HU: "Ungarn",
    ID: "Indonesien",
    IE: "Irland",
    IL: "Israel",
    IM: "Isle of Man",
    IN: "Indien",
    IO: "Britisches Territorium im Indischen Ozean",
    IQ: "Irak",
    IR: "Iran",
    IS: "Island",
    IT: "Italien",
    JE: "Jersey",
    JM: "Jamaika",
    JO: "Jordanien",
    JP: "Japan",
    KE: "Kenia",
    KG: "Kirgisistan",
    KH: "Kambodscha",
    KI: "Kiribati",
    KM: "Komoren",
    KN: "St. Kitts und Nevis",
    KP: "Nordkorea",
    KR: "S\xFCdkorea",
    KW: "Kuwait",
    KY: "Kaimaninseln",
    KZ: "Kasachstan",
    LA: "Laos",
    LB: "Libanon",
    LC: "St. Lucia",
    LI: "Liechtenstein",
    LK: "Sri Lanka",
    LR: "Liberia",
    LS: "Lesotho",
    LT: "Litauen",
    LU: "Luxemburg",
    LV: "Lettland",
    LY: "Libyen",
    MA: "Marokko",
    MC: "Monaco",
    MD: "Moldau",
    ME: "Montenegro",
    MF: "Saint-Martin",
    MG: "Madagaskar",
    MH: "Marshallinseln",
    MK: "Nordmazedonien",
    ML: "Mali",
    MM: "Myanmar",
    MN: "Mongolei",
    MO: "Macao",
    MP: "N\xF6rdliche Marianen",
    MQ: "Martinique",
    MR: "Mauretanien",
    MS: "Montserrat",
    MT: "Malta",
    MU: "Mauritius",
    MV: "Malediven",
    MW: "Malawi",
    MX: "Mexiko",
    MY: "Malaysia",
    MZ: "Mosambik",
    NA: "Namibia",
    NC: "Neukaledonien",
    NE: "Niger",
    NF: "Norfolkinsel",
    NG: "Nigeria",
    NI: "Nicaragua",
    NL: "Niederlande",
    NO: "Norwegen",
    NP: "Nepal",
    NR: "Nauru",
    NU: "Niue",
    NZ: "Neuseeland",
    OM: "Oman",
    PA: "Panama",
    PE: "Peru",
    PF: "Franz\xF6sisch-Polynesien",
    PG: "Papua-Neuguinea",
    PH: "Philippinen",
    PK: "Pakistan",
    PL: "Polen",
    PM: "St. Pierre und Miquelon",
    PN: "Pitcairninseln",
    PR: "Puerto Rico",
    PS: "Pal\xE4stina",
    PT: "Portugal",
    PW: "Palau",
    PY: "Paraguay",
    QA: "Katar",
    RE: "R\xE9union",
    RO: "Rum\xE4nien",
    RS: "Serbien",
    RU: "Russland",
    RW: "Ruanda",
    SA: "Saudi-Arabien",
    SB: "Salomonen",
    SC: "Seychellen",
    SD: "Sudan",
    SE: "Schweden",
    SG: "Singapur",
    SH: "St. Helena",
    SI: "Slowenien",
    SJ: "Spitzbergen und Jan Mayen",
    SK: "Slowakei",
    SL: "Sierra Leone",
    SM: "San Marino",
    SN: "Senegal",
    SO: "Somalia",
    SR: "Suriname",
    SS: "S\xFCdsudan",
    ST: "S\xE3o Tom\xE9 und Pr\xEDncipe",
    SV: "El Salvador",
    SX: "Sint Maarten",
    SY: "Syrien",
    SZ: "Eswatini",
    TC: "Turks- und Caicosinseln",
    TD: "Tschad",
    TF: "Franz\xF6sische S\xFCd- und Antarktisgebiete",
    TG: "Togo",
    TH: "Thailand",
    TJ: "Tadschikistan",
    TK: "Tokelau",
    TL: "Osttimor",
    TM: "Turkmenistan",
    TN: "Tunesien",
    TO: "Tonga",
    TR: "T\xFCrkei",
    TT: "Trinidad und Tobago",
    TV: "Tuvalu",
    TW: "Taiwan",
    TZ: "Tansania",
    UA: "Ukraine",
    UG: "Uganda",
    UM: "Kleinere Inselbesitzungen der Vereinigten Staaten",
    US: "Vereinigte Staaten",
    UY: "Uruguay",
    UZ: "Usbekistan",
    VA: "Vatikanstadt",
    VC: "St. Vincent und die Grenadinen",
    VE: "Venezuela",
    VG: "Britische Jungferninseln",
    VI: "Amerikanische Jungferninseln",
    VN: "Vietnam",
    VU: "Vanuatu",
    WF: "Wallis und Futuna",
    WS: "Samoa",
    XK: "Kosovo",
    YE: "Jemen",
    YT: "Mayotte",
    ZA: "S\xFCdafrika",
    ZM: "Sambia",
    ZW: "Simbabwe"
  },
  glob_footer: {
    product: "Produkt",
    account: "Konto"
  },
  glob_billing: {
    recentAccountMovements: "Aktuelle Kontobewegungen",
    loadingAccountMovements: "Kontobewegungen werden geladen...",
    noAccountMovements: "Keine Kontobewegungen gefunden.",
    date: "Datum",
    amount: "Betrag",
    type: "Typ",
    features: "Funktionen",
    credit: "Gutschrift",
    debit: "Belastung",
    billingOverview: "Abrechnungs\xFCbersicht",
    payAsYouGoCredits: "Pay-as-you-go Guthaben",
    noBalanceFound: "Kein Guthaben gefunden",
    addCredits: "Guthaben hinzuf\xFCgen",
    hybridBillingModel: "Hybrides Abrechnungsmodell",
    hybridBillingDescription: "Wenn Abonnement-Kontingente aufgebraucht sind, wechselt die Nutzung automatisch zu Pay-as-you-go Guthaben. Sie verlieren nie den Service - nur zus\xE4tzliche Nutzung \xFCber Ihre Planlimits hinaus.",
    yourReferralCode: "Ihr Empfehlungscode",
    shareDirectLink: "Oder teilen Sie diesen direkten Anmeldelink:",
    referralDescription: "Wenn sich jemand \xFCber Ihren Empfehlungslink anmeldet und seine erste Abonnementzahlung oder Einzahlung t\xE4tigt, erhalten Sie 10% dieses Betrags und sie erhalten einen 10% Bonus.",
    copy: "Kopieren",
    copied: "Kopiert",
    copyLink: "Link kopieren",
    linkCopied: "Link kopiert",
    referralStatistics: "Empfehlungsstatistiken",
    totalReferrals: "Empfehlungen gesamt",
    totalEarnings: "Gesamteinnahmen",
    pendingEarnings: "Ausstehende Einnahmen",
    recentReferrals: "Aktuelle Empfehlungen",
    firstPayment: "Erste Zahlung",
    bonus: "Bonus",
    status: "Status",
    processed: "Verarbeitet",
    pending: "Ausstehend",
    bonusPaid: "Bonus bezahlt",
    awaitingBonus: "Wartet auf Bonus",
    pendingPayment: "Zahlung ausstehend",
    noReferrals: "Noch keine Empfehlungen. Teilen Sie Ihren Empfehlungscode, um Provisionen zu verdienen!",
    loadingReferralHistory: "Empfehlungshistorie wird geladen...",
    loadingReferralInfo: "Empfehlungsinformationen werden geladen...",
    noActiveSubscription: "Kein aktives Abonnement",
    noActiveSubscriptionDescription: "Sie haben kein aktives Abonnement. Sie nutzen unser Pay-as-you-go Abrechnungsmodell.",
    currentSubscription: "Aktuelles Abonnement",
    plan: "Plan",
    periodStarted: "Periode gestartet",
    nextBilling: "N\xE4chste Abrechnung",
    nextCycle: "N\xE4chster Zyklus",
    free: "Kostenlos",
    unlimited: "Unbegrenzt",
    manageViaStripe: "\xDCber Stripe verwalten",
    cancel: "Abbrechen"
  },
  securityLog: {
    title: "Sicherheitsprotokoll",
    description: "Aktuelle sicherheitsrelevante Aktivit\xE4ten in deinem Konto. Fehlgeschlagene Ereignisse sind hervorgehoben.",
    when: "Wann",
    event: "Ereignis",
    location: "Ort",
    device: "Ger\xE4t",
    status: "Status",
    ok: "OK",
    failed: "Fehlgeschlagen",
    empty: "Noch keine Aktivit\xE4t aufgezeichnet.",
    errorTitle: "Sicherheitsprotokoll konnte nicht geladen werden"
  }
};

// src/i18n/locales/en/integrations.json
var integrations_default = {
  integrations: {
    list: {
      title: "Title",
      kind: "Kind",
      capabilities: "Capabilities",
      created: "Created",
      actions: "Actions"
    },
    empty: {
      title: "No integrations yet",
      description: "Wire up Slack, Discord, webhooks, email and more to react to events from this service."
    },
    actions: {
      create: "Create integration",
      edit: "Edit",
      delete: "Delete",
      viewDeliveries: "View deliveries",
      resend: "Resend",
      next: "Next",
      back: "Back",
      save: "Save",
      cancel: "Cancel",
      done: "Done"
    },
    form: {
      createTitle: "New integration",
      editTitle: "Edit integration",
      stepKind: "Pick adapter",
      stepKindDesc: "Choose where events should go",
      stepConfigure: "Configure",
      stepConfigureDesc: "Set credentials and options",
      pickAdapterHint: "Pick the destination for this integration. You can change settings later.",
      pickAdapterFirst: "Pick an adapter to continue.",
      secretsHeader: "Credentials",
      secretsHelp: "Stored encrypted at rest. Replace by re-entering \u2014 leave blank to keep the current value.",
      secretRevealedTitle: "Integration created"
    },
    fields: {
      integrationTitle: "Title",
      integrationTitleHelp: "Short label that you'll recognise in the list and in delivery logs.",
      url: "URL",
      customHeaders: "Custom headers (JSON)",
      customHeadersHelp: "Optional. Merged into the request, except for reserved signature/event headers.",
      zapierUrl: "Zapier catch-hook URL",
      slackWebhookUrl: "Slack webhook URL",
      slackWebhookUrlHelp: "Paste an Incoming Webhook URL from your Slack app \u2014 must start with https://hooks.slack.com/.",
      discordWebhookUrl: "Discord webhook URL",
      discordWebhookUrlHelp: "Channel \u2192 Edit Channel \u2192 Integrations \u2192 Webhooks \u2192 Copy URL.",
      discordUsername: "Display username",
      discordUsernameHelp: "Optional. Override the bot name shown in Discord.",
      sheetsWebhookUrl: "Apps Script Web App URL",
      sheetsWebhookUrlHelp: "Deploy your Apps Script as a Web App with `Anyone with the link` access and paste the /exec URL.",
      sheetName: "Sheet name",
      sheetNameHelp: "Optional. Forwarded to your script so it can route to a specific tab.",
      emailTo: "Recipients",
      emailToHelp: "One or more email addresses. Press Enter after each.",
      subjectTemplate: "Subject template",
      subjectTemplateHelp: "Plain text or simple {{placeholder}} substitution.",
      bodyTemplate: "Body template",
      bodyTemplateHelp: "Optional. Defaults to a JSON dump of the event payload.",
      formId: "Form ID",
      formIdHelp: "Target Formshive form's ID.",
      apiUrlOverride: "API URL override",
      apiUrlOverrideHelp: "Optional. For staging or self-hosted Formshive deployments.",
      submitToken: "Submit token",
      submitTokenHelp: "Optional bearer token if the target form requires one.",
      pipedriveDomain: "Pipedrive company domain",
      pipedriveDomainHelp: "e.g. acme.pipedrive.com",
      createPerson: "Create a Person on each submission",
      createLead: "Create a Lead on each submission",
      defaultOwnerId: "Default owner ID",
      defaultOrgId: "Default organization ID",
      leadTitleTemplate: "Lead title template",
      leadTitleTemplateHelp: "Optional. Defaults to the form title.",
      customFieldMapping: "Custom field mapping (JSON)",
      customFieldMappingHelp: "Map form field names \u2192 Pipedrive field keys.",
      apiKey: "API key",
      pipedriveApiKeyHelp: "Personal API token from Pipedrive (Settings \u2192 Personal preferences \u2192 API).",
      mailchimpListId: "Audience (list) ID",
      mailchimpListIdHelp: "Find under Audience \u2192 Settings \u2192 Audience name and defaults.",
      mailchimpStatus: "Status",
      mailchimpStatusSubscribed: "Subscribed",
      mailchimpStatusPending: "Pending (double opt-in)",
      doubleOptin: "Send double opt-in confirmation",
      mergeFieldMapping: "Merge field mapping (JSON)",
      mergeFieldMappingHelp: "Map form field names \u2192 Mailchimp merge tags.",
      defaultTags: "Default tags",
      defaultTagsHelp: "Tags to add to every subscriber created via this integration.",
      mailchimpApiKeyHelp: "Mailchimp API key \u2014 region prefix is auto-detected.",
      kitFormId: "Kit form ID",
      kitFormIdHelp: "Optional. If set, subscribers are added to this Kit form.",
      fieldMapping: "Field mapping (JSON)",
      fieldMappingHelp: "Map form field names \u2192 Kit subscriber attributes.",
      kitApiKeyHelp: "Kit (formerly ConvertKit) API secret."
    },
    errors: {
      titleRequired: "Title is required.",
      fieldRequired: "Required.",
      submitFailed: "Could not save integration",
      adapterRequired: "Pick an adapter first.",
      "required:formId": "Form ID is required.",
      "required:companyDomain": "Pipedrive domain is required.",
      "required:listId": "Audience ID is required.",
      "required:apiKey": "API key is required.",
      "required:subjectTemplate": "Subject template is required.",
      urlRequired: "URL is required.",
      urlInvalid: "Enter a valid URL (https://\u2026).",
      urlScheme: "URL must use http or https.",
      urlPrivate: "Private and metadata addresses are blocked.",
      slackHost: "Must be a https://hooks.slack.com/ URL.",
      discordHost: "Host must be discord.com.",
      discordPath: "URL must include /api/webhooks/.",
      googleHost: "Host must be a google.com domain (Apps Script /exec URL).",
      emailListEmpty: "Add at least one recipient.",
      emailInvalid: "One or more recipients aren't valid email addresses.",
      pipedriveDomain: "Domain must look like company.pipedrive.com.",
      mailchimpStatus: "Status must be 'Subscribed' or 'Pending'.",
      jsonInvalid: "Not valid JSON.",
      jsonObjectExpected: "JSON must be an object."
    },
    capabilities: {
      hmac: "HMAC signed",
      retry: "Auto-retry",
      replayable: "Replayable",
      idempotency: "Idempotent"
    },
    secret: {
      title: "Save this signing secret",
      warning: "This is the only time you'll see it. Store it somewhere safe \u2014 you can rotate later if it leaks.",
      copy: "Copy to clipboard",
      copied: "Copied"
    },
    outcomes: {
      success: "Success",
      retry: "Retrying",
      dlq: "Dead-lettered",
      replayed: "Replayed"
    },
    deliveries: {
      when: "When",
      event: "Event",
      attempt: "Attempt",
      outcome: "Outcome",
      status: "HTTP",
      latency: "Latency",
      error: "Error",
      filterOutcome: "Outcome",
      filterEvent: "Event type",
      allOutcomes: "All outcomes",
      allEvents: "All events",
      from: "From",
      to: "To",
      clearFilters: "Clear filters",
      empty: "No deliveries yet",
      emptyDescription: "Successful and failed delivery attempts will show up here.",
      detailsTitle: "Delivery details",
      successTitle: "Delivered successfully",
      successWithDetails: "Delivered (HTTP {{status}}) in {{latency}} ms",
      eventIdLabel: "Event ID",
      deliveryIdLabel: "Delivery ID",
      timestampLabel: "Timestamp",
      errorDetail: "Error",
      payload: "Payload excerpt"
    },
    events: {
      empty: "No event types are available for this scope yet.",
      message_received: {
        label: "Message received",
        description: "Fires when a new form submission is accepted (after spam checks)."
      }
    },
    adapters: {
      generic_webhook: {
        label: "Generic webhook",
        description: "POST signed JSON to any URL. Includes HMAC signature, idempotency key, and automatic retries."
      },
      zapier_webhook: {
        label: "Zapier",
        description: "Send events to a Zapier catch-hook so any Zap can pick them up."
      },
      slack_url: {
        label: "Slack",
        description: "Post a formatted message to a Slack channel via Incoming Webhook URL."
      },
      discord_url: {
        label: "Discord",
        description: "Post messages to a Discord channel via webhook URL."
      },
      google_sheets_webhook: {
        label: "Google Sheets",
        description: "Append a row to a Google Sheet via your own Apps Script Web App."
      },
      email: {
        label: "Email",
        description: "Send a notification email to one or more recipients."
      },
      formshive_endpoint: {
        label: "Formshive form",
        description: "POST events to another Formshive form. Useful for piping leads from one tenant into another."
      },
      api_pipedrive: {
        label: "Pipedrive",
        description: "Create a Person and optionally a Lead in Pipedrive on each form submission."
      },
      api_mailchimp: {
        label: "Mailchimp",
        description: "Add submitters to a Mailchimp audience with optional merge fields and tags."
      },
      api_kit: {
        label: "Kit (ConvertKit)",
        description: "Subscribe submitters in Kit, optionally attach to a Kit form, with tags and field mapping."
      }
    }
  }
};

// src/i18n/locales/en.json
var en_default = {
  glob_navigation: {
    home: "Home",
    pricing: "Pricing",
    docs: "Docs",
    news: "News",
    more: "More",
    login: "Login",
    signup: "Sign Up",
    getStarted: "Get Started",
    forms: "Forms",
    integrations: "Integrations",
    messages: "Messages",
    profile: "Profile",
    support: "Support",
    logout: "Logout",
    terms: "Terms",
    billing: "Billing",
    usageAndCredits: "Usage & Credits",
    subscriptions: "Subscriptions",
    referrals: "Referrals",
    apiKeys: "API Keys",
    securityLog: "Security log",
    chat: "Chat",
    sectionWorkspace: "Workspace",
    sectionAccount: "Account",
    sectionHelp: "Help"
  },
  auth: {
    login: "Login",
    signup: "Signup",
    logout: "Logout",
    step1: "Step 1",
    step2: "Step 2",
    authenticate: "Authenticate",
    enterKey: "Identifier",
    newHere: "New here? ",
    howToSignup: "How-To Sign-Up",
    alreadyAccount: "If you don't already have an account, one will be created for you, the first time you login. ",
    emailSection: "Email Link",
    googleSection: "Google",
    githubSection: "GitHub",
    microsoftSection: "Microsoft",
    nostrSection: "Nostr Browser Plugin",
    signupDescription: "Get a link sent to your email, to login. ",
    googleDescription: "Login with your Google account. ",
    githubDescription: "Login with your GitHub account. ",
    microsoftDescription: "Login with your Microsoft account. ",
    nostrDescription: "Signup using a supported ",
    browserPlugin: "browser plugin",
    signupLoginEmail: "Login with Email.",
    signupLoginGoogle: "Login with Google",
    signupLoginGithub: "Login with GitHub",
    signupLoginMicrosoft: "Login with Microsoft",
    signupLoginNostr: "Login with Nostr",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service"
  },
  login: {
    welcomeBack: "Welcome back",
    cardSubtitle: "Continue with a social provider or enter your email address.",
    orDivider: "OR",
    loginWithEmail: "Login with Email",
    magicLinkInfo: "We'll send a magic link to your inbox.",
    publicKeyRequired: "Public key is required",
    invalidEmail: "Invalid email",
    getPublicKey: "Get public key from extention",
    emailTab: "Email",
    googleTab: "Google",
    githubTab: "GitHub",
    microsoftTab: "Microsoft",
    nostrTab: "Nostr",
    oidcSsoTab: "GoFranz SSO",
    emailDescription: "Login with Email.",
    googleDescription: "Login with Google",
    nostrPluginDescription: "Login with Nos2x or similiar.",
    nostrKeyDescription: "Login with your private key.",
    nostrOtherDescription: "Provide signature via other means.",
    loadedPublicKey: "Loaded public key",
    noKeyLoaded: "No key loaded...",
    enterPublicKey: "Enter your public key",
    publicKeyPlaceholder: "32b72e6432f7282135cf124a8...",
    emailLabel: "Email address",
    emailPlaceholder: "your-email@provider.com",
    enterCode: "Enter the code from the email",
    codePlaceholder: "6fd7e67e-6f42-4354-b8f8-775bc1e52a50",
    privateKeyLabel: "Enter your private key",
    challengeResponseLabel: "Enter your challenge response",
    emailInstructions: "Click the link in the Email, or copy the code and paste it here.",
    nostrInstructions: "Your Nostr extention should prompt you to sign an authentication event to contiue.",
    nostrNoExtension: "No Nostr extension detected. Please install a Nostr signer extension (such as Alby or nos2x) and try again.",
    nostrChallengeFailed: "Nostr login failed. Please try signing in again.",
    challenge: "Challenge: ",
    loginWithExtension: "Login with extention",
    redirectingGoogle: "Redirecting to Google...",
    redirectingGithub: "Redirecting to GitHub...",
    redirectingMicrosoft: "Redirecting to Microsoft...",
    redirectingOidc: "Redirecting to GoFranz SSO...",
    githubNoChallenge: "Failed to get GitHub challenge",
    microsoftNoChallenge: "Failed to get Microsoft challenge",
    oidcNoChallenge: "Failed to get GoFranz SSO challenge",
    checkYourEmail: "Check your email for the login link",
    emailSent: "We've sent a login link to your email",
    clickLinkToLogin: "Click the link in the email to complete login",
    wrongEmail: "Wrong email? Try again",
    errorNotPrimaryEmail: "This email is not your primary account email.",
    errorDifferentAuthMethod: "This email is registered with a different authentication method",
    errorGoogleEmailNotVerified: "Google email not verified",
    errorInvalidAuthMethodGoogle: "This account uses Google login",
    errorInvalidAuthMethodGithub: "This account uses GitHub login",
    errorInvalidAuthMethodMicrosoft: "This account uses Microsoft login",
    errorInvalidAuthMethodNostr: "This account uses Nostr login",
    errorAuthMethodCollisionRemedyLink: "Go to sign in",
    callbackAccountExistsTitle: "You already have an account",
    callbackAccountExistsBody: "Welcome back \u2014 you've signed in here before.",
    callbackAccountExistsBodyGeneric: "This email signs in with a different method.",
    callbackMethodEyebrow: "Your sign-in method",
    callbackContinueWith: "Continue with {{method}}",
    callbackAddSsoNote: "You can add single sign-on later from Connected Accounts.",
    callbackErrorTitle: "Couldn't sign you in",
    callbackBackToSignIn: "Back to sign in",
    errorInvalidNostrSignature: "Invalid Nostr signature",
    errorNostrKeyMismatch: "Nostr key mismatch",
    errorOAuthFailed: "OAuth authentication failed",
    errorTokenExchange: "Failed to complete login. Please try again.",
    errorCodeExpired: "Login session expired. Please try logging in again.",
    invalidLoginResponse: "Invalid login response. Please try logging in again.",
    errorRateLimited: "Too many login attempts. Please wait {{seconds}} seconds and try again.",
    processing: "Processing login...",
    somethingWrong: "Something went wrong",
    invalidMagicLink: "Invalid magic link",
    noIdProvided: "No session ID provided",
    noChallengeProvided: "No challenge provided",
    back: "back",
    backToLogin: "Back to Login",
    cancel: "Cancel",
    submit: "Submit",
    loggingIn: "Logging in...",
    twoFactorAuth: "Two-Factor Authentication",
    twoFactorRequired: "Verification Required",
    enterTwoFactorCode: "Enter the 6-digit code from your authenticator app, or a recovery code.",
    verify: "Verify",
    useRecoveryCode: "You can also use a recovery code",
    invalid2FACode: "Invalid verification code. Please try again.",
    tooMany2FAAttempts: "Too many failed attempts. Please start over.",
    invalid2FASession: "Invalid or expired 2FA session."
  },
  glob_buttons: {
    save: "Save",
    cancel: "Cancel",
    submit: "Submit",
    create: "Create",
    update: "Update",
    updateUnsaved: "Update (Unsaved changes)",
    delete: "Delete",
    calculate: "Calculate",
    login: "Login",
    back: "back"
  },
  support: {
    title: "Support",
    getHelp: "Get Help with {{serviceName}}",
    description: "Have a question or need assistance? We're here to help! Fill out the form below and we'll get back to you as soon as possible.",
    thankYou: "Thank You!",
    successMessage: "Your support request has been submitted successfully. We'll get back to you as soon as possible.",
    submitAnother: "Submit Another Request",
    successAlert: "Success!",
    successAlertMessage: "Your support request has been submitted successfully!",
    nameLabel: "Name (Optional)",
    namePlaceholder: "Your full name",
    emailLabel: "Email",
    emailPlaceholder: "your.email@example.com",
    loggedInEmail: "Using your logged-in email",
    subjectLabel: "Subject",
    subjectPlaceholder: "Select the type of your request",
    messageLabel: "Message",
    messagePlaceholder: "Please provide details about your question or issue...",
    formInfo: "This form will include your login method (",
    formInfoEnd: ") and user identifier to help us assist you better.",
    submitting: "Submitting...",
    submitButton: "Submit Support Request",
    types: {
      general: "General Question",
      technical: "Technical Question",
      feature: "Feature Request",
      bug: "Report a Bug"
    }
  },
  colorScheme: {
    lightMode: "Light mode (click for dark)",
    darkMode: "Dark mode (click for auto)",
    autoMode: "Auto mode (click for light)",
    toggle: "Toggle color scheme"
  },
  forms: {
    titleDescription: "Visible to recipients of autoresponder."
  },
  privacy: {
    title: "Privacy",
    backHome: "back home",
    gdprCompliant: "\u{1F1E9}\u{1F1EA} GDPR compliant. Servers are located in Germany. "
  },
  terms: {
    title: "Terms of Service"
  },
  news: {
    title: "From our Blog",
    loading: "Loading ...",
    rssDescription: "You can subscribe to this Atom feed here "
  },
  profile: {
    balance: "Balance",
    addMoney: "Add Credits",
    noBalanceFound: "No balance found.",
    depositSuccess: "Deposit success",
    depositSuccessMessage: "Your deposit was successful. Card payments should be applied within seconds.",
    wirePaymentInfo: "Want to pay by wire or Bitcoin? (\u20AC100+): ",
    personalInformation: "Personal Information",
    personalInfoMessage: "We don't really know all that much about you.",
    personalInfoContact: "Send an email to ",
    personalInfoGreeting: " to say hi.",
    emails: "Emails",
    loginInformation: "Login Information",
    currentlyLoggedIn: "You are currently logged in with ",
    saveKeysAlert: "Save these keys in your password manager or somewhere safe. We cannot recover them. You can use them to access the service from other devices, and login to our other services.",
    downloadKeys: "Download keys",
    publicKey: "Public Key",
    publicKeyDescription: "Your public key is what identifies you; We store it on our server.",
    privateKey: "Private Key",
    privateKeyDescription: "Your private key is what you use to login and never leaves your computer.",
    security: "Security",
    dataPrivacy: "Data & Privacy",
    dataPrivacyDescription: "Export your data or delete your account.",
    manageMyData: "Manage my data",
    availableFunds: "Credits",
    lowBalance: "Top up your account to keep things running.",
    authProvider: "Authentication Provider",
    publicKeyHex: "Public Key (Hex)",
    emailsDescription: "Manage your email addresses for security alerts and notifications.",
    addEmail: "Add email",
    sessionHistory: "Session History",
    viewLog: "View",
    providerSubtitle: {
      NOSTR: "Decentralized Login",
      GOOGLE: "Google OAuth",
      GITHUB: "GitHub OAuth",
      MICROSOFT: "Microsoft OAuth",
      EMAIL_MAGIC_LINK: "Passwordless Email",
      MANUAL: "Self-managed Keypair",
      PRIVATE_KEY: "Private Key Login",
      OIDC: "Single Sign-On"
    }
  },
  oidc: {
    handoffTwoFactorBody: "Two-factor authentication is managed by GoFranz SSO. Enable or change your second factor there.",
    handoffTwoFactorButton: "Manage 2FA",
    securityLogBanner: "Showing this app's sign-in activity. Additional events from other apps are visible at GoFranz SSO.",
    securityLogLink: "Open GoFranz SSO",
    syncFromIdpButton: "Sync profile from GoFranz SSO",
    syncFromIdpInProgress: "Syncing\u2026"
  },
  dataPrivacy: {
    title: "Data & Privacy",
    exportTitle: "Export your data",
    exportDescription: "Download a copy of all your personal data in JSON format.",
    exportButton: "Download my data",
    exporting: "Preparing export...",
    deleteTitle: "Delete account",
    deleteWarning: "This will permanently delete your account, all your forms, messages, files, and billing history. This action cannot be undone.",
    deleteConfirmation: "Type DELETE to confirm",
    deleteButton: "Delete my account",
    typeDelete: "Type DELETE to confirm",
    exportError: "Failed to export data. Please try again.",
    deleteError: "Failed to delete account. Please try again."
  },
  common: {
    copy: "Copy",
    copied: "Copied!",
    done: "Done",
    cancel: "Cancel"
  },
  twoFactor: {
    loading: "Loading 2FA status...",
    enabled: "Enabled",
    disabled: "Disabled",
    description: "Add an extra layer of security to your account by requiring a verification code in addition to your password.",
    enable: "Enable Two-Factor Authentication",
    setupTitle: "Set Up Authenticator",
    scanQRCode: "Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)",
    cantScanQR: "Can't scan the QR code? Enter this secret manually:",
    next: "Next",
    verifySetup: "Verify Setup",
    enterCodeFromApp: "Enter the 6-digit code from your authenticator app to complete setup.",
    recoveryCodes: "Recovery Codes",
    saveRecoveryCodes: "Save these recovery codes in a safe place. You can use them to access your account if you lose your authenticator device. Each code can only be used once.",
    copyAllCodes: "Copy All Codes",
    recoveryCodesRemaining: "Recovery codes remaining",
    regenerateRecoveryCodes: "Regenerate Recovery Codes",
    regenerateWarning: "This will invalidate all existing recovery codes and generate new ones.",
    enterCodeToRegenerate: "Enter a code from your authenticator app to regenerate recovery codes.",
    regenerate: "Regenerate",
    disable2FA: "Disable Two-Factor Authentication",
    disableWarning: "Disabling 2FA will make your account less secure. You will only need your password to log in.",
    enterCodeToDisable: "Enter a code from your authenticator app or a recovery code to disable 2FA.",
    disable: "Disable"
  },
  emails: {
    accountEmail: "Account Email",
    accountEmailDescription: "This is your account email used for login and notifications, including low balance alerts.",
    makePrimary: "Make Primary",
    resendVerification: "Resend verification",
    verificationSent: "Verification email sent. Check your inbox.",
    accountEmailUpdated: "Account email updated successfully."
  },
  countries: {
    AD: "Andorra",
    AE: "United Arab Emirates",
    AF: "Afghanistan",
    AG: "Antigua and Barbuda",
    AI: "Anguilla",
    AL: "Albania",
    AM: "Armenia",
    AO: "Angola",
    AQ: "Antarctica",
    AR: "Argentina",
    AS: "American Samoa",
    AT: "Austria",
    AU: "Australia",
    AW: "Aruba",
    AX: "\xC5land Islands",
    AZ: "Azerbaijan",
    BA: "Bosnia and Herzegovina",
    BB: "Barbados",
    BD: "Bangladesh",
    BE: "Belgium",
    BF: "Burkina Faso",
    BG: "Bulgaria",
    BH: "Bahrain",
    BI: "Burundi",
    BJ: "Benin",
    BL: "Saint Barth\xE9lemy",
    BM: "Bermuda",
    BN: "Brunei",
    BO: "Bolivia",
    BQ: "Caribbean Netherlands",
    BR: "Brazil",
    BS: "Bahamas",
    BT: "Bhutan",
    BV: "Bouvet Island",
    BW: "Botswana",
    BY: "Belarus",
    BZ: "Belize",
    CA: "Canada",
    CC: "Cocos Islands",
    CD: "Democratic Republic of the Congo",
    CF: "Central African Republic",
    CG: "Republic of the Congo",
    CH: "Switzerland",
    CI: "C\xF4te d'Ivoire",
    CK: "Cook Islands",
    CL: "Chile",
    CM: "Cameroon",
    CN: "China",
    CO: "Colombia",
    CR: "Costa Rica",
    CU: "Cuba",
    CV: "Cape Verde",
    CW: "Cura\xE7ao",
    CX: "Christmas Island",
    CY: "Cyprus",
    CZ: "Czech Republic",
    DE: "Germany",
    DJ: "Djibouti",
    DK: "Denmark",
    DM: "Dominica",
    DO: "Dominican Republic",
    DZ: "Algeria",
    EC: "Ecuador",
    EE: "Estonia",
    EG: "Egypt",
    EH: "Western Sahara",
    ER: "Eritrea",
    ES: "Spain",
    ET: "Ethiopia",
    FI: "Finland",
    FJ: "Fiji",
    FK: "Falkland Islands",
    FM: "Micronesia",
    FO: "Faroe Islands",
    FR: "France",
    GA: "Gabon",
    GB: "United Kingdom",
    GD: "Grenada",
    GE: "Georgia",
    GF: "French Guiana",
    GG: "Guernsey",
    GH: "Ghana",
    GI: "Gibraltar",
    GL: "Greenland",
    GM: "Gambia",
    GN: "Guinea",
    GP: "Guadeloupe",
    GQ: "Equatorial Guinea",
    GR: "Greece",
    GS: "South Georgia and the South Sandwich Islands",
    GT: "Guatemala",
    GU: "Guam",
    GW: "Guinea-Bissau",
    GY: "Guyana",
    HK: "Hong Kong",
    HM: "Heard Island and McDonald Islands",
    HN: "Honduras",
    HR: "Croatia",
    HT: "Haiti",
    HU: "Hungary",
    ID: "Indonesia",
    IE: "Ireland",
    IL: "Israel",
    IM: "Isle of Man",
    IN: "India",
    IO: "British Indian Ocean Territory",
    IQ: "Iraq",
    IR: "Iran",
    IS: "Iceland",
    IT: "Italy",
    JE: "Jersey",
    JM: "Jamaica",
    JO: "Jordan",
    JP: "Japan",
    KE: "Kenya",
    KG: "Kyrgyzstan",
    KH: "Cambodia",
    KI: "Kiribati",
    KM: "Comoros",
    KN: "Saint Kitts and Nevis",
    KP: "North Korea",
    KR: "South Korea",
    KW: "Kuwait",
    KY: "Cayman Islands",
    KZ: "Kazakhstan",
    LA: "Laos",
    LB: "Lebanon",
    LC: "Saint Lucia",
    LI: "Liechtenstein",
    LK: "Sri Lanka",
    LR: "Liberia",
    LS: "Lesotho",
    LT: "Lithuania",
    LU: "Luxembourg",
    LV: "Latvia",
    LY: "Libya",
    MA: "Morocco",
    MC: "Monaco",
    MD: "Moldova",
    ME: "Montenegro",
    MF: "Saint Martin",
    MG: "Madagascar",
    MH: "Marshall Islands",
    MK: "North Macedonia",
    ML: "Mali",
    MM: "Myanmar",
    MN: "Mongolia",
    MO: "Macao",
    MP: "Northern Mariana Islands",
    MQ: "Martinique",
    MR: "Mauritania",
    MS: "Montserrat",
    MT: "Malta",
    MU: "Mauritius",
    MV: "Maldives",
    MW: "Malawi",
    MX: "Mexico",
    MY: "Malaysia",
    MZ: "Mozambique",
    NA: "Namibia",
    NC: "New Caledonia",
    NE: "Niger",
    NF: "Norfolk Island",
    NG: "Nigeria",
    NI: "Nicaragua",
    NL: "Netherlands",
    NO: "Norway",
    NP: "Nepal",
    NR: "Nauru",
    NU: "Niue",
    NZ: "New Zealand",
    OM: "Oman",
    PA: "Panama",
    PE: "Peru",
    PF: "French Polynesia",
    PG: "Papua New Guinea",
    PH: "Philippines",
    PK: "Pakistan",
    PL: "Poland",
    PM: "Saint Pierre and Miquelon",
    PN: "Pitcairn",
    PR: "Puerto Rico",
    PS: "Palestine",
    PT: "Portugal",
    PW: "Palau",
    PY: "Paraguay",
    QA: "Qatar",
    RE: "R\xE9union",
    RO: "Romania",
    RS: "Serbia",
    RU: "Russia",
    RW: "Rwanda",
    SA: "Saudi Arabia",
    SB: "Solomon Islands",
    SC: "Seychelles",
    SD: "Sudan",
    SE: "Sweden",
    SG: "Singapore",
    SH: "Saint Helena",
    SI: "Slovenia",
    SJ: "Svalbard and Jan Mayen",
    SK: "Slovakia",
    SL: "Sierra Leone",
    SM: "San Marino",
    SN: "Senegal",
    SO: "Somalia",
    SR: "Suriname",
    SS: "South Sudan",
    ST: "Sao Tome and Principe",
    SV: "El Salvador",
    SX: "Sint Maarten",
    SY: "Syria",
    SZ: "Eswatini",
    TC: "Turks and Caicos Islands",
    TD: "Chad",
    TF: "French Southern Territories",
    TG: "Togo",
    TH: "Thailand",
    TJ: "Tajikistan",
    TK: "Tokelau",
    TL: "Timor-Leste",
    TM: "Turkmenistan",
    TN: "Tunisia",
    TO: "Tonga",
    TR: "Turkey",
    TT: "Trinidad and Tobago",
    TV: "Tuvalu",
    TW: "Taiwan",
    TZ: "Tanzania",
    UA: "Ukraine",
    UG: "Uganda",
    UM: "United States Minor Outlying Islands",
    US: "United States",
    UY: "Uruguay",
    UZ: "Uzbekistan",
    VA: "Vatican City",
    VC: "Saint Vincent and the Grenadines",
    VE: "Venezuela",
    VG: "British Virgin Islands",
    VI: "U.S. Virgin Islands",
    VN: "Vietnam",
    VU: "Vanuatu",
    WF: "Wallis and Futuna",
    WS: "Samoa",
    XK: "Kosovo",
    YE: "Yemen",
    YT: "Mayotte",
    ZA: "South Africa",
    ZM: "Zambia",
    ZW: "Zimbabwe"
  },
  glob_footer: {
    product: "Product",
    account: "Account"
  },
  glob_billing: {
    recentAccountMovements: "Recent Account Movements",
    loadingAccountMovements: "Loading account movements...",
    noAccountMovements: "No account movements found.",
    date: "Date",
    amount: "Amount",
    type: "Type",
    features: "Features",
    credit: "Credit",
    debit: "Debit",
    billingOverview: "Billing Overview",
    payAsYouGoCredits: "Pay-as-you-go Credits",
    noBalanceFound: "No balance found",
    addCredits: "Add Credits",
    hybridBillingModel: "Hybrid Billing Model",
    hybridBillingDescription: "When subscription allowances are exhausted, usage automatically falls back to pay-as-you-go credits. You'll never lose service - just additional usage beyond your plan limits.",
    yourReferralCode: "Your Referral Code",
    shareDirectLink: "Or share this direct signup link:",
    referralDescription: "When someone signs up with your referral link and makes their first subscription payment or deposit, you earn 10% of that amount, and they receive a 10% bonus.",
    copy: "Copy",
    copied: "Copied",
    copyLink: "Copy Link",
    linkCopied: "Link Copied",
    referralStatistics: "Referral Statistics",
    totalReferrals: "Total Referrals",
    totalEarnings: "Total Earnings",
    pendingEarnings: "Pending Earnings",
    recentReferrals: "Recent Referrals",
    firstPayment: "First Payment",
    bonus: "Bonus",
    status: "Status",
    processed: "Processed",
    pending: "Pending",
    bonusPaid: "Bonus Paid",
    awaitingBonus: "Awaiting Bonus",
    pendingPayment: "Pending Payment",
    noReferrals: "No referrals yet. Share your referral code to start earning commissions!",
    loadingReferralHistory: "Loading referral history...",
    loadingReferralInfo: "Loading referral information...",
    noActiveSubscription: "No Active Subscription",
    noActiveSubscriptionDescription: "You don't have an active subscription. You're using our pay-as-you-go billing model.",
    currentSubscription: "Current Subscription",
    plan: "Plan",
    periodStarted: "Period Started",
    nextBilling: "Next Billing",
    nextCycle: "Next Cycle",
    free: "Free",
    unlimited: "Unlimited",
    manageViaStripe: "Manage via Stripe",
    cancel: "Cancel"
  },
  securityLog: {
    title: "Security log",
    description: "Recent security-relevant activity on your account. Failed events are highlighted.",
    when: "When",
    event: "Event",
    location: "Location",
    device: "Device",
    status: "Status",
    ok: "OK",
    failed: "Failed",
    empty: "No activity recorded yet.",
    errorTitle: "Could not load security log"
  },
  connectedAccounts: {
    title: "Connected Accounts",
    sectionTitle: "Authentication Providers",
    sectionDescription: "These are the ways you can sign in to this account.",
    backToProfile: "Back to Profile",
    description: "Sign-in methods linked to your account. Link an additional provider or remove one you no longer use.",
    primary: "Primary",
    linkedVia: "Linked via {{via}}",
    added: "Added",
    lastLogin: "last login",
    unlink: "Unlink",
    linkSso: "Connect single sign-on (SSO)",
    linkedSuccess: "Account linked",
    unlinkedSuccess: "Account unlinked",
    deletedSuccess: "Account deleted",
    deletedBody: "Your account has been permanently deleted.",
    ssoConnected: "Single sign-on is connected. Accounts using SSO are managed by your identity provider and cannot link additional methods.",
    hint: "Connect SSO to also sign in to this account with your single sign-on provider.",
    checkEmailTitle: "Check your email",
    checkEmail: "We sent a confirmation link to your email. Open it to finish.",
    processing: "Finishing up\u2026",
    redirecting: "Redirecting\u2026",
    nostrSignTitle: "Confirm with Nostr",
    nostrSignBody: "Sign a challenge with your Nostr extension to confirm it's you.",
    nostrSignButton: "Sign with Nostr",
    backToAccounts: "Back to Connected Accounts",
    tryAgain: "Try again",
    errorTitle: "Something went wrong",
    errorLoad: "Could not load connected accounts",
    errorGeneric: "Something went wrong. Please try again.",
    error: {
      identity_already_linked: "That account is already linked \u2014 to this or another user.",
      email_belongs_to_another_account: "This email already belongs to another account here. Sign in to that account to add single sign-on there, or remove it first \u2014 it can't be linked to this account.",
      oidc_linking_not_permitted: "This account already uses single sign-on and can't link additional methods.",
      step_up_invalid: "Re-authentication failed or expired. Please try again.",
      missing_proof: "The link request expired. Please start again.",
      missing_stepup: "Re-authentication did not complete. Please try again.",
      no_pending_flow: "We lost track of this request. Please start again.",
      unsafe_redirect: "The provider returned an unexpected address. Please try again.",
      nostr_extension_missing: "No Nostr signer was found. Install or unlock your extension and try again.",
      unknown: "Something went wrong. Please try again."
    }
  }
};

// src/i18n/locales/es.json
var es_default = {
  glob_navigation: {
    home: "Inicio",
    pricing: "Precios",
    docs: "Documentaci\xF3n",
    news: "Noticias",
    more: "M\xE1s",
    login: "Iniciar sesi\xF3n",
    signup: "Registrarse",
    getStarted: "Comenzar",
    forms: "Formularios",
    integrations: "Integraciones",
    messages: "Mensajes",
    profile: "Perfil",
    support: "Soporte",
    logout: "Cerrar sesi\xF3n",
    terms: "T\xE9rminos",
    billing: "Facturaci\xF3n",
    usageAndCredits: "Uso y cr\xE9ditos",
    subscriptions: "Suscripciones",
    referrals: "Referencias",
    apiKeys: "Claves API",
    securityLog: "Registro de seguridad",
    chat: "Chat",
    sectionWorkspace: "Espacio de trabajo",
    sectionAccount: "Cuenta",
    sectionHelp: "Ayuda"
  },
  auth: {
    login: "Iniciar sesi\xF3n",
    signup: "Registrarse",
    logout: "Cerrar sesi\xF3n",
    step1: "Paso 1",
    step2: "Paso 2",
    authenticate: "Autenticar",
    enterKey: "Identificador",
    newHere: "\xBFNuevo aqu\xED? ",
    howToSignup: "C\xF3mo registrarse",
    alreadyAccount: "Si a\xFAn no tiene una cuenta, se crear\xE1 una para usted la primera vez que inicie sesi\xF3n. ",
    emailSection: "Enlace por email",
    googleSection: "Google",
    githubSection: "GitHub",
    microsoftSection: "Microsoft",
    nostrSection: "Plugin de navegador Nostr",
    signupDescription: "Reciba un enlace enviado a su email para iniciar sesi\xF3n. ",
    googleDescription: "Inicie sesi\xF3n con su cuenta de Google. ",
    githubDescription: "Inicie sesi\xF3n con su cuenta de GitHub. ",
    microsoftDescription: "Inicie sesi\xF3n con su cuenta de Microsoft. ",
    nostrDescription: "Reg\xEDstrese usando un ",
    browserPlugin: "plugin de navegador",
    signupLoginEmail: "Iniciar sesi\xF3n con email.",
    signupLoginGoogle: "Iniciar sesi\xF3n con Google",
    signupLoginGithub: "Iniciar sesi\xF3n con GitHub",
    signupLoginMicrosoft: "Iniciar sesi\xF3n con Microsoft",
    signupLoginNostr: "Iniciar sesi\xF3n con Nostr",
    privacyPolicy: "Pol\xEDtica de Privacidad",
    termsOfService: "T\xE9rminos de Servicio"
  },
  login: {
    welcomeBack: "Bienvenido de nuevo",
    cardSubtitle: "Contin\xFAa con un proveedor social o introduce tu direcci\xF3n de correo electr\xF3nico.",
    orDivider: "O",
    loginWithEmail: "Iniciar sesi\xF3n con email",
    magicLinkInfo: "Te enviaremos un enlace m\xE1gico a tu bandeja de entrada.",
    publicKeyRequired: "Se requiere clave p\xFAblica",
    invalidEmail: "Email inv\xE1lido",
    getPublicKey: "Obtener clave p\xFAblica de la extensi\xF3n",
    emailTab: "Email",
    googleTab: "Google",
    githubTab: "GitHub",
    microsoftTab: "Microsoft",
    nostrTab: "Nostr",
    oidcSsoTab: "GoFranz SSO",
    emailDescription: "Iniciar sesi\xF3n con email.",
    googleDescription: "Iniciar sesi\xF3n con Google",
    nostrPluginDescription: "Iniciar sesi\xF3n con Nos2x o similar.",
    nostrKeyDescription: "Iniciar sesi\xF3n con su clave privada.",
    nostrOtherDescription: "Proporcionar firma por otros medios.",
    loadedPublicKey: "Clave p\xFAblica cargada",
    noKeyLoaded: "No se carg\xF3 ninguna clave...",
    enterPublicKey: "Ingrese su clave p\xFAblica",
    publicKeyPlaceholder: "32b72e6432f7282135cf124a8...",
    emailLabel: "Direcci\xF3n de email",
    emailPlaceholder: "su-email@proveedor.com",
    enterCode: "Ingrese el c\xF3digo del email",
    codePlaceholder: "6fd7e67e-6f42-4354-b8f8-775bc1e52a50",
    privateKeyLabel: "Ingrese su clave privada",
    challengeResponseLabel: "Ingrese su respuesta al desaf\xEDo",
    emailInstructions: "Haga clic en el enlace del email, o copie el c\xF3digo y p\xE9guelo aqu\xED.",
    nostrInstructions: "Su extensi\xF3n Nostr deber\xEDa solicitarle firmar un evento de autenticaci\xF3n para continuar.",
    challenge: "Desaf\xEDo: ",
    loginWithExtension: "Iniciar sesi\xF3n con extensi\xF3n",
    redirectingGoogle: "Redirigiendo a Google...",
    redirectingGithub: "Redirigiendo a GitHub...",
    redirectingMicrosoft: "Redirigiendo a Microsoft...",
    redirectingOidc: "Redirigiendo a GoFranz SSO...",
    githubNoChallenge: "Error al obtener el desaf\xEDo de GitHub",
    microsoftNoChallenge: "Error al obtener el desaf\xEDo de Microsoft",
    oidcNoChallenge: "Error al obtener el desaf\xEDo de GoFranz SSO",
    checkYourEmail: "Revisa tu correo electr\xF3nico para el enlace de inicio de sesi\xF3n",
    emailSent: "Hemos enviado un enlace de inicio de sesi\xF3n a tu correo electr\xF3nico",
    clickLinkToLogin: "Haz clic en el enlace del correo electr\xF3nico para completar el inicio de sesi\xF3n",
    wrongEmail: "\xBFCorreo electr\xF3nico incorrecto? Int\xE9ntalo de nuevo",
    errorNotPrimaryEmail: "Este correo electr\xF3nico no es tu correo electr\xF3nico principal de la cuenta.",
    errorDifferentAuthMethod: "Este correo electr\xF3nico est\xE1 registrado con un m\xE9todo de autenticaci\xF3n diferente",
    errorGoogleEmailNotVerified: "Correo de Google no verificado",
    errorInvalidAuthMethodGoogle: "Esta cuenta usa inicio de sesi\xF3n con Google",
    errorInvalidAuthMethodGithub: "Esta cuenta usa inicio de sesi\xF3n con GitHub",
    errorInvalidAuthMethodMicrosoft: "Esta cuenta usa inicio de sesi\xF3n con Microsoft",
    errorInvalidAuthMethodNostr: "Esta cuenta usa inicio de sesi\xF3n con Nostr",
    errorAuthMethodCollision: "Este correo electr\xF3nico ya est\xE1 registrado mediante {{method}}. Inicie sesi\xF3n con ese m\xE9todo.",
    errorAuthMethodCollisionGeneric: "Este correo electr\xF3nico ya est\xE1 registrado con otro m\xE9todo de inicio de sesi\xF3n. Utilice su m\xE9todo original.",
    errorInvalidNostrSignature: "Firma Nostr inv\xE1lida",
    errorNostrKeyMismatch: "Las claves Nostr no coinciden",
    errorOAuthFailed: "Autenticaci\xF3n OAuth fallida",
    errorTokenExchange: "Error al completar el inicio de sesi\xF3n. Int\xE9ntalo de nuevo.",
    errorCodeExpired: "La sesi\xF3n de inicio expir\xF3. Inicia sesi\xF3n de nuevo.",
    invalidLoginResponse: "Respuesta de inicio de sesi\xF3n no v\xE1lida. Int\xE9ntalo de nuevo.",
    errorRateLimited: "Demasiados intentos de inicio de sesi\xF3n. Espera {{seconds}} segundos e int\xE9ntalo de nuevo.",
    processing: "Procesando inicio de sesi\xF3n...",
    somethingWrong: "Algo sali\xF3 mal",
    invalidMagicLink: "Enlace m\xE1gico inv\xE1lido",
    noIdProvided: "No se proporcion\xF3 ID de sesi\xF3n",
    noChallengeProvided: "No se proporcion\xF3 desaf\xEDo",
    back: "atr\xE1s",
    cancel: "Cancelar",
    submit: "Enviar"
  },
  glob_buttons: {
    save: "Guardar",
    cancel: "Cancelar",
    submit: "Enviar",
    create: "Crear",
    update: "Actualizar",
    updateUnsaved: "Actualizar (Cambios sin guardar)",
    delete: "Eliminar",
    calculate: "Calcular",
    login: "Iniciar sesi\xF3n",
    back: "atr\xE1s"
  },
  support: {
    title: "Soporte",
    getHelp: "Obtener ayuda con {{serviceName}}",
    description: "\xBFTiene una pregunta o necesita asistencia? \xA1Estamos aqu\xED para ayudar! Complete el formulario a continuaci\xF3n y nos pondremos en contacto con usted lo antes posible.",
    thankYou: "\xA1Gracias!",
    successMessage: "Su solicitud de soporte ha sido enviada exitosamente. Nos pondremos en contacto con usted lo antes posible.",
    submitAnother: "Enviar otra solicitud",
    successAlert: "\xA1\xC9xito!",
    successAlertMessage: "\xA1Su solicitud de soporte ha sido enviada exitosamente!",
    nameLabel: "Nombre (Opcional)",
    namePlaceholder: "Su nombre completo",
    emailLabel: "Email",
    emailPlaceholder: "su.email@ejemplo.com",
    loggedInEmail: "Usando su email de sesi\xF3n iniciada",
    subjectLabel: "Asunto",
    subjectPlaceholder: "Seleccione el tipo de su solicitud",
    messageLabel: "Mensaje",
    messagePlaceholder: "Por favor proporcione detalles sobre su pregunta o problema...",
    formInfo: "Este formulario incluir\xE1 su m\xE9todo de inicio de sesi\xF3n (",
    formInfoEnd: ") e identificador de usuario para ayudarle mejor.",
    submitting: "Enviando...",
    submitButton: "Enviar solicitud de soporte",
    types: {
      general: "Pregunta general",
      technical: "Pregunta t\xE9cnica",
      feature: "Solicitud de funcionalidad",
      bug: "Reportar un error"
    }
  },
  colorScheme: {
    lightMode: "Modo claro (clic para oscuro)",
    darkMode: "Modo oscuro (clic para autom\xE1tico)",
    autoMode: "Modo autom\xE1tico (clic para claro)",
    toggle: "Cambiar esquema de color"
  },
  privacy: {
    title: "Privacidad",
    backHome: "volver al inicio",
    gdprCompliant: "\u{1F1E9}\u{1F1EA} Cumple con GDPR. Los servidores est\xE1n ubicados en Alemania. "
  },
  terms: {
    title: "T\xE9rminos de servicio"
  },
  news: {
    title: "Desde nuestro blog",
    loading: "Cargando ...",
    rssDescription: "Puede suscribirse a este feed Atom aqu\xED "
  },
  profile: {
    balance: "Saldo",
    addMoney: "Agregar dinero",
    noBalanceFound: "No se encontr\xF3 saldo.",
    depositSuccess: "Dep\xF3sito exitoso",
    depositSuccessMessage: "Su dep\xF3sito fue exitoso. Los pagos con tarjeta deber\xEDan aplicarse en segundos.",
    wirePaymentInfo: "\xBFQuiere pagar por transferencia o Bitcoin? (\u20AC100+): ",
    personalInformation: "Informaci\xF3n personal",
    personalInfoMessage: "En realidad no sabemos mucho sobre usted.",
    personalInfoContact: "Env\xEDe un email a ",
    personalInfoGreeting: " para saludar.",
    emails: "Emails",
    loginInformation: "Informaci\xF3n de inicio de sesi\xF3n",
    currentlyLoggedIn: "Actualmente ha iniciado sesi\xF3n con ",
    saveKeysAlert: "Guarde estas claves en su administrador de contrase\xF1as o en un lugar seguro. No podemos recuperarlas. Puede usarlas para acceder al servicio desde otros dispositivos e iniciar sesi\xF3n en nuestros otros servicios.",
    downloadKeys: "Descargar claves",
    publicKey: "Clave p\xFAblica",
    publicKeyDescription: "Su clave p\xFAblica es lo que le identifica; la almacenamos en nuestro servidor.",
    privateKey: "Clave privada",
    privateKeyDescription: "Su clave privada es lo que usa para iniciar sesi\xF3n y nunca sale de su computadora.",
    security: "Seguridad",
    dataPrivacy: "Datos y privacidad",
    dataPrivacyDescription: "Exporte sus datos o elimine su cuenta.",
    manageMyData: "Administrar mis datos",
    availableFunds: "Fondos disponibles",
    lowBalance: "Recargue su cuenta para mantener todo en marcha.",
    authProvider: "Proveedor de autenticaci\xF3n",
    publicKeyHex: "Clave p\xFAblica (Hex)",
    emailsDescription: "Gestione sus direcciones de correo para alertas de seguridad y notificaciones.",
    addEmail: "A\xF1adir correo",
    sessionHistory: "Historial de sesiones",
    viewLog: "Ver",
    providerSubtitle: {
      NOSTR: "Inicio de sesi\xF3n descentralizado",
      GOOGLE: "Google OAuth",
      GITHUB: "GitHub OAuth",
      MICROSOFT: "Microsoft OAuth",
      EMAIL_MAGIC_LINK: "Correo sin contrase\xF1a",
      MANUAL: "Par de claves autogestionado",
      PRIVATE_KEY: "Inicio con clave privada"
    }
  },
  emails: {
    accountEmail: "Correo de la cuenta",
    accountEmailDescription: "Este es su correo de cuenta usado para iniciar sesi\xF3n y notificaciones, incluidas las alertas de saldo bajo.",
    makePrimary: "Establecer como principal",
    resendVerification: "Reenviar verificaci\xF3n",
    verificationSent: "Correo de verificaci\xF3n enviado. Revise su bandeja de entrada.",
    accountEmailUpdated: "Correo de la cuenta actualizado correctamente."
  },
  glob_footer: {
    product: "Producto",
    account: "Cuenta"
  },
  glob_billing: {
    recentAccountMovements: "Movimientos recientes de cuenta",
    loadingAccountMovements: "Cargando movimientos de cuenta...",
    noAccountMovements: "No se encontraron movimientos de cuenta.",
    date: "Fecha",
    amount: "Cantidad",
    type: "Tipo",
    features: "Caracter\xEDsticas",
    credit: "Cr\xE9dito",
    debit: "D\xE9bito",
    billingOverview: "Resumen de facturaci\xF3n",
    payAsYouGoCredits: "Cr\xE9ditos de pago por uso",
    noBalanceFound: "No se encontr\xF3 saldo",
    addCredits: "Agregar cr\xE9ditos",
    hybridBillingModel: "Modelo de facturaci\xF3n h\xEDbrida",
    hybridBillingDescription: "Cuando se agotan las asignaciones de suscripci\xF3n, el uso vuelve autom\xE1ticamente a los cr\xE9ditos de pago por uso. Nunca perder\xE1s el servicio - solo uso adicional m\xE1s all\xE1 de los l\xEDmites de tu plan.",
    yourReferralCode: "Tu c\xF3digo de referencia",
    shareDirectLink: "O comparte este enlace de registro directo:",
    referralDescription: "Cuando alguien se registra con tu enlace de referencia y realiza su primer pago de suscripci\xF3n o dep\xF3sito, ganas el 10% de esa cantidad, y ellos reciben un bono del 10%.",
    copy: "Copiar",
    copied: "Copiado",
    copyLink: "Copiar enlace",
    linkCopied: "Enlace copiado",
    referralStatistics: "Estad\xEDsticas de referencia",
    totalReferrals: "Referencias totales",
    totalEarnings: "Ganancias totales",
    pendingEarnings: "Ganancias pendientes",
    recentReferrals: "Referencias recientes",
    firstPayment: "Primer pago",
    bonus: "Bono",
    status: "Estado",
    processed: "Procesado",
    pending: "Pendiente",
    bonusPaid: "Bono pagado",
    awaitingBonus: "Esperando bono",
    pendingPayment: "Pago pendiente",
    noReferrals: "A\xFAn no hay referencias. \xA1Comparte tu c\xF3digo de referencia para comenzar a ganar comisiones!",
    loadingReferralHistory: "Cargando historial de referencias...",
    loadingReferralInfo: "Cargando informaci\xF3n de referencia...",
    noActiveSubscription: "Sin suscripci\xF3n activa",
    noActiveSubscriptionDescription: "No tienes una suscripci\xF3n activa. Est\xE1s usando nuestro modelo de facturaci\xF3n de pago por uso.",
    currentSubscription: "Suscripci\xF3n actual",
    plan: "Plan",
    periodStarted: "Per\xEDodo iniciado",
    nextBilling: "Pr\xF3xima facturaci\xF3n",
    nextCycle: "Pr\xF3ximo ciclo",
    free: "Gratis",
    unlimited: "Ilimitado",
    manageViaStripe: "Gestionar v\xEDa Stripe",
    cancel: "Cancelar"
  },
  securityLog: {
    title: "Registro de seguridad",
    description: "Actividad reciente relacionada con la seguridad de tu cuenta. Los eventos fallidos aparecen resaltados.",
    when: "Cu\xE1ndo",
    event: "Evento",
    location: "Ubicaci\xF3n",
    device: "Dispositivo",
    status: "Estado",
    ok: "OK",
    failed: "Fallido",
    empty: "A\xFAn no hay actividad registrada.",
    errorTitle: "No se pudo cargar el registro de seguridad"
  }
};

// src/i18n/locales/fr.json
var fr_default = {
  glob_navigation: {
    home: "Accueil",
    pricing: "Tarifs",
    docs: "Documentation",
    news: "Actualit\xE9s",
    more: "Plus",
    login: "Se connecter",
    signup: "S'inscrire",
    getStarted: "Commencer",
    forms: "Formulaires",
    integrations: "Int\xE9grations",
    messages: "Messages",
    profile: "Profil",
    support: "Support",
    logout: "Se d\xE9connecter",
    terms: "Conditions",
    billing: "Facturation",
    usageAndCredits: "Utilisation et cr\xE9dits",
    subscriptions: "Abonnements",
    referrals: "Parrainages",
    apiKeys: "Cl\xE9s API",
    securityLog: "Journal de s\xE9curit\xE9",
    chat: "Chat",
    sectionWorkspace: "Espace de travail",
    sectionAccount: "Compte",
    sectionHelp: "Aide"
  },
  auth: {
    login: "Se connecter",
    signup: "S'inscrire",
    logout: "Se d\xE9connecter",
    step1: "\xC9tape 1",
    step2: "\xC9tape 2",
    authenticate: "Authentifier",
    enterKey: "Identifiant",
    newHere: "Nouveau ici ? ",
    howToSignup: "Comment s'inscrire",
    alreadyAccount: "Si vous n'avez pas encore de compte, un compte sera cr\xE9\xE9 pour vous lors de votre premi\xE8re connexion. ",
    emailSection: "Lien par email",
    googleSection: "Google",
    githubSection: "GitHub",
    microsoftSection: "Microsoft",
    nostrSection: "Plugin navigateur Nostr",
    signupDescription: "Recevez un lien envoy\xE9 \xE0 votre email pour vous connecter. ",
    googleDescription: "Connectez-vous avec votre compte Google. ",
    githubDescription: "Connectez-vous avec votre compte GitHub. ",
    microsoftDescription: "Connectez-vous avec votre compte Microsoft. ",
    nostrDescription: "Inscrivez-vous en utilisant un ",
    browserPlugin: "plugin de navigateur",
    signupLoginEmail: "Se connecter avec un email.",
    signupLoginGoogle: "Se connecter avec Google",
    signupLoginGithub: "Se connecter avec GitHub",
    signupLoginMicrosoft: "Se connecter avec Microsoft",
    signupLoginNostr: "Se connecter avec Nostr",
    privacyPolicy: "Politique de confidentialit\xE9",
    termsOfService: "Conditions d'utilisation"
  },
  login: {
    welcomeBack: "Bon retour",
    cardSubtitle: "Continuez avec un fournisseur social ou saisissez votre adresse e-mail.",
    orDivider: "OU",
    loginWithEmail: "Se connecter par e-mail",
    magicLinkInfo: "Nous enverrons un lien magique \xE0 votre bo\xEEte de r\xE9ception.",
    publicKeyRequired: "Cl\xE9 publique requise",
    invalidEmail: "Email invalide",
    getPublicKey: "Obtenir la cl\xE9 publique depuis l'extension",
    emailTab: "Email",
    googleTab: "Google",
    githubTab: "GitHub",
    microsoftTab: "Microsoft",
    nostrTab: "Nostr",
    oidcSsoTab: "GoFranz SSO",
    emailDescription: "Se connecter avec un email.",
    googleDescription: "Se connecter avec Google",
    nostrPluginDescription: "Se connecter avec Nos2x ou similaire.",
    nostrKeyDescription: "Se connecter avec votre cl\xE9 priv\xE9e.",
    nostrOtherDescription: "Fournir la signature par d'autres moyens.",
    loadedPublicKey: "Cl\xE9 publique charg\xE9e",
    noKeyLoaded: "Aucune cl\xE9 charg\xE9e...",
    enterPublicKey: "Saisissez votre cl\xE9 publique",
    publicKeyPlaceholder: "32b72e6432f7282135cf124a8...",
    emailLabel: "Adresse email",
    emailPlaceholder: "votre-email@fournisseur.com",
    enterCode: "Saisissez le code de l'email",
    codePlaceholder: "6fd7e67e-6f42-4354-b8f8-775bc1e52a50",
    privateKeyLabel: "Saisissez votre cl\xE9 priv\xE9e",
    challengeResponseLabel: "Saisissez votre r\xE9ponse au d\xE9fi",
    emailInstructions: "Cliquez sur le lien dans l'email, ou copiez le code et collez-le ici.",
    nostrInstructions: "Votre extension Nostr devrait vous demander de signer un \xE9v\xE9nement d'authentification pour continuer.",
    challenge: "D\xE9fi : ",
    loginWithExtension: "Se connecter avec l'extension",
    redirectingGoogle: "Redirection vers Google...",
    redirectingGithub: "Redirection vers GitHub...",
    redirectingMicrosoft: "Redirection vers Microsoft...",
    redirectingOidc: "Redirection vers GoFranz SSO...",
    githubNoChallenge: "\xC9chec de l'obtention du d\xE9fi GitHub",
    microsoftNoChallenge: "\xC9chec de l'obtention du d\xE9fi Microsoft",
    oidcNoChallenge: "\xC9chec de l'obtention du d\xE9fi GoFranz SSO",
    checkYourEmail: "V\xE9rifiez votre email pour le lien de connexion",
    emailSent: "Nous avons envoy\xE9 un lien de connexion \xE0 votre email",
    clickLinkToLogin: "Cliquez sur le lien dans l'email pour terminer la connexion",
    wrongEmail: "Mauvais email ? R\xE9essayez",
    errorNotPrimaryEmail: "Cet email n'est pas votre email de compte principal.",
    errorDifferentAuthMethod: "Cet email est enregistr\xE9 avec une m\xE9thode d'authentification diff\xE9rente",
    errorGoogleEmailNotVerified: "Email Google non v\xE9rifi\xE9",
    errorInvalidAuthMethodGoogle: "Ce compte utilise la connexion Google",
    errorInvalidAuthMethodGithub: "Ce compte utilise la connexion GitHub",
    errorInvalidAuthMethodMicrosoft: "Ce compte utilise la connexion Microsoft",
    errorInvalidAuthMethodNostr: "Ce compte utilise la connexion Nostr",
    errorAuthMethodCollision: "Cet e-mail est d\xE9j\xE0 enregistr\xE9 via {{method}}. Veuillez vous connecter avec cette m\xE9thode.",
    errorAuthMethodCollisionGeneric: "Cet e-mail est d\xE9j\xE0 enregistr\xE9 avec une autre m\xE9thode de connexion. Veuillez utiliser votre m\xE9thode d'origine.",
    errorInvalidNostrSignature: "Signature Nostr invalide",
    errorNostrKeyMismatch: "Cl\xE9 Nostr incompatible",
    errorOAuthFailed: "\xC9chec de l'authentification OAuth",
    errorTokenExchange: "\xC9chec de la connexion. Veuillez r\xE9essayer.",
    errorCodeExpired: "Session de connexion expir\xE9e. Veuillez vous reconnecter.",
    invalidLoginResponse: "R\xE9ponse de connexion invalide. Veuillez vous reconnecter.",
    errorRateLimited: "Trop de tentatives de connexion. Veuillez patienter {{seconds}} secondes et r\xE9essayer.",
    processing: "Traitement de la connexion...",
    somethingWrong: "Quelque chose s'est mal pass\xE9",
    invalidMagicLink: "Lien magique invalide",
    noIdProvided: "Aucun ID de session fourni",
    noChallengeProvided: "Aucun d\xE9fi fourni",
    back: "retour",
    cancel: "Annuler",
    submit: "Soumettre"
  },
  glob_buttons: {
    save: "Enregistrer",
    cancel: "Annuler",
    submit: "Soumettre",
    create: "Cr\xE9er",
    update: "Mettre \xE0 jour",
    updateUnsaved: "Mettre \xE0 jour (Modifications non sauvegard\xE9es)",
    delete: "Supprimer",
    calculate: "Calculer",
    login: "Se connecter",
    back: "retour"
  },
  support: {
    title: "Support",
    getHelp: "Obtenir de l'aide avec {{serviceName}}",
    description: "Vous avez une question ou besoin d'assistance ? Nous sommes l\xE0 pour vous aider ! Remplissez le formulaire ci-dessous et nous vous r\xE9pondrons d\xE8s que possible.",
    thankYou: "Merci !",
    successMessage: "Votre demande de support a \xE9t\xE9 soumise avec succ\xE8s. Nous vous r\xE9pondrons d\xE8s que possible.",
    submitAnother: "Soumettre une autre demande",
    successAlert: "Succ\xE8s !",
    successAlertMessage: "Votre demande de support a \xE9t\xE9 soumise avec succ\xE8s !",
    nameLabel: "Nom (Optionnel)",
    namePlaceholder: "Votre nom complet",
    emailLabel: "Email",
    emailPlaceholder: "votre.email@exemple.com",
    loggedInEmail: "Utilisation de votre email de connexion",
    subjectLabel: "Sujet",
    subjectPlaceholder: "S\xE9lectionnez le type de votre demande",
    messageLabel: "Message",
    messagePlaceholder: "Veuillez fournir des d\xE9tails sur votre question ou probl\xE8me...",
    formInfo: "Ce formulaire inclura votre m\xE9thode de connexion (",
    formInfoEnd: ") et identifiant utilisateur pour mieux vous aider.",
    submitting: "Envoi en cours...",
    submitButton: "Soumettre la demande de support",
    types: {
      general: "Question g\xE9n\xE9rale",
      technical: "Question technique",
      feature: "Demande de fonctionnalit\xE9",
      bug: "Signaler un bug"
    }
  },
  colorScheme: {
    lightMode: "Mode clair (clic pour sombre)",
    darkMode: "Mode sombre (clic pour auto)",
    autoMode: "Mode automatique (clic pour clair)",
    toggle: "Basculer le sch\xE9ma de couleurs"
  },
  privacy: {
    title: "Confidentialit\xE9",
    backHome: "retour \xE0 l'accueil",
    gdprCompliant: "\u{1F1E9}\u{1F1EA} Conforme RGPD. Les serveurs sont situ\xE9s en Allemagne. "
  },
  terms: {
    title: "Conditions de service"
  },
  news: {
    title: "De notre blog",
    loading: "Chargement ...",
    rssDescription: "Vous pouvez vous abonner \xE0 ce flux Atom ici "
  },
  profile: {
    balance: "Solde",
    addMoney: "Ajouter de l'argent",
    noBalanceFound: "Aucun solde trouv\xE9.",
    depositSuccess: "D\xE9p\xF4t r\xE9ussi",
    depositSuccessMessage: "Votre d\xE9p\xF4t a \xE9t\xE9 effectu\xE9 avec succ\xE8s. Les paiements par carte devraient \xEAtre appliqu\xE9s en quelques secondes.",
    wirePaymentInfo: "Vous souhaitez payer par virement ou Bitcoin ? (\u20AC100+) : ",
    personalInformation: "Informations personnelles",
    personalInfoMessage: "Nous ne savons vraiment pas grand-chose sur vous.",
    personalInfoContact: "Envoyez un email \xE0 ",
    personalInfoGreeting: " pour dire bonjour.",
    emails: "Emails",
    loginInformation: "Informations de connexion",
    currentlyLoggedIn: "Vous \xEAtes actuellement connect\xE9 avec ",
    saveKeysAlert: "Sauvegardez ces cl\xE9s dans votre gestionnaire de mots de passe ou dans un endroit s\xFBr. Nous ne pouvons pas les r\xE9cup\xE9rer. Vous pouvez les utiliser pour acc\xE9der au service depuis d'autres appareils et vous connecter \xE0 nos autres services.",
    downloadKeys: "T\xE9l\xE9charger les cl\xE9s",
    publicKey: "Cl\xE9 publique",
    publicKeyDescription: "Votre cl\xE9 publique est ce qui vous identifie ; nous la stockons sur notre serveur.",
    privateKey: "Cl\xE9 priv\xE9e",
    privateKeyDescription: "Votre cl\xE9 priv\xE9e est ce que vous utilisez pour vous connecter et ne quitte jamais votre ordinateur.",
    security: "S\xE9curit\xE9",
    dataPrivacy: "Donn\xE9es et confidentialit\xE9",
    dataPrivacyDescription: "Exportez vos donn\xE9es ou supprimez votre compte.",
    manageMyData: "G\xE9rer mes donn\xE9es",
    availableFunds: "Fonds disponibles",
    lowBalance: "Rechargez votre compte pour que tout continue de fonctionner.",
    authProvider: "Fournisseur d'authentification",
    publicKeyHex: "Cl\xE9 publique (Hex)",
    emailsDescription: "G\xE9rez vos adresses e-mail pour les alertes de s\xE9curit\xE9 et les notifications.",
    addEmail: "Ajouter un e-mail",
    sessionHistory: "Historique des sessions",
    viewLog: "Voir",
    providerSubtitle: {
      NOSTR: "Connexion d\xE9centralis\xE9e",
      GOOGLE: "Google OAuth",
      GITHUB: "GitHub OAuth",
      MICROSOFT: "Microsoft OAuth",
      EMAIL_MAGIC_LINK: "E-mail sans mot de passe",
      MANUAL: "Paire de cl\xE9s auto-g\xE9r\xE9e",
      PRIVATE_KEY: "Connexion par cl\xE9 priv\xE9e"
    }
  },
  emails: {
    accountEmail: "E-mail du compte",
    accountEmailDescription: "Ceci est votre e-mail de compte utilis\xE9 pour la connexion et les notifications, y compris les alertes de solde faible.",
    makePrimary: "D\xE9finir comme principal",
    resendVerification: "Renvoyer la v\xE9rification",
    verificationSent: "E-mail de v\xE9rification envoy\xE9. V\xE9rifiez votre bo\xEEte de r\xE9ception.",
    accountEmailUpdated: "E-mail du compte mis \xE0 jour avec succ\xE8s."
  },
  glob_footer: {
    product: "Produit",
    account: "Compte"
  },
  glob_billing: {
    recentAccountMovements: "Mouvements de compte r\xE9cents",
    loadingAccountMovements: "Chargement des mouvements de compte...",
    noAccountMovements: "Aucun mouvement de compte trouv\xE9.",
    date: "Date",
    amount: "Montant",
    type: "Type",
    features: "Fonctionnalit\xE9s",
    credit: "Cr\xE9dit",
    debit: "D\xE9bit",
    billingOverview: "Aper\xE7u de facturation",
    payAsYouGoCredits: "Cr\xE9dits \xE0 la demande",
    noBalanceFound: "Aucun solde trouv\xE9",
    addCredits: "Ajouter des cr\xE9dits",
    hybridBillingModel: "Mod\xE8le de facturation hybride",
    hybridBillingDescription: "Lorsque les allocations d'abonnement sont \xE9puis\xE9es, l'utilisation bascule automatiquement sur les cr\xE9dits \xE0 la demande. Vous ne perdrez jamais le service - juste une utilisation suppl\xE9mentaire au-del\xE0 de vos limites de plan.",
    yourReferralCode: "Votre code de parrainage",
    shareDirectLink: "Ou partagez ce lien d'inscription direct :",
    referralDescription: "Lorsque quelqu'un s'inscrit avec votre lien de parrainage et effectue son premier paiement d'abonnement ou d\xE9p\xF4t, vous gagnez 10% de ce montant, et ils re\xE7oivent un bonus de 10%.",
    copy: "Copier",
    copied: "Copi\xE9",
    copyLink: "Copier le lien",
    linkCopied: "Lien copi\xE9",
    referralStatistics: "Statistiques de parrainage",
    totalReferrals: "Parrainages totaux",
    totalEarnings: "Gains totaux",
    pendingEarnings: "Gains en attente",
    recentReferrals: "Parrainages r\xE9cents",
    firstPayment: "Premier paiement",
    bonus: "Bonus",
    status: "Statut",
    processed: "Trait\xE9",
    pending: "En attente",
    bonusPaid: "Bonus pay\xE9",
    awaitingBonus: "En attente du bonus",
    pendingPayment: "Paiement en attente",
    noReferrals: "Aucun parrainage encore. Partagez votre code de parrainage pour commencer \xE0 gagner des commissions !",
    loadingReferralHistory: "Chargement de l'historique de parrainage...",
    loadingReferralInfo: "Chargement des informations de parrainage...",
    noActiveSubscription: "Aucun abonnement actif",
    noActiveSubscriptionDescription: "Vous n'avez pas d'abonnement actif. Vous utilisez notre mod\xE8le de facturation \xE0 la demande.",
    currentSubscription: "Abonnement actuel",
    plan: "Plan",
    periodStarted: "P\xE9riode commenc\xE9e",
    nextBilling: "Prochaine facturation",
    nextCycle: "Prochain cycle",
    free: "Gratuit",
    unlimited: "Illimit\xE9",
    manageViaStripe: "G\xE9rer via Stripe",
    cancel: "Annuler"
  },
  securityLog: {
    title: "Journal de s\xE9curit\xE9",
    description: "Activit\xE9 r\xE9cente li\xE9e \xE0 la s\xE9curit\xE9 de ton compte. Les \xE9v\xE9nements en \xE9chec sont mis en \xE9vidence.",
    when: "Quand",
    event: "\xC9v\xE9nement",
    location: "Lieu",
    device: "Appareil",
    status: "Statut",
    ok: "OK",
    failed: "\xC9chec",
    empty: "Aucune activit\xE9 enregistr\xE9e pour le moment.",
    errorTitle: "Impossible de charger le journal de s\xE9curit\xE9"
  }
};

// src/i18n/locales/pt.json
var pt_default = {
  glob_navigation: {
    home: "In\xEDcio",
    pricing: "Pre\xE7os",
    docs: "Documenta\xE7\xE3o",
    news: "Not\xEDcias",
    more: "Mais",
    login: "Entrar",
    signup: "Cadastrar",
    getStarted: "Come\xE7ar",
    forms: "Formul\xE1rios",
    integrations: "Integra\xE7\xF5es",
    messages: "Mensagens",
    profile: "Perfil",
    support: "Suporte",
    logout: "Sair",
    terms: "Termos",
    billing: "Faturamento",
    usageAndCredits: "Uso e cr\xE9ditos",
    subscriptions: "Assinaturas",
    referrals: "Indica\xE7\xF5es",
    apiKeys: "Chaves API",
    securityLog: "Registro de seguran\xE7a",
    chat: "Chat",
    sectionWorkspace: "Espa\xE7o de trabalho",
    sectionAccount: "Conta",
    sectionHelp: "Ajuda"
  },
  auth: {
    login: "Entrar",
    signup: "Cadastrar",
    logout: "Sair",
    step1: "Passo 1",
    step2: "Passo 2",
    authenticate: "Autenticar",
    enterKey: "Identificador",
    newHere: "Novo aqui? ",
    howToSignup: "Como se cadastrar",
    alreadyAccount: "Se voc\xEA ainda n\xE3o tem uma conta, uma ser\xE1 criada para voc\xEA na primeira vez que fizer login. ",
    emailSection: "Link por email",
    googleSection: "Google",
    githubSection: "GitHub",
    microsoftSection: "Microsoft",
    nostrSection: "Plugin do navegador Nostr",
    signupDescription: "Receba um link enviado para seu email para fazer login. ",
    googleDescription: "Fa\xE7a login com sua conta do Google. ",
    githubDescription: "Fa\xE7a login com sua conta do GitHub. ",
    microsoftDescription: "Fa\xE7a login com sua conta da Microsoft. ",
    nostrDescription: "Cadastre-se usando um ",
    browserPlugin: "plugin de navegador",
    signupLoginEmail: "Entrar com email.",
    signupLoginGoogle: "Entrar com Google",
    signupLoginGithub: "Entrar com GitHub",
    signupLoginMicrosoft: "Entrar com Microsoft",
    signupLoginNostr: "Entrar com Nostr",
    privacyPolicy: "Pol\xEDtica de Privacidade",
    termsOfService: "Termos de Servi\xE7o"
  },
  login: {
    welcomeBack: "Bem-vindo de volta",
    cardSubtitle: "Continue com um provedor social ou insira seu endere\xE7o de e-mail.",
    orDivider: "OU",
    loginWithEmail: "Entrar com E-mail",
    magicLinkInfo: "Enviaremos um link m\xE1gico para a sua caixa de entrada.",
    publicKeyRequired: "Chave p\xFAblica \xE9 obrigat\xF3ria",
    invalidEmail: "Email inv\xE1lido",
    getPublicKey: "Obter chave p\xFAblica da extens\xE3o",
    emailTab: "Email",
    googleTab: "Google",
    githubTab: "GitHub",
    microsoftTab: "Microsoft",
    nostrTab: "Nostr",
    oidcSsoTab: "GoFranz SSO",
    emailDescription: "Entrar com email.",
    googleDescription: "Entrar com Google",
    nostrPluginDescription: "Entrar com Nos2x ou similar.",
    nostrKeyDescription: "Entrar com sua chave privada.",
    nostrOtherDescription: "Fornecer assinatura por outros meios.",
    loadedPublicKey: "Chave p\xFAblica carregada",
    noKeyLoaded: "Nenhuma chave carregada...",
    enterPublicKey: "Digite sua chave p\xFAblica",
    publicKeyPlaceholder: "32b72e6432f7282135cf124a8...",
    emailLabel: "Endere\xE7o de email",
    emailPlaceholder: "seu-email@provedor.com",
    enterCode: "Digite o c\xF3digo do email",
    codePlaceholder: "6fd7e67e-6f42-4354-b8f8-775bc1e52a50",
    privateKeyLabel: "Digite sua chave privada",
    challengeResponseLabel: "Digite sua resposta ao desafio",
    emailInstructions: "Clique no link do email, ou copie o c\xF3digo e cole aqui.",
    nostrInstructions: "Sua extens\xE3o Nostr deve solicitar que voc\xEA assine um evento de autentica\xE7\xE3o para continuar.",
    challenge: "Desafio: ",
    loginWithExtension: "Entrar com extens\xE3o",
    redirectingGoogle: "Redirecionando para o Google...",
    redirectingGithub: "Redirecionando para o GitHub...",
    redirectingMicrosoft: "Redirecionando para o Microsoft...",
    redirectingOidc: "Redirecionando para o GoFranz SSO...",
    githubNoChallenge: "Falha ao obter o desafio do GitHub",
    microsoftNoChallenge: "Falha ao obter o desafio do Microsoft",
    oidcNoChallenge: "Falha ao obter o desafio do GoFranz SSO",
    checkYourEmail: "Verifique seu email para o link de login",
    emailSent: "Enviamos um link de login para seu email",
    clickLinkToLogin: "Clique no link no email para concluir o login",
    wrongEmail: "Email errado? Tente novamente",
    errorNotPrimaryEmail: "Este email n\xE3o \xE9 seu email principal da conta.",
    errorDifferentAuthMethod: "Este email est\xE1 registrado com um m\xE9todo de autentica\xE7\xE3o diferente",
    errorGoogleEmailNotVerified: "Email do Google n\xE3o verificado",
    errorInvalidAuthMethodGoogle: "Esta conta usa login com Google",
    errorInvalidAuthMethodGithub: "Esta conta usa login com GitHub",
    errorInvalidAuthMethodMicrosoft: "Esta conta usa login com Microsoft",
    errorInvalidAuthMethodNostr: "Esta conta usa login com Nostr",
    errorAuthMethodCollision: "Este e-mail j\xE1 est\xE1 registrado atrav\xE9s de {{method}}. Inicie sess\xE3o com esse m\xE9todo.",
    errorAuthMethodCollisionGeneric: "Este e-mail j\xE1 est\xE1 registrado com outro m\xE9todo de in\xEDcio de sess\xE3o. Utilize o seu m\xE9todo original.",
    errorInvalidNostrSignature: "Assinatura Nostr inv\xE1lida",
    errorNostrKeyMismatch: "Chaves Nostr n\xE3o correspondem",
    errorOAuthFailed: "Falha na autentica\xE7\xE3o OAuth",
    errorTokenExchange: "Falha ao completar o login. Tente novamente.",
    errorCodeExpired: "Sess\xE3o de login expirada. Fa\xE7a login novamente.",
    invalidLoginResponse: "Resposta de login inv\xE1lida. Fa\xE7a login novamente.",
    errorRateLimited: "Muitas tentativas de login. Aguarde {{seconds}} segundos e tente novamente.",
    processing: "Processando login...",
    somethingWrong: "Algo deu errado",
    invalidMagicLink: "Link m\xE1gico inv\xE1lido",
    noIdProvided: "Nenhum ID de sess\xE3o fornecido",
    noChallengeProvided: "Nenhum desafio fornecido",
    back: "voltar",
    cancel: "Cancelar",
    submit: "Enviar",
    twoFactorAuth: "Autentica\xE7\xE3o de dois fatores",
    twoFactorRequired: "Verifica\xE7\xE3o necess\xE1ria",
    enterTwoFactorCode: "Digite o c\xF3digo de 6 d\xEDgitos do seu aplicativo autenticador ou um c\xF3digo de recupera\xE7\xE3o.",
    verify: "Verificar",
    useRecoveryCode: "Voc\xEA tamb\xE9m pode usar um c\xF3digo de recupera\xE7\xE3o",
    invalid2FACode: "C\xF3digo de verifica\xE7\xE3o inv\xE1lido. Por favor, tente novamente.",
    tooMany2FAAttempts: "Muitas tentativas falhas. Por favor, comece novamente.",
    invalid2FASession: "Sess\xE3o 2FA inv\xE1lida ou expirada.",
    backToLogin: "Voltar ao login"
  },
  glob_buttons: {
    save: "Salvar",
    cancel: "Cancelar",
    submit: "Enviar",
    create: "Criar",
    update: "Atualizar",
    updateUnsaved: "Atualizar (Altera\xE7\xF5es n\xE3o salvas)",
    delete: "Excluir",
    calculate: "Calcular",
    login: "Entrar",
    back: "voltar"
  },
  support: {
    title: "Suporte",
    getHelp: "Obter ajuda com {{serviceName}}",
    description: "Tem uma pergunta ou precisa de assist\xEAncia? Estamos aqui para ajudar! Preencha o formul\xE1rio abaixo e entraremos em contato o mais breve poss\xEDvel.",
    thankYou: "Obrigado!",
    successMessage: "Sua solicita\xE7\xE3o de suporte foi enviada com sucesso. Entraremos em contato o mais breve poss\xEDvel.",
    submitAnother: "Enviar outra solicita\xE7\xE3o",
    successAlert: "Sucesso!",
    successAlertMessage: "Sua solicita\xE7\xE3o de suporte foi enviada com sucesso!",
    nameLabel: "Nome (Opcional)",
    namePlaceholder: "Seu nome completo",
    emailLabel: "Email",
    emailPlaceholder: "seu.email@exemplo.com",
    loggedInEmail: "Usando seu email logado",
    subjectLabel: "Assunto",
    subjectPlaceholder: "Selecione o tipo da sua solicita\xE7\xE3o",
    messageLabel: "Mensagem",
    messagePlaceholder: "Por favor, forne\xE7a detalhes sobre sua pergunta ou problema...",
    formInfo: "Este formul\xE1rio incluir\xE1 seu m\xE9todo de login (",
    formInfoEnd: ") e identificador de usu\xE1rio para ajud\xE1-lo melhor.",
    submitting: "Enviando...",
    submitButton: "Enviar solicita\xE7\xE3o de suporte",
    types: {
      general: "Pergunta geral",
      technical: "Pergunta t\xE9cnica",
      feature: "Solicita\xE7\xE3o de funcionalidade",
      bug: "Reportar um bug"
    }
  },
  colorScheme: {
    lightMode: "Modo claro (clique para escuro)",
    darkMode: "Modo escuro (clique para autom\xE1tico)",
    autoMode: "Modo autom\xE1tico (clique para claro)",
    toggle: "Alternar esquema de cores"
  },
  privacy: {
    title: "Privacidade",
    backHome: "voltar ao in\xEDcio",
    gdprCompliant: "\u{1F1E9}\u{1F1EA} Compat\xEDvel com GDPR. Servidores est\xE3o localizados na Alemanha. "
  },
  terms: {
    title: "Termos de servi\xE7o"
  },
  news: {
    title: "Do nosso blog",
    loading: "Carregando ...",
    rssDescription: "Voc\xEA pode se inscrever neste feed Atom aqui "
  },
  profile: {
    balance: "Saldo",
    addMoney: "Adicionar dinheiro",
    noBalanceFound: "Nenhum saldo encontrado.",
    depositSuccess: "Dep\xF3sito bem-sucedido",
    depositSuccessMessage: "Seu dep\xF3sito foi bem-sucedido. Pagamentos com cart\xE3o devem ser aplicados em segundos.",
    wirePaymentInfo: "Quer pagar por transfer\xEAncia ou Bitcoin? (\u20AC100+): ",
    personalInformation: "Informa\xE7\xF5es pessoais",
    personalInfoMessage: "N\xF3s realmente n\xE3o sabemos muito sobre voc\xEA.",
    personalInfoContact: "Envie um email para ",
    personalInfoGreeting: " para dizer oi.",
    emails: "Emails",
    loginInformation: "Informa\xE7\xF5es de login",
    currentlyLoggedIn: "Voc\xEA est\xE1 atualmente logado com ",
    saveKeysAlert: "Salve essas chaves em seu gerenciador de senhas ou em um local seguro. N\xE3o podemos recuper\xE1-las. Voc\xEA pode us\xE1-las para acessar o servi\xE7o de outros dispositivos e fazer login em nossos outros servi\xE7os.",
    downloadKeys: "Baixar chaves",
    publicKey: "Chave p\xFAblica",
    publicKeyDescription: "Sua chave p\xFAblica \xE9 o que te identifica; n\xF3s a armazenamos em nosso servidor.",
    privateKey: "Chave privada",
    privateKeyDescription: "Sua chave privada \xE9 o que voc\xEA usa para fazer login e nunca sai do seu computador.",
    security: "Seguran\xE7a",
    dataPrivacy: "Dados e privacidade",
    dataPrivacyDescription: "Exporte seus dados ou exclua sua conta.",
    manageMyData: "Gerenciar meus dados",
    availableFunds: "Fundos dispon\xEDveis",
    lowBalance: "Recarregue sua conta para manter tudo funcionando.",
    authProvider: "Provedor de autentica\xE7\xE3o",
    publicKeyHex: "Chave p\xFAblica (Hex)",
    emailsDescription: "Gerencie seus e-mails para alertas de seguran\xE7a e notifica\xE7\xF5es.",
    addEmail: "Adicionar e-mail",
    sessionHistory: "Hist\xF3rico de sess\xF5es",
    viewLog: "Ver",
    providerSubtitle: {
      NOSTR: "Login descentralizado",
      GOOGLE: "Google OAuth",
      GITHUB: "GitHub OAuth",
      MICROSOFT: "Microsoft OAuth",
      EMAIL_MAGIC_LINK: "E-mail sem senha",
      MANUAL: "Par de chaves autogerido",
      PRIVATE_KEY: "Login com chave privada"
    }
  },
  common: {
    copy: "Copiar",
    copied: "Copiado!",
    done: "Conclu\xEDdo",
    cancel: "Cancelar"
  },
  twoFactor: {
    loading: "Carregando status 2FA...",
    enabled: "Ativado",
    disabled: "Desativado",
    description: "Adicione uma camada extra de seguran\xE7a \xE0 sua conta exigindo um c\xF3digo de verifica\xE7\xE3o al\xE9m da sua senha.",
    enable: "Ativar autentica\xE7\xE3o de dois fatores",
    setupTitle: "Configurar autentica\xE7\xE3o de dois fatores",
    scanQRCode: "Escaneie este c\xF3digo QR com seu aplicativo autenticador (ex: Google Authenticator, Authy).",
    cantScanQR: "N\xE3o consegue escanear o c\xF3digo QR? Insira esta chave secreta manualmente:",
    next: "Pr\xF3ximo",
    verifySetup: "Verificar configura\xE7\xE3o",
    enterCodeFromApp: "Digite o c\xF3digo de 6 d\xEDgitos do seu aplicativo autenticador para confirmar a configura\xE7\xE3o.",
    recoveryCodes: "C\xF3digos de recupera\xE7\xE3o",
    saveRecoveryCodes: "Salve estes c\xF3digos de recupera\xE7\xE3o em um local seguro. Eles podem ser usados para acessar sua conta se voc\xEA perder o acesso ao seu aplicativo autenticador.",
    copyAllCodes: "Copiar todos os c\xF3digos",
    recoveryCodesRemaining: "C\xF3digos de recupera\xE7\xE3o restantes",
    regenerateRecoveryCodes: "Regenerar c\xF3digos de recupera\xE7\xE3o",
    regenerateWarning: "Isso invalidar\xE1 todos os c\xF3digos de recupera\xE7\xE3o existentes e gerar\xE1 novos.",
    enterCodeToRegenerate: "Digite um c\xF3digo do seu aplicativo autenticador para regenerar os c\xF3digos de recupera\xE7\xE3o.",
    regenerate: "Regenerar",
    disable2FA: "Desativar autentica\xE7\xE3o de dois fatores",
    disableWarning: "Desativar 2FA tornar\xE1 sua conta menos segura. Voc\xEA precisar\xE1 apenas da sua senha para fazer login.",
    enterCodeToDisable: "Digite um c\xF3digo do seu aplicativo autenticador ou um c\xF3digo de recupera\xE7\xE3o para desativar 2FA.",
    disable: "Desativar"
  },
  emails: {
    accountEmail: "E-mail da conta",
    accountEmailDescription: "Este \xE9 seu e-mail de conta usado para login e notifica\xE7\xF5es, incluindo alertas de saldo baixo.",
    makePrimary: "Definir como principal",
    resendVerification: "Reenviar verifica\xE7\xE3o",
    verificationSent: "E-mail de verifica\xE7\xE3o enviado. Verifique sua caixa de entrada.",
    accountEmailUpdated: "E-mail da conta atualizado com sucesso."
  },
  glob_footer: {
    product: "Produto",
    account: "Conta"
  },
  glob_billing: {
    recentAccountMovements: "Movimentos recentes da conta",
    loadingAccountMovements: "Carregando movimentos da conta...",
    noAccountMovements: "Nenhum movimento de conta encontrado.",
    date: "Data",
    amount: "Valor",
    type: "Tipo",
    features: "Recursos",
    credit: "Cr\xE9dito",
    debit: "D\xE9bito",
    billingOverview: "Resumo de faturamento",
    payAsYouGoCredits: "Cr\xE9ditos pr\xE9-pagos",
    noBalanceFound: "Nenhum saldo encontrado",
    addCredits: "Adicionar cr\xE9ditos",
    hybridBillingModel: "Modelo de faturamento h\xEDbrido",
    hybridBillingDescription: "Quando as permiss\xF5es de assinatura s\xE3o esgotadas, o uso volta automaticamente para os cr\xE9ditos pr\xE9-pagos. Voc\xEA nunca perder\xE1 o servi\xE7o - apenas uso adicional al\xE9m dos limites do seu plano.",
    yourReferralCode: "Seu c\xF3digo de indica\xE7\xE3o",
    shareDirectLink: "Ou compartilhe este link de cadastro direto:",
    referralDescription: "Quando algu\xE9m se cadastra com seu link de indica\xE7\xE3o e faz o primeiro pagamento de assinatura ou dep\xF3sito, voc\xEA ganha 10% desse valor, e eles recebem um b\xF4nus de 10%.",
    copy: "Copiar",
    copied: "Copiado",
    copyLink: "Copiar link",
    linkCopied: "Link copiado",
    referralStatistics: "Estat\xEDsticas de indica\xE7\xE3o",
    totalReferrals: "Indica\xE7\xF5es totais",
    totalEarnings: "Ganhos totais",
    pendingEarnings: "Ganhos pendentes",
    recentReferrals: "Indica\xE7\xF5es recentes",
    firstPayment: "Primeiro pagamento",
    bonus: "B\xF4nus",
    status: "Status",
    processed: "Processado",
    pending: "Pendente",
    bonusPaid: "B\xF4nus pago",
    awaitingBonus: "Aguardando b\xF4nus",
    pendingPayment: "Pagamento pendente",
    noReferrals: "Ainda n\xE3o h\xE1 indica\xE7\xF5es. Compartilhe seu c\xF3digo de indica\xE7\xE3o para come\xE7ar a ganhar comiss\xF5es!",
    loadingReferralHistory: "Carregando hist\xF3rico de indica\xE7\xF5es...",
    loadingReferralInfo: "Carregando informa\xE7\xF5es de indica\xE7\xE3o...",
    noActiveSubscription: "Nenhuma assinatura ativa",
    noActiveSubscriptionDescription: "Voc\xEA n\xE3o tem uma assinatura ativa. Voc\xEA est\xE1 usando nosso modelo de faturamento pr\xE9-pago.",
    currentSubscription: "Assinatura atual",
    plan: "Plano",
    periodStarted: "Per\xEDodo iniciado",
    nextBilling: "Pr\xF3xima cobran\xE7a",
    nextCycle: "Pr\xF3ximo ciclo",
    free: "Gr\xE1tis",
    unlimited: "Ilimitado",
    manageViaStripe: "Gerenciar via Stripe",
    cancel: "Cancelar"
  },
  securityLog: {
    title: "Registo de seguran\xE7a",
    description: "Atividade recente relacionada com a seguran\xE7a da tua conta. Os eventos falhados aparecem destacados.",
    when: "Quando",
    event: "Evento",
    location: "Localiza\xE7\xE3o",
    device: "Dispositivo",
    status: "Estado",
    ok: "OK",
    failed: "Falhou",
    empty: "Ainda n\xE3o h\xE1 atividade registada.",
    errorTitle: "N\xE3o foi poss\xEDvel carregar o registo de seguran\xE7a"
  }
};

// src/i18n/locales/th.json
var th_default = {
  glob_navigation: {
    home: "\u0E2B\u0E19\u0E49\u0E32\u0E41\u0E23\u0E01",
    pricing: "\u0E23\u0E32\u0E04\u0E32",
    docs: "\u0E40\u0E2D\u0E01\u0E2A\u0E32\u0E23",
    news: "\u0E02\u0E48\u0E32\u0E27\u0E2A\u0E32\u0E23",
    more: "\u0E40\u0E1E\u0E34\u0E48\u0E21\u0E40\u0E15\u0E34\u0E21",
    login: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A",
    signup: "\u0E2A\u0E21\u0E31\u0E04\u0E23\u0E2A\u0E21\u0E32\u0E0A\u0E34\u0E01",
    getStarted: "\u0E40\u0E23\u0E34\u0E48\u0E21\u0E15\u0E49\u0E19\u0E43\u0E0A\u0E49\u0E07\u0E32\u0E19",
    forms: "\u0E41\u0E1A\u0E1A\u0E1F\u0E2D\u0E23\u0E4C\u0E21",
    integrations: "\u0E01\u0E32\u0E23\u0E23\u0E27\u0E21\u0E23\u0E30\u0E1A\u0E1A",
    messages: "\u0E02\u0E49\u0E2D\u0E04\u0E27\u0E32\u0E21",
    profile: "\u0E42\u0E1B\u0E23\u0E44\u0E1F\u0E25\u0E4C",
    support: "\u0E01\u0E32\u0E23\u0E2A\u0E19\u0E31\u0E1A\u0E2A\u0E19\u0E38\u0E19",
    logout: "\u0E2D\u0E2D\u0E01\u0E08\u0E32\u0E01\u0E23\u0E30\u0E1A\u0E1A",
    terms: "\u0E40\u0E07\u0E37\u0E48\u0E2D\u0E19\u0E44\u0E02",
    billing: "\u0E01\u0E32\u0E23\u0E40\u0E23\u0E35\u0E22\u0E01\u0E40\u0E01\u0E47\u0E1A\u0E40\u0E07\u0E34\u0E19",
    usageAndCredits: "\u0E01\u0E32\u0E23\u0E43\u0E0A\u0E49\u0E07\u0E32\u0E19\u0E41\u0E25\u0E30\u0E40\u0E04\u0E23\u0E14\u0E34\u0E15",
    subscriptions: "\u0E01\u0E32\u0E23\u0E2A\u0E21\u0E31\u0E04\u0E23\u0E2A\u0E21\u0E32\u0E0A\u0E34\u0E01",
    referrals: "\u0E01\u0E32\u0E23\u0E41\u0E19\u0E30\u0E19\u0E33",
    apiKeys: "\u0E04\u0E35\u0E22\u0E4C API",
    securityLog: "\u0E1A\u0E31\u0E19\u0E17\u0E36\u0E01\u0E04\u0E27\u0E32\u0E21\u0E1B\u0E25\u0E2D\u0E14\u0E20\u0E31\u0E22",
    chat: "\u0E41\u0E0A\u0E17",
    sectionWorkspace: "\u0E1E\u0E37\u0E49\u0E19\u0E17\u0E35\u0E48\u0E17\u0E33\u0E07\u0E32\u0E19",
    sectionAccount: "\u0E1A\u0E31\u0E0D\u0E0A\u0E35",
    sectionHelp: "\u0E0A\u0E48\u0E27\u0E22\u0E40\u0E2B\u0E25\u0E37\u0E2D"
  },
  auth: {
    login: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A",
    signup: "\u0E2A\u0E21\u0E31\u0E04\u0E23\u0E2A\u0E21\u0E32\u0E0A\u0E34\u0E01",
    logout: "\u0E2D\u0E2D\u0E01\u0E08\u0E32\u0E01\u0E23\u0E30\u0E1A\u0E1A",
    step1: "\u0E02\u0E31\u0E49\u0E19\u0E15\u0E2D\u0E19\u0E17\u0E35\u0E48 1",
    step2: "\u0E02\u0E31\u0E49\u0E19\u0E15\u0E2D\u0E19\u0E17\u0E35\u0E48 2",
    authenticate: "\u0E22\u0E37\u0E19\u0E22\u0E31\u0E19\u0E15\u0E31\u0E27\u0E15\u0E19",
    enterKey: "\u0E15\u0E31\u0E27\u0E23\u0E30\u0E1A\u0E38",
    newHere: "\u0E43\u0E2B\u0E21\u0E48\u0E17\u0E35\u0E48\u0E19\u0E35\u0E48? ",
    howToSignup: "\u0E27\u0E34\u0E18\u0E35\u0E2A\u0E21\u0E31\u0E04\u0E23\u0E2A\u0E21\u0E32\u0E0A\u0E34\u0E01",
    alreadyAccount: "\u0E2B\u0E32\u0E01\u0E04\u0E38\u0E13\u0E22\u0E31\u0E07\u0E44\u0E21\u0E48\u0E21\u0E35\u0E1A\u0E31\u0E0D\u0E0A\u0E35 \u0E1A\u0E31\u0E0D\u0E0A\u0E35\u0E08\u0E30\u0E16\u0E39\u0E01\u0E2A\u0E23\u0E49\u0E32\u0E07\u0E43\u0E2B\u0E49\u0E04\u0E38\u0E13\u0E43\u0E19\u0E04\u0E23\u0E31\u0E49\u0E07\u0E41\u0E23\u0E01\u0E17\u0E35\u0E48\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A ",
    emailSection: "\u0E25\u0E34\u0E07\u0E01\u0E4C\u0E2D\u0E35\u0E40\u0E21\u0E25",
    googleSection: "Google",
    githubSection: "GitHub",
    microsoftSection: "Microsoft",
    nostrSection: "\u0E1B\u0E25\u0E31\u0E4A\u0E01\u0E2D\u0E34\u0E19\u0E40\u0E1A\u0E23\u0E32\u0E27\u0E4C\u0E40\u0E0B\u0E2D\u0E23\u0E4C Nostr",
    signupDescription: "\u0E23\u0E31\u0E1A\u0E25\u0E34\u0E07\u0E01\u0E4C\u0E17\u0E35\u0E48\u0E2A\u0E48\u0E07\u0E44\u0E1B\u0E22\u0E31\u0E07\u0E2D\u0E35\u0E40\u0E21\u0E25\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E40\u0E1E\u0E37\u0E48\u0E2D\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A ",
    googleDescription: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E14\u0E49\u0E27\u0E22\u0E1A\u0E31\u0E0D\u0E0A\u0E35 Google \u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13 ",
    githubDescription: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E14\u0E49\u0E27\u0E22\u0E1A\u0E31\u0E0D\u0E0A\u0E35 GitHub \u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13 ",
    microsoftDescription: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E14\u0E49\u0E27\u0E22\u0E1A\u0E31\u0E0D\u0E0A\u0E35 Microsoft \u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13 ",
    nostrDescription: "\u0E2A\u0E21\u0E31\u0E04\u0E23\u0E2A\u0E21\u0E32\u0E0A\u0E34\u0E01\u0E42\u0E14\u0E22\u0E43\u0E0A\u0E49",
    browserPlugin: "\u0E1B\u0E25\u0E31\u0E4A\u0E01\u0E2D\u0E34\u0E19\u0E40\u0E1A\u0E23\u0E32\u0E27\u0E4C\u0E40\u0E0B\u0E2D\u0E23\u0E4C",
    signupLoginEmail: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E14\u0E49\u0E27\u0E22\u0E2D\u0E35\u0E40\u0E21\u0E25",
    signupLoginGoogle: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E14\u0E49\u0E27\u0E22 Google",
    signupLoginGithub: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E14\u0E49\u0E27\u0E22 GitHub",
    signupLoginMicrosoft: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E14\u0E49\u0E27\u0E22 Microsoft",
    signupLoginNostr: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E14\u0E49\u0E27\u0E22 Nostr",
    privacyPolicy: "\u0E19\u0E42\u0E22\u0E1A\u0E32\u0E22\u0E04\u0E27\u0E32\u0E21\u0E40\u0E1B\u0E47\u0E19\u0E2A\u0E48\u0E27\u0E19\u0E15\u0E31\u0E27",
    termsOfService: "\u0E02\u0E49\u0E2D\u0E01\u0E33\u0E2B\u0E19\u0E14\u0E01\u0E32\u0E23\u0E43\u0E2B\u0E49\u0E1A\u0E23\u0E34\u0E01\u0E32\u0E23"
  },
  login: {
    welcomeBack: "\u0E22\u0E34\u0E19\u0E14\u0E35\u0E15\u0E49\u0E2D\u0E19\u0E23\u0E31\u0E1A\u0E01\u0E25\u0E31\u0E1A",
    cardSubtitle: "\u0E14\u0E33\u0E40\u0E19\u0E34\u0E19\u0E01\u0E32\u0E23\u0E15\u0E48\u0E2D\u0E14\u0E49\u0E27\u0E22\u0E1C\u0E39\u0E49\u0E43\u0E2B\u0E49\u0E1A\u0E23\u0E34\u0E01\u0E32\u0E23\u0E42\u0E0B\u0E40\u0E0A\u0E35\u0E22\u0E25\u0E2B\u0E23\u0E37\u0E2D\u0E1B\u0E49\u0E2D\u0E19\u0E17\u0E35\u0E48\u0E2D\u0E22\u0E39\u0E48\u0E2D\u0E35\u0E40\u0E21\u0E25\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13",
    orDivider: "\u0E2B\u0E23\u0E37\u0E2D",
    loginWithEmail: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E14\u0E49\u0E27\u0E22\u0E2D\u0E35\u0E40\u0E21\u0E25",
    magicLinkInfo: "\u0E40\u0E23\u0E32\u0E08\u0E30\u0E2A\u0E48\u0E07\u0E25\u0E34\u0E07\u0E01\u0E4C\u0E40\u0E27\u0E17\u0E21\u0E19\u0E15\u0E23\u0E4C\u0E44\u0E1B\u0E22\u0E31\u0E07\u0E01\u0E25\u0E48\u0E2D\u0E07\u0E08\u0E14\u0E2B\u0E21\u0E32\u0E22\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13",
    publicKeyRequired: "\u0E08\u0E33\u0E40\u0E1B\u0E47\u0E19\u0E15\u0E49\u0E2D\u0E07\u0E21\u0E35\u0E04\u0E35\u0E22\u0E4C\u0E2A\u0E32\u0E18\u0E32\u0E23\u0E13\u0E30",
    invalidEmail: "\u0E2D\u0E35\u0E40\u0E21\u0E25\u0E44\u0E21\u0E48\u0E16\u0E39\u0E01\u0E15\u0E49\u0E2D\u0E07",
    getPublicKey: "\u0E23\u0E31\u0E1A\u0E04\u0E35\u0E22\u0E4C\u0E2A\u0E32\u0E18\u0E32\u0E23\u0E13\u0E30\u0E08\u0E32\u0E01\u0E2A\u0E48\u0E27\u0E19\u0E02\u0E22\u0E32\u0E22",
    emailTab: "\u0E2D\u0E35\u0E40\u0E21\u0E25",
    googleTab: "Google",
    githubTab: "GitHub",
    microsoftTab: "Microsoft",
    nostrTab: "Nostr",
    oidcSsoTab: "GoFranz SSO",
    emailDescription: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E14\u0E49\u0E27\u0E22\u0E2D\u0E35\u0E40\u0E21\u0E25",
    googleDescription: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E14\u0E49\u0E27\u0E22 Google",
    nostrPluginDescription: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E14\u0E49\u0E27\u0E22 Nos2x \u0E2B\u0E23\u0E37\u0E2D\u0E04\u0E25\u0E49\u0E32\u0E22\u0E01\u0E31\u0E19",
    nostrKeyDescription: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E14\u0E49\u0E27\u0E22\u0E04\u0E35\u0E22\u0E4C\u0E2A\u0E48\u0E27\u0E19\u0E15\u0E31\u0E27\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13",
    nostrOtherDescription: "\u0E43\u0E2B\u0E49\u0E25\u0E32\u0E22\u0E40\u0E0B\u0E47\u0E19\u0E1C\u0E48\u0E32\u0E19\u0E27\u0E34\u0E18\u0E35\u0E2D\u0E37\u0E48\u0E19",
    loadedPublicKey: "\u0E42\u0E2B\u0E25\u0E14\u0E04\u0E35\u0E22\u0E4C\u0E2A\u0E32\u0E18\u0E32\u0E23\u0E13\u0E30\u0E41\u0E25\u0E49\u0E27",
    noKeyLoaded: "\u0E44\u0E21\u0E48\u0E44\u0E14\u0E49\u0E42\u0E2B\u0E25\u0E14\u0E04\u0E35\u0E22\u0E4C...",
    enterPublicKey: "\u0E1B\u0E49\u0E2D\u0E19\u0E04\u0E35\u0E22\u0E4C\u0E2A\u0E32\u0E18\u0E32\u0E23\u0E13\u0E30\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13",
    publicKeyPlaceholder: "32b72e6432f7282135cf124a8...",
    emailLabel: "\u0E17\u0E35\u0E48\u0E2D\u0E22\u0E39\u0E48\u0E2D\u0E35\u0E40\u0E21\u0E25",
    emailPlaceholder: "your-email@provider.com",
    enterCode: "\u0E1B\u0E49\u0E2D\u0E19\u0E23\u0E2B\u0E31\u0E2A\u0E08\u0E32\u0E01\u0E2D\u0E35\u0E40\u0E21\u0E25",
    codePlaceholder: "6fd7e67e-6f42-4354-b8f8-775bc1e52a50",
    privateKeyLabel: "\u0E1B\u0E49\u0E2D\u0E19\u0E04\u0E35\u0E22\u0E4C\u0E2A\u0E48\u0E27\u0E19\u0E15\u0E31\u0E27\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13",
    challengeResponseLabel: "\u0E1B\u0E49\u0E2D\u0E19\u0E01\u0E32\u0E23\u0E15\u0E2D\u0E1A\u0E2A\u0E19\u0E2D\u0E07\u0E04\u0E27\u0E32\u0E21\u0E17\u0E49\u0E32\u0E17\u0E32\u0E22\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13",
    emailInstructions: "\u0E04\u0E25\u0E34\u0E01\u0E25\u0E34\u0E07\u0E01\u0E4C\u0E43\u0E19\u0E2D\u0E35\u0E40\u0E21\u0E25 \u0E2B\u0E23\u0E37\u0E2D\u0E04\u0E31\u0E14\u0E25\u0E2D\u0E01\u0E23\u0E2B\u0E31\u0E2A\u0E41\u0E25\u0E30\u0E27\u0E32\u0E07\u0E17\u0E35\u0E48\u0E19\u0E35\u0E48",
    nostrInstructions: "\u0E2A\u0E48\u0E27\u0E19\u0E02\u0E22\u0E32\u0E22 Nostr \u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E04\u0E27\u0E23\u0E41\u0E08\u0E49\u0E07\u0E43\u0E2B\u0E49\u0E04\u0E38\u0E13\u0E25\u0E07\u0E19\u0E32\u0E21\u0E43\u0E19\u0E40\u0E2B\u0E15\u0E38\u0E01\u0E32\u0E23\u0E13\u0E4C\u0E01\u0E32\u0E23\u0E22\u0E37\u0E19\u0E22\u0E31\u0E19\u0E15\u0E31\u0E27\u0E15\u0E19\u0E40\u0E1E\u0E37\u0E48\u0E2D\u0E14\u0E33\u0E40\u0E19\u0E34\u0E19\u0E01\u0E32\u0E23\u0E15\u0E48\u0E2D",
    challenge: "\u0E04\u0E27\u0E32\u0E21\u0E17\u0E49\u0E32\u0E17\u0E32\u0E22: ",
    loginWithExtension: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E14\u0E49\u0E27\u0E22\u0E2A\u0E48\u0E27\u0E19\u0E02\u0E22\u0E32\u0E22",
    redirectingGoogle: "\u0E01\u0E33\u0E25\u0E31\u0E07\u0E40\u0E1B\u0E25\u0E35\u0E48\u0E22\u0E19\u0E40\u0E2A\u0E49\u0E19\u0E17\u0E32\u0E07\u0E44\u0E1B\u0E22\u0E31\u0E07 Google...",
    redirectingGithub: "\u0E01\u0E33\u0E25\u0E31\u0E07\u0E40\u0E1B\u0E25\u0E35\u0E48\u0E22\u0E19\u0E40\u0E2A\u0E49\u0E19\u0E17\u0E32\u0E07\u0E44\u0E1B\u0E22\u0E31\u0E07 GitHub...",
    redirectingMicrosoft: "\u0E01\u0E33\u0E25\u0E31\u0E07\u0E40\u0E1B\u0E25\u0E35\u0E48\u0E22\u0E19\u0E40\u0E2A\u0E49\u0E19\u0E17\u0E32\u0E07\u0E44\u0E1B\u0E22\u0E31\u0E07 Microsoft...",
    redirectingOidc: "\u0E01\u0E33\u0E25\u0E31\u0E07\u0E40\u0E1B\u0E25\u0E35\u0E48\u0E22\u0E19\u0E40\u0E2A\u0E49\u0E19\u0E17\u0E32\u0E07\u0E44\u0E1B\u0E22\u0E31\u0E07 GoFranz SSO...",
    githubNoChallenge: "\u0E44\u0E21\u0E48\u0E2A\u0E32\u0E21\u0E32\u0E23\u0E16\u0E23\u0E31\u0E1A\u0E04\u0E27\u0E32\u0E21\u0E17\u0E49\u0E32\u0E17\u0E32\u0E22\u0E08\u0E32\u0E01 GitHub \u0E44\u0E14\u0E49",
    microsoftNoChallenge: "\u0E44\u0E21\u0E48\u0E2A\u0E32\u0E21\u0E32\u0E23\u0E16\u0E23\u0E31\u0E1A\u0E04\u0E27\u0E32\u0E21\u0E17\u0E49\u0E32\u0E17\u0E32\u0E22\u0E08\u0E32\u0E01 Microsoft \u0E44\u0E14\u0E49",
    oidcNoChallenge: "\u0E44\u0E21\u0E48\u0E2A\u0E32\u0E21\u0E32\u0E23\u0E16\u0E23\u0E31\u0E1A\u0E04\u0E27\u0E32\u0E21\u0E17\u0E49\u0E32\u0E17\u0E32\u0E22\u0E08\u0E32\u0E01 GoFranz SSO \u0E44\u0E14\u0E49",
    checkYourEmail: "Check your email for the login link",
    emailSent: "We've sent a login link to your email",
    clickLinkToLogin: "Click the link in the email to complete login",
    wrongEmail: "Wrong email? Try again",
    errorNotPrimaryEmail: "This email is not your primary account email.",
    errorDifferentAuthMethod: "This email is registered with a different authentication method",
    errorGoogleEmailNotVerified: "Google email not verified",
    errorInvalidAuthMethodGoogle: "This account uses Google login",
    errorInvalidAuthMethodGithub: "This account uses GitHub login",
    errorInvalidAuthMethodMicrosoft: "This account uses Microsoft login",
    errorInvalidAuthMethodNostr: "This account uses Nostr login",
    errorAuthMethodCollision: "\u0E2D\u0E35\u0E40\u0E21\u0E25\u0E19\u0E35\u0E49\u0E25\u0E07\u0E17\u0E30\u0E40\u0E1A\u0E35\u0E22\u0E19\u0E1C\u0E48\u0E32\u0E19 {{method}} \u0E44\u0E27\u0E49\u0E41\u0E25\u0E49\u0E27 \u0E01\u0E23\u0E38\u0E13\u0E32\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E14\u0E49\u0E27\u0E22\u0E27\u0E34\u0E18\u0E35\u0E19\u0E31\u0E49\u0E19\u0E41\u0E17\u0E19",
    errorAuthMethodCollisionGeneric: "\u0E2D\u0E35\u0E40\u0E21\u0E25\u0E19\u0E35\u0E49\u0E25\u0E07\u0E17\u0E30\u0E40\u0E1A\u0E35\u0E22\u0E19\u0E14\u0E49\u0E27\u0E22\u0E27\u0E34\u0E18\u0E35\u0E01\u0E32\u0E23\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E2D\u0E37\u0E48\u0E19\u0E44\u0E27\u0E49\u0E41\u0E25\u0E49\u0E27 \u0E01\u0E23\u0E38\u0E13\u0E32\u0E43\u0E0A\u0E49\u0E27\u0E34\u0E18\u0E35\u0E40\u0E14\u0E34\u0E21\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13",
    errorInvalidNostrSignature: "Invalid Nostr signature",
    errorNostrKeyMismatch: "Nostr key mismatch",
    errorOAuthFailed: "OAuth authentication failed",
    errorTokenExchange: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E44\u0E21\u0E48\u0E2A\u0E33\u0E40\u0E23\u0E47\u0E08 \u0E01\u0E23\u0E38\u0E13\u0E32\u0E25\u0E2D\u0E07\u0E43\u0E2B\u0E21\u0E48\u0E2D\u0E35\u0E01\u0E04\u0E23\u0E31\u0E49\u0E07",
    errorCodeExpired: "\u0E40\u0E0B\u0E2A\u0E0A\u0E31\u0E19\u0E01\u0E32\u0E23\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E2B\u0E21\u0E14\u0E2D\u0E32\u0E22\u0E38 \u0E01\u0E23\u0E38\u0E13\u0E32\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E2D\u0E35\u0E01\u0E04\u0E23\u0E31\u0E49\u0E07",
    invalidLoginResponse: "\u0E01\u0E32\u0E23\u0E15\u0E2D\u0E1A\u0E01\u0E25\u0E31\u0E1A\u0E01\u0E32\u0E23\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E44\u0E21\u0E48\u0E16\u0E39\u0E01\u0E15\u0E49\u0E2D\u0E07 \u0E01\u0E23\u0E38\u0E13\u0E32\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E2D\u0E35\u0E01\u0E04\u0E23\u0E31\u0E49\u0E07",
    errorRateLimited: "\u0E1E\u0E22\u0E32\u0E22\u0E32\u0E21\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E21\u0E32\u0E01\u0E40\u0E01\u0E34\u0E19\u0E44\u0E1B \u0E01\u0E23\u0E38\u0E13\u0E32\u0E23\u0E2D {{seconds}} \u0E27\u0E34\u0E19\u0E32\u0E17\u0E35\u0E41\u0E25\u0E49\u0E27\u0E25\u0E2D\u0E07\u0E43\u0E2B\u0E21\u0E48\u0E2D\u0E35\u0E01\u0E04\u0E23\u0E31\u0E49\u0E07",
    processing: "Processing login...",
    somethingWrong: "Something went wrong",
    invalidMagicLink: "Invalid magic link",
    noIdProvided: "No session ID provided",
    noChallengeProvided: "No challenge provided",
    back: "\u0E01\u0E25\u0E31\u0E1A",
    cancel: "\u0E22\u0E01\u0E40\u0E25\u0E34\u0E01",
    submit: "\u0E2A\u0E48\u0E07"
  },
  glob_buttons: {
    save: "\u0E1A\u0E31\u0E19\u0E17\u0E36\u0E01",
    cancel: "\u0E22\u0E01\u0E40\u0E25\u0E34\u0E01",
    submit: "\u0E2A\u0E48\u0E07",
    create: "\u0E2A\u0E23\u0E49\u0E32\u0E07",
    update: "\u0E2D\u0E31\u0E1B\u0E40\u0E14\u0E15",
    updateUnsaved: "\u0E2D\u0E31\u0E1B\u0E40\u0E14\u0E15 (\u0E01\u0E32\u0E23\u0E40\u0E1B\u0E25\u0E35\u0E48\u0E22\u0E19\u0E41\u0E1B\u0E25\u0E07\u0E17\u0E35\u0E48\u0E44\u0E21\u0E48\u0E44\u0E14\u0E49\u0E1A\u0E31\u0E19\u0E17\u0E36\u0E01)",
    delete: "\u0E25\u0E1A",
    calculate: "\u0E04\u0E33\u0E19\u0E27\u0E13",
    login: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A",
    back: "\u0E01\u0E25\u0E31\u0E1A"
  },
  support: {
    title: "\u0E01\u0E32\u0E23\u0E2A\u0E19\u0E31\u0E1A\u0E2A\u0E19\u0E38\u0E19",
    getHelp: "\u0E23\u0E31\u0E1A\u0E04\u0E27\u0E32\u0E21\u0E0A\u0E48\u0E27\u0E22\u0E40\u0E2B\u0E25\u0E37\u0E2D\u0E08\u0E32\u0E01 {{serviceName}}",
    description: "\u0E21\u0E35\u0E04\u0E33\u0E16\u0E32\u0E21\u0E2B\u0E23\u0E37\u0E2D\u0E15\u0E49\u0E2D\u0E07\u0E01\u0E32\u0E23\u0E04\u0E27\u0E32\u0E21\u0E0A\u0E48\u0E27\u0E22\u0E40\u0E2B\u0E25\u0E37\u0E2D? \u0E40\u0E23\u0E32\u0E1E\u0E23\u0E49\u0E2D\u0E21\u0E0A\u0E48\u0E27\u0E22\u0E40\u0E2B\u0E25\u0E37\u0E2D! \u0E01\u0E23\u0E2D\u0E01\u0E41\u0E1A\u0E1A\u0E1F\u0E2D\u0E23\u0E4C\u0E21\u0E14\u0E49\u0E32\u0E19\u0E25\u0E48\u0E32\u0E07\u0E41\u0E25\u0E30\u0E40\u0E23\u0E32\u0E08\u0E30\u0E15\u0E34\u0E14\u0E15\u0E48\u0E2D\u0E01\u0E25\u0E31\u0E1A\u0E42\u0E14\u0E22\u0E40\u0E23\u0E47\u0E27\u0E17\u0E35\u0E48\u0E2A\u0E38\u0E14",
    thankYou: "\u0E02\u0E2D\u0E1A\u0E04\u0E38\u0E13!",
    successMessage: "\u0E04\u0E33\u0E02\u0E2D\u0E2A\u0E19\u0E31\u0E1A\u0E2A\u0E19\u0E38\u0E19\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E44\u0E14\u0E49\u0E23\u0E31\u0E1A\u0E01\u0E32\u0E23\u0E2A\u0E48\u0E07\u0E40\u0E23\u0E35\u0E22\u0E1A\u0E23\u0E49\u0E2D\u0E22\u0E41\u0E25\u0E49\u0E27 \u0E40\u0E23\u0E32\u0E08\u0E30\u0E15\u0E34\u0E14\u0E15\u0E48\u0E2D\u0E01\u0E25\u0E31\u0E1A\u0E42\u0E14\u0E22\u0E40\u0E23\u0E47\u0E27\u0E17\u0E35\u0E48\u0E2A\u0E38\u0E14",
    submitAnother: "\u0E2A\u0E48\u0E07\u0E04\u0E33\u0E02\u0E2D\u0E2D\u0E37\u0E48\u0E19",
    successAlert: "\u0E2A\u0E33\u0E40\u0E23\u0E47\u0E08!",
    successAlertMessage: "\u0E04\u0E33\u0E02\u0E2D\u0E2A\u0E19\u0E31\u0E1A\u0E2A\u0E19\u0E38\u0E19\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E44\u0E14\u0E49\u0E23\u0E31\u0E1A\u0E01\u0E32\u0E23\u0E2A\u0E48\u0E07\u0E40\u0E23\u0E35\u0E22\u0E1A\u0E23\u0E49\u0E2D\u0E22\u0E41\u0E25\u0E49\u0E27!",
    nameLabel: "\u0E0A\u0E37\u0E48\u0E2D (\u0E44\u0E21\u0E48\u0E1A\u0E31\u0E07\u0E04\u0E31\u0E1A)",
    namePlaceholder: "\u0E0A\u0E37\u0E48\u0E2D\u0E40\u0E15\u0E47\u0E21\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13",
    emailLabel: "\u0E2D\u0E35\u0E40\u0E21\u0E25",
    emailPlaceholder: "your.email@example.com",
    loggedInEmail: "\u0E43\u0E0A\u0E49\u0E2D\u0E35\u0E40\u0E21\u0E25\u0E17\u0E35\u0E48\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13",
    subjectLabel: "\u0E40\u0E23\u0E37\u0E48\u0E2D\u0E07",
    subjectPlaceholder: "\u0E40\u0E25\u0E37\u0E2D\u0E01\u0E1B\u0E23\u0E30\u0E40\u0E20\u0E17\u0E02\u0E2D\u0E07\u0E04\u0E33\u0E02\u0E2D\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13",
    messageLabel: "\u0E02\u0E49\u0E2D\u0E04\u0E27\u0E32\u0E21",
    messagePlaceholder: "\u0E42\u0E1B\u0E23\u0E14\u0E43\u0E2B\u0E49\u0E23\u0E32\u0E22\u0E25\u0E30\u0E40\u0E2D\u0E35\u0E22\u0E14\u0E40\u0E01\u0E35\u0E48\u0E22\u0E27\u0E01\u0E31\u0E1A\u0E04\u0E33\u0E16\u0E32\u0E21\u0E2B\u0E23\u0E37\u0E2D\u0E1B\u0E31\u0E0D\u0E2B\u0E32\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13...",
    formInfo: "\u0E41\u0E1A\u0E1A\u0E1F\u0E2D\u0E23\u0E4C\u0E21\u0E19\u0E35\u0E49\u0E08\u0E30\u0E23\u0E27\u0E21\u0E27\u0E34\u0E18\u0E35\u0E01\u0E32\u0E23\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13 (",
    formInfoEnd: ") \u0E41\u0E25\u0E30\u0E15\u0E31\u0E27\u0E23\u0E30\u0E1A\u0E38\u0E1C\u0E39\u0E49\u0E43\u0E0A\u0E49\u0E40\u0E1E\u0E37\u0E48\u0E2D\u0E0A\u0E48\u0E27\u0E22\u0E40\u0E2B\u0E25\u0E37\u0E2D\u0E04\u0E38\u0E13\u0E44\u0E14\u0E49\u0E14\u0E35\u0E02\u0E36\u0E49\u0E19",
    submitting: "\u0E01\u0E33\u0E25\u0E31\u0E07\u0E2A\u0E48\u0E07...",
    submitButton: "\u0E2A\u0E48\u0E07\u0E04\u0E33\u0E02\u0E2D\u0E2A\u0E19\u0E31\u0E1A\u0E2A\u0E19\u0E38\u0E19",
    types: {
      general: "\u0E04\u0E33\u0E16\u0E32\u0E21\u0E17\u0E31\u0E48\u0E27\u0E44\u0E1B",
      technical: "\u0E04\u0E33\u0E16\u0E32\u0E21\u0E17\u0E32\u0E07\u0E40\u0E17\u0E04\u0E19\u0E34\u0E04",
      feature: "\u0E04\u0E33\u0E02\u0E2D\u0E1F\u0E35\u0E40\u0E08\u0E2D\u0E23\u0E4C",
      bug: "\u0E23\u0E32\u0E22\u0E07\u0E32\u0E19\u0E02\u0E49\u0E2D\u0E1C\u0E34\u0E14\u0E1E\u0E25\u0E32\u0E14"
    }
  },
  colorScheme: {
    lightMode: "\u0E42\u0E2B\u0E21\u0E14\u0E2A\u0E27\u0E48\u0E32\u0E07 (\u0E04\u0E25\u0E34\u0E01\u0E2A\u0E33\u0E2B\u0E23\u0E31\u0E1A\u0E42\u0E2B\u0E21\u0E14\u0E21\u0E37\u0E14)",
    darkMode: "\u0E42\u0E2B\u0E21\u0E14\u0E21\u0E37\u0E14 (\u0E04\u0E25\u0E34\u0E01\u0E2A\u0E33\u0E2B\u0E23\u0E31\u0E1A\u0E42\u0E2B\u0E21\u0E14\u0E2D\u0E31\u0E15\u0E42\u0E19\u0E21\u0E31\u0E15\u0E34)",
    autoMode: "\u0E42\u0E2B\u0E21\u0E14\u0E2D\u0E31\u0E15\u0E42\u0E19\u0E21\u0E31\u0E15\u0E34 (\u0E04\u0E25\u0E34\u0E01\u0E2A\u0E33\u0E2B\u0E23\u0E31\u0E1A\u0E42\u0E2B\u0E21\u0E14\u0E2A\u0E27\u0E48\u0E32\u0E07)",
    toggle: "\u0E2A\u0E25\u0E31\u0E1A\u0E23\u0E39\u0E1B\u0E41\u0E1A\u0E1A\u0E2A\u0E35"
  },
  privacy: {
    title: "\u0E04\u0E27\u0E32\u0E21\u0E40\u0E1B\u0E47\u0E19\u0E2A\u0E48\u0E27\u0E19\u0E15\u0E31\u0E27",
    backHome: "\u0E01\u0E25\u0E31\u0E1A\u0E2B\u0E19\u0E49\u0E32\u0E41\u0E23\u0E01",
    gdprCompliant: "\u{1F1E9}\u{1F1EA} \u0E2A\u0E2D\u0E14\u0E04\u0E25\u0E49\u0E2D\u0E07\u0E01\u0E31\u0E1A GDPR \u0E40\u0E0B\u0E34\u0E23\u0E4C\u0E1F\u0E40\u0E27\u0E2D\u0E23\u0E4C\u0E15\u0E31\u0E49\u0E07\u0E2D\u0E22\u0E39\u0E48\u0E43\u0E19\u0E40\u0E22\u0E2D\u0E23\u0E21\u0E19\u0E35 "
  },
  terms: {
    title: "\u0E40\u0E07\u0E37\u0E48\u0E2D\u0E19\u0E44\u0E02\u0E01\u0E32\u0E23\u0E43\u0E2B\u0E49\u0E1A\u0E23\u0E34\u0E01\u0E32\u0E23"
  },
  news: {
    title: "\u0E08\u0E32\u0E01\u0E1A\u0E25\u0E47\u0E2D\u0E01\u0E02\u0E2D\u0E07\u0E40\u0E23\u0E32",
    loading: "\u0E01\u0E33\u0E25\u0E31\u0E07\u0E42\u0E2B\u0E25\u0E14 ...",
    rssDescription: "\u0E04\u0E38\u0E13\u0E2A\u0E32\u0E21\u0E32\u0E23\u0E16\u0E2A\u0E21\u0E31\u0E04\u0E23\u0E23\u0E31\u0E1A Atom feed \u0E19\u0E35\u0E49\u0E17\u0E35\u0E48\u0E19\u0E35\u0E48 "
  },
  profile: {
    balance: "\u0E22\u0E2D\u0E14\u0E40\u0E07\u0E34\u0E19",
    addMoney: "\u0E40\u0E1E\u0E34\u0E48\u0E21\u0E40\u0E07\u0E34\u0E19",
    noBalanceFound: "\u0E44\u0E21\u0E48\u0E1E\u0E1A\u0E22\u0E2D\u0E14\u0E40\u0E07\u0E34\u0E19",
    depositSuccess: "\u0E1D\u0E32\u0E01\u0E40\u0E07\u0E34\u0E19\u0E2A\u0E33\u0E40\u0E23\u0E47\u0E08",
    depositSuccessMessage: "\u0E01\u0E32\u0E23\u0E1D\u0E32\u0E01\u0E40\u0E07\u0E34\u0E19\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E2A\u0E33\u0E40\u0E23\u0E47\u0E08\u0E41\u0E25\u0E49\u0E27 \u0E01\u0E32\u0E23\u0E0A\u0E33\u0E23\u0E30\u0E40\u0E07\u0E34\u0E19\u0E14\u0E49\u0E27\u0E22\u0E1A\u0E31\u0E15\u0E23\u0E04\u0E27\u0E23\u0E08\u0E30\u0E1B\u0E23\u0E32\u0E01\u0E0F\u0E43\u0E19\u0E44\u0E21\u0E48\u0E01\u0E35\u0E48\u0E27\u0E34\u0E19\u0E32\u0E17\u0E35",
    wirePaymentInfo: "\u0E15\u0E49\u0E2D\u0E07\u0E01\u0E32\u0E23\u0E0A\u0E33\u0E23\u0E30\u0E40\u0E07\u0E34\u0E19\u0E42\u0E14\u0E22\u0E01\u0E32\u0E23\u0E42\u0E2D\u0E19\u0E2B\u0E23\u0E37\u0E2D Bitcoin? (\u20AC100+): ",
    personalInformation: "\u0E02\u0E49\u0E2D\u0E21\u0E39\u0E25\u0E2A\u0E48\u0E27\u0E19\u0E1A\u0E38\u0E04\u0E04\u0E25",
    personalInfoMessage: "\u0E40\u0E23\u0E32\u0E44\u0E21\u0E48\u0E04\u0E48\u0E2D\u0E22\u0E23\u0E39\u0E49\u0E2D\u0E30\u0E44\u0E23\u0E40\u0E01\u0E35\u0E48\u0E22\u0E27\u0E01\u0E31\u0E1A\u0E04\u0E38\u0E13\u0E40\u0E17\u0E48\u0E32\u0E44\u0E2B\u0E23\u0E48",
    personalInfoContact: "\u0E2A\u0E48\u0E07\u0E2D\u0E35\u0E40\u0E21\u0E25\u0E44\u0E1B\u0E17\u0E35\u0E48 ",
    personalInfoGreeting: " \u0E40\u0E1E\u0E37\u0E48\u0E2D\u0E17\u0E31\u0E01\u0E17\u0E32\u0E22",
    emails: "\u0E2D\u0E35\u0E40\u0E21\u0E25",
    loginInformation: "\u0E02\u0E49\u0E2D\u0E21\u0E39\u0E25\u0E01\u0E32\u0E23\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A",
    currentlyLoggedIn: "\u0E04\u0E38\u0E13\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E2D\u0E22\u0E39\u0E48\u0E14\u0E49\u0E27\u0E22 ",
    saveKeysAlert: "\u0E1A\u0E31\u0E19\u0E17\u0E36\u0E01\u0E04\u0E35\u0E22\u0E4C\u0E40\u0E2B\u0E25\u0E48\u0E32\u0E19\u0E35\u0E49\u0E43\u0E19\u0E15\u0E31\u0E27\u0E08\u0E31\u0E14\u0E01\u0E32\u0E23\u0E23\u0E2B\u0E31\u0E2A\u0E1C\u0E48\u0E32\u0E19\u0E2B\u0E23\u0E37\u0E2D\u0E17\u0E35\u0E48\u0E1B\u0E25\u0E2D\u0E14\u0E20\u0E31\u0E22 \u0E40\u0E23\u0E32\u0E44\u0E21\u0E48\u0E2A\u0E32\u0E21\u0E32\u0E23\u0E16\u0E01\u0E39\u0E49\u0E04\u0E37\u0E19\u0E44\u0E14\u0E49 \u0E04\u0E38\u0E13\u0E2A\u0E32\u0E21\u0E32\u0E23\u0E16\u0E43\u0E0A\u0E49\u0E40\u0E1E\u0E37\u0E48\u0E2D\u0E40\u0E02\u0E49\u0E32\u0E16\u0E36\u0E07\u0E1A\u0E23\u0E34\u0E01\u0E32\u0E23\u0E08\u0E32\u0E01\u0E2D\u0E38\u0E1B\u0E01\u0E23\u0E13\u0E4C\u0E2D\u0E37\u0E48\u0E19\u0E41\u0E25\u0E30\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E1A\u0E23\u0E34\u0E01\u0E32\u0E23\u0E2D\u0E37\u0E48\u0E19\u0E02\u0E2D\u0E07\u0E40\u0E23\u0E32",
    downloadKeys: "\u0E14\u0E32\u0E27\u0E19\u0E4C\u0E42\u0E2B\u0E25\u0E14\u0E04\u0E35\u0E22\u0E4C",
    publicKey: "\u0E04\u0E35\u0E22\u0E4C\u0E2A\u0E32\u0E18\u0E32\u0E23\u0E13\u0E30",
    publicKeyDescription: "\u0E04\u0E35\u0E22\u0E4C\u0E2A\u0E32\u0E18\u0E32\u0E23\u0E13\u0E30\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E04\u0E37\u0E2D\u0E2A\u0E34\u0E48\u0E07\u0E17\u0E35\u0E48\u0E23\u0E30\u0E1A\u0E38\u0E15\u0E31\u0E27\u0E15\u0E19\u0E04\u0E38\u0E13 \u0E40\u0E23\u0E32\u0E40\u0E01\u0E47\u0E1A\u0E44\u0E27\u0E49\u0E1A\u0E19\u0E40\u0E0B\u0E34\u0E23\u0E4C\u0E1F\u0E40\u0E27\u0E2D\u0E23\u0E4C\u0E02\u0E2D\u0E07\u0E40\u0E23\u0E32",
    privateKey: "\u0E04\u0E35\u0E22\u0E4C\u0E2A\u0E48\u0E27\u0E19\u0E15\u0E31\u0E27",
    privateKeyDescription: "\u0E04\u0E35\u0E22\u0E4C\u0E2A\u0E48\u0E27\u0E19\u0E15\u0E31\u0E27\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E04\u0E37\u0E2D\u0E2A\u0E34\u0E48\u0E07\u0E17\u0E35\u0E48\u0E04\u0E38\u0E13\u0E43\u0E0A\u0E49\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E41\u0E25\u0E30\u0E44\u0E21\u0E48\u0E40\u0E04\u0E22\u0E2D\u0E2D\u0E01\u0E08\u0E32\u0E01\u0E04\u0E2D\u0E21\u0E1E\u0E34\u0E27\u0E40\u0E15\u0E2D\u0E23\u0E4C\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13",
    security: "\u0E04\u0E27\u0E32\u0E21\u0E1B\u0E25\u0E2D\u0E14\u0E20\u0E31\u0E22",
    dataPrivacy: "\u0E02\u0E49\u0E2D\u0E21\u0E39\u0E25\u0E41\u0E25\u0E30\u0E04\u0E27\u0E32\u0E21\u0E40\u0E1B\u0E47\u0E19\u0E2A\u0E48\u0E27\u0E19\u0E15\u0E31\u0E27",
    dataPrivacyDescription: "\u0E2A\u0E48\u0E07\u0E2D\u0E2D\u0E01\u0E02\u0E49\u0E2D\u0E21\u0E39\u0E25\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E2B\u0E23\u0E37\u0E2D\u0E25\u0E1A\u0E1A\u0E31\u0E0D\u0E0A\u0E35\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13",
    manageMyData: "\u0E08\u0E31\u0E14\u0E01\u0E32\u0E23\u0E02\u0E49\u0E2D\u0E21\u0E39\u0E25\u0E02\u0E2D\u0E07\u0E09\u0E31\u0E19",
    availableFunds: "\u0E22\u0E2D\u0E14\u0E40\u0E07\u0E34\u0E19\u0E17\u0E35\u0E48\u0E43\u0E0A\u0E49\u0E44\u0E14\u0E49",
    lowBalance: "\u0E40\u0E15\u0E34\u0E21\u0E40\u0E07\u0E34\u0E19\u0E43\u0E19\u0E1A\u0E31\u0E0D\u0E0A\u0E35\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E40\u0E1E\u0E37\u0E48\u0E2D\u0E43\u0E2B\u0E49\u0E17\u0E38\u0E01\u0E2D\u0E22\u0E48\u0E32\u0E07\u0E17\u0E33\u0E07\u0E32\u0E19\u0E15\u0E48\u0E2D\u0E44\u0E1B",
    authProvider: "\u0E1C\u0E39\u0E49\u0E43\u0E2B\u0E49\u0E1A\u0E23\u0E34\u0E01\u0E32\u0E23\u0E01\u0E32\u0E23\u0E22\u0E37\u0E19\u0E22\u0E31\u0E19\u0E15\u0E31\u0E27\u0E15\u0E19",
    publicKeyHex: "\u0E04\u0E35\u0E22\u0E4C\u0E2A\u0E32\u0E18\u0E32\u0E23\u0E13\u0E30 (Hex)",
    emailsDescription: "\u0E08\u0E31\u0E14\u0E01\u0E32\u0E23\u0E17\u0E35\u0E48\u0E2D\u0E22\u0E39\u0E48\u0E2D\u0E35\u0E40\u0E21\u0E25\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E2A\u0E33\u0E2B\u0E23\u0E31\u0E1A\u0E01\u0E32\u0E23\u0E41\u0E08\u0E49\u0E07\u0E40\u0E15\u0E37\u0E2D\u0E19\u0E04\u0E27\u0E32\u0E21\u0E1B\u0E25\u0E2D\u0E14\u0E20\u0E31\u0E22",
    addEmail: "\u0E40\u0E1E\u0E34\u0E48\u0E21\u0E2D\u0E35\u0E40\u0E21\u0E25",
    sessionHistory: "\u0E1B\u0E23\u0E30\u0E27\u0E31\u0E15\u0E34\u0E40\u0E0B\u0E2A\u0E0A\u0E31\u0E19",
    viewLog: "\u0E14\u0E39",
    providerSubtitle: {
      NOSTR: "\u0E01\u0E32\u0E23\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E41\u0E1A\u0E1A\u0E01\u0E23\u0E30\u0E08\u0E32\u0E22\u0E28\u0E39\u0E19\u0E22\u0E4C",
      GOOGLE: "Google OAuth",
      GITHUB: "GitHub OAuth",
      MICROSOFT: "Microsoft OAuth",
      EMAIL_MAGIC_LINK: "\u0E2D\u0E35\u0E40\u0E21\u0E25\u0E44\u0E21\u0E48\u0E43\u0E0A\u0E49\u0E23\u0E2B\u0E31\u0E2A\u0E1C\u0E48\u0E32\u0E19",
      MANUAL: "\u0E04\u0E39\u0E48\u0E04\u0E35\u0E22\u0E4C\u0E17\u0E35\u0E48\u0E08\u0E31\u0E14\u0E01\u0E32\u0E23\u0E40\u0E2D\u0E07",
      PRIVATE_KEY: "\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E14\u0E49\u0E27\u0E22\u0E04\u0E35\u0E22\u0E4C\u0E2A\u0E48\u0E27\u0E19\u0E15\u0E31\u0E27"
    }
  },
  emails: {
    accountEmail: "\u0E2D\u0E35\u0E40\u0E21\u0E25\u0E1A\u0E31\u0E0D\u0E0A\u0E35",
    accountEmailDescription: "\u0E19\u0E35\u0E48\u0E04\u0E37\u0E2D\u0E2D\u0E35\u0E40\u0E21\u0E25\u0E1A\u0E31\u0E0D\u0E0A\u0E35\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E17\u0E35\u0E48\u0E43\u0E0A\u0E49\u0E2A\u0E33\u0E2B\u0E23\u0E31\u0E1A\u0E40\u0E02\u0E49\u0E32\u0E2A\u0E39\u0E48\u0E23\u0E30\u0E1A\u0E1A\u0E41\u0E25\u0E30\u0E01\u0E32\u0E23\u0E41\u0E08\u0E49\u0E07\u0E40\u0E15\u0E37\u0E2D\u0E19 \u0E23\u0E27\u0E21\u0E16\u0E36\u0E07\u0E01\u0E32\u0E23\u0E41\u0E08\u0E49\u0E07\u0E40\u0E15\u0E37\u0E2D\u0E19\u0E22\u0E2D\u0E14\u0E40\u0E07\u0E34\u0E19\u0E04\u0E07\u0E40\u0E2B\u0E25\u0E37\u0E2D\u0E15\u0E48\u0E33",
    makePrimary: "\u0E15\u0E31\u0E49\u0E07\u0E40\u0E1B\u0E47\u0E19\u0E2B\u0E25\u0E31\u0E01",
    resendVerification: "\u0E2A\u0E48\u0E07\u0E01\u0E32\u0E23\u0E22\u0E37\u0E19\u0E22\u0E31\u0E19\u0E2D\u0E35\u0E01\u0E04\u0E23\u0E31\u0E49\u0E07",
    verificationSent: "\u0E2A\u0E48\u0E07\u0E2D\u0E35\u0E40\u0E21\u0E25\u0E22\u0E37\u0E19\u0E22\u0E31\u0E19\u0E41\u0E25\u0E49\u0E27 \u0E15\u0E23\u0E27\u0E08\u0E2A\u0E2D\u0E1A\u0E01\u0E25\u0E48\u0E2D\u0E07\u0E08\u0E14\u0E2B\u0E21\u0E32\u0E22\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13",
    accountEmailUpdated: "\u0E2D\u0E31\u0E1B\u0E40\u0E14\u0E15\u0E2D\u0E35\u0E40\u0E21\u0E25\u0E1A\u0E31\u0E0D\u0E0A\u0E35\u0E2A\u0E33\u0E40\u0E23\u0E47\u0E08\u0E41\u0E25\u0E49\u0E27"
  },
  glob_footer: {
    product: "\u0E1C\u0E25\u0E34\u0E15\u0E20\u0E31\u0E13\u0E11\u0E4C",
    account: "\u0E1A\u0E31\u0E0D\u0E0A\u0E35"
  },
  glob_billing: {
    recentAccountMovements: "\u0E01\u0E32\u0E23\u0E40\u0E04\u0E25\u0E37\u0E48\u0E2D\u0E19\u0E44\u0E2B\u0E27\u0E1A\u0E31\u0E0D\u0E0A\u0E35\u0E25\u0E48\u0E32\u0E2A\u0E38\u0E14",
    loadingAccountMovements: "\u0E01\u0E33\u0E25\u0E31\u0E07\u0E42\u0E2B\u0E25\u0E14\u0E01\u0E32\u0E23\u0E40\u0E04\u0E25\u0E37\u0E48\u0E2D\u0E19\u0E44\u0E2B\u0E27\u0E1A\u0E31\u0E0D\u0E0A\u0E35...",
    noAccountMovements: "\u0E44\u0E21\u0E48\u0E1E\u0E1A\u0E01\u0E32\u0E23\u0E40\u0E04\u0E25\u0E37\u0E48\u0E2D\u0E19\u0E44\u0E2B\u0E27\u0E1A\u0E31\u0E0D\u0E0A\u0E35",
    date: "\u0E27\u0E31\u0E19\u0E17\u0E35\u0E48",
    amount: "\u0E08\u0E33\u0E19\u0E27\u0E19",
    type: "\u0E1B\u0E23\u0E30\u0E40\u0E20\u0E17",
    features: "\u0E04\u0E38\u0E13\u0E2A\u0E21\u0E1A\u0E31\u0E15\u0E34",
    credit: "\u0E40\u0E04\u0E23\u0E14\u0E34\u0E15",
    debit: "\u0E40\u0E14\u0E1A\u0E34\u0E15",
    billingOverview: "\u0E20\u0E32\u0E1E\u0E23\u0E27\u0E21\u0E01\u0E32\u0E23\u0E40\u0E23\u0E35\u0E22\u0E01\u0E40\u0E01\u0E47\u0E1A\u0E40\u0E07\u0E34\u0E19",
    payAsYouGoCredits: "\u0E40\u0E04\u0E23\u0E14\u0E34\u0E15\u0E41\u0E1A\u0E1A\u0E08\u0E48\u0E32\u0E22\u0E15\u0E32\u0E21\u0E43\u0E0A\u0E49",
    noBalanceFound: "\u0E44\u0E21\u0E48\u0E1E\u0E1A\u0E22\u0E2D\u0E14\u0E40\u0E07\u0E34\u0E19",
    addCredits: "\u0E40\u0E1E\u0E34\u0E48\u0E21\u0E40\u0E04\u0E23\u0E14\u0E34\u0E15",
    hybridBillingModel: "\u0E42\u0E21\u0E40\u0E14\u0E25\u0E01\u0E32\u0E23\u0E40\u0E23\u0E35\u0E22\u0E01\u0E40\u0E01\u0E47\u0E1A\u0E40\u0E07\u0E34\u0E19\u0E41\u0E1A\u0E1A\u0E1C\u0E2A\u0E21",
    hybridBillingDescription: "\u0E40\u0E21\u0E37\u0E48\u0E2D\u0E42\u0E04\u0E27\u0E15\u0E49\u0E32\u0E01\u0E32\u0E23\u0E2A\u0E21\u0E31\u0E04\u0E23\u0E2A\u0E21\u0E32\u0E0A\u0E34\u0E01\u0E2B\u0E21\u0E14 \u0E01\u0E32\u0E23\u0E43\u0E0A\u0E49\u0E07\u0E32\u0E19\u0E08\u0E30\u0E40\u0E1B\u0E25\u0E35\u0E48\u0E22\u0E19\u0E44\u0E1B\u0E43\u0E0A\u0E49\u0E40\u0E04\u0E23\u0E14\u0E34\u0E15\u0E41\u0E1A\u0E1A\u0E08\u0E48\u0E32\u0E22\u0E15\u0E32\u0E21\u0E43\u0E0A\u0E49\u0E42\u0E14\u0E22\u0E2D\u0E31\u0E15\u0E42\u0E19\u0E21\u0E31\u0E15\u0E34 \u0E04\u0E38\u0E13\u0E08\u0E30\u0E44\u0E21\u0E48\u0E40\u0E2A\u0E35\u0E22\u0E01\u0E32\u0E23\u0E1A\u0E23\u0E34\u0E01\u0E32\u0E23 - \u0E40\u0E1E\u0E35\u0E22\u0E07\u0E41\u0E04\u0E48\u0E01\u0E32\u0E23\u0E43\u0E0A\u0E49\u0E07\u0E32\u0E19\u0E40\u0E1E\u0E34\u0E48\u0E21\u0E40\u0E15\u0E34\u0E21\u0E40\u0E01\u0E34\u0E19\u0E02\u0E35\u0E14\u0E08\u0E33\u0E01\u0E31\u0E14\u0E41\u0E1C\u0E19\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13",
    yourReferralCode: "\u0E23\u0E2B\u0E31\u0E2A\u0E41\u0E19\u0E30\u0E19\u0E33\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13",
    shareDirectLink: "\u0E2B\u0E23\u0E37\u0E2D\u0E41\u0E0A\u0E23\u0E4C\u0E25\u0E34\u0E07\u0E01\u0E4C\u0E2A\u0E21\u0E31\u0E04\u0E23\u0E42\u0E14\u0E22\u0E15\u0E23\u0E07\u0E19\u0E35\u0E49:",
    referralDescription: "\u0E40\u0E21\u0E37\u0E48\u0E2D\u0E21\u0E35\u0E04\u0E19\u0E2A\u0E21\u0E31\u0E04\u0E23\u0E14\u0E49\u0E27\u0E22\u0E25\u0E34\u0E07\u0E01\u0E4C\u0E41\u0E19\u0E30\u0E19\u0E33\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E41\u0E25\u0E30\u0E17\u0E33\u0E01\u0E32\u0E23\u0E0A\u0E33\u0E23\u0E30\u0E40\u0E07\u0E34\u0E19\u0E2A\u0E21\u0E31\u0E04\u0E23\u0E2A\u0E21\u0E32\u0E0A\u0E34\u0E01\u0E2B\u0E23\u0E37\u0E2D\u0E1D\u0E32\u0E01\u0E40\u0E07\u0E34\u0E19\u0E04\u0E23\u0E31\u0E49\u0E07\u0E41\u0E23\u0E01 \u0E04\u0E38\u0E13\u0E08\u0E30\u0E44\u0E14\u0E49\u0E23\u0E31\u0E1A 10% \u0E02\u0E2D\u0E07\u0E08\u0E33\u0E19\u0E27\u0E19\u0E19\u0E31\u0E49\u0E19 \u0E41\u0E25\u0E30\u0E1E\u0E27\u0E01\u0E40\u0E02\u0E32\u0E08\u0E30\u0E44\u0E14\u0E49\u0E23\u0E31\u0E1A\u0E42\u0E1A\u0E19\u0E31\u0E2A 10%",
    copy: "\u0E04\u0E31\u0E14\u0E25\u0E2D\u0E01",
    copied: "\u0E04\u0E31\u0E14\u0E25\u0E2D\u0E01\u0E41\u0E25\u0E49\u0E27",
    copyLink: "\u0E04\u0E31\u0E14\u0E25\u0E2D\u0E01\u0E25\u0E34\u0E07\u0E01\u0E4C",
    linkCopied: "\u0E04\u0E31\u0E14\u0E25\u0E2D\u0E01\u0E25\u0E34\u0E07\u0E01\u0E4C\u0E41\u0E25\u0E49\u0E27",
    referralStatistics: "\u0E2A\u0E16\u0E34\u0E15\u0E34\u0E01\u0E32\u0E23\u0E41\u0E19\u0E30\u0E19\u0E33",
    totalReferrals: "\u0E01\u0E32\u0E23\u0E41\u0E19\u0E30\u0E19\u0E33\u0E17\u0E31\u0E49\u0E07\u0E2B\u0E21\u0E14",
    totalEarnings: "\u0E23\u0E32\u0E22\u0E44\u0E14\u0E49\u0E17\u0E31\u0E49\u0E07\u0E2B\u0E21\u0E14",
    pendingEarnings: "\u0E23\u0E32\u0E22\u0E44\u0E14\u0E49\u0E17\u0E35\u0E48\u0E23\u0E2D\u0E14\u0E33\u0E40\u0E19\u0E34\u0E19\u0E01\u0E32\u0E23",
    recentReferrals: "\u0E01\u0E32\u0E23\u0E41\u0E19\u0E30\u0E19\u0E33\u0E25\u0E48\u0E32\u0E2A\u0E38\u0E14",
    firstPayment: "\u0E01\u0E32\u0E23\u0E0A\u0E33\u0E23\u0E30\u0E40\u0E07\u0E34\u0E19\u0E04\u0E23\u0E31\u0E49\u0E07\u0E41\u0E23\u0E01",
    bonus: "\u0E42\u0E1A\u0E19\u0E31\u0E2A",
    status: "\u0E2A\u0E16\u0E32\u0E19\u0E30",
    processed: "\u0E14\u0E33\u0E40\u0E19\u0E34\u0E19\u0E01\u0E32\u0E23\u0E41\u0E25\u0E49\u0E27",
    pending: "\u0E23\u0E2D\u0E14\u0E33\u0E40\u0E19\u0E34\u0E19\u0E01\u0E32\u0E23",
    bonusPaid: "\u0E08\u0E48\u0E32\u0E22\u0E42\u0E1A\u0E19\u0E31\u0E2A\u0E41\u0E25\u0E49\u0E27",
    awaitingBonus: "\u0E23\u0E2D\u0E42\u0E1A\u0E19\u0E31\u0E2A",
    pendingPayment: "\u0E01\u0E32\u0E23\u0E0A\u0E33\u0E23\u0E30\u0E40\u0E07\u0E34\u0E19\u0E23\u0E2D\u0E14\u0E33\u0E40\u0E19\u0E34\u0E19\u0E01\u0E32\u0E23",
    noReferrals: "\u0E22\u0E31\u0E07\u0E44\u0E21\u0E48\u0E21\u0E35\u0E01\u0E32\u0E23\u0E41\u0E19\u0E30\u0E19\u0E33 \u0E41\u0E0A\u0E23\u0E4C\u0E23\u0E2B\u0E31\u0E2A\u0E41\u0E19\u0E30\u0E19\u0E33\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E40\u0E1E\u0E37\u0E48\u0E2D\u0E40\u0E23\u0E34\u0E48\u0E21\u0E23\u0E31\u0E1A\u0E04\u0E48\u0E32\u0E04\u0E2D\u0E21\u0E21\u0E34\u0E0A\u0E0A\u0E31\u0E48\u0E19!",
    loadingReferralHistory: "\u0E01\u0E33\u0E25\u0E31\u0E07\u0E42\u0E2B\u0E25\u0E14\u0E1B\u0E23\u0E30\u0E27\u0E31\u0E15\u0E34\u0E01\u0E32\u0E23\u0E41\u0E19\u0E30\u0E19\u0E33...",
    loadingReferralInfo: "\u0E01\u0E33\u0E25\u0E31\u0E07\u0E42\u0E2B\u0E25\u0E14\u0E02\u0E49\u0E2D\u0E21\u0E39\u0E25\u0E01\u0E32\u0E23\u0E41\u0E19\u0E30\u0E19\u0E33...",
    noActiveSubscription: "\u0E44\u0E21\u0E48\u0E21\u0E35\u0E01\u0E32\u0E23\u0E2A\u0E21\u0E31\u0E04\u0E23\u0E2A\u0E21\u0E32\u0E0A\u0E34\u0E01\u0E17\u0E35\u0E48\u0E43\u0E0A\u0E49\u0E07\u0E32\u0E19\u0E2D\u0E22\u0E39\u0E48",
    noActiveSubscriptionDescription: "\u0E04\u0E38\u0E13\u0E44\u0E21\u0E48\u0E21\u0E35\u0E01\u0E32\u0E23\u0E2A\u0E21\u0E31\u0E04\u0E23\u0E2A\u0E21\u0E32\u0E0A\u0E34\u0E01\u0E17\u0E35\u0E48\u0E43\u0E0A\u0E49\u0E07\u0E32\u0E19\u0E2D\u0E22\u0E39\u0E48 \u0E04\u0E38\u0E13\u0E01\u0E33\u0E25\u0E31\u0E07\u0E43\u0E0A\u0E49\u0E42\u0E21\u0E40\u0E14\u0E25\u0E01\u0E32\u0E23\u0E40\u0E23\u0E35\u0E22\u0E01\u0E40\u0E01\u0E47\u0E1A\u0E40\u0E07\u0E34\u0E19\u0E41\u0E1A\u0E1A\u0E08\u0E48\u0E32\u0E22\u0E15\u0E32\u0E21\u0E43\u0E0A\u0E49\u0E02\u0E2D\u0E07\u0E40\u0E23\u0E32",
    currentSubscription: "\u0E01\u0E32\u0E23\u0E2A\u0E21\u0E31\u0E04\u0E23\u0E2A\u0E21\u0E32\u0E0A\u0E34\u0E01\u0E1B\u0E31\u0E08\u0E08\u0E38\u0E1A\u0E31\u0E19",
    plan: "\u0E41\u0E1C\u0E19",
    periodStarted: "\u0E23\u0E30\u0E22\u0E30\u0E40\u0E27\u0E25\u0E32\u0E40\u0E23\u0E34\u0E48\u0E21\u0E15\u0E49\u0E19",
    nextBilling: "\u0E01\u0E32\u0E23\u0E40\u0E23\u0E35\u0E22\u0E01\u0E40\u0E01\u0E47\u0E1A\u0E40\u0E07\u0E34\u0E19\u0E04\u0E23\u0E31\u0E49\u0E07\u0E15\u0E48\u0E2D\u0E44\u0E1B",
    nextCycle: "\u0E23\u0E2D\u0E1A\u0E15\u0E48\u0E2D\u0E44\u0E1B",
    free: "\u0E1F\u0E23\u0E35",
    unlimited: "\u0E44\u0E21\u0E48\u0E08\u0E33\u0E01\u0E31\u0E14",
    manageViaStripe: "\u0E08\u0E31\u0E14\u0E01\u0E32\u0E23\u0E1C\u0E48\u0E32\u0E19 Stripe",
    cancel: "\u0E22\u0E01\u0E40\u0E25\u0E34\u0E01"
  },
  securityLog: {
    title: "\u0E1A\u0E31\u0E19\u0E17\u0E36\u0E01\u0E04\u0E27\u0E32\u0E21\u0E1B\u0E25\u0E2D\u0E14\u0E20\u0E31\u0E22",
    description: "\u0E01\u0E34\u0E08\u0E01\u0E23\u0E23\u0E21\u0E25\u0E48\u0E32\u0E2A\u0E38\u0E14\u0E17\u0E35\u0E48\u0E40\u0E01\u0E35\u0E48\u0E22\u0E27\u0E02\u0E49\u0E2D\u0E07\u0E01\u0E31\u0E1A\u0E04\u0E27\u0E32\u0E21\u0E1B\u0E25\u0E2D\u0E14\u0E20\u0E31\u0E22\u0E02\u0E2D\u0E07\u0E1A\u0E31\u0E0D\u0E0A\u0E35\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13 \u0E40\u0E2B\u0E15\u0E38\u0E01\u0E32\u0E23\u0E13\u0E4C\u0E17\u0E35\u0E48\u0E25\u0E49\u0E21\u0E40\u0E2B\u0E25\u0E27\u0E08\u0E30\u0E16\u0E39\u0E01\u0E40\u0E19\u0E49\u0E19",
    when: "\u0E40\u0E21\u0E37\u0E48\u0E2D\u0E44\u0E23",
    event: "\u0E40\u0E2B\u0E15\u0E38\u0E01\u0E32\u0E23\u0E13\u0E4C",
    location: "\u0E15\u0E33\u0E41\u0E2B\u0E19\u0E48\u0E07",
    device: "\u0E2D\u0E38\u0E1B\u0E01\u0E23\u0E13\u0E4C",
    status: "\u0E2A\u0E16\u0E32\u0E19\u0E30",
    ok: "\u0E2A\u0E33\u0E40\u0E23\u0E47\u0E08",
    failed: "\u0E25\u0E49\u0E21\u0E40\u0E2B\u0E25\u0E27",
    empty: "\u0E22\u0E31\u0E07\u0E44\u0E21\u0E48\u0E21\u0E35\u0E01\u0E34\u0E08\u0E01\u0E23\u0E23\u0E21\u0E17\u0E35\u0E48\u0E1A\u0E31\u0E19\u0E17\u0E36\u0E01\u0E44\u0E27\u0E49",
    errorTitle: "\u0E44\u0E21\u0E48\u0E2A\u0E32\u0E21\u0E32\u0E23\u0E16\u0E42\u0E2B\u0E25\u0E14\u0E1A\u0E31\u0E19\u0E17\u0E36\u0E01\u0E04\u0E27\u0E32\u0E21\u0E1B\u0E25\u0E2D\u0E14\u0E20\u0E31\u0E22\u0E44\u0E14\u0E49"
  }
};

// src/i18n/locales/zh.json
var zh_default = {
  glob_navigation: {
    home: "\u9996\u9875",
    pricing: "\u4EF7\u683C",
    docs: "\u6587\u6863",
    news: "\u65B0\u95FB",
    more: "\u66F4\u591A",
    login: "\u767B\u5F55",
    signup: "\u6CE8\u518C",
    getStarted: "\u5F00\u59CB\u4F7F\u7528",
    forms: "\u8868\u5355",
    integrations: "\u96C6\u6210",
    messages: "\u6D88\u606F",
    profile: "\u6863\u6848",
    support: "\u652F\u6301",
    logout: "\u767B\u51FA",
    terms: "\u6761\u6B3E",
    billing: "\u8D26\u5355",
    usageAndCredits: "\u7528\u91CF\u4E0E\u4F59\u989D",
    subscriptions: "\u8BA2\u9605",
    referrals: "\u63A8\u8350",
    apiKeys: "API\u5BC6\u94A5",
    securityLog: "\u5B89\u5168\u65E5\u5FD7",
    chat: "\u804A\u5929",
    sectionWorkspace: "\u5DE5\u4F5C\u533A",
    sectionAccount: "\u8D26\u6237",
    sectionHelp: "\u5E2E\u52A9"
  },
  auth: {
    login: "\u767B\u5F55",
    signup: "\u6CE8\u518C",
    logout: "\u767B\u51FA",
    step1: "\u6B65\u9AA4 1",
    step2: "\u6B65\u9AA4 2",
    authenticate: "\u9A8C\u8BC1",
    enterKey: "\u6807\u8BC6\u7B26",
    newHere: "\u65B0\u7528\u6237\uFF1F",
    howToSignup: "\u5982\u4F55\u6CE8\u518C",
    alreadyAccount: "\u5982\u679C\u60A8\u8FD8\u6CA1\u6709\u8D26\u6237\uFF0C\u6211\u4EEC\u4F1A\u5728\u60A8\u9996\u6B21\u767B\u5F55\u65F6\u4E3A\u60A8\u521B\u5EFA\u4E00\u4E2A\u3002",
    emailSection: "\u90AE\u4EF6\u94FE\u63A5",
    googleSection: "Google",
    githubSection: "GitHub",
    microsoftSection: "Microsoft",
    nostrSection: "Nostr \u6D4F\u89C8\u5668\u63D2\u4EF6",
    signupDescription: "\u83B7\u53D6\u53D1\u9001\u5230\u60A8\u90AE\u7BB1\u7684\u767B\u5F55\u94FE\u63A5\u3002",
    googleDescription: "\u4F7F\u7528\u60A8\u7684 Google \u8D26\u6237\u767B\u5F55\u3002",
    githubDescription: "\u4F7F\u7528\u60A8\u7684 GitHub \u8D26\u6237\u767B\u5F55\u3002",
    microsoftDescription: "\u4F7F\u7528\u60A8\u7684 Microsoft \u8D26\u6237\u767B\u5F55\u3002",
    nostrDescription: "\u4F7F\u7528\u652F\u6301\u7684",
    browserPlugin: "\u6D4F\u89C8\u5668\u63D2\u4EF6",
    signupLoginEmail: "\u4F7F\u7528\u90AE\u7BB1\u767B\u5F55\u3002",
    signupLoginGoogle: "\u4F7F\u7528 Google \u767B\u5F55",
    signupLoginGithub: "\u4F7F\u7528 GitHub \u767B\u5F55",
    signupLoginMicrosoft: "\u4F7F\u7528 Microsoft \u767B\u5F55",
    signupLoginNostr: "\u4F7F\u7528 Nostr \u767B\u5F55",
    privacyPolicy: "\u9690\u79C1\u653F\u7B56",
    termsOfService: "\u670D\u52A1\u6761\u6B3E"
  },
  login: {
    welcomeBack: "\u6B22\u8FCE\u56DE\u6765",
    cardSubtitle: "\u4F7F\u7528\u793E\u4EA4\u8D26\u53F7\u7EE7\u7EED\u6216\u8F93\u5165\u60A8\u7684\u7535\u5B50\u90AE\u4EF6\u5730\u5740\u3002",
    orDivider: "\u6216",
    loginWithEmail: "\u4F7F\u7528\u90AE\u7BB1\u767B\u5F55",
    magicLinkInfo: "\u6211\u4EEC\u5C06\u5411\u60A8\u7684\u6536\u4EF6\u7BB1\u53D1\u9001\u4E00\u4E2A\u9B54\u6CD5\u94FE\u63A5\u3002",
    publicKeyRequired: "\u9700\u8981\u516C\u94A5",
    invalidEmail: "\u65E0\u6548\u90AE\u7BB1",
    getPublicKey: "\u4ECE\u6269\u5C55\u83B7\u53D6\u516C\u94A5",
    emailTab: "\u90AE\u7BB1",
    googleTab: "Google",
    githubTab: "GitHub",
    microsoftTab: "Microsoft",
    nostrTab: "Nostr",
    oidcSsoTab: "GoFranz SSO",
    emailDescription: "\u4F7F\u7528\u90AE\u7BB1\u767B\u5F55\u3002",
    googleDescription: "\u4F7F\u7528 Google \u767B\u5F55",
    nostrPluginDescription: "\u4F7F\u7528 Nos2x \u6216\u7C7B\u4F3C\u5DE5\u5177\u767B\u5F55\u3002",
    nostrKeyDescription: "\u4F7F\u7528\u60A8\u7684\u79C1\u94A5\u767B\u5F55\u3002",
    nostrOtherDescription: "\u901A\u8FC7\u5176\u4ED6\u65B9\u5F0F\u63D0\u4F9B\u7B7E\u540D\u3002",
    loadedPublicKey: "\u5DF2\u52A0\u8F7D\u516C\u94A5",
    noKeyLoaded: "\u672A\u52A0\u8F7D\u5BC6\u94A5...",
    enterPublicKey: "\u8F93\u5165\u60A8\u7684\u516C\u94A5",
    publicKeyPlaceholder: "32b72e6432f7282135cf124a8...",
    emailLabel: "\u90AE\u7BB1\u5730\u5740",
    emailPlaceholder: "your-email@provider.com",
    enterCode: "\u8F93\u5165\u90AE\u4EF6\u4E2D\u7684\u4EE3\u7801",
    codePlaceholder: "6fd7e67e-6f42-4354-b8f8-775bc1e52a50",
    privateKeyLabel: "\u8F93\u5165\u60A8\u7684\u79C1\u94A5",
    challengeResponseLabel: "\u8F93\u5165\u60A8\u7684\u6311\u6218\u54CD\u5E94",
    emailInstructions: "\u70B9\u51FB\u90AE\u4EF6\u4E2D\u7684\u94FE\u63A5\uFF0C\u6216\u590D\u5236\u4EE3\u7801\u5E76\u7C98\u8D34\u5230\u8FD9\u91CC\u3002",
    nostrInstructions: "\u60A8\u7684 Nostr \u6269\u5C55\u5E94\u8BE5\u4F1A\u63D0\u793A\u60A8\u7B7E\u7F72\u8EAB\u4EFD\u9A8C\u8BC1\u4E8B\u4EF6\u4EE5\u7EE7\u7EED\u3002",
    challenge: "\u6311\u6218\uFF1A",
    loginWithExtension: "\u4F7F\u7528\u6269\u5C55\u767B\u5F55",
    redirectingGoogle: "\u6B63\u5728\u91CD\u5B9A\u5411\u5230 Google...",
    redirectingGithub: "\u6B63\u5728\u91CD\u5B9A\u5411\u5230 GitHub...",
    redirectingMicrosoft: "\u6B63\u5728\u91CD\u5B9A\u5411\u5230 Microsoft...",
    redirectingOidc: "\u6B63\u5728\u91CD\u5B9A\u5411\u5230 GoFranz SSO...",
    githubNoChallenge: "\u83B7\u53D6 GitHub \u6311\u6218\u5931\u8D25",
    microsoftNoChallenge: "\u83B7\u53D6 Microsoft \u6311\u6218\u5931\u8D25",
    oidcNoChallenge: "\u83B7\u53D6 GoFranz SSO \u6311\u6218\u5931\u8D25",
    checkYourEmail: "Check your email for the login link",
    emailSent: "We've sent a login link to your email",
    clickLinkToLogin: "Click the link in the email to complete login",
    wrongEmail: "Wrong email? Try again",
    errorNotPrimaryEmail: "This email is not your primary account email.",
    errorDifferentAuthMethod: "This email is registered with a different authentication method",
    errorGoogleEmailNotVerified: "Google email not verified",
    errorInvalidAuthMethodGoogle: "This account uses Google login",
    errorInvalidAuthMethodGithub: "This account uses GitHub login",
    errorInvalidAuthMethodMicrosoft: "This account uses Microsoft login",
    errorInvalidAuthMethodNostr: "This account uses Nostr login",
    errorAuthMethodCollision: "\u8BE5\u90AE\u7BB1\u5DF2\u901A\u8FC7 {{method}} \u6CE8\u518C\u3002\u8BF7\u6539\u7528\u8BE5\u65B9\u5F0F\u767B\u5F55\u3002",
    errorAuthMethodCollisionGeneric: "\u8BE5\u90AE\u7BB1\u5DF2\u4F7F\u7528\u5176\u4ED6\u767B\u5F55\u65B9\u5F0F\u6CE8\u518C\u3002\u8BF7\u4F7F\u7528\u60A8\u7684\u539F\u59CB\u767B\u5F55\u65B9\u5F0F\u3002",
    errorInvalidNostrSignature: "Invalid Nostr signature",
    errorNostrKeyMismatch: "Nostr key mismatch",
    errorOAuthFailed: "OAuth authentication failed",
    errorTokenExchange: "\u767B\u5F55\u5931\u8D25\uFF0C\u8BF7\u91CD\u8BD5\u3002",
    errorCodeExpired: "\u767B\u5F55\u4F1A\u8BDD\u5DF2\u8FC7\u671F\uFF0C\u8BF7\u91CD\u65B0\u767B\u5F55\u3002",
    invalidLoginResponse: "\u65E0\u6548\u7684\u767B\u5F55\u54CD\u5E94\uFF0C\u8BF7\u91CD\u65B0\u767B\u5F55\u3002",
    errorRateLimited: "\u767B\u5F55\u5C1D\u8BD5\u8FC7\u591A\uFF0C\u8BF7\u7B49\u5F85 {{seconds}} \u79D2\u540E\u91CD\u8BD5\u3002",
    processing: "Processing login...",
    somethingWrong: "Something went wrong",
    invalidMagicLink: "Invalid magic link",
    noIdProvided: "No session ID provided",
    noChallengeProvided: "No challenge provided",
    back: "\u8FD4\u56DE",
    cancel: "\u53D6\u6D88",
    submit: "\u63D0\u4EA4"
  },
  glob_buttons: {
    save: "\u4FDD\u5B58",
    cancel: "\u53D6\u6D88",
    submit: "\u63D0\u4EA4",
    create: "\u521B\u5EFA",
    update: "\u66F4\u65B0",
    updateUnsaved: "\u66F4\u65B0\uFF08\u672A\u4FDD\u5B58\u7684\u66F4\u6539\uFF09",
    delete: "\u5220\u9664",
    calculate: "\u8BA1\u7B97",
    login: "\u767B\u5F55",
    back: "\u8FD4\u56DE"
  },
  support: {
    title: "\u652F\u6301",
    getHelp: "\u83B7\u53D6 {{serviceName}} \u5E2E\u52A9",
    description: "\u6709\u95EE\u9898\u6216\u9700\u8981\u5E2E\u52A9\uFF1F\u6211\u4EEC\u5728\u8FD9\u91CC\u4E3A\u60A8\u670D\u52A1\uFF01\u8BF7\u586B\u5199\u4E0B\u9762\u7684\u8868\u5355\uFF0C\u6211\u4EEC\u4F1A\u5C3D\u5FEB\u56DE\u590D\u60A8\u3002",
    thankYou: "\u8C22\u8C22\uFF01",
    successMessage: "\u60A8\u7684\u652F\u6301\u8BF7\u6C42\u5DF2\u6210\u529F\u63D0\u4EA4\u3002\u6211\u4EEC\u4F1A\u5C3D\u5FEB\u56DE\u590D\u60A8\u3002",
    submitAnother: "\u63D0\u4EA4\u53E6\u4E00\u4E2A\u8BF7\u6C42",
    successAlert: "\u6210\u529F\uFF01",
    successAlertMessage: "\u60A8\u7684\u652F\u6301\u8BF7\u6C42\u5DF2\u6210\u529F\u63D0\u4EA4\uFF01",
    nameLabel: "\u59D3\u540D\uFF08\u53EF\u9009\uFF09",
    namePlaceholder: "\u60A8\u7684\u5168\u540D",
    emailLabel: "\u90AE\u7BB1",
    emailPlaceholder: "your.email@example.com",
    loggedInEmail: "\u4F7F\u7528\u60A8\u7684\u767B\u5F55\u90AE\u7BB1",
    subjectLabel: "\u4E3B\u9898",
    subjectPlaceholder: "\u9009\u62E9\u60A8\u7684\u8BF7\u6C42\u7C7B\u578B",
    messageLabel: "\u6D88\u606F",
    messagePlaceholder: "\u8BF7\u63D0\u4F9B\u5173\u4E8E\u60A8\u95EE\u9898\u7684\u8BE6\u7EC6\u4FE1\u606F...",
    formInfo: "\u6B64\u8868\u5355\u5C06\u5305\u542B\u60A8\u7684\u767B\u5F55\u65B9\u5F0F\uFF08",
    formInfoEnd: "\uFF09\u548C\u7528\u6237\u6807\u8BC6\u7B26\uFF0C\u4EE5\u4FBF\u66F4\u597D\u5730\u4E3A\u60A8\u63D0\u4F9B\u5E2E\u52A9\u3002",
    submitting: "\u63D0\u4EA4\u4E2D...",
    submitButton: "\u63D0\u4EA4\u652F\u6301\u8BF7\u6C42",
    types: {
      general: "\u4E00\u822C\u95EE\u9898",
      technical: "\u6280\u672F\u95EE\u9898",
      feature: "\u529F\u80FD\u8BF7\u6C42",
      bug: "\u62A5\u544A\u9519\u8BEF"
    }
  },
  colorScheme: {
    lightMode: "\u6D45\u8272\u6A21\u5F0F\uFF08\u70B9\u51FB\u5207\u6362\u6DF1\u8272\uFF09",
    darkMode: "\u6DF1\u8272\u6A21\u5F0F\uFF08\u70B9\u51FB\u5207\u6362\u81EA\u52A8\uFF09",
    autoMode: "\u81EA\u52A8\u6A21\u5F0F\uFF08\u70B9\u51FB\u5207\u6362\u6D45\u8272\uFF09",
    toggle: "\u5207\u6362\u989C\u8272\u65B9\u6848"
  },
  privacy: {
    title: "\u9690\u79C1",
    backHome: "\u8FD4\u56DE\u9996\u9875",
    gdprCompliant: "\u{1F1E9}\u{1F1EA} \u7B26\u5408 GDPR\u3002\u670D\u52A1\u5668\u4F4D\u4E8E\u5FB7\u56FD\u3002"
  },
  terms: {
    title: "\u670D\u52A1\u6761\u6B3E"
  },
  news: {
    title: "\u6765\u81EA\u6211\u4EEC\u7684\u535A\u5BA2",
    loading: "\u52A0\u8F7D\u4E2D...",
    rssDescription: "\u60A8\u53EF\u4EE5\u5728\u8FD9\u91CC\u8BA2\u9605\u6B64 Atom \u8BA2\u9605\u6E90"
  },
  profile: {
    balance: "\u4F59\u989D",
    addMoney: "\u5145\u503C",
    noBalanceFound: "\u672A\u627E\u5230\u4F59\u989D\u3002",
    depositSuccess: "\u5145\u503C\u6210\u529F",
    depositSuccessMessage: "\u60A8\u7684\u5145\u503C\u5DF2\u6210\u529F\u3002\u94F6\u884C\u5361\u652F\u4ED8\u5E94\u5728\u51E0\u79D2\u949F\u5185\u5230\u8D26\u3002",
    wirePaymentInfo: "\u60F3\u8981\u901A\u8FC7\u6C47\u6B3E\u6216\u6BD4\u7279\u5E01\u4ED8\u6B3E\uFF1F\uFF08\u20AC100+\uFF09\uFF1A",
    personalInformation: "\u4E2A\u4EBA\u4FE1\u606F",
    personalInfoMessage: "\u6211\u4EEC\u5176\u5B9E\u5BF9\u60A8\u4E86\u89E3\u4E0D\u591A\u3002",
    personalInfoContact: "\u53D1\u9001\u90AE\u4EF6\u81F3",
    personalInfoGreeting: "\u6253\u4E2A\u62DB\u547C\u3002",
    emails: "\u90AE\u7BB1",
    loginInformation: "\u767B\u5F55\u4FE1\u606F",
    currentlyLoggedIn: "\u60A8\u5F53\u524D\u767B\u5F55\u65B9\u5F0F\uFF1A",
    saveKeysAlert: "\u5C06\u8FD9\u4E9B\u5BC6\u94A5\u4FDD\u5B58\u5728\u60A8\u7684\u5BC6\u7801\u7BA1\u7406\u5668\u6216\u5B89\u5168\u4F4D\u7F6E\u3002\u6211\u4EEC\u65E0\u6CD5\u6062\u590D\u5B83\u4EEC\u3002\u60A8\u53EF\u4EE5\u4F7F\u7528\u5B83\u4EEC\u4ECE\u5176\u4ED6\u8BBE\u5907\u8BBF\u95EE\u670D\u52A1\uFF0C\u5E76\u767B\u5F55\u6211\u4EEC\u7684\u5176\u4ED6\u670D\u52A1\u3002",
    downloadKeys: "\u4E0B\u8F7D\u5BC6\u94A5",
    publicKey: "\u516C\u94A5",
    publicKeyDescription: "\u60A8\u7684\u516C\u94A5\u7528\u4E8E\u8BC6\u522B\u60A8\u7684\u8EAB\u4EFD\uFF1B\u6211\u4EEC\u5C06\u5176\u5B58\u50A8\u5728\u670D\u52A1\u5668\u4E0A\u3002",
    privateKey: "\u79C1\u94A5",
    privateKeyDescription: "\u60A8\u7684\u79C1\u94A5\u7528\u4E8E\u767B\u5F55\uFF0C\u6C38\u8FDC\u4E0D\u4F1A\u79BB\u5F00\u60A8\u7684\u8BA1\u7B97\u673A\u3002",
    security: "\u5B89\u5168",
    dataPrivacy: "\u6570\u636E\u4E0E\u9690\u79C1",
    dataPrivacyDescription: "\u5BFC\u51FA\u60A8\u7684\u6570\u636E\u6216\u5220\u9664\u60A8\u7684\u8D26\u6237\u3002",
    manageMyData: "\u7BA1\u7406\u6211\u7684\u6570\u636E",
    availableFunds: "\u53EF\u7528\u8D44\u91D1",
    lowBalance: "\u4E3A\u60A8\u7684\u8D26\u6237\u5145\u503C\u4EE5\u4FDD\u6301\u670D\u52A1\u8FD0\u884C\u3002",
    authProvider: "\u8EAB\u4EFD\u9A8C\u8BC1\u63D0\u4F9B\u5546",
    publicKeyHex: "\u516C\u94A5 (Hex)",
    emailsDescription: "\u7BA1\u7406\u60A8\u7684\u7535\u5B50\u90AE\u4EF6\u5730\u5740\uFF0C\u4EE5\u63A5\u6536\u5B89\u5168\u8B66\u62A5\u548C\u901A\u77E5\u3002",
    addEmail: "\u6DFB\u52A0\u90AE\u7BB1",
    sessionHistory: "\u4F1A\u8BDD\u5386\u53F2",
    viewLog: "\u67E5\u770B",
    providerSubtitle: {
      NOSTR: "\u53BB\u4E2D\u5FC3\u5316\u767B\u5F55",
      GOOGLE: "Google OAuth",
      GITHUB: "GitHub OAuth",
      MICROSOFT: "Microsoft OAuth",
      EMAIL_MAGIC_LINK: "\u514D\u5BC6\u7801\u90AE\u7BB1",
      MANUAL: "\u81EA\u7BA1\u7406\u5BC6\u94A5\u5BF9",
      PRIVATE_KEY: "\u79C1\u94A5\u767B\u5F55"
    }
  },
  emails: {
    accountEmail: "\u8D26\u6237\u90AE\u7BB1",
    accountEmailDescription: "\u8FD9\u662F\u60A8\u7684\u8D26\u6237\u90AE\u7BB1\uFF0C\u7528\u4E8E\u767B\u5F55\u548C\u901A\u77E5\uFF0C\u5305\u62EC\u4F4E\u4F59\u989D\u63D0\u9192\u3002",
    makePrimary: "\u8BBE\u4E3A\u4E3B\u90AE\u7BB1",
    resendVerification: "\u91CD\u65B0\u53D1\u9001\u9A8C\u8BC1",
    verificationSent: "\u9A8C\u8BC1\u90AE\u4EF6\u5DF2\u53D1\u9001\u3002\u8BF7\u68C0\u67E5\u60A8\u7684\u6536\u4EF6\u7BB1\u3002",
    accountEmailUpdated: "\u8D26\u6237\u90AE\u7BB1\u66F4\u65B0\u6210\u529F\u3002"
  },
  glob_footer: {
    product: "\u4EA7\u54C1",
    account: "\u8D26\u6237"
  },
  glob_billing: {
    recentAccountMovements: "\u6700\u8FD1\u8D26\u6237\u53D8\u52A8",
    loadingAccountMovements: "\u6B63\u5728\u52A0\u8F7D\u8D26\u6237\u53D8\u52A8...",
    noAccountMovements: "\u672A\u627E\u5230\u8D26\u6237\u53D8\u52A8\u3002",
    date: "\u65E5\u671F",
    amount: "\u91D1\u989D",
    type: "\u7C7B\u578B",
    features: "\u529F\u80FD",
    credit: "\u6536\u5165",
    debit: "\u652F\u51FA",
    billingOverview: "\u8D26\u5355\u6982\u89C8",
    payAsYouGoCredits: "\u6309\u9700\u4ED8\u8D39\u79EF\u5206",
    noBalanceFound: "\u672A\u627E\u5230\u4F59\u989D",
    addCredits: "\u6DFB\u52A0\u79EF\u5206",
    hybridBillingModel: "\u6DF7\u5408\u8BA1\u8D39\u6A21\u5F0F",
    hybridBillingDescription: "\u5F53\u8BA2\u9605\u989D\u5EA6\u7528\u5C3D\u65F6\uFF0C\u4F7F\u7528\u4F1A\u81EA\u52A8\u56DE\u843D\u5230\u6309\u9700\u4ED8\u8D39\u79EF\u5206\u3002\u60A8\u6C38\u8FDC\u4E0D\u4F1A\u5931\u53BB\u670D\u52A1 - \u53EA\u662F\u8D85\u51FA\u8BA1\u5212\u9650\u5236\u7684\u989D\u5916\u4F7F\u7528\u3002",
    yourReferralCode: "\u60A8\u7684\u63A8\u8350\u7801",
    shareDirectLink: "\u6216\u5206\u4EAB\u6B64\u76F4\u63A5\u6CE8\u518C\u94FE\u63A5\uFF1A",
    referralDescription: "\u5F53\u6709\u4EBA\u901A\u8FC7\u60A8\u7684\u63A8\u8350\u94FE\u63A5\u6CE8\u518C\u5E76\u5B8C\u6210\u9996\u6B21\u8BA2\u9605\u4ED8\u6B3E\u6216\u5145\u503C\u65F6\uFF0C\u60A8\u53EF\u4EE5\u83B7\u5F97\u8BE5\u91D1\u989D\u768410%\uFF0C\u4ED6\u4EEC\u4E5F\u4F1A\u83B7\u5F9710%\u7684\u5956\u52B1\u3002",
    copy: "\u590D\u5236",
    copied: "\u5DF2\u590D\u5236",
    copyLink: "\u590D\u5236\u94FE\u63A5",
    linkCopied: "\u94FE\u63A5\u5DF2\u590D\u5236",
    referralStatistics: "\u63A8\u8350\u7EDF\u8BA1",
    totalReferrals: "\u603B\u63A8\u8350\u6570",
    totalEarnings: "\u603B\u6536\u76CA",
    pendingEarnings: "\u5F85\u5904\u7406\u6536\u76CA",
    recentReferrals: "\u6700\u8FD1\u63A8\u8350",
    firstPayment: "\u9996\u6B21\u4ED8\u6B3E",
    bonus: "\u5956\u52B1",
    status: "\u72B6\u6001",
    processed: "\u5DF2\u5904\u7406",
    pending: "\u5F85\u5904\u7406",
    bonusPaid: "\u5956\u52B1\u5DF2\u652F\u4ED8",
    awaitingBonus: "\u7B49\u5F85\u5956\u52B1",
    pendingPayment: "\u5F85\u4ED8\u6B3E",
    noReferrals: "\u5C1A\u672A\u6709\u63A8\u8350\u3002\u5206\u4EAB\u60A8\u7684\u63A8\u8350\u7801\u5F00\u59CB\u8D5A\u53D6\u4F63\u91D1\uFF01",
    loadingReferralHistory: "\u6B63\u5728\u52A0\u8F7D\u63A8\u8350\u5386\u53F2...",
    loadingReferralInfo: "\u6B63\u5728\u52A0\u8F7D\u63A8\u8350\u4FE1\u606F...",
    noActiveSubscription: "\u65E0\u6D3B\u8DC3\u8BA2\u9605",
    noActiveSubscriptionDescription: "\u60A8\u6CA1\u6709\u6D3B\u8DC3\u7684\u8BA2\u9605\u3002\u60A8\u6B63\u5728\u4F7F\u7528\u6211\u4EEC\u7684\u6309\u9700\u4ED8\u8D39\u8BA1\u8D39\u6A21\u5F0F\u3002",
    currentSubscription: "\u5F53\u524D\u8BA2\u9605",
    plan: "\u8BA1\u5212",
    periodStarted: "\u5468\u671F\u5F00\u59CB",
    nextBilling: "\u4E0B\u6B21\u8BA1\u8D39",
    nextCycle: "\u4E0B\u4E2A\u5468\u671F",
    free: "\u514D\u8D39",
    unlimited: "\u65E0\u9650\u5236",
    manageViaStripe: "\u901A\u8FC7Stripe\u7BA1\u7406",
    cancel: "\u53D6\u6D88"
  },
  securityLog: {
    title: "\u5B89\u5168\u65E5\u5FD7",
    description: "\u60A8\u8D26\u6237\u6700\u8FD1\u4E0E\u5B89\u5168\u76F8\u5173\u7684\u6D3B\u52A8\u3002\u5931\u8D25\u7684\u4E8B\u4EF6\u4F1A\u88AB\u7A81\u51FA\u663E\u793A\u3002",
    when: "\u65F6\u95F4",
    event: "\u4E8B\u4EF6",
    location: "\u4F4D\u7F6E",
    device: "\u8BBE\u5907",
    status: "\u72B6\u6001",
    ok: "\u6210\u529F",
    failed: "\u5931\u8D25",
    empty: "\u6682\u65E0\u6D3B\u52A8\u8BB0\u5F55\u3002",
    errorTitle: "\u65E0\u6CD5\u52A0\u8F7D\u5B89\u5168\u65E5\u5FD7"
  }
};

// src/i18n/index.ts
var libraryI18n = i18n.createInstance();
libraryI18n.use(initReactI18next).init({
  resources: {
    en: { translation: { ...en_default, ...integrations_default } },
    // Other locales fall back to en for the integrations namespace until
    // translations are added.
    de: { translation: { ...de_default, ...integrations_default } },
    es: { translation: { ...es_default, ...integrations_default } },
    fr: { translation: { ...fr_default, ...integrations_default } },
    pt: { translation: { ...pt_default, ...integrations_default } },
    zh: { translation: { ...zh_default, ...integrations_default } },
    th: { translation: { ...th_default, ...integrations_default } },
    ar: { translation: { ...ar_default, ...integrations_default } }
  },
  lng: typeof window !== "undefined" ? localStorage.getItem("saved-language") || navigator.language.split("-")[0] || "en" : "en",
  // Set language synchronously
  fallbackLng: "en",
  defaultNS: "translation",
  interpolation: {
    escapeValue: false
  }
});
var i18n_default = libraryI18n;

// src/components/I18nProvider.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
function LibraryI18nProvider({ children }) {
  return /* @__PURE__ */ jsx6(I18nextProvider, { i18n: i18n_default, children });
}

// src/components/withLibraryI18n.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
function withLibraryI18n(Component) {
  const WrappedComponent = (props) => /* @__PURE__ */ jsx7(LibraryI18nProvider, { children: /* @__PURE__ */ jsx7(Component, { ...props }) });
  WrappedComponent.displayName = `withLibraryI18n(${Component.displayName || Component.name || "Component"})`;
  return WrappedComponent;
}

// src/components/Common/ColorSchemeToggle.tsx
import { ActionIcon as ActionIcon2, Tooltip, useMantineColorScheme } from "@mantine/core";
import { IconDeviceDesktop, IconMoon, IconSun } from "@tabler/icons-react";
import { jsx as jsx8 } from "react/jsx-runtime";
function ColorSchemeToggle() {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const toggleColorScheme = () => {
    if (colorScheme === "light") {
      setColorScheme("dark");
    } else if (colorScheme === "dark") {
      setColorScheme("auto");
    } else {
      setColorScheme("light");
    }
  };
  const getIcon = () => {
    switch (colorScheme) {
      case "light":
        return /* @__PURE__ */ jsx8(IconSun, { size: 18 });
      case "dark":
        return /* @__PURE__ */ jsx8(IconMoon, { size: 18 });
      case "auto":
        return /* @__PURE__ */ jsx8(IconDeviceDesktop, { size: 18 });
      default:
        return /* @__PURE__ */ jsx8(IconSun, { size: 18 });
    }
  };
  const getTooltipLabel = () => {
    switch (colorScheme) {
      case "light":
        return "Light mode (click for dark)";
      case "dark":
        return "Dark mode (click for auto)";
      case "auto":
        return "Auto mode (click for light)";
      default:
        return "Toggle color scheme";
    }
  };
  return /* @__PURE__ */ jsx8(Tooltip, { label: getTooltipLabel(), children: /* @__PURE__ */ jsx8(
    ActionIcon2,
    {
      onClick: toggleColorScheme,
      variant: "subtle",
      size: "lg",
      "aria-label": "Toggle color scheme",
      children: getIcon()
    }
  ) });
}

// src/components/ConnectedAccounts/ConnectedAccounts.tsx
import {
  ActionIcon as ActionIcon3,
  Alert,
  Badge as Badge2,
  Button as Button2,
  Card,
  Group as Group3,
  Loader,
  Modal,
  Stack as Stack2,
  Text as Text5,
  ThemeIcon,
  Tooltip as Tooltip2
} from "@mantine/core";
import {
  IconAlertCircle,
  IconBrandGithub,
  IconBrandGoogle,
  IconBrandWindows,
  IconKey,
  IconLink,
  IconMail,
  IconShieldLock,
  IconUnlink,
  IconUserPlus
} from "@tabler/icons-react";
import { useCallback as useCallback2, useEffect as useEffect2, useState as useState3 } from "react";
import { useTranslation as useTranslation3 } from "react-i18next";

// src/components/ConnectedAccounts/useConnectedAccountsFlow.ts
import { isSafeRedirectTarget } from "@gofranz/common";
import { useCallback } from "react";

// src/components/ConnectedAccounts/types.ts
var FLOW_STORAGE_KEY = "connectedAccounts.pendingFlow";
function readPendingFlow() {
  try {
    const raw = sessionStorage.getItem(FLOW_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
function writePendingFlow(flow) {
  sessionStorage.setItem(FLOW_STORAGE_KEY, JSON.stringify(flow));
}
function clearPendingFlow() {
  sessionStorage.removeItem(FLOW_STORAGE_KEY);
}

// src/components/ConnectedAccounts/useConnectedAccountsFlow.ts
async function waitForNostr(timeoutMs = 4e3) {
  const deadline = Date.now() + timeoutMs;
  for (; ; ) {
    const nostr = window.nostr;
    if (nostr?.signEvent) return nostr;
    if (Date.now() >= deadline) return null;
    await new Promise((r) => setTimeout(r, 120));
  }
}
async function signNostrChallenge(challenge) {
  const nostr = await waitForNostr();
  if (!nostr) {
    throw new Error("nostr_extension_missing");
  }
  return nostr.signEvent({
    kind: 22242,
    created_at: Math.floor(Date.now() / 1e3),
    tags: [["challenge", challenge]],
    content: ""
  });
}
function redirectTo(authUrl) {
  if (!isSafeRedirectTarget(authUrl)) {
    return { kind: "error", code: "unsafe_redirect" };
  }
  window.location.assign(authUrl);
  return { kind: "redirecting" };
}
function useConnectedAccountsFlow(api) {
  const dispatchStepUp = useCallback(
    async (purpose, challenge, onStepUpToken) => {
      const method = challenge.method.toLowerCase();
      if (challenge.auth_url) {
        return redirectTo(challenge.auth_url);
      }
      if (method === "nostr") {
        if (!challenge.challenge) {
          return { kind: "error", code: "step_up_invalid" };
        }
        const nostrChallenge = challenge.challenge;
        return {
          kind: "nostr_sign_required",
          sign: async () => {
            try {
              const signed = await signNostrChallenge(nostrChallenge);
              const { step_up_token } = await api.reauthNostrVerify(purpose, signed);
              return onStepUpToken(step_up_token);
            } catch (e) {
              return {
                kind: "error",
                code: e instanceof Error ? e.message : "step_up_invalid"
              };
            }
          }
        };
      }
      if (method === "email_magic_link" || challenge.email_sent) {
        return { kind: "email_pending" };
      }
      return { kind: "error", code: "step_up_invalid" };
    },
    [api]
  );
  const startLink = useCallback(async () => {
    const { auth_url } = await api.linkOidcInitiate();
    writePendingFlow({ action: "link" });
    return redirectTo(auth_url);
  }, [api]);
  const startUnlink = useCallback(
    async (id) => {
      writePendingFlow({ action: `unlink:${id}` });
      const challenge = await api.reauthInitiate("unlink");
      return dispatchStepUp("unlink", challenge, async (token) => {
        await api.unlinkIdentity(id, token);
        clearPendingFlow();
        return { kind: "completed" };
      });
    },
    [api, dispatchStepUp]
  );
  const startDelete = useCallback(
    async (confirmation) => {
      writePendingFlow({ action: "delete" });
      const challenge = await api.reauthInitiate("delete");
      return dispatchStepUp("delete", challenge, async (token) => {
        await api.deleteAccount(confirmation, token);
        clearPendingFlow();
        return { kind: "deleted" };
      });
    },
    [api, dispatchStepUp]
  );
  const resumeFromLanding = useCallback(
    async (params) => {
      const { status } = params;
      if (status === "error") {
        clearPendingFlow();
        return { kind: "error", code: params.error || "unknown" };
      }
      if (status === "proof") {
        if (!params.proof) {
          return { kind: "error", code: "missing_proof" };
        }
        writePendingFlow({ action: "link", proof: params.proof });
        const challenge = await api.reauthInitiate("link");
        return dispatchStepUp("link", challenge, async (token) => {
          await api.linkCommit(params.proof, token);
          clearPendingFlow();
          return { kind: "completed" };
        });
      }
      if (status === "stepup") {
        if (!params.stepup) {
          return { kind: "error", code: "missing_stepup" };
        }
        const pending = readPendingFlow();
        if (!pending) {
          return { kind: "error", code: "no_pending_flow" };
        }
        if (pending.action === "link") {
          if (!pending.proof) {
            clearPendingFlow();
            return { kind: "error", code: "missing_proof" };
          }
          await api.linkCommit(pending.proof, params.stepup);
          clearPendingFlow();
          return { kind: "completed" };
        }
        if (pending.action.startsWith("unlink:")) {
          const id = pending.action.slice("unlink:".length);
          await api.unlinkIdentity(id, params.stepup);
          clearPendingFlow();
          return { kind: "completed" };
        }
        if (pending.action === "delete") {
          await api.deleteAccount("DELETE", params.stepup);
          clearPendingFlow();
          return { kind: "deleted" };
        }
        clearPendingFlow();
        return { kind: "error", code: "no_pending_flow" };
      }
      return { kind: "error", code: "unknown" };
    },
    [api, dispatchStepUp]
  );
  return { startLink, startUnlink, startDelete, resumeFromLanding };
}

// src/components/ConnectedAccounts/ConnectedAccounts.tsx
import { Fragment as Fragment3, jsx as jsx9, jsxs as jsxs5 } from "react/jsx-runtime";
function methodIcon(method) {
  switch (method) {
    case "Google":
      return /* @__PURE__ */ jsx9(IconBrandGoogle, { size: 18 });
    case "GitHub":
      return /* @__PURE__ */ jsx9(IconBrandGithub, { size: 18 });
    case "Microsoft":
      return /* @__PURE__ */ jsx9(IconBrandWindows, { size: 18 });
    case "EmailMagicLink":
      return /* @__PURE__ */ jsx9(IconMail, { size: 18 });
    case "Nostr":
      return /* @__PURE__ */ jsx9(IconKey, { size: 18 });
    case "Oidc":
      return /* @__PURE__ */ jsx9(IconShieldLock, { size: 18 });
    default:
      return /* @__PURE__ */ jsx9(IconShieldLock, { size: 18 });
  }
}
function methodLabel(method, t) {
  switch (method) {
    case "EmailMagicLink":
      return t("connectedAccounts.methodEmailMagicLink", "Email magic link");
    case "Oidc":
      return t("login.oidcSsoTab", "GoFranz SSO");
    default:
      return method;
  }
}
function formatDate(ts) {
  if (!ts) return "\u2014";
  return new Date(ts).toLocaleDateString(void 0, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}
function ConnectedAccounts({ api, notifications: notifications3 }) {
  const { t } = useTranslation3();
  const flow = useConnectedAccountsFlow(api);
  const [identities, setIdentities] = useState3(null);
  const [error, setError] = useState3(null);
  const [busyId, setBusyId] = useState3(null);
  const [emailPending, setEmailPending] = useState3(false);
  const [nostrSignStep, setNostrSignStep] = useState3(null);
  const [signing, setSigning] = useState3(false);
  const refresh = useCallback2(async () => {
    setError(null);
    try {
      const data = await api.listIdentities();
      setIdentities(data);
    } catch (e) {
      setError(e?.message || t("connectedAccounts.errorLoad", "Could not load connected accounts"));
    }
  }, [api, t]);
  useEffect2(() => {
    refresh();
  }, [refresh]);
  const usableCount = (identities ?? []).filter((i) => i.id !== null).length;
  const hasOidc = (identities ?? []).some((i) => i.method === "Oidc");
  const handleStep = (step) => {
    switch (step.kind) {
      case "email_pending":
        setEmailPending(true);
        break;
      case "nostr_sign_required":
        setNostrSignStep(step);
        break;
      case "completed":
        notifications3?.show({
          message: t("connectedAccounts.linkedSuccess", "Account linked"),
          color: "green"
        });
        refresh();
        break;
      case "error":
        notifications3?.show({
          message: t(`connectedAccounts.error.${step.code}`, t("connectedAccounts.errorGeneric")),
          color: "red"
        });
        break;
    }
  };
  const onLink = async () => {
    if (busyId) return;
    setBusyId("link");
    try {
      handleStep(await flow.startLink());
    } catch (e) {
      notifications3?.show({
        message: e?.message || t("connectedAccounts.errorGeneric"),
        color: "red"
      });
    } finally {
      setBusyId(null);
    }
  };
  const onUnlink = async (id) => {
    if (busyId) return;
    setBusyId(id);
    try {
      handleStep(await flow.startUnlink(id));
    } catch (e) {
      notifications3?.show({
        message: e?.message || t("connectedAccounts.errorGeneric"),
        color: "red"
      });
    } finally {
      setBusyId(null);
    }
  };
  const onConfirmNostrSign = async () => {
    if (!nostrSignStep) return;
    setSigning(true);
    const pending = nostrSignStep;
    setNostrSignStep(null);
    try {
      handleStep(await pending.sign());
    } finally {
      setSigning(false);
    }
  };
  if (identities === null && !error) {
    return /* @__PURE__ */ jsx9(Group3, { justify: "center", py: "xl", children: /* @__PURE__ */ jsx9(Loader, { size: "sm" }) });
  }
  return /* @__PURE__ */ jsxs5(Stack2, { gap: "md", children: [
    /* @__PURE__ */ jsx9(
      Modal,
      {
        opened: nostrSignStep !== null,
        onClose: () => setNostrSignStep(null),
        title: t("connectedAccounts.nostrSignTitle", "Confirm with Nostr"),
        centered: true,
        children: /* @__PURE__ */ jsxs5(Stack2, { gap: "md", children: [
          /* @__PURE__ */ jsx9(Text5, { size: "sm", c: "dimmed", children: t(
            "connectedAccounts.nostrSignBody",
            "Sign a challenge with your Nostr extension to confirm it's you."
          ) }),
          /* @__PURE__ */ jsx9(Button2, { onClick: onConfirmNostrSign, loading: signing, children: t("connectedAccounts.nostrSignButton", "Sign with Nostr") })
        ] })
      }
    ),
    error && /* @__PURE__ */ jsx9(Alert, { icon: /* @__PURE__ */ jsx9(IconAlertCircle, { size: 16 }), color: "red", children: error }),
    emailPending && /* @__PURE__ */ jsx9(
      Alert,
      {
        icon: /* @__PURE__ */ jsx9(IconMail, { size: 16 }),
        color: "blue",
        title: t("connectedAccounts.checkEmailTitle", "Check your email"),
        children: t(
          "connectedAccounts.checkEmail",
          "We sent a confirmation link to your email. Open it to finish."
        )
      }
    ),
    /* @__PURE__ */ jsx9(Stack2, { gap: "sm", children: (identities ?? []).map((identity) => {
      const canUnlink = identity.id !== null && !identity.is_primary && usableCount > 1;
      const rowKey = identity.id ?? `pseudo-${identity.method}-${identity.display_key}`;
      return /* @__PURE__ */ jsx9(Card, { withBorder: true, radius: "md", padding: "md", children: /* @__PURE__ */ jsxs5(Group3, { justify: "space-between", wrap: "nowrap", children: [
        /* @__PURE__ */ jsxs5(Group3, { wrap: "nowrap", gap: "sm", style: { minWidth: 0 }, children: [
          /* @__PURE__ */ jsx9(ThemeIcon, { variant: "light", radius: "md", size: "lg", children: methodIcon(identity.method) }),
          /* @__PURE__ */ jsxs5(Stack2, { gap: 2, style: { minWidth: 0 }, children: [
            /* @__PURE__ */ jsxs5(Group3, { gap: "xs", wrap: "nowrap", children: [
              /* @__PURE__ */ jsx9(Text5, { fw: 600, size: "sm", children: methodLabel(identity.method, t) }),
              identity.is_primary && /* @__PURE__ */ jsx9(Badge2, { size: "sm", variant: "light", color: "blue", radius: "sm", children: t("connectedAccounts.primary", "Primary") })
            ] }),
            /* @__PURE__ */ jsx9(Text5, { size: "sm", c: "dimmed", truncate: true, children: identity.display_key }),
            /* @__PURE__ */ jsxs5(Text5, { size: "xs", c: "dimmed", children: [
              identity.linked_via ? t("connectedAccounts.linkedVia", "Linked via {{via}}", {
                via: identity.linked_via
              }) : t("connectedAccounts.added", "Added"),
              " ",
              formatDate(identity.created_at),
              identity.last_login_at ? ` \xB7 ${t("connectedAccounts.lastLogin", "last login")} ${formatDate(identity.last_login_at)}` : ""
            ] })
          ] })
        ] }),
        canUnlink && /* @__PURE__ */ jsx9(Tooltip2, { label: t("connectedAccounts.unlink", "Unlink"), withArrow: true, children: /* @__PURE__ */ jsx9(
          ActionIcon3,
          {
            variant: "subtle",
            color: "red",
            onClick: () => onUnlink(identity.id),
            loading: busyId === identity.id,
            disabled: busyId !== null && busyId !== identity.id,
            "aria-label": t("connectedAccounts.unlink", "Unlink"),
            children: /* @__PURE__ */ jsx9(IconUnlink, { size: 18 })
          }
        ) })
      ] }) }, rowKey);
    }) }),
    hasOidc ? /* @__PURE__ */ jsx9(Alert, { icon: /* @__PURE__ */ jsx9(IconShieldLock, { size: 16 }), color: "gray", variant: "light", children: t(
      "connectedAccounts.ssoConnected",
      "Single sign-on is connected. Accounts using SSO are managed by your identity provider and cannot link additional methods."
    ) }) : /* @__PURE__ */ jsxs5(Fragment3, { children: [
      /* @__PURE__ */ jsx9(Group3, { children: /* @__PURE__ */ jsx9(
        Button2,
        {
          leftSection: /* @__PURE__ */ jsx9(IconUserPlus, { size: 16 }),
          onClick: onLink,
          loading: busyId === "link",
          disabled: busyId !== null && busyId !== "link",
          children: t("connectedAccounts.linkSso", "Connect single sign-on (SSO)")
        }
      ) }),
      /* @__PURE__ */ jsxs5(Group3, { gap: 6, c: "dimmed", children: [
        /* @__PURE__ */ jsx9(IconLink, { size: 14 }),
        /* @__PURE__ */ jsx9(Text5, { size: "xs", children: t(
          "connectedAccounts.hint",
          "Connect SSO to also sign in to this account with your single sign-on provider."
        ) })
      ] })
    ] })
  ] });
}

// src/components/ConnectedAccounts/ConnectedAccountsLanding.tsx
import { Alert as Alert2, Box as Box2, Button as Button3, Center, Loader as Loader2, Stack as Stack3, Text as Text6, Title as Title2 } from "@mantine/core";
import { IconAlertCircle as IconAlertCircle2, IconCircleCheck } from "@tabler/icons-react";
import { useEffect as useEffect3, useRef, useState as useState4 } from "react";
import { useTranslation as useTranslation4 } from "react-i18next";
import { jsx as jsx10, jsxs as jsxs6 } from "react/jsx-runtime";
function parseLandingParams() {
  const hash = window.location.hash;
  const qs = hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : "";
  const params = new URLSearchParams(qs);
  return {
    status: params.get("status"),
    proof: params.get("proof"),
    stepup: params.get("stepup"),
    error: params.get("error")
  };
}
function ConnectedAccountsLanding({
  api,
  connectedAccountsHref,
  onDone,
  onDeleted
}) {
  const { t } = useTranslation4();
  const flow = useConnectedAccountsFlow(api);
  const [step, setStep] = useState4(null);
  const ran = useRef(false);
  useEffect3(() => {
    if (ran.current) return;
    ran.current = true;
    const params = parseLandingParams();
    if (window.location.hash.includes("?")) {
      const cleanHash = window.location.hash.slice(0, window.location.hash.indexOf("?"));
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}${cleanHash}`
      );
    }
    flow.resumeFromLanding(params).then((result) => {
      setStep(result);
      if (result.kind === "completed") {
        setTimeout(onDone, 1200);
      }
      if (result.kind === "deleted") {
        setTimeout(() => onDeleted?.(), 1200);
      }
    }).catch((e) => {
      setStep({ kind: "error", code: e?.response?.data?.error || e?.message || "unknown" });
    });
  }, []);
  return /* @__PURE__ */ jsx10(Center, { mih: "50vh", children: /* @__PURE__ */ jsxs6(Box2, { maw: 420, w: "100%", children: [
    step === null && /* @__PURE__ */ jsxs6(Stack3, { align: "center", gap: "md", children: [
      /* @__PURE__ */ jsx10(Loader2, {}),
      /* @__PURE__ */ jsx10(Text6, { c: "dimmed", children: t("connectedAccounts.processing", "Finishing up\u2026") })
    ] }),
    step?.kind === "redirecting" && /* @__PURE__ */ jsxs6(Stack3, { align: "center", gap: "md", children: [
      /* @__PURE__ */ jsx10(Loader2, {}),
      /* @__PURE__ */ jsx10(Text6, { c: "dimmed", children: t("connectedAccounts.redirecting", "Redirecting\u2026") })
    ] }),
    step?.kind === "nostr_sign_required" && /* @__PURE__ */ jsxs6(Stack3, { align: "center", gap: "md", children: [
      /* @__PURE__ */ jsx10(Title2, { order: 4, children: t("connectedAccounts.nostrSignTitle", "Confirm with Nostr") }),
      /* @__PURE__ */ jsx10(Text6, { c: "dimmed", ta: "center", children: t(
        "connectedAccounts.nostrSignBody",
        "Sign a challenge with your Nostr extension to confirm it's you."
      ) }),
      /* @__PURE__ */ jsx10(
        Button3,
        {
          onClick: async () => {
            const pending = step;
            setStep(null);
            const result = await pending.sign();
            setStep(result);
            if (result.kind === "completed") {
              setTimeout(onDone, 1200);
            }
            if (result.kind === "deleted") {
              setTimeout(() => onDeleted?.(), 1200);
            }
          },
          children: t("connectedAccounts.nostrSignButton", "Sign with Nostr")
        }
      )
    ] }),
    step?.kind === "email_pending" && /* @__PURE__ */ jsx10(
      Alert2,
      {
        icon: /* @__PURE__ */ jsx10(IconAlertCircle2, { size: 16 }),
        color: "blue",
        title: t("connectedAccounts.checkEmailTitle", "Check your email"),
        children: t(
          "connectedAccounts.checkEmail",
          "We sent a confirmation link to your email. Open it to finish."
        )
      }
    ),
    step?.kind === "completed" && /* @__PURE__ */ jsxs6(Stack3, { align: "center", gap: "md", children: [
      /* @__PURE__ */ jsx10(IconCircleCheck, { size: 48, color: "var(--mantine-color-green-6)" }),
      /* @__PURE__ */ jsx10(Title2, { order: 3, children: t("connectedAccounts.linkedSuccess", "Account linked") }),
      /* @__PURE__ */ jsx10(Button3, { variant: "light", component: "a", href: connectedAccountsHref, onClick: onDone, children: t("connectedAccounts.backToProfile", "Back to Profile") })
    ] }),
    step?.kind === "deleted" && /* @__PURE__ */ jsxs6(Stack3, { align: "center", gap: "md", children: [
      /* @__PURE__ */ jsx10(IconCircleCheck, { size: 48, color: "var(--mantine-color-green-6)" }),
      /* @__PURE__ */ jsx10(Title2, { order: 3, children: t("connectedAccounts.deletedSuccess", "Account deleted") }),
      /* @__PURE__ */ jsx10(Text6, { c: "dimmed", ta: "center", children: t("connectedAccounts.deletedBody", "Your account has been permanently deleted.") })
    ] }),
    step?.kind === "error" && /* @__PURE__ */ jsxs6(Stack3, { gap: "md", children: [
      /* @__PURE__ */ jsx10(
        Alert2,
        {
          icon: /* @__PURE__ */ jsx10(IconAlertCircle2, { size: 16 }),
          color: "red",
          title: t("connectedAccounts.errorTitle", "Something went wrong"),
          children: t(`connectedAccounts.error.${step.code}`, t("connectedAccounts.errorGeneric"))
        }
      ),
      /* @__PURE__ */ jsx10(Button3, { variant: "light", component: "a", href: connectedAccountsHref, onClick: onDone, children: t("connectedAccounts.tryAgain", "Try again") })
    ] })
  ] }) });
}

// src/components/ConnectedAccounts/ConnectedAccountsPage.tsx
import { Stack as Stack4, Text as Text7, Title as Title3 } from "@mantine/core";
import { useTranslation as useTranslation5 } from "react-i18next";
import { Fragment as Fragment4, jsx as jsx11, jsxs as jsxs7 } from "react/jsx-runtime";
function ConnectedAccountsPage(props) {
  const { t } = useTranslation5();
  return /* @__PURE__ */ jsxs7(Fragment4, { children: [
    /* @__PURE__ */ jsxs7(Stack4, { gap: 4, mb: "lg", children: [
      /* @__PURE__ */ jsx11(Title3, { order: 1, size: "h2", fw: 600, style: { letterSpacing: "-0.01em" }, children: t("connectedAccounts.title", "Connected Accounts") }),
      /* @__PURE__ */ jsx11(Text7, { size: "sm", c: "dimmed", lh: 1.5, children: t(
        "connectedAccounts.description",
        "Sign-in methods linked to your account. Link an additional provider or remove one you no longer use."
      ) })
    ] }),
    /* @__PURE__ */ jsx11(ConnectedAccounts, { ...props })
  ] });
}

// src/components/Login/Login.tsx
import {
  decodeNostrPublicKey,
  LOGIN_METHOD
} from "@gofranz/common";
import {
  Button as Button4,
  Center as Center2,
  Divider,
  Group as Group4,
  Loader as Loader3,
  PinInput,
  SimpleGrid,
  Stack as Stack5,
  Text as Text8,
  TextInput,
  Title as Title4,
  Tooltip as Tooltip3
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconBrandGithub as IconBrandGithub2,
  IconBrandGoogle as IconBrandGoogle2,
  IconBrandWindows as IconBrandWindows2,
  IconInfoCircle,
  IconKey as IconKey2,
  IconShieldCheck,
  IconShieldLock as IconShieldLock2
} from "@tabler/icons-react";
import { useState as useState5 } from "react";
import { useTranslation as useTranslation6 } from "react-i18next";
import { Fragment as Fragment5, jsx as jsx12, jsxs as jsxs8 } from "react/jsx-runtime";
var StepOne = ({
  googleLogin,
  githubLogin,
  microsoftLogin,
  nostrLogin,
  oidcLogin,
  magicLinkLoginChallenge,
  loginMethods,
  isBusy,
  t
}) => {
  const formRequestEmailMagicLink = useForm({
    initialValues: {
      email: ""
    },
    validate: {
      email: (value) => /^\S+@\S+$/.test(value) ? null : t("login.invalidEmail")
    }
  });
  const hasEmailMagicLinkLogin = loginMethods?.includes(LOGIN_METHOD.EMAIL_MAGIC_LINK);
  const socialButtons = [];
  if (loginMethods?.includes(LOGIN_METHOD.GOOGLE)) {
    socialButtons.push({
      key: LOGIN_METHOD.GOOGLE,
      label: t("login.googleTab"),
      icon: /* @__PURE__ */ jsx12(IconBrandGoogle2, { size: 20 }),
      onClick: googleLogin
    });
  }
  if (loginMethods?.includes(LOGIN_METHOD.GITHUB)) {
    socialButtons.push({
      key: LOGIN_METHOD.GITHUB,
      label: t("login.githubTab"),
      icon: /* @__PURE__ */ jsx12(IconBrandGithub2, { size: 20 }),
      onClick: githubLogin
    });
  }
  if (loginMethods?.includes(LOGIN_METHOD.MICROSOFT)) {
    socialButtons.push({
      key: LOGIN_METHOD.MICROSOFT,
      label: t("login.microsoftTab"),
      icon: /* @__PURE__ */ jsx12(IconBrandWindows2, { size: 20 }),
      onClick: microsoftLogin
    });
  }
  if (loginMethods?.includes(LOGIN_METHOD.NOSTR)) {
    socialButtons.push({
      key: LOGIN_METHOD.NOSTR,
      label: t("login.nostrTab"),
      icon: /* @__PURE__ */ jsx12(IconKey2, { size: 20 }),
      onClick: nostrLogin
    });
  }
  if (loginMethods?.includes(LOGIN_METHOD.OIDC)) {
    socialButtons.push({
      key: LOGIN_METHOD.OIDC,
      label: t("login.oidcSsoTab"),
      icon: /* @__PURE__ */ jsx12(IconShieldCheck, { size: 20 }),
      onClick: oidcLogin
    });
  }
  const cols = Math.min(Math.max(socialButtons.length, 1), 3);
  return /* @__PURE__ */ jsxs8(Stack5, { gap: "lg", children: [
    /* @__PURE__ */ jsxs8(Stack5, { gap: "xs", children: [
      /* @__PURE__ */ jsx12(Title4, { order: 3, fw: 600, children: t("login.welcomeBack") }),
      /* @__PURE__ */ jsx12(Text8, { size: "sm", c: "dimmed", children: t("login.cardSubtitle") })
    ] }),
    socialButtons.length > 0 && /* @__PURE__ */ jsx12(SimpleGrid, { cols, spacing: "sm", children: socialButtons.map((btn) => /* @__PURE__ */ jsx12(Tooltip3, { label: btn.label, withArrow: true, children: /* @__PURE__ */ jsx12(
      Button4,
      {
        variant: "default",
        size: "md",
        px: "sm",
        onClick: btn.onClick,
        loading: isBusy,
        "aria-label": btn.label,
        children: btn.icon
      }
    ) }, btn.key)) }),
    socialButtons.length > 0 && hasEmailMagicLinkLogin && /* @__PURE__ */ jsx12(Divider, { label: t("login.orDivider"), labelPosition: "center" }),
    hasEmailMagicLinkLogin && /* @__PURE__ */ jsx12(
      "form",
      {
        onSubmit: formRequestEmailMagicLink.onSubmit(
          () => magicLinkLoginChallenge(formRequestEmailMagicLink.values.email)
        ),
        children: /* @__PURE__ */ jsxs8(Stack5, { gap: "sm", children: [
          /* @__PURE__ */ jsx12(
            TextInput,
            {
              label: t("login.emailLabel"),
              placeholder: t("login.emailPlaceholder"),
              type: "email",
              autoComplete: "email",
              ...formRequestEmailMagicLink.getInputProps("email")
            }
          ),
          /* @__PURE__ */ jsx12(Button4, { type: "submit", loading: isBusy, fullWidth: true, size: "md", children: t("login.loginWithEmail") }),
          /* @__PURE__ */ jsxs8(Group4, { gap: "xs", wrap: "nowrap", align: "center", children: [
            /* @__PURE__ */ jsx12(IconInfoCircle, { size: 16, style: { flexShrink: 0 } }),
            /* @__PURE__ */ jsx12(Text8, { size: "sm", c: "dimmed", children: t("login.magicLinkInfo") })
          ] })
        ] })
      }
    )
  ] });
};
var StepTwo = ({ loginMethod, setActiveStep, t }) => {
  return /* @__PURE__ */ jsxs8(Fragment5, { children: [
    loginMethod === LOGIN_METHOD.EMAIL_MAGIC_LINK && /* @__PURE__ */ jsx12(Fragment5, { children: /* @__PURE__ */ jsxs8(Stack5, { gap: "md", children: [
      /* @__PURE__ */ jsx12(Text8, { size: "lg", fw: 500, children: t("login.emailSent") }),
      /* @__PURE__ */ jsx12(Text8, { children: t("login.clickLinkToLogin") }),
      /* @__PURE__ */ jsx12(Text8, { size: "sm", c: "dimmed", children: t("login.wrongEmail") }),
      /* @__PURE__ */ jsx12(Button4, { variant: "outline", onClick: () => setActiveStep(0), children: t("login.back") })
    ] }) }),
    loginMethod === LOGIN_METHOD.NOSTR && /* @__PURE__ */ jsxs8(Fragment5, { children: [
      /* @__PURE__ */ jsx12(Text8, { mb: "md", children: t("login.nostrInstructions") }),
      /* @__PURE__ */ jsx12(Center2, { children: /* @__PURE__ */ jsx12(Loader3, { size: "xl" }) })
    ] }),
    loginMethod === LOGIN_METHOD.GOOGLE && /* @__PURE__ */ jsxs8(Fragment5, { children: [
      /* @__PURE__ */ jsx12(Text8, { mb: "md", children: t("login.redirectingGoogle") }),
      /* @__PURE__ */ jsx12(Center2, { children: /* @__PURE__ */ jsx12(Loader3, { size: "xl" }) })
    ] }),
    loginMethod === LOGIN_METHOD.GITHUB && /* @__PURE__ */ jsxs8(Fragment5, { children: [
      /* @__PURE__ */ jsx12(Text8, { mb: "md", children: t("login.redirectingGithub") }),
      /* @__PURE__ */ jsx12(Center2, { children: /* @__PURE__ */ jsx12(Loader3, { size: "xl" }) })
    ] }),
    loginMethod === LOGIN_METHOD.MICROSOFT && /* @__PURE__ */ jsxs8(Fragment5, { children: [
      /* @__PURE__ */ jsx12(Text8, { mb: "md", children: t("login.redirectingMicrosoft") }),
      /* @__PURE__ */ jsx12(Center2, { children: /* @__PURE__ */ jsx12(Loader3, { size: "xl" }) })
    ] }),
    loginMethod === LOGIN_METHOD.OIDC && /* @__PURE__ */ jsxs8(Fragment5, { children: [
      /* @__PURE__ */ jsx12(Text8, { mb: "md", children: t("login.redirectingOidc") }),
      /* @__PURE__ */ jsx12(Center2, { children: /* @__PURE__ */ jsx12(Loader3, { size: "xl" }) })
    ] })
  ] });
};
var StepTwoFactor = ({
  pendingToken,
  verify2FA,
  loginDoneCb,
  setActiveStep,
  setHasError,
  t
}) => {
  const [code, setCode] = useState5("");
  const [isBusy, setIsBusy] = useState5(false);
  const handleVerify = async () => {
    if (code.length < 6) return;
    setIsBusy(true);
    setHasError(null);
    try {
      await verify2FA({
        pending_token: pendingToken,
        code
      });
      loginDoneCb();
    } catch (e) {
      console.error(e);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === "object" && errorData.message) {
        setHasError(errorData.message);
      } else if (e?.response?.status === 401) {
        setHasError(t("login.invalid2FACode"));
      } else if (e?.response?.status === 429) {
        setHasError(t("login.tooMany2FAAttempts"));
        setActiveStep(0);
      } else {
        setHasError(`Error: ${e?.message || e}`);
      }
      setCode("");
    } finally {
      setIsBusy(false);
    }
  };
  return /* @__PURE__ */ jsxs8(Stack5, { gap: "md", align: "center", children: [
    /* @__PURE__ */ jsx12(IconShieldLock2, { size: 48, stroke: 1.5 }),
    /* @__PURE__ */ jsx12(Text8, { size: "lg", fw: 500, children: t("login.twoFactorRequired") }),
    /* @__PURE__ */ jsx12(Text8, { size: "sm", c: "dimmed", ta: "center", children: t("login.enterTwoFactorCode") }),
    /* @__PURE__ */ jsx12(
      PinInput,
      {
        length: 6,
        type: "number",
        value: code,
        onChange: setCode,
        oneTimeCode: true,
        disabled: isBusy,
        size: "lg"
      }
    ),
    /* @__PURE__ */ jsx12(Button4, { onClick: handleVerify, loading: isBusy, disabled: code.length < 6, fullWidth: true, children: t("login.verify") }),
    /* @__PURE__ */ jsx12(Text8, { size: "sm", c: "dimmed", ta: "center", children: t("login.useRecoveryCode") }),
    /* @__PURE__ */ jsx12(Button4, { variant: "subtle", size: "xs", onClick: () => setActiveStep(0), children: t("login.back") })
  ] });
};
function Login(props) {
  const { t } = useTranslation6();
  const [activeStep, setActiveStep] = useState5(0);
  const [loginMethod, setLoginMethod] = useState5(LOGIN_METHOD.EMAIL_MAGIC_LINK);
  const [isBusy, setIsBusy] = useState5(false);
  const [hasError, setHasError] = useState5(null);
  const [pendingToken, setPendingToken] = useState5(null);
  const nostrLogin = async () => {
    setLoginMethod(LOGIN_METHOD.NOSTR);
    setHasError(null);
    if (!window.nostr) {
      setHasError(t("login.nostrNoExtension"));
      setActiveStep(0);
      return;
    }
    setIsBusy(true);
    setActiveStep(1);
    try {
      const publicKey = await window.nostr.getPublicKey();
      if (!publicKey) {
        setHasError(t("login.nostrNoPublicKey"));
        return;
      }
      const challenge = await props.login({
        type: "NOSTR",
        content: {
          public_key: decodeNostrPublicKey(publicKey)
        }
      });
      if (challenge?.type !== "NOSTR") {
        setHasError(t("login.nostrNoChallenge"));
        return;
      }
      const loginResult = await props.loginChallenge({
        type: "NOSTR",
        content: {
          id: challenge.content.id,
          response: await window.nostr.signEvent({
            kind: 22242,
            created_at: Math.floor(Date.now() / 1e3),
            tags: [["challenge", challenge.content.challenge]],
            content: ""
          })
        }
      });
      if (!loginResult) {
        setHasError(t("login.nostrNoChallengeResponse"));
        return;
      }
      if (loginResult.status === "requires_2fa") {
        setPendingToken(loginResult.data.pending_token);
        setActiveStep(2);
        return;
      }
      props.loginDoneCb();
    } catch (e) {
      console.error(e);
      setActiveStep(0);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === "object" && errorData.error) {
        switch (errorData.error) {
          case "challenge_mismatch":
          case "invalid_challenge":
            setHasError(t("login.nostrChallengeFailed"));
            break;
          default:
            setHasError(errorData.message || `Error: ${e?.message || e}`);
        }
      } else {
        setHasError(`Error: ${e?.message || e}`);
      }
    } finally {
      setIsBusy(false);
    }
  };
  const googleLogin = async () => {
    setIsBusy(true);
    setLoginMethod(LOGIN_METHOD.GOOGLE);
    setHasError(null);
    setActiveStep(1);
    try {
      const googleChallenge = await props.login({
        type: LOGIN_METHOD.GOOGLE,
        content: {}
      });
      if (!googleChallenge || googleChallenge.type !== LOGIN_METHOD.GOOGLE) {
        setHasError(t("login.googleNoChallenge"));
        return;
      }
      window.location.href = googleChallenge.content.auth_url;
    } catch (e) {
      console.error(e);
      setActiveStep(0);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === "object" && errorData.message) {
        setHasError(errorData.message);
      } else {
        setHasError(`Error: ${e?.message || e}`);
      }
    } finally {
      setIsBusy(false);
    }
  };
  const githubLogin = async () => {
    setIsBusy(true);
    setLoginMethod(LOGIN_METHOD.GITHUB);
    setHasError(null);
    setActiveStep(1);
    try {
      const githubChallenge = await props.login({
        type: LOGIN_METHOD.GITHUB,
        content: {}
      });
      if (!githubChallenge || githubChallenge.type !== LOGIN_METHOD.GITHUB) {
        setHasError(t("login.githubNoChallenge"));
        return;
      }
      window.location.href = githubChallenge.content.auth_url;
    } catch (e) {
      console.error(e);
      setActiveStep(0);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === "object" && errorData.message) {
        setHasError(errorData.message);
      } else {
        setHasError(`Error: ${e?.message || e}`);
      }
    } finally {
      setIsBusy(false);
    }
  };
  const oidcLogin = async () => {
    setIsBusy(true);
    setLoginMethod(LOGIN_METHOD.OIDC);
    setHasError(null);
    setActiveStep(1);
    try {
      const oidcChallenge = await props.login({
        type: LOGIN_METHOD.OIDC,
        content: {}
      });
      if (!oidcChallenge || oidcChallenge.type !== LOGIN_METHOD.OIDC) {
        setHasError(t("login.oidcNoChallenge"));
        return;
      }
      window.location.href = oidcChallenge.content.auth_url;
    } catch (e) {
      console.error(e);
      setActiveStep(0);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === "object" && errorData.message) {
        setHasError(errorData.message);
      } else {
        setHasError(`Error: ${e?.message || e}`);
      }
    } finally {
      setIsBusy(false);
    }
  };
  const microsoftLogin = async () => {
    setIsBusy(true);
    setLoginMethod(LOGIN_METHOD.MICROSOFT);
    setHasError(null);
    setActiveStep(1);
    try {
      const microsoftChallenge = await props.login({
        type: LOGIN_METHOD.MICROSOFT,
        content: {}
      });
      if (!microsoftChallenge || microsoftChallenge.type !== LOGIN_METHOD.MICROSOFT) {
        setHasError(t("login.microsoftNoChallenge"));
        return;
      }
      window.location.href = microsoftChallenge.content.auth_url;
    } catch (e) {
      console.error(e);
      setActiveStep(0);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === "object" && errorData.message) {
        setHasError(errorData.message);
      } else {
        setHasError(`Error: ${e?.message || e}`);
      }
    } finally {
      setIsBusy(false);
    }
  };
  const magicLinkLoginChallenge = async (email2) => {
    setIsBusy(true);
    setLoginMethod(LOGIN_METHOD.EMAIL_MAGIC_LINK);
    setHasError(null);
    try {
      const emailMagicLinkChallenge = await props.login({
        type: LOGIN_METHOD.EMAIL_MAGIC_LINK,
        content: {
          email: email2
        }
      });
      if (!emailMagicLinkChallenge || emailMagicLinkChallenge.type !== LOGIN_METHOD.EMAIL_MAGIC_LINK) {
        setHasError(t("login.emailNoChallenge"));
        return;
      }
      setActiveStep(1);
    } catch (e) {
      console.error(e);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === "object" && errorData.message) {
        setHasError(errorData.message);
      } else {
        setHasError(`Error: ${e?.message || e}`);
      }
    } finally {
      setIsBusy(false);
    }
  };
  const ErrorMessage = () => /* @__PURE__ */ jsx12(Text8, { c: "red", mb: "md", children: hasError });
  return /* @__PURE__ */ jsxs8(Fragment5, { children: [
    activeStep === 0 && /* @__PURE__ */ jsx12(
      StepOne,
      {
        googleLogin,
        githubLogin,
        microsoftLogin,
        nostrLogin,
        oidcLogin,
        magicLinkLoginChallenge,
        loginMethods: props.loginMethods,
        isBusy,
        t
      }
    ),
    activeStep === 1 && /* @__PURE__ */ jsx12(StepTwo, { loginMethod, setActiveStep, t }),
    activeStep === 2 && pendingToken && /* @__PURE__ */ jsx12(
      StepTwoFactor,
      {
        pendingToken,
        verify2FA: props.verify2FA,
        loginDoneCb: props.loginDoneCb,
        setActiveStep,
        setHasError,
        t
      }
    ),
    /* @__PURE__ */ jsx12(ErrorMessage, {})
  ] });
}

// src/components/Login/LoginCallback.tsx
import {
  isSafeSameOriginTarget,
  sessionExpiriesFromLoginSuccess
} from "@gofranz/common";
import { Box as Box3, Button as Button5, Center as Center3, Group as Group5, Loader as Loader4, Stack as Stack6, Text as Text9, ThemeIcon as ThemeIcon2, Title as Title5 } from "@mantine/core";
import {
  IconAlertTriangle,
  IconBrandGithub as IconBrandGithub3,
  IconBrandGoogle as IconBrandGoogle3,
  IconBrandWindows as IconBrandWindows3,
  IconKey as IconKey3,
  IconMail as IconMail2,
  IconShieldLock as IconShieldLock3
} from "@tabler/icons-react";
import { useEffect as useEffect4, useRef as useRef2, useState as useState6 } from "react";
import { useTranslation as useTranslation7 } from "react-i18next";
import { jsx as jsx13, jsxs as jsxs9 } from "react/jsx-runtime";
var METHOD_ICON = {
  nostr: IconKey3,
  email_magic_link: IconMail2,
  google: IconBrandGoogle3,
  github: IconBrandGithub3,
  microsoft: IconBrandWindows3
};
var AUTH_METHOD_LABELS = {
  nostr: "Nostr",
  email_magic_link: "email magic link",
  google: "Google",
  github: "GitHub",
  microsoft: "Microsoft"
};
function stripRefreshFlag(href, paramName = "refreshed") {
  try {
    const url = new URL(href, window.location.origin);
    url.searchParams.delete(paramName);
    const [hashPath, hashQs] = url.hash.split("?");
    if (hashQs) {
      const hashParams = new URLSearchParams(hashQs);
      hashParams.delete(paramName);
      const rebuilt = hashParams.toString();
      url.hash = rebuilt ? `${hashPath}?${rebuilt}` : hashPath;
    }
    return url.toString();
  } catch {
    return href;
  }
}
function LoginCallback(props) {
  const { t } = useTranslation7();
  const [hasError, setHasError] = useState6([]);
  const [collision, setCollision] = useState6(
    null
  );
  const [isExchanging, setIsExchanging] = useState6(false);
  const exchangeAttempted = useRef2(false);
  useEffect4(() => {
    if (hasError.length > 0 || collision) props.onError?.();
  }, [hasError, collision]);
  useEffect4(() => {
    if (exchangeAttempted.current) return;
    exchangeAttempted.current = true;
    const controller = new AbortController();
    const url = new URL(window.location.href);
    const hashParams = new URLSearchParams(url.hash.split("?")[1]);
    const error = hashParams.get("error");
    const errorMessage = hashParams.get("error_message");
    const originalMethod = hashParams.get("original_method");
    if (error === "auth_method_collision") {
      const label = originalMethod ? AUTH_METHOD_LABELS[originalMethod] : void 0;
      setCollision({ method: originalMethod || null, label: label ?? null });
      return;
    }
    if (error) {
      const errors = [];
      switch (error) {
        case "google_email_not_verified":
          errors.push(t("login.errorGoogleEmailNotVerified"), errorMessage || "");
          break;
        case "invalid_auth_method_google":
          errors.push(t("login.errorInvalidAuthMethodGoogle"), errorMessage || "");
          break;
        case "invalid_auth_method_github":
          errors.push(t("login.errorInvalidAuthMethodGithub"), errorMessage || "");
          break;
        case "invalid_auth_method_microsoft":
          errors.push(t("login.errorInvalidAuthMethodMicrosoft"), errorMessage || "");
          break;
        case "invalid_auth_method_nostr":
          errors.push(t("login.errorInvalidAuthMethodNostr"), errorMessage || "");
          break;
        default:
          errors.push(t("login.errorOAuthFailed"), errorMessage || error);
      }
      setHasError(errors.filter((e) => e));
      return;
    }
    const code = hashParams.get("code");
    const loginMethod = hashParams.get("login_method");
    const returnTo = hashParams.get("return_to");
    if (!code) {
      setHasError([t("login.invalidLoginResponse")]);
      return;
    }
    setIsExchanging(true);
    fetch(`${props.apiBaseUrl}/login/exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
      credentials: "include",
      signal: controller.signal
    }).then(async (res) => {
      if (!res.ok) {
        if (res.status === 429) {
          const retryAfter = res.headers.get("Retry-After");
          throw new Error(t("login.errorRateLimited", { seconds: retryAfter || "60" }));
        }
        const body = await res.json().catch(() => ({}));
        if (body.error === "expired_or_used_auth_code") {
          throw new Error(t("login.errorCodeExpired"));
        }
        throw new Error(body.message || t("login.errorTokenExchange"));
      }
      return res.json();
    }).then((data) => {
      props.setSession({
        isLoggedIn: true,
        userId: data.user_id,
        accessToken: data.access_token,
        email: data.email,
        displayName: data.display_name,
        ...sessionExpiriesFromLoginSuccess(data),
        method: loginMethod
      });
      if (returnTo) {
        const stripped = stripRefreshFlag(returnTo);
        if (isSafeSameOriginTarget(stripped)) {
          window.location.assign(stripped);
          return;
        }
        console.warn(
          "LoginCallback: refusing unsafe return_to; falling back to default landing",
          { returnTo }
        );
      }
      props.loginDoneCb();
    }).catch((err) => {
      if (err.name === "AbortError") return;
      setIsExchanging(false);
      setHasError([err.message || t("login.errorTokenExchange")]);
    });
    return () => controller.abort();
  }, []);
  const CollisionCard = () => {
    const label = collision?.label ?? null;
    const MethodIcon = collision?.method && METHOD_ICON[collision.method] || IconShieldLock3;
    return /* @__PURE__ */ jsxs9(Stack6, { align: "center", gap: "lg", ta: "center", children: [
      /* @__PURE__ */ jsx13(ThemeIcon2, { size: 64, radius: "xl", variant: "light", color: "primary", children: /* @__PURE__ */ jsx13(MethodIcon, { size: 30, stroke: 1.5 }) }),
      /* @__PURE__ */ jsxs9(Stack6, { gap: 6, children: [
        /* @__PURE__ */ jsx13(Title5, { order: 3, fw: 600, children: t("login.callbackAccountExistsTitle", "You already have an account") }),
        /* @__PURE__ */ jsx13(Text9, { size: "sm", c: "dimmed", children: label ? t("login.callbackAccountExistsBody", "Welcome back \u2014 you've signed in here before.") : t(
          "login.callbackAccountExistsBodyGeneric",
          "This email signs in with a different method."
        ) })
      ] }),
      label && /* @__PURE__ */ jsxs9(Stack6, { gap: 8, align: "center", children: [
        /* @__PURE__ */ jsx13(Text9, { size: "xs", fw: 700, c: "dimmed", tt: "uppercase", style: { letterSpacing: "0.07em" }, children: t("login.callbackMethodEyebrow", "Your sign-in method") }),
        /* @__PURE__ */ jsxs9(
          Group5,
          {
            gap: 8,
            px: "md",
            py: 6,
            wrap: "nowrap",
            style: {
              border: "1px solid var(--mantine-color-default-border)",
              borderRadius: "var(--mantine-radius-xl)"
            },
            children: [
              /* @__PURE__ */ jsx13(MethodIcon, { size: 16, stroke: 1.6 }),
              /* @__PURE__ */ jsx13(Text9, { size: "sm", fw: 500, tt: "capitalize", children: label })
            ]
          }
        )
      ] }),
      /* @__PURE__ */ jsxs9(Stack6, { gap: 8, w: "100%", children: [
        /* @__PURE__ */ jsx13(Button5, { component: "a", href: props.loginHref || "#/login", fullWidth: true, size: "md", children: label ? t("login.callbackContinueWith", "Continue with {{method}}", { method: label }) : t("login.errorAuthMethodCollisionRemedyLink", "Go to sign in") }),
        /* @__PURE__ */ jsx13(Text9, { size: "xs", c: "dimmed", children: t(
          "login.callbackAddSsoNote",
          "You can add single sign-on later from Connected Accounts."
        ) })
      ] })
    ] });
  };
  const ErrorCard = () => /* @__PURE__ */ jsxs9(Stack6, { align: "center", gap: "lg", ta: "center", children: [
    /* @__PURE__ */ jsx13(ThemeIcon2, { size: 64, radius: "xl", variant: "light", color: "red", children: /* @__PURE__ */ jsx13(IconAlertTriangle, { size: 30, stroke: 1.5 }) }),
    /* @__PURE__ */ jsxs9(Stack6, { gap: 6, children: [
      /* @__PURE__ */ jsx13(Title5, { order: 3, fw: 600, children: t("login.callbackErrorTitle", "Couldn't sign you in") }),
      hasError.map((error, index) => /* @__PURE__ */ jsx13(Text9, { size: "sm", c: index === 0 ? void 0 : "dimmed", children: error }, index))
    ] }),
    /* @__PURE__ */ jsx13(
      Button5,
      {
        component: "a",
        href: props.loginHref || "#/login",
        variant: "light",
        color: "gray",
        fullWidth: true,
        size: "md",
        children: t("login.callbackBackToSignIn", "Back to sign in")
      }
    )
  ] });
  return /* @__PURE__ */ jsxs9(Box3, { maw: 360, mx: "auto", children: [
    isExchanging && /* @__PURE__ */ jsx13(Center3, { children: /* @__PURE__ */ jsxs9(Stack6, { align: "center", gap: "sm", children: [
      /* @__PURE__ */ jsx13(Loader4, { size: "sm" }),
      /* @__PURE__ */ jsx13(Text9, { size: "sm", c: "dimmed", children: t("login.loggingIn") })
    ] }) }),
    collision && /* @__PURE__ */ jsx13(CollisionCard, {}),
    hasError.length > 0 && /* @__PURE__ */ jsx13(ErrorCard, {})
  ] });
}

// src/components/Login/index.ts
var Login2 = withLibraryI18n(Login);
var LoginCallback2 = withLibraryI18n(LoginCallback);

// src/components/VerifiedEmail/Create.tsx
import { Button as Button6, Text as Text10, TextInput as TextInput2 } from "@mantine/core";
import { useForm as useForm2 } from "@mantine/form";
import { useState as useState7 } from "react";
import { Fragment as Fragment6, jsx as jsx14, jsxs as jsxs10 } from "react/jsx-runtime";
function CreateVerifiedEmail(props) {
  const [isBusy, setIsBusy] = useState7(false);
  const [error, setError] = useState7("");
  const form = useForm2({
    initialValues: {
      email: ""
    },
    validate: {
      email: (value) => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(value) ? null : "Invalid Email"
    }
  });
  const submitForm = async () => {
    const newEmail = {
      email: form.values.email
    };
    setIsBusy(true);
    try {
      await props.submitFormCb(newEmail);
      form.reset();
    } catch (e) {
      setError(`Error: ${e}`);
      console.error(e);
    } finally {
      setIsBusy(false);
    }
  };
  return /* @__PURE__ */ jsxs10(Fragment6, { children: [
    /* @__PURE__ */ jsxs10("form", { onSubmit: form.onSubmit(submitForm), children: [
      /* @__PURE__ */ jsx14(
        TextInput2,
        {
          label: "Email",
          placeholder: "Email",
          value: form.values.email,
          onChange: (event) => form.setFieldValue("email", event.currentTarget.value),
          error: form.errors.email && "Invalid Email",
          mb: "md"
        }
      ),
      /* @__PURE__ */ jsx14(Button6, { type: "submit", loading: isBusy, children: "Verify" })
    ] }),
    error && /* @__PURE__ */ jsx14(Text10, { color: "red", children: error })
  ] });
}

// src/components/Entity/Entity.tsx
import { Text as Text11, Title as Title6 } from "@mantine/core";
import { Fragment as Fragment7, jsx as jsx15, jsxs as jsxs11 } from "react/jsx-runtime";
function Entity({ title, description, data, render, reload }) {
  return /* @__PURE__ */ jsxs11(Fragment7, { children: [
    /* @__PURE__ */ jsx15(Title6, { order: 3, children: title }),
    description && /* @__PURE__ */ jsx15(Text11, { children: description }),
    render({ data, setParentLoading: () => {
    }, reload })
  ] });
}

// src/components/Entity/EntityCreatePage.tsx
import { useNavigate as useNavigate2 } from "react-router-dom";

// src/hooks/useIdpHandoff.ts
import {
  buildIdpHandoffUrl,
  fetchOidcHandoffConfig
} from "@gofranz/common";
import { useEffect as useEffect5, useState as useState8 } from "react";
var configCache = /* @__PURE__ */ new Map();
function getConfig(apiRoot) {
  if (!configCache.has(apiRoot)) {
    configCache.set(apiRoot, fetchOidcHandoffConfig(apiRoot));
  }
  return configCache.get(apiRoot);
}
function useIdpHandoff(apiRoot) {
  const [config, setConfig] = useState8(null);
  const [loading, setLoading] = useState8(true);
  useEffect5(() => {
    let cancelled = false;
    getConfig(apiRoot).then((cfg) => {
      if (!cancelled) {
        setConfig(cfg);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [apiRoot]);
  return {
    config,
    loading,
    buildUrl: (action, returnTo) => {
      if (!config) return null;
      return buildIdpHandoffUrl({ config, action, returnTo });
    },
    accountManagementUri: config?.account_management_uri ?? null
  };
}

// src/hooks/useLanguageAwareRouting.ts
import { useTranslation as useTranslation8 } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
var SUPPORTED_LANGUAGES = ["en", "de", "fr", "es", "pt", "zh", "th", "ar"];
function useLanguageAwareRouting() {
  const { i18n: i18n2 } = useTranslation8();
  const location = useLocation();
  const navigate = useNavigate();
  const getCurrentLanguage = () => {
    const path = location.pathname;
    const match = path.match(/^\/([a-z]{2})(?:\/|$)/);
    if (match && SUPPORTED_LANGUAGES.includes(match[1])) {
      return match[1];
    }
    if (i18n2.language && SUPPORTED_LANGUAGES.includes(i18n2.language)) {
      return i18n2.language;
    }
    return "en";
  };
  const createLanguageURL = (path, language) => {
    const lang = language || getCurrentLanguage();
    if (!path.startsWith("/")) {
      path = `/${path}`;
    }
    const cleanPath = path.replace(/^\/([a-z]{2})(\/|$)/, "$2");
    const normalizedPath = cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
    if (lang === "en") {
      return normalizedPath;
    }
    return `/${lang}${normalizedPath}`;
  };
  const isActive = (path, deep = false) => {
    const currentLang = getCurrentLanguage();
    const expectedPath = createLanguageURL(path, currentLang);
    if (deep) {
      return location.pathname === expectedPath || location.pathname.startsWith(`${expectedPath}/`);
    }
    return location.pathname === expectedPath;
  };
  const navigateWithLanguage = (path, language) => {
    navigate(createLanguageURL(path, language));
  };
  const getStoredLanguage2 = () => {
    try {
      const stored = localStorage.getItem("checkoutbay-language") || localStorage.getItem("formshive-language");
      if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
        return stored;
      }
    } catch (error) {
      console.warn("Failed to read language from localStorage:", error);
    }
    const browserLang = navigator.language.split("-")[0];
    return SUPPORTED_LANGUAGES.includes(browserLang) ? browserLang : "en";
  };
  const redirectFromBaseURL2 = () => {
    const currentHash = window.location.hash;
    if (!currentHash || currentHash === "#" || currentHash === "#/") {
      const preferredLanguage = getStoredLanguage2() || i18n2.language || "en";
      let newHash;
      if (preferredLanguage === "en") {
        newHash = "#/";
      } else {
        newHash = `#/${preferredLanguage}/`;
      }
      window.location.replace(window.location.origin + window.location.pathname + newHash);
      return true;
    }
    return false;
  };
  const currentLanguage = getCurrentLanguage();
  return {
    createLanguageURL,
    isActive,
    currentLanguage,
    getCurrentLanguage,
    navigateWithLanguage,
    redirectFromBaseURL: redirectFromBaseURL2,
    getStoredLanguage: getStoredLanguage2
  };
}

// src/hooks/useOidcRefreshOnReturn.ts
import { LOGIN_METHOD as LOGIN_METHOD2 } from "@gofranz/common";
import { useEffect as useEffect6, useRef as useRef3 } from "react";
function useOidcRefreshOnReturn({
  session,
  auth,
  paramName = "refreshed"
}) {
  const firedRef = useRef3(false);
  useEffect6(() => {
    if (firedRef.current) return;
    if (!session || session.method !== LOGIN_METHOD2.OIDC) return;
    if (!auth) return;
    const searchParams = new URLSearchParams(window.location.search);
    const hashQuery = window.location.hash.split("?")[1] ?? "";
    const hashParams = new URLSearchParams(hashQuery);
    const hasRefreshFlag = searchParams.has(paramName) || hashParams.has(paramName);
    if (!hasRefreshFlag) return;
    firedRef.current = true;
    const stripped = stripRefreshFlag2(window.location.href, paramName);
    auth.initiateOidcRefresh(stripped).then(
      (authUrl) => {
        window.location.href = authUrl;
      },
      (err) => {
        console.warn("[oidc] silent refresh-on-return failed:", err);
      }
    );
  }, [session, auth, paramName]);
}
function stripRefreshFlag2(href, paramName) {
  const url = new URL(href);
  url.searchParams.delete(paramName);
  const [hashPath, hashQs] = url.hash.split("?");
  if (hashQs) {
    const hashParams = new URLSearchParams(hashQs);
    hashParams.delete(paramName);
    const rebuilt = hashParams.toString();
    url.hash = rebuilt ? `${hashPath}?${rebuilt}` : hashPath;
  }
  return url.toString();
}

// src/hooks/usePagination.ts
import { useCallback as useCallback3, useEffect as useEffect7, useState as useState9 } from "react";
function usePagination({
  perPage = 10,
  initialPage = 1,
  fetchData,
  additionalParams = {}
}) {
  const [data, setData] = useState9([]);
  const [loading, setLoading] = useState9(false);
  const [error, setError] = useState9(null);
  const [total, setTotal] = useState9(0);
  const [page, setPage] = useState9(initialPage);
  const [currentAdditionalParams, setCurrentAdditionalParams] = useState9(additionalParams);
  const loadData = useCallback3(
    async (targetPage, params = {}) => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchData({
          nextPage: targetPage,
          ...currentAdditionalParams,
          ...params
        });
        setData(result.data);
        setTotal(result.total);
        setPage(targetPage);
        return result.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch data");
        setData([]);
        setTotal(0);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [fetchData, currentAdditionalParams]
  );
  const refetch = useCallback3(() => {
    loadData(page);
  }, [loadData, page]);
  const handleSetPage = useCallback3(
    async (newPage) => {
      return loadData(newPage);
    },
    [loadData]
  );
  const setAdditionalParams = useCallback3(
    (params) => {
      setCurrentAdditionalParams(params);
      loadData(1, params);
    },
    [loadData]
  );
  const loadPage = useCallback3(
    async (targetPage, params = {}) => {
      return loadData(targetPage, params);
    },
    [loadData]
  );
  useEffect7(() => {
    loadData(initialPage);
  }, []);
  const paginationConfig = {
    total,
    initial: initialPage,
    perPage
  };
  return {
    data,
    loading,
    error,
    total,
    page,
    perPage,
    paginationConfig,
    refetch,
    setPage: handleSetPage,
    setAdditionalParams,
    loadPage
  };
}

// src/hooks/useSessionCheck.ts
import { useEffect as useEffect8, useRef as useRef4 } from "react";
function useSessionCheck({
  auth,
  isLoggedIn,
  logout,
  checkInterval = 1e4
}) {
  const { createLanguageURL } = useLanguageAwareRouting();
  const consecutiveFailures = useRef4(0);
  const maxConsecutiveFailures = 3;
  const isCheckingRef = useRef4(false);
  const checkSession = async () => {
    if (!isLoggedIn || isCheckingRef.current) {
      return;
    }
    isCheckingRef.current = true;
    try {
      if (!auth?.hasValidAccessToken()) {
        if (consecutiveFailures.current >= maxConsecutiveFailures) {
          console.warn(
            `Too many consecutive refresh failures (${consecutiveFailures.current}), logging out`
          );
          await logout();
          window.location.replace(createLanguageURL("/"));
          return;
        }
        console.info(
          "Access token expired. Refreshing via /v1/login/refresh (cookie credential)..."
        );
        try {
          await auth?.refresh();
          consecutiveFailures.current = 0;
          console.debug("Token refresh successful");
        } catch (error) {
          consecutiveFailures.current++;
          console.warn(
            `Token refresh failed (attempt ${consecutiveFailures.current}/${maxConsecutiveFailures}):`,
            error
          );
          if (error.message?.includes("Authentication failed") || consecutiveFailures.current >= maxConsecutiveFailures) {
            console.warn("Session invalidated, logging out");
            await logout();
            window.location.replace(createLanguageURL("/"));
          }
        }
      } else {
        consecutiveFailures.current = 0;
      }
    } finally {
      isCheckingRef.current = false;
    }
  };
  useEffect8(() => {
    checkSession();
    const intervalId = setInterval(checkSession, checkInterval);
    return () => clearInterval(intervalId);
  }, [isLoggedIn, checkInterval]);
  return { checkSession };
}

// src/components/Entity/EntityCreatePage.tsx
import { jsx as jsx16 } from "react/jsx-runtime";
function GeneralizedCreatePage({
  CreateComponent,
  createFunction,
  redirectPath
}) {
  const nav = useNavigate2();
  const { createLanguageURL } = useLanguageAwareRouting();
  const submit = async (params, newItem) => {
    const item = await createFunction(params, newItem);
    nav(createLanguageURL(redirectPath(item)));
  };
  return /* @__PURE__ */ jsx16(CreateComponent, { submitFormCb: submit });
}

// src/components/Entity/EntityFormCreate.tsx
import {
  axiosFieldValidationErrorToFormErrors,
  hasFieldValidationError
} from "@gofranz/common";
import { Button as Button7, Text as Text12, Title as Title7 } from "@mantine/core";
import { useForm as useForm3 } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useState as useState10 } from "react";
import { Fragment as Fragment8, jsx as jsx17, jsxs as jsxs12 } from "react/jsx-runtime";
function EntityFormCreate({
  title,
  description,
  initialValues,
  validation,
  submitFormCb,
  renderFields,
  primaryEntityId
}) {
  const [isBusy, setIsBusy] = useState10(false);
  const [error, setError] = useState10("");
  const setLoading = (loading) => {
    setIsBusy(loading);
  };
  const getInitialValidation = () => {
    if (typeof validation === "function") {
      const result = validation(initialValues);
      return result;
    }
    return validation;
  };
  const form = useForm3({
    mode: "controlled",
    initialValues,
    validate: getInitialValidation()
  });
  const handleFormSubmit = async (values) => {
    if (typeof validation === "function") {
      const validationRules = validation(values);
      const errors = {};
      if (validationRules) {
        Object.entries(validationRules).forEach(([fieldName, validator]) => {
          if (validator && typeof validator === "function") {
            const fieldValue = values[fieldName];
            const error2 = validator(fieldValue);
            if (error2) {
              errors[fieldName] = error2;
            }
          }
        });
      }
      if (Object.keys(errors).length > 0) {
        form.setErrors(errors);
        showWarningNotification(
          "Validation Error",
          "Please fix the highlighted fields and try again.",
          notifications
        );
        return;
      }
    } else {
      const validationResult = form.validate();
      if (validationResult.hasErrors) {
        showWarningNotification(
          "Validation Error",
          "Please fix the highlighted fields and try again.",
          notifications
        );
        return;
      }
    }
    await submitForm(
      {
        primaryEntityId
      },
      values
    );
  };
  const submitForm = async (params, data) => {
    setIsBusy(true);
    try {
      setSubmittedValues(data);
      await submitFormCb(params, data);
      setError("");
    } catch (e) {
      setError(`${e}`);
      if (hasFieldValidationError(e)) {
        console.log(axiosFieldValidationErrorToFormErrors(e));
        form.setErrors(axiosFieldValidationErrorToFormErrors(e));
      }
    } finally {
      setIsBusy(false);
    }
  };
  const [submittedValues, setSubmittedValues] = useState10(null);
  return /* @__PURE__ */ jsxs12(Fragment8, { children: [
    /* @__PURE__ */ jsx17(Title7, { order: 3, children: title }),
    description && /* @__PURE__ */ jsx17(Text12, { children: description }),
    /* @__PURE__ */ jsxs12("form", { onSubmit: form.onSubmit(handleFormSubmit), children: [
      renderFields({
        form,
        submittedValues,
        setParentLoading: setLoading,
        primaryEntityId,
        entityId: void 0,
        // Not needed for creation
        isEditing: false
      }),
      /* @__PURE__ */ jsx17(Button7, { type: "submit", loading: isBusy, disabled: isBusy, mt: "xs", children: "Create" })
    ] }),
    error && /* @__PURE__ */ jsx17(Text12, { c: "red", children: error })
  ] });
}

// src/components/Entity/EntityFormEdit.tsx
import {
  axiosFieldValidationErrorToFormErrors as axiosFieldValidationErrorToFormErrors2,
  hasFieldValidationError as hasFieldValidationError2
} from "@gofranz/common";
import { Button as Button8, Text as Text13, Title as Title8 } from "@mantine/core";
import { useForm as useForm4 } from "@mantine/form";
import { notifications as notifications2 } from "@mantine/notifications";
import { useState as useState11 } from "react";
import { Fragment as Fragment9, jsx as jsx18, jsxs as jsxs13 } from "react/jsx-runtime";
function EntityFormEdit({
  title,
  description,
  initialValues,
  validation,
  submitFormCb,
  id,
  renderFields,
  primaryEntityId
}) {
  const [isBusy, setIsBusy] = useState11(false);
  const [error, setError] = useState11("");
  const setLoading = (loading) => {
    setIsBusy(loading);
  };
  const convertEntityToEdit = (entity) => {
    return entity;
  };
  const getInitialValidation = () => {
    if (typeof validation === "function") {
      const editValues = convertEntityToEdit(initialValues);
      const result = validation(editValues);
      return result;
    }
    return validation;
  };
  const form = useForm4({
    mode: "controlled",
    initialValues: convertEntityToEdit(initialValues),
    validate: getInitialValidation()
  });
  const handleFormSubmit = async (values) => {
    if (typeof validation === "function") {
      const validationRules = validation(values);
      const errors = {};
      if (validationRules) {
        Object.entries(validationRules).forEach(([fieldName, validator]) => {
          if (validator && typeof validator === "function") {
            const fieldValue = values[fieldName];
            const error2 = validator(fieldValue);
            if (error2) {
              errors[fieldName] = error2;
            }
          }
        });
      }
      if (Object.keys(errors).length > 0) {
        form.setErrors(errors);
        showWarningNotification(
          "Validation Error",
          "Please fix the highlighted fields and try again.",
          notifications2
        );
        return;
      }
    } else {
      const validationResult = form.validate();
      if (validationResult.hasErrors) {
        showWarningNotification(
          "Validation Error",
          "Please fix the highlighted fields and try again.",
          notifications2
        );
        return;
      }
    }
    await submitForm(values);
  };
  const submitForm = async (data) => {
    setIsBusy(true);
    try {
      setSubmittedValues(data);
      await submitFormCb(
        {
          primaryEntityId,
          entityId: id
        },
        data
      );
      setError("");
    } catch (e) {
      setError(`${e}`);
      if (hasFieldValidationError2(e)) {
        console.log(axiosFieldValidationErrorToFormErrors2(e));
        form.setErrors(axiosFieldValidationErrorToFormErrors2(e));
      }
    } finally {
      setIsBusy(false);
    }
  };
  const [submittedValues, setSubmittedValues] = useState11(null);
  return /* @__PURE__ */ jsxs13(Fragment9, { children: [
    /* @__PURE__ */ jsx18(Title8, { order: 3, children: title }),
    description && /* @__PURE__ */ jsx18(Text13, { children: description }),
    /* @__PURE__ */ jsxs13("form", { onSubmit: form.onSubmit(handleFormSubmit), children: [
      renderFields({
        form,
        submittedValues,
        setParentLoading: setLoading,
        primaryEntityId,
        entityId: id,
        isEditing: true
      }),
      /* @__PURE__ */ jsx18(Button8, { type: "submit", loading: isBusy, disabled: isBusy, mt: "xs", children: "Update" })
    ] }),
    error && /* @__PURE__ */ jsx18(Text13, { c: "red", children: error })
  ] });
}

// src/components/Entity/EntityViewPage.tsx
import { useEffect as useEffect9, useState as useState12 } from "react";
import { useParams } from "react-router-dom";
import { Fragment as Fragment10, jsx as jsx19 } from "react/jsx-runtime";
var ViewPageMode = /* @__PURE__ */ ((ViewPageMode2) => {
  ViewPageMode2["View"] = "view";
  ViewPageMode2["Edit"] = "edit";
  ViewPageMode2["AltView"] = "altView";
  ViewPageMode2["AltView2"] = "altView2";
  ViewPageMode2["AltView3"] = "altView3";
  return ViewPageMode2;
})(ViewPageMode || {});
function GeneralizedViewPage({
  DetailComponent,
  getFunction,
  submitCb,
  deleteCb
}) {
  const { primary_entity_id, entity_id } = useParams();
  const [item, setItem] = useState12(void 0);
  useEffect9(() => {
    const getItem = async () => {
      if (!primary_entity_id || !entity_id) {
        return;
      }
      const res = await getFunction({
        primaryEntityId: primary_entity_id,
        entityId: entity_id
      });
      if (res) {
        setItem(res);
      }
    };
    getItem();
  }, [primary_entity_id, entity_id, getFunction]);
  const reload = async () => {
    if (!primary_entity_id || !entity_id) {
      return;
    }
    const res = await getFunction({
      primaryEntityId: primary_entity_id,
      entityId: entity_id
    });
    if (res) {
      setItem(res);
    }
  };
  return /* @__PURE__ */ jsx19(Fragment10, { children: item && /* @__PURE__ */ jsx19(
    DetailComponent,
    {
      item,
      submitCb: submitCb || (async () => {
        console.log("submitCb not implemented");
      }),
      deleteCb: deleteCb || (async () => {
        console.log("deleteCb not implemented");
      }),
      reload: reload || (async () => {
        console.log("reload not implemented");
      })
    }
  ) });
}

// src/components/Security/OidcHandoffHints.tsx
import { buildIdpHandoffUrl as buildIdpHandoffUrl2 } from "@gofranz/common";
import { Anchor as Anchor2, Button as Button9, Group as Group6, Notification, Stack as Stack7, Text as Text14 } from "@mantine/core";
import { IconExternalLink, IconRefresh, IconShieldCheck as IconShieldCheck2 } from "@tabler/icons-react";
import { useTranslation as useTranslation9 } from "react-i18next";
import { jsx as jsx20, jsxs as jsxs14 } from "react/jsx-runtime";
function IdpHandoffButton({
  config,
  action,
  returnTo,
  label,
  variant
}) {
  const url = buildIdpHandoffUrl2({ config, action, returnTo });
  if (!url) return null;
  return /* @__PURE__ */ jsx20(
    Button9,
    {
      component: "a",
      href: url,
      variant: variant ?? "light",
      rightSection: /* @__PURE__ */ jsx20(IconExternalLink, { size: 16 }),
      children: label
    }
  );
}
function OidcTwoFactorRedirect({ config, returnTo }) {
  const { t } = useTranslation9();
  return /* @__PURE__ */ jsxs14(Stack7, { gap: "sm", children: [
    /* @__PURE__ */ jsx20(Text14, { size: "sm", c: "dimmed", children: t("oidc.handoffTwoFactorBody") }),
    config && /* @__PURE__ */ jsx20(Group6, { children: /* @__PURE__ */ jsx20(
      IdpHandoffButton,
      {
        config,
        action: "2fa",
        returnTo,
        label: t("oidc.handoffTwoFactorButton")
      }
    ) })
  ] });
}
function OidcSecurityLogBanner({ config }) {
  const { t } = useTranslation9();
  return /* @__PURE__ */ jsx20(
    Notification,
    {
      withCloseButton: false,
      withBorder: true,
      color: "gray",
      icon: /* @__PURE__ */ jsx20(IconShieldCheck2, { size: 18 }),
      title: t("oidc.securityLogBanner"),
      mb: "md",
      children: config && /* @__PURE__ */ jsxs14(Anchor2, { href: config.account_management_uri, size: "sm", mt: "xs", children: [
        t("oidc.securityLogLink"),
        /* @__PURE__ */ jsx20(IconExternalLink, { size: 14, style: { verticalAlign: "middle", marginLeft: 4 } })
      ] })
    }
  );
}
function OidcSyncFromIdpButton({ onSync, busy }) {
  const { t } = useTranslation9();
  return /* @__PURE__ */ jsx20(
    Button9,
    {
      variant: "subtle",
      size: "xs",
      loading: busy,
      leftSection: /* @__PURE__ */ jsx20(IconRefresh, { size: 14 }),
      onClick: () => {
        void onSync();
      },
      children: busy ? t("oidc.syncFromIdpInProgress") : t("oidc.syncFromIdpButton")
    }
  );
}

// src/components/Security/SecurityLog.tsx
import { Alert as Alert3, Badge as Badge3, Box as Box4, Flex, Select, Stack as Stack8, Text as Text15, ThemeIcon as ThemeIcon3, Tooltip as Tooltip4 } from "@mantine/core";
import { IconAlertCircle as IconAlertCircle3, IconAlertTriangle as IconAlertTriangle2, IconShieldCheck as IconShieldCheck3 } from "@tabler/icons-react";
import { DataTable } from "mantine-datatable";
import { useEffect as useEffect10, useRef as useRef5, useState as useState13 } from "react";
import { useTranslation as useTranslation10 } from "react-i18next";
import { Fragment as Fragment11, jsx as jsx21, jsxs as jsxs15 } from "react/jsx-runtime";
function formatRelative(ts, now) {
  const then = new Date(ts).getTime();
  const deltaSec = Math.floor((now.getTime() - then) / 1e3);
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const deltaMin = Math.floor(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin}m ago`;
  const deltaHr = Math.floor(deltaMin / 60);
  if (deltaHr < 24) return `${deltaHr}h ago`;
  const deltaDay = Math.floor(deltaHr / 24);
  if (deltaDay < 30) return `${deltaDay}d ago`;
  return new Date(ts).toLocaleDateString();
}
function formatAbsolute(ts) {
  return new Date(ts).toLocaleString(void 0, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short"
  });
}
function SecurityLog({ getSecurityLog, storageKey, defaultPerPage = 50 }) {
  const { t } = useTranslation10();
  const [perPage, setPerPage] = useState13(() => {
    if (storageKey && typeof window !== "undefined" && window.localStorage) {
      const saved = localStorage.getItem(storageKey);
      return saved ? parseInt(saved, 10) : defaultPerPage;
    }
    return defaultPerPage;
  });
  const perPageRef = useRef5(perPage);
  const [now, setNow] = useState13(() => /* @__PURE__ */ new Date());
  useEffect10(() => {
    const id = setInterval(() => setNow(/* @__PURE__ */ new Date()), 6e4);
    return () => clearInterval(id);
  }, []);
  const fetchSecurityLog = async (params) => {
    const { nextPage } = params;
    const currentPerPage = perPageRef.current;
    const res = await getSecurityLog({
      page: nextPage - 1,
      per_page: currentPerPage
    });
    return {
      data: res.data,
      total: res.total
    };
  };
  const pagination = usePagination({
    perPage,
    fetchData: fetchSecurityLog
  });
  const handlePerPageChange = (value) => {
    const newPerPage = parseInt(value || `${defaultPerPage}`, 10);
    setPerPage(newPerPage);
    perPageRef.current = newPerPage;
    if (storageKey && typeof window !== "undefined" && window.localStorage) {
      localStorage.setItem(storageKey, newPerPage.toString());
    }
    pagination.loadPage(1);
  };
  const paginationOptions = [
    { value: "10", label: "10" },
    { value: "20", label: "20" },
    { value: "30", label: "30" },
    { value: "40", label: "40" },
    { value: "50", label: "50" }
  ];
  const columns = [
    {
      accessor: "timestamp",
      title: t("securityLog.when"),
      render: (row) => /* @__PURE__ */ jsx21(Tooltip4, { label: formatAbsolute(row.timestamp), withArrow: true, children: /* @__PURE__ */ jsx21(Text15, { size: "sm", children: formatRelative(row.timestamp, now) }) })
    },
    {
      accessor: "operation_label",
      title: t("securityLog.event"),
      render: (row) => /* @__PURE__ */ jsx21(Text15, { size: "sm", children: row.operation_label })
    },
    {
      accessor: "location",
      title: t("securityLog.location"),
      render: (row) => /* @__PURE__ */ jsx21(Text15, { size: "sm", c: "dimmed", children: row.location })
    },
    {
      accessor: "device",
      title: t("securityLog.device"),
      render: (row) => /* @__PURE__ */ jsx21(Text15, { size: "sm", c: "dimmed", children: row.device })
    },
    {
      accessor: "success",
      title: t("securityLog.status"),
      render: (row) => row.success ? /* @__PURE__ */ jsx21(Badge3, { color: "green", variant: "light", radius: "sm", children: t("securityLog.ok") }) : /* @__PURE__ */ jsx21(
        Badge3,
        {
          color: "red",
          variant: "light",
          radius: "sm",
          leftSection: /* @__PURE__ */ jsx21(IconAlertTriangle2, { size: 12 }),
          children: t("securityLog.failed")
        }
      )
    }
  ];
  return /* @__PURE__ */ jsxs15(Stack8, { gap: "md", children: [
    /* @__PURE__ */ jsx21(Flex, { justify: "flex-end", children: /* @__PURE__ */ jsx21(
      Select,
      {
        data: paginationOptions,
        value: perPage.toString(),
        onChange: handlePerPageChange,
        size: "sm",
        w: 80
      }
    ) }),
    pagination.error && /* @__PURE__ */ jsx21(Alert3, { icon: /* @__PURE__ */ jsx21(IconAlertCircle3, { size: 16 }), color: "red", title: t("securityLog.errorTitle"), children: pagination.error }),
    /* @__PURE__ */ jsx21(
      DataTable,
      {
        minHeight: (pagination.data?.length ?? 0) === 0 ? 200 : 0,
        withTableBorder: true,
        borderRadius: "md",
        shadow: "xs",
        records: pagination.data || [],
        columns,
        totalRecords: pagination.total,
        recordsPerPage: pagination.perPage,
        page: pagination.page,
        onPageChange: pagination.setPage,
        fetching: pagination.loading,
        noRecordsText: "",
        emptyState: (pagination.data?.length ?? 0) === 0 ? /* @__PURE__ */ jsx21(Box4, { py: "xl", px: "md", children: /* @__PURE__ */ jsxs15(Stack8, { align: "center", gap: "xs", children: [
          /* @__PURE__ */ jsx21(ThemeIcon3, { size: 48, radius: "xl", variant: "light", color: "gray", children: /* @__PURE__ */ jsx21(IconShieldCheck3, { size: 24 }) }),
          /* @__PURE__ */ jsx21(Text15, { size: "sm", fw: 500, children: t("securityLog.empty") }),
          /* @__PURE__ */ jsx21(Text15, { size: "sm", c: "dimmed", ta: "center", maw: 320, children: t(
            "securityLog.emptyDescription",
            "Sign-ins, account changes, and other security-relevant events will appear here."
          ) })
        ] }) }) : /* @__PURE__ */ jsx21(Fragment11, {})
      }
    )
  ] });
}

// src/components/VerifiedEmail/Detail.tsx
import { Card as Card2, Text as Text16 } from "@mantine/core";
import { useEffect as useEffect11, useState as useState14 } from "react";
import { Fragment as Fragment12, jsx as jsx22, jsxs as jsxs16 } from "react/jsx-runtime";
function VerifiedEmailDetail(props) {
  const [isBusy, setIsBusy] = useState14(false);
  const [verifiedEmails, setVerifiedEmails] = useState14([]);
  useEffect11(() => {
    const getVerifiedEmails = async () => {
      setIsBusy(true);
      try {
        const data = await props.getVerifiedEmails();
        setVerifiedEmails(data.data);
      } catch (e) {
        alert(e);
        console.error(e);
      } finally {
        setIsBusy(false);
      }
    };
    getVerifiedEmails();
  }, []);
  const submitCb = async (newEmail) => {
    try {
      setIsBusy(true);
      await props.submitFormCb(newEmail);
      const data = await props.getVerifiedEmails();
      setVerifiedEmails(data.data);
    } catch (e) {
      alert(e);
      console.error(e);
    } finally {
      setIsBusy(false);
    }
  };
  const verifyCb = async (id) => {
    try {
      setIsBusy(true);
      await props.verifyCb(id);
      const data = await props.getVerifiedEmails();
      setVerifiedEmails(data.data);
    } catch (e) {
      alert(e);
      console.error(e);
    } finally {
      setIsBusy(false);
    }
  };
  const deleteCb = async (id) => {
    try {
      setIsBusy(true);
      await props.deleteCb(id);
      const data = await props.getVerifiedEmails();
      setVerifiedEmails(data.data);
    } catch (e) {
      alert(e);
      console.error(e);
    } finally {
      setIsBusy(false);
    }
  };
  const setAccountEmailCb = async (id) => {
    try {
      setIsBusy(true);
      await props.setAccountEmailCb(id);
      const data = await props.getVerifiedEmails();
      setVerifiedEmails(data.data);
    } catch (e) {
      alert(e);
      console.error(e);
    } finally {
      setIsBusy(false);
    }
  };
  const Emails = () => verifiedEmails.map((email2) => /* @__PURE__ */ jsx22(Card2, { shadow: "xs", radius: "md", mb: "md", withBorder: true, children: /* @__PURE__ */ jsx22(
    VerifiedEmailComponent,
    {
      verifiedEmail: email2,
      isBusy,
      verifyCb,
      deleteCb,
      setAccountEmailCb
    },
    `verified-email-${email2.id}`
  ) }, email2.id));
  const EmailsNotFount = () => /* @__PURE__ */ jsx22(Text16, { children: "No verified emails found." });
  return /* @__PURE__ */ jsxs16(Fragment12, { children: [
    verifiedEmails.length > 0 ? /* @__PURE__ */ jsx22(Emails, {}) : /* @__PURE__ */ jsx22(EmailsNotFount, {}),
    /* @__PURE__ */ jsx22(CreateVerifiedEmail, { submitFormCb: submitCb })
  ] });
}

// src/components/index.ts
var News2 = withLibraryI18n(News);
var VerifiedEmailComponent2 = withLibraryI18n(VerifiedEmailComponent);

// src/integrations/AdapterConfigFields.tsx
import {
  Checkbox,
  NumberInput,
  PasswordInput,
  Select as Select2,
  Stack as Stack9,
  TagsInput,
  Textarea,
  TextInput as TextInput3
} from "@mantine/core";
import { useTranslation as useTranslation11 } from "react-i18next";
import { jsx as jsx23 } from "react/jsx-runtime";
function AdapterConfigFields({
  fields,
  pathPrefix,
  form,
  disabled
}) {
  const { t } = useTranslation11();
  if (fields.length === 0) return null;
  return /* @__PURE__ */ jsx23(Stack9, { gap: "sm", children: fields.map((field) => {
    const path = `${pathPrefix}.${field.key}`;
    const inputProps = form.getInputProps(path);
    const label = t(field.label);
    const description = field.helperText ? t(field.helperText) : void 0;
    const placeholder = field.placeholder;
    const baseProps = {
      label,
      description,
      required: field.required,
      disabled,
      placeholder
    };
    switch (field.type) {
      case "password":
        return /* @__PURE__ */ jsx23(
          PasswordInput,
          {
            ...baseProps,
            ...inputProps,
            autoComplete: "new-password"
          },
          field.key
        );
      case "select":
        return /* @__PURE__ */ jsx23(
          Select2,
          {
            ...baseProps,
            data: (field.options ?? []).map((o) => ({
              value: o.value,
              label: t(o.label)
            })),
            ...inputProps
          },
          field.key
        );
      case "checkbox":
        return /* @__PURE__ */ jsx23(
          Checkbox,
          {
            label,
            description,
            disabled,
            ...inputProps,
            checked: Boolean(inputProps.value)
          },
          field.key
        );
      case "number":
        return /* @__PURE__ */ jsx23(NumberInput, { ...baseProps, ...inputProps, allowDecimal: false }, field.key);
      case "email-list":
      case "array-string":
        return /* @__PURE__ */ jsx23(
          TagsInput,
          {
            ...baseProps,
            clearable: true,
            value: Array.isArray(inputProps.value) ? inputProps.value : [],
            onChange: (val) => form.setFieldValue(path, val)
          },
          field.key
        );
      case "json":
        return /* @__PURE__ */ jsx23(
          Textarea,
          {
            ...baseProps,
            autosize: true,
            minRows: 3,
            maxRows: 10,
            ...inputProps,
            value: typeof inputProps.value === "string" ? inputProps.value : inputProps.value !== void 0 && inputProps.value !== null ? JSON.stringify(inputProps.value, null, 2) : ""
          },
          field.key
        );
      default:
        return /* @__PURE__ */ jsx23(TextInput3, { ...baseProps, ...inputProps }, field.key);
    }
  }) });
}

// src/integrations/AdapterPickerGrid.tsx
import { Card as Card3, Group as Group7, SimpleGrid as SimpleGrid2, Stack as Stack10, Text as Text17, ThemeIcon as ThemeIcon4 } from "@mantine/core";
import {
  IconBolt,
  IconBrandDiscord,
  IconBrandSlack,
  IconBuildingStore,
  IconForms,
  IconMail as IconMail3,
  IconMailForward,
  IconPlugConnected,
  IconTable,
  IconWebhook
} from "@tabler/icons-react";
import { useTranslation as useTranslation12 } from "react-i18next";
import { jsx as jsx24, jsxs as jsxs17 } from "react/jsx-runtime";
var ICON_MAP = {
  webhook: IconWebhook,
  bolt: IconBolt,
  "brand-slack": IconBrandSlack,
  "brand-discord": IconBrandDiscord,
  table: IconTable,
  mail: IconMail3,
  forms: IconForms,
  "building-store": IconBuildingStore,
  "mail-forward": IconMailForward
};
function AdapterPickerGrid({ adapters, selected, onSelect }) {
  const { t } = useTranslation12();
  return /* @__PURE__ */ jsx24(SimpleGrid2, { cols: { base: 1, sm: 2, md: 3 }, spacing: "md", children: adapters.map((meta) => {
    const Icon = resolveIcon(meta.icon);
    const isSelected = selected === meta.kind;
    return /* @__PURE__ */ jsx24(
      Card3,
      {
        withBorder: true,
        shadow: isSelected ? "sm" : void 0,
        padding: "md",
        radius: "md",
        style: {
          cursor: "pointer",
          borderColor: isSelected ? "var(--mantine-color-blue-5)" : void 0
        },
        onClick: () => onSelect(meta.kind),
        children: /* @__PURE__ */ jsxs17(Stack10, { gap: "xs", children: [
          /* @__PURE__ */ jsxs17(Group7, { gap: "sm", wrap: "nowrap", children: [
            /* @__PURE__ */ jsx24(ThemeIcon4, { variant: "light", size: 36, radius: "md", children: /* @__PURE__ */ jsx24(Icon, { size: 20 }) }),
            /* @__PURE__ */ jsx24(Text17, { fw: 500, children: t(meta.label) })
          ] }),
          /* @__PURE__ */ jsx24(Text17, { size: "sm", c: "dimmed", children: t(meta.description) })
        ] })
      },
      meta.kind
    );
  }) });
}
function resolveIcon(name) {
  return ICON_MAP[name] ?? IconPlugConnected;
}

// src/integrations/adapters/registry.ts
var isNonEmptyString = (v) => typeof v === "string" && v.trim().length > 0;
var required = (key) => (value) => isNonEmptyString(value) ? null : `integrations.errors.required:${key}`;
var tryUrl = (value) => {
  if (!isNonEmptyString(value)) return null;
  try {
    return new URL(value);
  } catch {
    return null;
  }
};
var urlValidator = (value) => {
  if (!isNonEmptyString(value)) return "integrations.errors.urlRequired";
  return tryUrl(value) ? null : "integrations.errors.urlInvalid";
};
var ssrfGuard = (value) => {
  const url = tryUrl(value);
  if (!url) return urlValidator(value);
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return "integrations.errors.urlScheme";
  }
  const host = url.hostname.toLowerCase();
  const blocked = [
    "localhost",
    "127.0.0.1",
    "0.0.0.0",
    "::1",
    "169.254.169.254"
    // AWS / GCP metadata service
  ];
  if (blocked.includes(host)) return "integrations.errors.urlPrivate";
  if (host.endsWith(".local") || host.endsWith(".internal")) {
    return "integrations.errors.urlPrivate";
  }
  if (/^10\./.test(host)) return "integrations.errors.urlPrivate";
  if (/^192\.168\./.test(host)) return "integrations.errors.urlPrivate";
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(host)) return "integrations.errors.urlPrivate";
  return null;
};
var slackHostValidator = (value) => {
  const generic = urlValidator(value);
  if (generic) return generic;
  const url = tryUrl(value);
  return url.host === "hooks.slack.com" ? null : "integrations.errors.slackHost";
};
var discordHostValidator = (value) => {
  const generic = urlValidator(value);
  if (generic) return generic;
  const url = tryUrl(value);
  const okHost = url.host === "discord.com" || url.host === "discordapp.com";
  if (!okHost) return "integrations.errors.discordHost";
  if (!url.pathname.startsWith("/api/webhooks/")) {
    return "integrations.errors.discordPath";
  }
  return null;
};
var googleHostValidator = (value) => {
  const generic = ssrfGuard(value);
  if (generic) return generic;
  const url = tryUrl(value);
  const ok = url.host.endsWith("google.com") || url.host.endsWith("googleusercontent.com");
  return ok ? null : "integrations.errors.googleHost";
};
var emailListValidator = (value) => {
  const list = Array.isArray(value) ? value : [];
  if (list.length === 0) return "integrations.errors.emailListEmpty";
  for (const addr of list) {
    if (typeof addr !== "string" || !addr.includes("@")) {
      return "integrations.errors.emailInvalid";
    }
  }
  return null;
};
var pipedriveDomainValidator = (value) => {
  if (!isNonEmptyString(value)) return "integrations.errors.required:companyDomain";
  const re = /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]\.pipedrive\.com$/;
  return re.test(value) ? null : "integrations.errors.pipedriveDomain";
};
var mailchimpStatusValidator = (value) => {
  if (value === "subscribed" || value === "pending") return null;
  return "integrations.errors.mailchimpStatus";
};
var jsonObjectValidator = (value) => {
  if (value === void 0 || value === null || value === "") return null;
  if (typeof value === "object") return null;
  if (typeof value !== "string") return "integrations.errors.jsonInvalid";
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return null;
    return "integrations.errors.jsonObjectExpected";
  } catch {
    return "integrations.errors.jsonInvalid";
  }
};
var URL_FIELD = (key, labelKey, validator = ssrfGuard) => ({
  key,
  label: labelKey,
  type: "url",
  required: true,
  placeholder: "https://...",
  validate: validator
});
var genericWebhook = {
  kind: "generic_webhook",
  label: "integrations.adapters.generic_webhook.label",
  description: "integrations.adapters.generic_webhook.description",
  icon: "webhook",
  capabilities: { retry: true, hmac: true, replayable: true, idempotency: true },
  configFields: [
    URL_FIELD("url", "integrations.fields.url"),
    {
      key: "custom_headers",
      label: "integrations.fields.customHeaders",
      helperText: "integrations.fields.customHeadersHelp",
      type: "json",
      required: false,
      placeholder: '{"X-Tenant": "acme"}',
      validate: jsonObjectValidator
    }
  ],
  secretsFields: []
};
var zapierWebhook = {
  kind: "zapier_webhook",
  label: "integrations.adapters.zapier_webhook.label",
  description: "integrations.adapters.zapier_webhook.description",
  icon: "bolt",
  capabilities: { retry: true, hmac: true, replayable: true, idempotency: true },
  configFields: [URL_FIELD("url", "integrations.fields.zapierUrl")],
  secretsFields: []
};
var slackUrl = {
  kind: "slack_url",
  label: "integrations.adapters.slack_url.label",
  description: "integrations.adapters.slack_url.description",
  icon: "brand-slack",
  capabilities: { retry: true, hmac: false, replayable: true, idempotency: false },
  configFields: [
    {
      key: "webhook_url",
      label: "integrations.fields.slackWebhookUrl",
      helperText: "integrations.fields.slackWebhookUrlHelp",
      type: "url",
      required: true,
      placeholder: "https://hooks.slack.com/services/...",
      validate: slackHostValidator
    }
  ],
  secretsFields: []
};
var discordUrl = {
  kind: "discord_url",
  label: "integrations.adapters.discord_url.label",
  description: "integrations.adapters.discord_url.description",
  icon: "brand-discord",
  capabilities: { retry: true, hmac: false, replayable: true, idempotency: false },
  configFields: [
    {
      key: "webhook_url",
      label: "integrations.fields.discordWebhookUrl",
      helperText: "integrations.fields.discordWebhookUrlHelp",
      type: "url",
      required: true,
      placeholder: "https://discord.com/api/webhooks/...",
      validate: discordHostValidator
    },
    {
      key: "username",
      label: "integrations.fields.discordUsername",
      helperText: "integrations.fields.discordUsernameHelp",
      type: "text",
      required: false
    }
  ],
  secretsFields: []
};
var googleSheets = {
  kind: "google_sheets_webhook",
  label: "integrations.adapters.google_sheets_webhook.label",
  description: "integrations.adapters.google_sheets_webhook.description",
  icon: "table",
  capabilities: { retry: true, hmac: false, replayable: true, idempotency: false },
  configFields: [
    {
      key: "webhook_url",
      label: "integrations.fields.sheetsWebhookUrl",
      helperText: "integrations.fields.sheetsWebhookUrlHelp",
      type: "url",
      required: true,
      placeholder: "https://script.google.com/macros/s/.../exec",
      validate: googleHostValidator
    },
    {
      key: "sheet_name",
      label: "integrations.fields.sheetName",
      helperText: "integrations.fields.sheetNameHelp",
      type: "text",
      required: false
    }
  ],
  secretsFields: []
};
var email = {
  kind: "email",
  label: "integrations.adapters.email.label",
  description: "integrations.adapters.email.description",
  icon: "mail",
  capabilities: { retry: false, hmac: false, replayable: false, idempotency: false },
  configFields: [
    {
      key: "to",
      label: "integrations.fields.emailTo",
      helperText: "integrations.fields.emailToHelp",
      type: "email-list",
      required: true,
      validate: emailListValidator
    },
    {
      key: "subject_template",
      label: "integrations.fields.subjectTemplate",
      helperText: "integrations.fields.subjectTemplateHelp",
      type: "text",
      required: true,
      validate: required("subjectTemplate")
    },
    {
      key: "body_template",
      label: "integrations.fields.bodyTemplate",
      helperText: "integrations.fields.bodyTemplateHelp",
      type: "text",
      required: false
    }
  ],
  secretsFields: []
};
var formshiveEndpoint = {
  kind: "formshive_endpoint",
  label: "integrations.adapters.formshive_endpoint.label",
  description: "integrations.adapters.formshive_endpoint.description",
  icon: "forms",
  capabilities: { retry: true, hmac: true, replayable: true, idempotency: true },
  configFields: [
    {
      key: "form_id",
      label: "integrations.fields.formId",
      helperText: "integrations.fields.formIdHelp",
      type: "text",
      required: true,
      validate: required("formId")
    },
    {
      key: "api_url_override",
      label: "integrations.fields.apiUrlOverride",
      helperText: "integrations.fields.apiUrlOverrideHelp",
      type: "url",
      required: false,
      validate: (v) => isNonEmptyString(v) ? ssrfGuard(v) : null
    }
  ],
  secretsFields: [
    {
      key: "submit_token",
      label: "integrations.fields.submitToken",
      helperText: "integrations.fields.submitTokenHelp",
      type: "password",
      required: false
    }
  ]
};
var pipedrive = {
  kind: "api_pipedrive",
  label: "integrations.adapters.api_pipedrive.label",
  description: "integrations.adapters.api_pipedrive.description",
  icon: "building-store",
  capabilities: { retry: true, hmac: false, replayable: true, idempotency: false },
  configFields: [
    {
      key: "company_domain",
      label: "integrations.fields.pipedriveDomain",
      helperText: "integrations.fields.pipedriveDomainHelp",
      type: "text",
      required: true,
      placeholder: "mycompany.pipedrive.com",
      validate: pipedriveDomainValidator
    },
    {
      key: "create_person",
      label: "integrations.fields.createPerson",
      type: "checkbox",
      required: false
    },
    {
      key: "create_lead",
      label: "integrations.fields.createLead",
      type: "checkbox",
      required: false
    },
    {
      key: "default_owner_id",
      label: "integrations.fields.defaultOwnerId",
      type: "number",
      required: false
    },
    {
      key: "default_org_id",
      label: "integrations.fields.defaultOrgId",
      type: "number",
      required: false
    },
    {
      key: "lead_title_template",
      label: "integrations.fields.leadTitleTemplate",
      helperText: "integrations.fields.leadTitleTemplateHelp",
      type: "text",
      required: false
    },
    {
      key: "custom_field_mapping",
      label: "integrations.fields.customFieldMapping",
      helperText: "integrations.fields.customFieldMappingHelp",
      type: "json",
      required: false,
      placeholder: '{"email": "primary_email"}',
      validate: jsonObjectValidator
    }
  ],
  secretsFields: [
    {
      key: "api_key",
      label: "integrations.fields.apiKey",
      helperText: "integrations.fields.pipedriveApiKeyHelp",
      type: "password",
      required: true,
      validate: required("apiKey")
    }
  ]
};
var mailchimp = {
  kind: "api_mailchimp",
  label: "integrations.adapters.api_mailchimp.label",
  description: "integrations.adapters.api_mailchimp.description",
  // Tabler doesn't ship a Mailchimp brand glyph; the generic mail icon is the
  // closest match without pulling in another icon set.
  icon: "mail",
  capabilities: { retry: true, hmac: false, replayable: true, idempotency: false },
  configFields: [
    {
      key: "list_id",
      label: "integrations.fields.mailchimpListId",
      helperText: "integrations.fields.mailchimpListIdHelp",
      type: "text",
      required: true,
      validate: required("listId")
    },
    {
      key: "status",
      label: "integrations.fields.mailchimpStatus",
      type: "select",
      required: true,
      options: [
        { value: "subscribed", label: "integrations.fields.mailchimpStatusSubscribed" },
        { value: "pending", label: "integrations.fields.mailchimpStatusPending" }
      ],
      validate: mailchimpStatusValidator
    },
    {
      key: "double_optin",
      label: "integrations.fields.doubleOptin",
      type: "checkbox",
      required: false
    },
    {
      key: "merge_field_mapping",
      label: "integrations.fields.mergeFieldMapping",
      helperText: "integrations.fields.mergeFieldMappingHelp",
      type: "json",
      required: false,
      placeholder: '{"name": "FNAME"}',
      validate: jsonObjectValidator
    },
    {
      key: "default_tags",
      label: "integrations.fields.defaultTags",
      helperText: "integrations.fields.defaultTagsHelp",
      type: "array-string",
      required: false
    }
  ],
  secretsFields: [
    {
      key: "api_key",
      label: "integrations.fields.apiKey",
      helperText: "integrations.fields.mailchimpApiKeyHelp",
      type: "password",
      required: true,
      validate: required("apiKey")
    }
  ]
};
var kit = {
  kind: "api_kit",
  label: "integrations.adapters.api_kit.label",
  description: "integrations.adapters.api_kit.description",
  icon: "mail-forward",
  capabilities: { retry: true, hmac: false, replayable: true, idempotency: false },
  configFields: [
    {
      key: "form_id",
      label: "integrations.fields.kitFormId",
      helperText: "integrations.fields.kitFormIdHelp",
      type: "text",
      required: false
    },
    {
      key: "field_mapping",
      label: "integrations.fields.fieldMapping",
      helperText: "integrations.fields.fieldMappingHelp",
      type: "json",
      required: false,
      placeholder: '{"email": "email_address"}',
      validate: jsonObjectValidator
    },
    {
      key: "default_tags",
      label: "integrations.fields.defaultTags",
      helperText: "integrations.fields.defaultTagsHelp",
      type: "array-string",
      required: false
    }
  ],
  secretsFields: [
    {
      key: "api_key",
      label: "integrations.fields.apiKey",
      helperText: "integrations.fields.kitApiKeyHelp",
      type: "password",
      required: true,
      validate: required("apiKey")
    }
  ]
};
var ADAPTER_REGISTRY = {
  generic_webhook: genericWebhook,
  zapier_webhook: zapierWebhook,
  slack_url: slackUrl,
  discord_url: discordUrl,
  google_sheets_webhook: googleSheets,
  email,
  formshive_endpoint: formshiveEndpoint,
  api_pipedrive: pipedrive,
  api_mailchimp: mailchimp,
  api_kit: kit
};
function getAdapterMeta(kind) {
  return ADAPTER_REGISTRY[kind];
}
function listAdapters() {
  return Object.values(ADAPTER_REGISTRY);
}
var FORMSHIVE_EVENT_TYPES = [
  {
    type: "message.received",
    label: "integrations.events.message_received.label",
    description: "integrations.events.message_received.description"
  }
];

// src/integrations/EventSubscriptionCheckboxGrid.tsx
import { Checkbox as Checkbox2, Stack as Stack11, Text as Text18 } from "@mantine/core";
import { useTranslation as useTranslation13 } from "react-i18next";
import { jsx as jsx25 } from "react/jsx-runtime";
function EventSubscriptionCheckboxGrid({
  availableEvents,
  selectedEventTypes,
  onChange,
  disabled
}) {
  const { t } = useTranslation13();
  const toggle = (type, checked) => {
    if (checked) {
      if (!selectedEventTypes.includes(type)) {
        onChange([...selectedEventTypes, type]);
      }
    } else {
      onChange(selectedEventTypes.filter((t2) => t2 !== type));
    }
  };
  if (availableEvents.length === 0) {
    return /* @__PURE__ */ jsx25(Text18, { size: "sm", c: "dimmed", children: t("integrations.events.empty") });
  }
  return /* @__PURE__ */ jsx25(Stack11, { gap: "sm", children: availableEvents.map((event) => /* @__PURE__ */ jsx25(
    Checkbox2,
    {
      disabled,
      label: t(event.label),
      description: t(event.description),
      checked: selectedEventTypes.includes(event.type),
      onChange: (e) => toggle(event.type, e.currentTarget.checked)
    },
    event.type
  )) });
}

// src/integrations/HmacSecretReveal.tsx
import { ActionIcon as ActionIcon4, Alert as Alert4, CopyButton, Group as Group8, Text as Text19, Tooltip as Tooltip5 } from "@mantine/core";
import { IconCheck, IconCopy, IconKey as IconKey4 } from "@tabler/icons-react";
import { useTranslation as useTranslation14 } from "react-i18next";
import { jsx as jsx26, jsxs as jsxs18 } from "react/jsx-runtime";
function HmacSecretReveal({ secret }) {
  const { t } = useTranslation14();
  return /* @__PURE__ */ jsxs18(
    Alert4,
    {
      color: "yellow",
      icon: /* @__PURE__ */ jsx26(IconKey4, { size: 18 }),
      title: t("integrations.secret.title"),
      variant: "light",
      children: [
        /* @__PURE__ */ jsx26(Text19, { size: "sm", mb: "xs", children: t("integrations.secret.warning") }),
        /* @__PURE__ */ jsxs18(Group8, { gap: "xs", wrap: "nowrap", children: [
          /* @__PURE__ */ jsx26(
            Text19,
            {
              ff: "monospace",
              size: "sm",
              style: {
                wordBreak: "break-all",
                background: "var(--mantine-color-gray-1)",
                padding: "4px 8px",
                borderRadius: 4,
                flex: 1
              },
              children: secret
            }
          ),
          /* @__PURE__ */ jsx26(CopyButton, { value: secret, children: ({ copied, copy }) => /* @__PURE__ */ jsx26(
            Tooltip5,
            {
              label: copied ? t("integrations.secret.copied") : t("integrations.secret.copy"),
              children: /* @__PURE__ */ jsx26(ActionIcon4, { variant: "light", onClick: copy, size: "lg", children: copied ? /* @__PURE__ */ jsx26(IconCheck, { size: 16 }) : /* @__PURE__ */ jsx26(IconCopy, { size: 16 }) })
            }
          ) })
        ] })
      ]
    }
  );
}

// src/integrations/IntegrationDeliveryHistory.tsx
import {
  ActionIcon as ActionIcon5,
  Badge as Badge4,
  Box as Box5,
  Button as Button10,
  Code,
  Group as Group9,
  Select as Select3,
  Stack as Stack12,
  Text as Text20,
  TextInput as TextInput4,
  ThemeIcon as ThemeIcon5,
  Tooltip as Tooltip6
} from "@mantine/core";
import {
  IconAlertCircle as IconAlertCircle4,
  IconChevronDown,
  IconChevronRight,
  IconCircleCheck as IconCircleCheck2,
  IconHistory,
  IconRefresh as IconRefresh2
} from "@tabler/icons-react";
import { DataTable as DataTable2 } from "mantine-datatable";
import { useState as useState15 } from "react";
import { useTranslation as useTranslation15 } from "react-i18next";
import { Fragment as Fragment13, jsx as jsx27, jsxs as jsxs19 } from "react/jsx-runtime";
var OUTCOME_COLORS = {
  success: "green",
  retry: "yellow",
  dlq: "red",
  replayed: "blue"
};
function formatTimestamp(ts) {
  return new Date(ts).toLocaleString(void 0, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
}
function IntegrationDeliveryHistory({
  deliveries,
  loading,
  totalCount,
  page,
  pageSize,
  onPageChange,
  filters,
  onFilterChange,
  onResend,
  eventTypeOptions
}) {
  const { t } = useTranslation15();
  const [expandedIds, setExpandedIds] = useState15([]);
  const outcomeOptions = [
    { value: "", label: t("integrations.deliveries.allOutcomes") },
    { value: "success", label: t("integrations.outcomes.success") },
    { value: "retry", label: t("integrations.outcomes.retry") },
    { value: "dlq", label: t("integrations.outcomes.dlq") },
    { value: "replayed", label: t("integrations.outcomes.replayed") }
  ];
  const columns = [
    {
      accessor: "expand",
      title: "",
      width: 36,
      render: (row) => /* @__PURE__ */ jsx27(Box5, { c: "dimmed", style: { display: "flex", alignItems: "center" }, children: expandedIds.includes(row.id) ? /* @__PURE__ */ jsx27(IconChevronDown, { size: 16 }) : /* @__PURE__ */ jsx27(IconChevronRight, { size: 16 }) })
    },
    {
      accessor: "created_at",
      title: t("integrations.deliveries.when"),
      render: (row) => /* @__PURE__ */ jsx27(Text20, { size: "sm", children: formatTimestamp(row.created_at) })
    },
    {
      accessor: "event_type",
      title: t("integrations.deliveries.event"),
      render: (row) => /* @__PURE__ */ jsx27(Text20, { size: "sm", c: "dimmed", children: row.event_id.slice(0, 8) })
    },
    {
      accessor: "attempt_number",
      title: t("integrations.deliveries.attempt"),
      render: (row) => /* @__PURE__ */ jsxs19(Text20, { size: "sm", children: [
        "#",
        row.attempt_number
      ] })
    },
    {
      accessor: "outcome",
      title: t("integrations.deliveries.outcome"),
      render: (row) => /* @__PURE__ */ jsx27(Badge4, { color: OUTCOME_COLORS[row.outcome], variant: "light", radius: "sm", children: t(`integrations.outcomes.${row.outcome}`) })
    },
    {
      accessor: "http_status",
      title: t("integrations.deliveries.status"),
      render: (row) => row.http_status ? /* @__PURE__ */ jsx27(Text20, { size: "sm", children: row.http_status }) : /* @__PURE__ */ jsx27(Text20, { size: "sm", c: "dimmed", children: "\u2014" })
    },
    {
      accessor: "latency_ms",
      title: t("integrations.deliveries.latency"),
      render: (row) => row.latency_ms !== void 0 ? /* @__PURE__ */ jsxs19(Text20, { size: "sm", children: [
        row.latency_ms,
        " ms"
      ] }) : /* @__PURE__ */ jsx27(Text20, { size: "sm", c: "dimmed", children: "\u2014" })
    },
    {
      accessor: "error_class",
      title: t("integrations.deliveries.error"),
      render: (row) => row.error_class ? /* @__PURE__ */ jsx27(Tooltip6, { label: row.error_detail ?? "", multiline: true, maw: 400, disabled: !row.error_detail, children: /* @__PURE__ */ jsx27(Text20, { size: "sm", c: "red", children: row.error_class }) }) : /* @__PURE__ */ jsx27(Text20, { size: "sm", c: "dimmed", children: "\u2014" })
    },
    {
      accessor: "actions",
      title: t("integrations.list.actions"),
      textAlign: "right",
      render: (row) => /* @__PURE__ */ jsx27(Tooltip6, { label: t("integrations.actions.resend"), children: /* @__PURE__ */ jsx27(
        ActionIcon5,
        {
          variant: "subtle",
          onClick: (e) => {
            e.stopPropagation();
            onResend(row.id);
          },
          children: /* @__PURE__ */ jsx27(IconRefresh2, { size: 16 })
        }
      ) })
    }
  ];
  const renderDetails = (row) => {
    const isSuccess = row.outcome === "success" || row.outcome === "replayed";
    const hasError = !!row.error_class || !!row.error_detail;
    const hasPayload = row.payload_excerpt !== void 0 && row.payload_excerpt !== null;
    return /* @__PURE__ */ jsx27(Box5, { p: "md", style: { background: "var(--mantine-color-gray-0)" }, children: /* @__PURE__ */ jsxs19(Stack12, { gap: "xs", children: [
      /* @__PURE__ */ jsxs19(Group9, { gap: "xs", children: [
        isSuccess && !hasError ? /* @__PURE__ */ jsx27(IconCircleCheck2, { size: 16, color: "var(--mantine-color-green-6)" }) : /* @__PURE__ */ jsx27(IconAlertCircle4, { size: 16 }),
        /* @__PURE__ */ jsx27(Text20, { fw: 500, size: "sm", children: isSuccess && !hasError ? row.http_status !== void 0 && row.latency_ms !== void 0 ? t("integrations.deliveries.successWithDetails", {
          status: row.http_status,
          latency: row.latency_ms
        }) : t("integrations.deliveries.successTitle") : t("integrations.deliveries.detailsTitle") })
      ] }),
      /* @__PURE__ */ jsxs19(Group9, { gap: "md", wrap: "wrap", children: [
        /* @__PURE__ */ jsxs19(Stack12, { gap: 2, children: [
          /* @__PURE__ */ jsx27(Text20, { size: "xs", c: "dimmed", children: t("integrations.deliveries.eventIdLabel") }),
          /* @__PURE__ */ jsx27(Code, { children: row.event_id })
        ] }),
        /* @__PURE__ */ jsxs19(Stack12, { gap: 2, children: [
          /* @__PURE__ */ jsx27(Text20, { size: "xs", c: "dimmed", children: t("integrations.deliveries.deliveryIdLabel") }),
          /* @__PURE__ */ jsx27(Code, { children: row.id })
        ] }),
        /* @__PURE__ */ jsxs19(Stack12, { gap: 2, children: [
          /* @__PURE__ */ jsx27(Text20, { size: "xs", c: "dimmed", children: t("integrations.deliveries.timestampLabel") }),
          /* @__PURE__ */ jsx27(Text20, { size: "sm", children: new Date(row.created_at).toLocaleString() })
        ] })
      ] }),
      row.error_detail && /* @__PURE__ */ jsxs19(Stack12, { gap: 4, children: [
        /* @__PURE__ */ jsx27(Text20, { size: "xs", c: "dimmed", children: t("integrations.deliveries.errorDetail") }),
        /* @__PURE__ */ jsx27(Code, { block: true, children: row.error_detail })
      ] }),
      hasPayload && /* @__PURE__ */ jsxs19(Stack12, { gap: 4, children: [
        /* @__PURE__ */ jsx27(Text20, { size: "xs", c: "dimmed", children: t("integrations.deliveries.payload") }),
        /* @__PURE__ */ jsx27(Code, { block: true, children: typeof row.payload_excerpt === "string" ? row.payload_excerpt : JSON.stringify(row.payload_excerpt, null, 2) })
      ] })
    ] }) });
  };
  return /* @__PURE__ */ jsxs19(Stack12, { gap: "md", children: [
    /* @__PURE__ */ jsxs19(Group9, { wrap: "wrap", gap: "sm", align: "flex-end", children: [
      /* @__PURE__ */ jsx27(
        Select3,
        {
          label: t("integrations.deliveries.filterOutcome"),
          data: outcomeOptions,
          value: filters.outcome ?? "",
          onChange: (v) => onFilterChange({ ...filters, outcome: v || "" }),
          placeholder: t("integrations.deliveries.filterOutcome"),
          clearable: true,
          w: 180,
          size: "xs"
        }
      ),
      eventTypeOptions && eventTypeOptions.length > 0 && /* @__PURE__ */ jsx27(
        Select3,
        {
          label: t("integrations.deliveries.filterEvent"),
          data: [
            { value: "", label: t("integrations.deliveries.allEvents") },
            ...eventTypeOptions
          ],
          value: filters.event_type ?? "",
          onChange: (v) => onFilterChange({ ...filters, event_type: v ?? void 0 }),
          placeholder: t("integrations.deliveries.filterEvent"),
          clearable: true,
          w: 220,
          size: "xs"
        }
      ),
      /* @__PURE__ */ jsx27(
        TextInput4,
        {
          type: "datetime-local",
          value: filters.from ?? "",
          onChange: (e) => onFilterChange({ ...filters, from: e.currentTarget.value }),
          placeholder: t("integrations.deliveries.from"),
          label: t("integrations.deliveries.from"),
          size: "xs"
        }
      ),
      /* @__PURE__ */ jsx27(
        TextInput4,
        {
          type: "datetime-local",
          value: filters.to ?? "",
          onChange: (e) => onFilterChange({ ...filters, to: e.currentTarget.value }),
          placeholder: t("integrations.deliveries.to"),
          label: t("integrations.deliveries.to"),
          size: "xs"
        }
      ),
      /* @__PURE__ */ jsx27(
        Button10,
        {
          variant: "subtle",
          size: "xs",
          onClick: () => onFilterChange({}),
          disabled: !filters.outcome && !filters.event_type && !filters.from && !filters.to,
          children: t("integrations.deliveries.clearFilters")
        }
      )
    ] }),
    /* @__PURE__ */ jsx27(
      DataTable2,
      {
        minHeight: deliveries.length === 0 ? 280 : void 0,
        withTableBorder: true,
        borderRadius: "md",
        shadow: "xs",
        records: deliveries,
        columns,
        totalRecords: totalCount,
        recordsPerPage: pageSize,
        page,
        onPageChange,
        fetching: loading,
        noRecordsText: "",
        rowExpansion: {
          allowMultiple: true,
          expanded: {
            recordIds: expandedIds,
            onRecordIdsChange: (ids) => setExpandedIds(ids)
          },
          content: ({ record }) => renderDetails(record)
        },
        emptyState: deliveries.length === 0 ? /* @__PURE__ */ jsx27(Box5, { py: "xl", px: "md", children: /* @__PURE__ */ jsxs19(Stack12, { align: "center", gap: "xs", children: [
          /* @__PURE__ */ jsx27(ThemeIcon5, { size: 48, radius: "xl", variant: "light", color: "gray", children: /* @__PURE__ */ jsx27(IconHistory, { size: 24 }) }),
          /* @__PURE__ */ jsx27(Text20, { size: "sm", fw: 500, children: t("integrations.deliveries.empty") }),
          /* @__PURE__ */ jsx27(Text20, { size: "sm", c: "dimmed", ta: "center", maw: 360, children: t("integrations.deliveries.emptyDescription") })
        ] }) }) : /* @__PURE__ */ jsx27(Fragment13, {})
      }
    )
  ] });
}

// src/integrations/IntegrationForm.tsx
import { Alert as Alert5, Button as Button11, Group as Group10, Stack as Stack13, Stepper, Text as Text21, TextInput as TextInput5, Title as Title9 } from "@mantine/core";
import { useForm as useForm5 } from "@mantine/form";
import { IconAlertCircle as IconAlertCircle5 } from "@tabler/icons-react";
import { useMemo, useState as useState16 } from "react";
import { useTranslation as useTranslation16 } from "react-i18next";
import { Fragment as Fragment14, jsx as jsx28, jsxs as jsxs20 } from "react/jsx-runtime";
function coerceFieldValue(field, raw) {
  if (raw === void 0 || raw === null) return void 0;
  if (field.type === "json") {
    if (typeof raw === "object") return raw;
    if (typeof raw === "string") {
      const trimmed = raw.trim();
      if (trimmed.length === 0) return void 0;
      try {
        return JSON.parse(trimmed);
      } catch {
        return raw;
      }
    }
    return raw;
  }
  if (field.type === "number") {
    if (raw === "") return void 0;
    const n = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(n) ? n : void 0;
  }
  if (field.type === "checkbox") {
    return Boolean(raw);
  }
  if (field.type === "email-list" || field.type === "array-string") {
    return Array.isArray(raw) ? raw : [];
  }
  return raw;
}
function IntegrationForm({ mode, initialValues, onSubmit, onCancel }) {
  const { t } = useTranslation16();
  const adapters = useMemo(() => listAdapters(), []);
  const [step, setStep] = useState16(mode === "edit" ? 1 : 0);
  const [submitting, setSubmitting] = useState16(false);
  const [error, setError] = useState16(null);
  const [revealedSecret, setRevealedSecret] = useState16(null);
  const form = useForm5({
    mode: "controlled",
    initialValues: {
      title: initialValues?.title ?? "",
      kind: initialValues?.kind ?? "",
      config: initialValues?.config ?? {},
      secrets: {}
    },
    validate: {
      title: (value) => value && value.trim().length > 0 ? null : t("integrations.errors.titleRequired")
    }
  });
  const selectedKind = form.values.kind;
  const selectedMeta = selectedKind ? getAdapterMeta(selectedKind) : void 0;
  const handlePickAdapter = (kind) => {
    form.setFieldValue("kind", kind);
    form.setFieldValue("config", {});
    form.setFieldValue("secrets", {});
    setStep(1);
  };
  const validateAdapterFields = () => {
    if (!selectedMeta) return false;
    const all = [
      ...selectedMeta.configFields.map((field) => ({ field, group: "config" })),
      ...selectedMeta.secretsFields.map((field) => ({ field, group: "secrets" }))
    ];
    let ok = true;
    for (const { field, group } of all) {
      const value = form.values[group][field.key];
      if (field.required && (value === void 0 || value === null || value === "")) {
        form.setFieldError(`${group}.${field.key}`, t("integrations.errors.fieldRequired"));
        ok = false;
        continue;
      }
      if (field.validate) {
        const msg = field.validate(value);
        if (msg) {
          form.setFieldError(`${group}.${field.key}`, t(msg, msg));
          ok = false;
        }
      }
    }
    return ok;
  };
  const handleSubmit = async () => {
    setError(null);
    if (!selectedMeta) {
      setError(t("integrations.errors.adapterRequired"));
      return;
    }
    const titleValidation = form.validate();
    if (titleValidation.hasErrors) return;
    if (!validateAdapterFields()) return;
    const config = {};
    for (const field of selectedMeta.configFields) {
      const v = coerceFieldValue(field, form.values.config[field.key]);
      if (v !== void 0) config[field.key] = v;
    }
    const secrets = {};
    for (const field of selectedMeta.secretsFields) {
      const v = coerceFieldValue(field, form.values.secrets[field.key]);
      if (v !== void 0) secrets[field.key] = v;
    }
    const payload = {
      title: form.values.title.trim(),
      kind: selectedMeta.kind,
      config,
      secrets: Object.keys(secrets).length > 0 ? secrets : void 0
    };
    try {
      setSubmitting(true);
      const result = await onSubmit(payload);
      if (mode === "create" && result.hmac_secret_b64) {
        setRevealedSecret(result.hmac_secret_b64);
        setStep(2);
        return;
      }
      onCancel();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };
  if (revealedSecret) {
    return /* @__PURE__ */ jsxs20(Stack13, { gap: "md", children: [
      /* @__PURE__ */ jsx28(Title9, { order: 3, children: t("integrations.form.secretRevealedTitle") }),
      /* @__PURE__ */ jsx28(HmacSecretReveal, { secret: revealedSecret }),
      /* @__PURE__ */ jsx28(Group10, { justify: "flex-end", children: /* @__PURE__ */ jsx28(Button11, { onClick: onCancel, children: t("integrations.actions.done") }) })
    ] });
  }
  return /* @__PURE__ */ jsxs20(Stack13, { gap: "md", children: [
    /* @__PURE__ */ jsx28(Title9, { order: 3, children: mode === "create" ? t("integrations.form.createTitle") : t("integrations.form.editTitle") }),
    /* @__PURE__ */ jsxs20(
      Stepper,
      {
        active: step,
        onStepClick: mode === "edit" ? void 0 : setStep,
        allowNextStepsSelect: false,
        children: [
          /* @__PURE__ */ jsx28(
            Stepper.Step,
            {
              label: t("integrations.form.stepKind"),
              description: t("integrations.form.stepKindDesc"),
              allowStepSelect: mode !== "edit",
              children: /* @__PURE__ */ jsxs20(Stack13, { mt: "md", children: [
                /* @__PURE__ */ jsx28(Text21, { size: "sm", c: "dimmed", children: t("integrations.form.pickAdapterHint") }),
                /* @__PURE__ */ jsx28(
                  AdapterPickerGrid,
                  {
                    adapters,
                    selected: selectedKind,
                    onSelect: handlePickAdapter
                  }
                )
              ] })
            }
          ),
          /* @__PURE__ */ jsx28(
            Stepper.Step,
            {
              label: t("integrations.form.stepConfigure"),
              description: t("integrations.form.stepConfigureDesc"),
              allowStepSelect: false,
              children: /* @__PURE__ */ jsx28(Stack13, { mt: "md", gap: "md", children: selectedMeta ? /* @__PURE__ */ jsxs20(Fragment14, { children: [
                /* @__PURE__ */ jsx28(Text21, { size: "sm", c: "dimmed", children: t(selectedMeta.description) }),
                /* @__PURE__ */ jsx28(
                  TextInput5,
                  {
                    label: t("integrations.fields.integrationTitle"),
                    description: t("integrations.fields.integrationTitleHelp"),
                    required: true,
                    disabled: submitting,
                    ...form.getInputProps("title")
                  }
                ),
                /* @__PURE__ */ jsx28(
                  AdapterConfigFields,
                  {
                    fields: selectedMeta.configFields,
                    pathPrefix: "config",
                    form,
                    disabled: submitting
                  }
                ),
                selectedMeta.secretsFields.length > 0 && /* @__PURE__ */ jsxs20(Stack13, { gap: "xs", children: [
                  /* @__PURE__ */ jsx28(Text21, { fw: 500, size: "sm", children: t("integrations.form.secretsHeader") }),
                  /* @__PURE__ */ jsx28(Text21, { size: "xs", c: "dimmed", children: t("integrations.form.secretsHelp") }),
                  /* @__PURE__ */ jsx28(
                    AdapterConfigFields,
                    {
                      fields: selectedMeta.secretsFields,
                      pathPrefix: "secrets",
                      form,
                      disabled: submitting
                    }
                  )
                ] })
              ] }) : /* @__PURE__ */ jsx28(Text21, { c: "dimmed", children: t("integrations.form.pickAdapterFirst") }) })
            }
          )
        ]
      }
    ),
    error && /* @__PURE__ */ jsx28(
      Alert5,
      {
        color: "red",
        icon: /* @__PURE__ */ jsx28(IconAlertCircle5, { size: 16 }),
        title: t("integrations.errors.submitFailed"),
        children: error
      }
    ),
    /* @__PURE__ */ jsxs20(Group10, { justify: "space-between", children: [
      /* @__PURE__ */ jsx28(Button11, { variant: "subtle", onClick: onCancel, disabled: submitting, children: t("integrations.actions.cancel") }),
      /* @__PURE__ */ jsxs20(Group10, { children: [
        step === 1 && mode === "create" && /* @__PURE__ */ jsx28(Button11, { variant: "default", onClick: () => setStep(0), disabled: submitting, children: t("integrations.actions.back") }),
        step === 0 && mode === "create" && /* @__PURE__ */ jsx28(Button11, { onClick: () => selectedMeta && setStep(1), disabled: !selectedMeta, children: t("integrations.actions.next") }),
        step === 1 && /* @__PURE__ */ jsx28(Button11, { onClick: handleSubmit, loading: submitting, children: mode === "create" ? t("integrations.actions.create") : t("integrations.actions.save") })
      ] })
    ] })
  ] });
}

// src/integrations/IntegrationsList.tsx
import {
  ActionIcon as ActionIcon6,
  Badge as Badge5,
  Box as Box6,
  Button as Button12,
  Group as Group11,
  Stack as Stack14,
  Text as Text22,
  ThemeIcon as ThemeIcon6,
  Tooltip as Tooltip7
} from "@mantine/core";
import {
  IconHistory as IconHistory2,
  IconPencil,
  IconPlugConnected as IconPlugConnected2,
  IconPlus,
  IconTrash as IconTrash2
} from "@tabler/icons-react";
import { DataTable as DataTable3 } from "mantine-datatable";
import { useTranslation as useTranslation17 } from "react-i18next";
import { Fragment as Fragment15, jsx as jsx29, jsxs as jsxs21 } from "react/jsx-runtime";
function formatTimestamp2(ts) {
  return new Date(ts).toLocaleString(void 0, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}
function IntegrationsList({
  integrations,
  loading,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onCreate,
  onEdit,
  onDelete,
  onViewDeliveries
}) {
  const { t } = useTranslation17();
  const columns = [
    {
      accessor: "title",
      title: t("integrations.list.title"),
      render: (row) => /* @__PURE__ */ jsx29(Text22, { size: "sm", fw: 500, children: row.title })
    },
    {
      accessor: "kind",
      title: t("integrations.list.kind"),
      render: (row) => {
        const meta = getAdapterMeta(row.kind);
        return /* @__PURE__ */ jsx29(Text22, { size: "sm", children: meta ? t(meta.label) : row.kind });
      }
    },
    {
      accessor: "capabilities",
      title: t("integrations.list.capabilities"),
      render: (row) => {
        const meta = getAdapterMeta(row.kind);
        if (!meta) return null;
        const caps = meta.capabilities;
        return /* @__PURE__ */ jsxs21(Group11, { gap: 4, wrap: "wrap", children: [
          caps.hmac && /* @__PURE__ */ jsx29(Badge5, { variant: "light", color: "blue", size: "sm", children: t("integrations.capabilities.hmac") }),
          caps.retry && /* @__PURE__ */ jsx29(Badge5, { variant: "light", color: "grape", size: "sm", children: t("integrations.capabilities.retry") }),
          caps.replayable && /* @__PURE__ */ jsx29(Badge5, { variant: "light", color: "teal", size: "sm", children: t("integrations.capabilities.replayable") }),
          caps.idempotency && /* @__PURE__ */ jsx29(Badge5, { variant: "light", color: "cyan", size: "sm", children: t("integrations.capabilities.idempotency") })
        ] });
      }
    },
    {
      accessor: "created_at",
      title: t("integrations.list.created"),
      render: (row) => /* @__PURE__ */ jsx29(Text22, { size: "sm", c: "dimmed", children: formatTimestamp2(row.created_at) })
    },
    {
      accessor: "actions",
      title: t("integrations.list.actions"),
      textAlign: "right",
      render: (row) => /* @__PURE__ */ jsxs21(Group11, { gap: 4, justify: "flex-end", wrap: "nowrap", children: [
        /* @__PURE__ */ jsx29(Tooltip7, { label: t("integrations.actions.viewDeliveries"), children: /* @__PURE__ */ jsx29(
          ActionIcon6,
          {
            variant: "subtle",
            onClick: (e) => {
              e.stopPropagation();
              onViewDeliveries(row);
            },
            children: /* @__PURE__ */ jsx29(IconHistory2, { size: 16 })
          }
        ) }),
        /* @__PURE__ */ jsx29(Tooltip7, { label: t("integrations.actions.edit"), children: /* @__PURE__ */ jsx29(
          ActionIcon6,
          {
            variant: "subtle",
            onClick: (e) => {
              e.stopPropagation();
              onEdit(row);
            },
            children: /* @__PURE__ */ jsx29(IconPencil, { size: 16 })
          }
        ) }),
        /* @__PURE__ */ jsx29(Tooltip7, { label: t("integrations.actions.delete"), children: /* @__PURE__ */ jsx29(
          ActionIcon6,
          {
            variant: "subtle",
            color: "red",
            onClick: (e) => {
              e.stopPropagation();
              onDelete(row);
            },
            children: /* @__PURE__ */ jsx29(IconTrash2, { size: 16 })
          }
        ) })
      ] })
    }
  ];
  return /* @__PURE__ */ jsxs21(Stack14, { gap: "md", children: [
    /* @__PURE__ */ jsx29(Group11, { justify: "flex-end", children: /* @__PURE__ */ jsx29(Button12, { leftSection: /* @__PURE__ */ jsx29(IconPlus, { size: 16 }), onClick: onCreate, children: t("integrations.actions.create") }) }),
    /* @__PURE__ */ jsx29(
      DataTable3,
      {
        minHeight: integrations.length === 0 ? 280 : void 0,
        withTableBorder: true,
        borderRadius: "md",
        shadow: "xs",
        records: integrations,
        columns,
        totalRecords: totalCount,
        recordsPerPage: pageSize,
        page,
        onPageChange,
        fetching: loading,
        noRecordsText: "",
        emptyState: integrations.length === 0 ? /* @__PURE__ */ jsx29(Box6, { py: "xl", px: "md", children: /* @__PURE__ */ jsxs21(Stack14, { align: "center", gap: "xs", children: [
          /* @__PURE__ */ jsx29(ThemeIcon6, { size: 48, radius: "xl", variant: "light", color: "gray", children: /* @__PURE__ */ jsx29(IconPlugConnected2, { size: 24 }) }),
          /* @__PURE__ */ jsx29(Text22, { size: "sm", fw: 500, children: t("integrations.empty.title") }),
          /* @__PURE__ */ jsx29(Text22, { size: "sm", c: "dimmed", ta: "center", maw: 360, children: t("integrations.empty.description") })
        ] }) }) : /* @__PURE__ */ jsx29(Fragment15, {})
      }
    )
  ] });
}

// src/lib/languageDetection.ts
var SUPPORTED_LANGUAGES2 = ["en", "de", "fr", "es", "pt", "zh", "th", "ar"];
var LANGUAGE_STORAGE_KEY = "saved-language";
function getLanguageFromHash() {
  const hash = window.location.hash;
  const match = hash.match(/^#\/([a-z]{2})(?:\/|$)/);
  if (match && SUPPORTED_LANGUAGES2.includes(match[1])) {
    return match[1];
  }
  return null;
}
function getLanguageFromURL() {
  const urlParams = new URLSearchParams(window.location.search);
  const langParam = urlParams.get("lang");
  if (langParam && SUPPORTED_LANGUAGES2.includes(langParam)) {
    return langParam;
  }
  return null;
}
function getStoredLanguage() {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES2.includes(stored)) {
      return stored;
    }
  } catch (error) {
    console.warn("Failed to read language from localStorage:", error);
  }
  return null;
}
function storeLanguage(language) {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch (error) {
    console.warn("Failed to store language in localStorage:", error);
  }
}
function getBrowserLanguage() {
  const browserLang = navigator.language.split("-")[0];
  return SUPPORTED_LANGUAGES2.includes(browserLang) ? browserLang : "en";
}
function redirectFromBaseURL() {
  const currentHash = window.location.hash;
  if (!currentHash || currentHash === "#" || currentHash === "#/") {
    const preferredLanguage = getStoredLanguage() || getBrowserLanguage();
    let newHash;
    if (preferredLanguage === "en") {
      newHash = "#/";
    } else {
      newHash = `#/${preferredLanguage}/`;
    }
    window.location.replace(window.location.origin + window.location.pathname + newHash);
    return;
  }
}
function detectAndSetLanguage(i18nInstance) {
  redirectFromBaseURL();
  const detectedLanguage = getLanguageFromHash() || getLanguageFromURL() || getStoredLanguage() || getBrowserLanguage();
  if (i18nInstance && i18nInstance.language !== detectedLanguage) {
    i18nInstance.changeLanguage(detectedLanguage);
  }
  storeLanguage(detectedLanguage);
}
function updateHashWithLanguage(language, currentPath) {
  if (!SUPPORTED_LANGUAGES2.includes(language)) {
    return;
  }
  let path = currentPath || window.location.hash.slice(1);
  const langMatch = path.match(/^\/([a-z]{2})(?:\/|$)/);
  if (langMatch && SUPPORTED_LANGUAGES2.includes(langMatch[1])) {
    path = path.replace(/^\/[a-z]{2}/, "");
  }
  if (!path.startsWith("/")) {
    path = `/${path}`;
  }
  let newHash;
  if (language === "en") {
    newHash = `#${path}`;
  } else {
    newHash = `#/${language}${path}`;
  }
  window.history.replaceState(null, "", newHash);
  storeLanguage(language);
}

// src/pages/Login2FAPage.tsx
import {
  sessionExpiriesFromLoginSuccess as sessionExpiriesFromLoginSuccess2
} from "@gofranz/common";
import {
  alpha,
  Button as Button13,
  Card as Card4,
  Center as Center4,
  PinInput as PinInput2,
  Stack as Stack15,
  Text as Text23,
  Title as Title10,
  useComputedColorScheme
} from "@mantine/core";
import { IconShieldLock as IconShieldLock4 } from "@tabler/icons-react";
import { useEffect as useEffect12, useState as useState17 } from "react";
import { useTranslation as useTranslation18 } from "react-i18next";
import { useNavigate as useNavigate3 } from "react-router-dom";
import { jsx as jsx30, jsxs as jsxs22 } from "react/jsx-runtime";
function Login2FAPage(props) {
  const { t } = useTranslation18();
  const isDark = useComputedColorScheme("light") === "dark";
  const nav = useNavigate3();
  const { createLanguageURL } = useLanguageAwareRouting();
  const {
    setSession,
    verify2FA,
    onLoginSuccess,
    titleGradient = { from: "pink", to: "yellow" },
    titleClassName
  } = props;
  const [pendingToken, setPendingToken] = useState17(null);
  const [loginMethod, setLoginMethod] = useState17(null);
  const [code, setCode] = useState17("");
  const [isBusy, setIsBusy] = useState17(false);
  const [hasError, setHasError] = useState17(null);
  useEffect12(() => {
    const url = new URL(window.location.href);
    const hashParams = new URLSearchParams(url.hash.split("?")[1]);
    const token = hashParams.get("pending_token");
    const method = hashParams.get("login_method");
    if (!token) {
      setHasError(t("login.invalid2FASession"));
      return;
    }
    setPendingToken(token);
    setLoginMethod(method);
  }, [t]);
  const handleVerify = async () => {
    if (!pendingToken || code.length < 6) return;
    setIsBusy(true);
    setHasError(null);
    try {
      const result = await verify2FA({
        pending_token: pendingToken,
        code
      });
      setSession({
        isLoggedIn: true,
        userId: result.user_id,
        accessToken: result.access_token,
        email: result.email,
        displayName: result.display_name,
        ...sessionExpiriesFromLoginSuccess2(result),
        method: loginMethod
      });
      if (onLoginSuccess) {
        onLoginSuccess();
      } else {
        nav(createLanguageURL("/account"));
      }
    } catch (e) {
      console.error(e);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === "object" && errorData.message) {
        setHasError(errorData.message);
      } else if (e?.response?.status === 401) {
        setHasError(t("login.invalid2FACode"));
      } else if (e?.response?.status === 429) {
        setHasError(t("login.tooMany2FAAttempts"));
        nav(createLanguageURL("/login"));
      } else {
        setHasError(`Error: ${e?.message || e}`);
      }
      setCode("");
    } finally {
      setIsBusy(false);
    }
  };
  const handleBack = () => {
    nav(createLanguageURL("/login"));
  };
  return /* @__PURE__ */ jsx30(Center4, { h: "100vh", children: /* @__PURE__ */ jsxs22(Stack15, { children: [
    /* @__PURE__ */ jsx30(Title10, { order: 1, className: titleClassName, ta: "center", children: /* @__PURE__ */ jsx30(Text23, { inherit: true, variant: "gradient", component: "span", gradient: titleGradient, children: t("login.twoFactorAuth") }) }),
    /* @__PURE__ */ jsx30(
      Card4,
      {
        maw: 400,
        p: "xl",
        bg: isDark ? alpha("var(--mantine-color-gray-8)", 0.6) : alpha("var(--mantine-color-gray-0)", 0.6),
        children: pendingToken ? /* @__PURE__ */ jsxs22(Stack15, { gap: "md", align: "center", children: [
          /* @__PURE__ */ jsx30(IconShieldLock4, { size: 48, stroke: 1.5 }),
          /* @__PURE__ */ jsx30(Text23, { size: "lg", fw: 500, children: t("login.twoFactorRequired") }),
          /* @__PURE__ */ jsx30(Text23, { size: "sm", c: "dimmed", ta: "center", children: t("login.enterTwoFactorCode") }),
          /* @__PURE__ */ jsx30(
            PinInput2,
            {
              length: 6,
              type: "number",
              value: code,
              onChange: setCode,
              oneTimeCode: true,
              disabled: isBusy,
              size: "lg"
            }
          ),
          hasError && /* @__PURE__ */ jsx30(Text23, { c: "red", size: "sm", ta: "center", children: hasError }),
          /* @__PURE__ */ jsx30(Button13, { onClick: handleVerify, loading: isBusy, disabled: code.length < 6, fullWidth: true, children: t("login.verify") }),
          /* @__PURE__ */ jsx30(Text23, { size: "sm", c: "dimmed", ta: "center", children: t("login.useRecoveryCode") }),
          /* @__PURE__ */ jsx30(Button13, { variant: "subtle", size: "xs", onClick: handleBack, children: t("login.back") })
        ] }) : /* @__PURE__ */ jsxs22(Stack15, { gap: "md", align: "center", children: [
          hasError && /* @__PURE__ */ jsx30(Text23, { c: "red", size: "sm", ta: "center", children: hasError }),
          /* @__PURE__ */ jsx30(Button13, { onClick: handleBack, children: t("login.backToLogin") })
        ] })
      }
    )
  ] }) });
}

// src/pages/LoginCallbackPage.tsx
import { alpha as alpha2, Card as Card5, Center as Center5, Stack as Stack16, Text as Text24, Title as Title11, useComputedColorScheme as useComputedColorScheme2 } from "@mantine/core";
import { useState as useState18 } from "react";
import { useTranslation as useTranslation19 } from "react-i18next";
import { useNavigate as useNavigate4 } from "react-router-dom";
import { jsx as jsx31, jsxs as jsxs23 } from "react/jsx-runtime";
function LoginCallbackPage(props) {
  const { t } = useTranslation19();
  const isDark = useComputedColorScheme2("light") === "dark";
  const nav = useNavigate4();
  const { createLanguageURL } = useLanguageAwareRouting();
  const [failed, setFailed] = useState18(false);
  const {
    setSession,
    onLoginSuccess,
    titleGradient = { from: "pink", to: "yellow" },
    titleClassName
  } = props;
  const loginDoneCb = () => {
    if (onLoginSuccess) {
      onLoginSuccess();
    } else {
      nav(createLanguageURL("/account"));
    }
  };
  return /* @__PURE__ */ jsx31(Center5, { h: "100vh", px: "md", children: /* @__PURE__ */ jsxs23(Stack16, { w: "100%", maw: 400, align: "center", children: [
    !failed && /* @__PURE__ */ jsx31(Title11, { order: 1, className: titleClassName, ta: "center", children: /* @__PURE__ */ jsx31(Text24, { inherit: true, variant: "gradient", component: "span", gradient: titleGradient, children: t("login.loggingIn") }) }),
    /* @__PURE__ */ jsx31(
      Card5,
      {
        maw: 400,
        w: "100%",
        p: "xl",
        radius: "lg",
        withBorder: true,
        shadow: "sm",
        bg: isDark ? alpha2("var(--mantine-color-gray-8)", 0.6) : "var(--mantine-color-default)",
        children: /* @__PURE__ */ jsx31(
          LoginCallback,
          {
            setSession,
            loginDoneCb,
            apiBaseUrl: props.apiBaseUrl,
            loginHref: `#${createLanguageURL("/login")}`,
            onError: () => setFailed(true)
          }
        )
      }
    )
  ] }) });
}

// src/pages/LoginPage.tsx
import {
  LOGIN_METHOD as LOGIN_METHOD3
} from "@gofranz/common";
import { Anchor as Anchor3, Card as Card6, Center as Center6, Group as Group12, Image, Stack as Stack17, Text as Text25 } from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import { useState as useState19 } from "react";
import { useTranslation as useTranslation20 } from "react-i18next";
import { Link, useNavigate as useNavigate5 } from "react-router-dom";
import { jsx as jsx32, jsxs as jsxs24 } from "react/jsx-runtime";
function LoginPage(props) {
  const { t } = useTranslation20();
  const [hasNewAccountButton, setHasNewAccountButton] = useState19(props.showSignupButton !== false);
  const nav = useNavigate5();
  const { createLanguageURL } = useLanguageAwareRouting();
  const {
    login,
    loginChallenge,
    verify2FA,
    onLoginSuccess,
    signupRoute = "/signup",
    privacyRoute = "/privacy",
    termsRoute = "/terms",
    logoSrc = "/logo.svg",
    loginMethods = [
      LOGIN_METHOD3.NOSTR,
      LOGIN_METHOD3.GITHUB,
      LOGIN_METHOD3.GOOGLE,
      LOGIN_METHOD3.EMAIL_MAGIC_LINK,
      LOGIN_METHOD3.MICROSOFT
    ]
  } = props;
  const loginCb = async (loginRequest) => {
    setHasNewAccountButton(false);
    return login(loginRequest);
  };
  const loginDoneCb = () => {
    if (onLoginSuccess) {
      onLoginSuccess();
    } else {
      nav(createLanguageURL("/account"));
    }
  };
  const brandName = props.brandName;
  const logo = props.logo ?? /* @__PURE__ */ jsx32(Image, { src: logoSrc, alt: "Logo", w: 48, h: 48, fit: "contain" });
  return /* @__PURE__ */ jsx32(Center6, { mih: "100vh", py: "xl", children: /* @__PURE__ */ jsxs24(Stack17, { align: "center", gap: "lg", w: "100%", maw: 420, px: "md", children: [
    /* @__PURE__ */ jsxs24(Stack17, { align: "center", gap: "xs", children: [
      logo,
      brandName && /* @__PURE__ */ jsx32(Text25, { fw: 600, size: "lg", children: brandName })
    ] }),
    /* @__PURE__ */ jsx32(Card6, { withBorder: true, radius: "md", p: "lg", w: "100%", shadow: "xs", children: /* @__PURE__ */ jsx32(
      Login,
      {
        login: loginCb,
        loginChallenge,
        verify2FA,
        loginDoneCb,
        loginMethods
      }
    ) }),
    /* @__PURE__ */ jsxs24(Stack17, { align: "center", gap: "sm", children: [
      hasNewAccountButton && /* @__PURE__ */ jsxs24(Group12, { gap: "xs", align: "center", children: [
        /* @__PURE__ */ jsx32(Text25, { size: "sm", c: "dimmed", children: t("auth.newHere") }),
        /* @__PURE__ */ jsx32(Anchor3, { component: Link, to: signupRoute, size: "sm", fw: 500, children: /* @__PURE__ */ jsxs24(Group12, { gap: 4, align: "center", wrap: "nowrap", children: [
          t("auth.howToSignup"),
          /* @__PURE__ */ jsx32(IconArrowRight, { size: 14 })
        ] }) })
      ] }),
      /* @__PURE__ */ jsxs24(Group12, { gap: "lg", children: [
        /* @__PURE__ */ jsx32(Anchor3, { component: Link, to: privacyRoute, size: "xs", c: "dimmed", children: t("auth.privacyPolicy") }),
        /* @__PURE__ */ jsx32(Anchor3, { component: Link, to: termsRoute, size: "xs", c: "dimmed", children: t("auth.termsOfService") })
      ] })
    ] })
  ] }) });
}

// src/pages/MagicLinkLoginPage.tsx
import {
  LOGIN_METHOD as LOGIN_METHOD4
} from "@gofranz/common";
import { alpha as alpha3, Card as Card7, Center as Center7, Stack as Stack18, Text as Text26, Title as Title12, useComputedColorScheme as useComputedColorScheme3 } from "@mantine/core";
import { useEffect as useEffect13, useState as useState20 } from "react";
import { useTranslation as useTranslation21 } from "react-i18next";
import { useNavigate as useNavigate6 } from "react-router-dom";
import { jsx as jsx33, jsxs as jsxs25 } from "react/jsx-runtime";
function MagicLinkLoginPage(props) {
  const { t } = useTranslation21();
  const isDark = useComputedColorScheme3("light") === "dark";
  const [errors, setErrors] = useState20([]);
  const [isBusy, setIsBusy] = useState20(false);
  const nav = useNavigate6();
  const { createLanguageURL } = useLanguageAwareRouting();
  const {
    loginChallenge,
    onLoginSuccess,
    titleGradient = { from: "pink", to: "yellow" },
    titleClassName
  } = props;
  const submitChallenge = async (id, challenge) => {
    setIsBusy(true);
    try {
      await loginChallenge({
        type: LOGIN_METHOD4.EMAIL_MAGIC_LINK,
        content: {
          id,
          challenge
        }
      });
      setIsBusy(false);
      if (onLoginSuccess) {
        onLoginSuccess();
      } else {
        nav(createLanguageURL("/account"));
      }
    } catch (e) {
      console.error(e);
      setIsBusy(false);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === "object" && errorData.error && errorData.message) {
        switch (errorData.error) {
          case "invalid_auth_method":
            setErrors([t("login.errorDifferentAuthMethod"), errorData.message]);
            break;
          case "email_not_primary":
            setErrors([t("login.errorNotPrimaryEmail"), errorData.message]);
            break;
          case "invalid_challenge":
          case "challenge_mismatch":
            setErrors([t("login.invalidMagicLink"), errorData.message]);
            break;
          default:
            setErrors([t("login.somethingWrong"), errorData.message]);
        }
      } else {
        const errorMessage = e?.message || e?.toString() || "";
        setErrors([t("login.somethingWrong"), errorMessage]);
      }
    }
  };
  useEffect13(() => {
    const login = async () => {
      const url = new URL(window.location.href);
      const hashParams = new URLSearchParams(url.hash.split("?")[1]);
      const urlParamChallenge = hashParams.get("challenge");
      const urlParamId = hashParams.get("id");
      const loginErrors = [];
      if (urlParamId && urlParamChallenge) {
        await submitChallenge(urlParamId, urlParamChallenge);
      } else {
        if (!urlParamId) {
          loginErrors.push(t("login.noIdProvided"));
        }
        if (!urlParamChallenge) {
          loginErrors.push(t("login.noChallengeProvided"));
        }
        setErrors(loginErrors);
      }
    };
    login();
  }, []);
  return /* @__PURE__ */ jsx33(Center7, { h: "100vh", children: /* @__PURE__ */ jsxs25(Stack18, { children: [
    /* @__PURE__ */ jsx33(Title12, { order: 1, className: titleClassName, ta: "center", children: /* @__PURE__ */ jsx33(Text26, { inherit: true, variant: "gradient", component: "span", gradient: titleGradient, children: t("auth.login") }) }),
    /* @__PURE__ */ jsx33(
      Card7,
      {
        maw: 400,
        p: "md",
        bg: isDark ? alpha3("var(--mantine-color-gray-8)", 0.6) : alpha3("var(--mantine-color-gray-0)", 0.6),
        children: isBusy ? /* @__PURE__ */ jsx33(Stack18, { gap: "md", children: /* @__PURE__ */ jsx33(Title12, { order: 3, children: t("login.processing") }) }) : errors.length > 0 ? /* @__PURE__ */ jsxs25(Stack18, { gap: "md", children: [
          /* @__PURE__ */ jsx33(Title12, { order: 4, children: t("login.somethingWrong") }),
          errors.map((error, index) => /* @__PURE__ */ jsx33(Text26, { c: index === 0 ? "red" : "dimmed", children: error }, index))
        ] }) : /* @__PURE__ */ jsx33(Text26, { children: t("login.somethingWrong") })
      }
    )
  ] }) });
}

// src/pages/SignupPage.tsx
import { LOGIN_METHOD as LOGIN_METHOD5 } from "@gofranz/common";
import {
  Anchor as Anchor4,
  alpha as alpha4,
  Button as Button14,
  Card as Card8,
  Center as Center8,
  Stack as Stack19,
  Text as Text27,
  Title as Title13,
  useComputedColorScheme as useComputedColorScheme4
} from "@mantine/core";
import {
  IconBrandGithub as IconBrandGithub4,
  IconBrandGoogle as IconBrandGoogle4,
  IconBrandWindows as IconBrandWindows4,
  IconBrowser,
  IconRecordMail
} from "@tabler/icons-react";
import { useTranslation as useTranslation22 } from "react-i18next";
import { Link as Link2 } from "react-router-dom";
import { Fragment as Fragment16, jsx as jsx34, jsxs as jsxs26 } from "react/jsx-runtime";
function SignupPage(props) {
  const { t } = useTranslation22();
  const { createLanguageURL } = useLanguageAwareRouting();
  const isDark = useComputedColorScheme4("light") === "dark";
  const {
    titleGradient = { from: "pink", to: "yellow" },
    titleClassName,
    loginMethods = [
      LOGIN_METHOD5.NOSTR,
      LOGIN_METHOD5.GITHUB,
      LOGIN_METHOD5.GOOGLE,
      LOGIN_METHOD5.EMAIL_MAGIC_LINK,
      LOGIN_METHOD5.MICROSOFT
    ]
  } = props;
  const hasGoogleLogin = loginMethods?.includes(LOGIN_METHOD5.GOOGLE);
  const hasGithubLogin = loginMethods?.includes(LOGIN_METHOD5.GITHUB);
  const hasMicrosoftLogin = loginMethods?.includes(LOGIN_METHOD5.MICROSOFT);
  const hasNostrLogin = loginMethods?.includes(LOGIN_METHOD5.NOSTR);
  const hasEmailMagicLinkLogin = loginMethods?.includes(LOGIN_METHOD5.EMAIL_MAGIC_LINK);
  return /* @__PURE__ */ jsx34(Center8, { h: "100vh", children: /* @__PURE__ */ jsxs26(Stack19, { align: "center", children: [
    /* @__PURE__ */ jsx34(Title13, { order: 1, className: titleClassName, ta: "center", children: /* @__PURE__ */ jsx34(Text27, { inherit: true, variant: "gradient", component: "span", gradient: titleGradient, children: t("auth.signup") }) }),
    /* @__PURE__ */ jsxs26(
      Card8,
      {
        maw: 400,
        p: "md",
        bg: isDark ? alpha4("var(--mantine-color-gray-8)", 0.6) : alpha4("var(--mantine-color-gray-0)", 0.6),
        children: [
          /* @__PURE__ */ jsx34(Text27, { children: t("auth.alreadyAccount") }),
          hasEmailMagicLinkLogin && /* @__PURE__ */ jsxs26(Fragment16, { children: [
            /* @__PURE__ */ jsxs26(Title13, { order: 3, mt: "md", children: [
              /* @__PURE__ */ jsx34(IconRecordMail, { size: "1.1rem" }),
              " ",
              t("auth.emailSection")
            ] }),
            /* @__PURE__ */ jsx34(Text27, { mb: "xs", children: t("auth.signupDescription") }),
            /* @__PURE__ */ jsx34(
              Button14,
              {
                component: Link2,
                to: createLanguageURL("/login?kind=EMAIL_MAGIC_LINK"),
                variant: "light",
                children: t("auth.signupLoginEmail")
              }
            )
          ] }),
          hasGoogleLogin && /* @__PURE__ */ jsxs26(Fragment16, { children: [
            /* @__PURE__ */ jsxs26(Title13, { order: 3, mt: "md", children: [
              /* @__PURE__ */ jsx34(IconBrandGoogle4, { size: "1.1rem" }),
              " ",
              t("auth.googleSection")
            ] }),
            /* @__PURE__ */ jsx34(Text27, { mb: "xs", children: t("auth.googleDescription") }),
            /* @__PURE__ */ jsx34(Button14, { component: Link2, to: createLanguageURL("/login?kind=GOOGLE"), variant: "light", children: t("auth.signupLoginGoogle") })
          ] }),
          hasGithubLogin && /* @__PURE__ */ jsxs26(Fragment16, { children: [
            /* @__PURE__ */ jsxs26(Title13, { order: 3, mt: "md", children: [
              /* @__PURE__ */ jsx34(IconBrandGithub4, { size: "1.1rem" }),
              " ",
              t("auth.githubSection")
            ] }),
            /* @__PURE__ */ jsx34(Text27, { mb: "xs", children: t("auth.githubDescription") }),
            /* @__PURE__ */ jsx34(Button14, { component: Link2, to: createLanguageURL("/login?kind=GITHUB"), variant: "light", children: t("auth.signupLoginGithub") })
          ] }),
          hasMicrosoftLogin && /* @__PURE__ */ jsxs26(Fragment16, { children: [
            /* @__PURE__ */ jsxs26(Title13, { order: 3, mt: "md", children: [
              /* @__PURE__ */ jsx34(IconBrandWindows4, { size: "1.1rem" }),
              " ",
              t("auth.microsoftSection")
            ] }),
            /* @__PURE__ */ jsx34(Text27, { mb: "xs", children: t("auth.microsoftDescription") }),
            /* @__PURE__ */ jsx34(
              Button14,
              {
                component: Link2,
                to: createLanguageURL("/login?kind=MICROSOFT"),
                variant: "light",
                children: t("auth.signupLoginMicrosoft")
              }
            )
          ] }),
          hasNostrLogin && /* @__PURE__ */ jsxs26(Fragment16, { children: [
            /* @__PURE__ */ jsxs26(Title13, { order: 3, mt: "md", children: [
              /* @__PURE__ */ jsx34(IconBrowser, { size: "1.1rem" }),
              " ",
              t("auth.nostrSection")
            ] }),
            /* @__PURE__ */ jsxs26(Text27, { mb: "xs", children: [
              t("auth.nostrDescription"),
              /* @__PURE__ */ jsx34(Anchor4, { href: "https://blog.formshive.com/posts/get-started/", children: t("auth.browserPlugin") }),
              "."
            ] }),
            /* @__PURE__ */ jsx34(Button14, { component: Link2, to: createLanguageURL("/login?kind=NOSTR"), variant: "light", children: t("auth.signupLoginNostr") })
          ] })
        ]
      }
    )
  ] }) });
}

// src/pages/SupportPage.tsx
import {
  Alert as Alert6,
  Button as Button15,
  Card as Card9,
  Group as Group13,
  Select as Select4,
  Stack as Stack20,
  Text as Text28,
  Textarea as Textarea2,
  TextInput as TextInput6,
  Title as Title14
} from "@mantine/core";
import { useForm as useForm6 } from "@mantine/form";
import { IconCheck as IconCheck2 } from "@tabler/icons-react";
import { useEffect as useEffect14, useState as useState21 } from "react";
import { useTranslation as useTranslation23 } from "react-i18next";
import { Fragment as Fragment17, jsx as jsx35, jsxs as jsxs27 } from "react/jsx-runtime";
var SUBJECT_OPTIONS = [];
function SupportPage({
  session,
  formSubmissionEndpoint = "https://api.formshive.com/v1/digest/2ce22659-397b-412c-abe8-a64ce53dc4a0",
  serviceName = "Formshive"
}) {
  const { t } = useTranslation23();
  const [isSubmitting, setIsSubmitting] = useState21(false);
  const [isSubmitted, setIsSubmitted] = useState21(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState21(false);
  SUBJECT_OPTIONS = [
    { value: "general", label: t("support.types.general") },
    { value: "technical", label: t("support.types.technical") },
    { value: "feature", label: t("support.types.feature") },
    { value: "bug", label: t("support.types.bug") }
  ];
  const loginMethod = session?.method;
  const publicKey = session?.publicKey;
  const isEmailLogin = loginMethod === "EMAIL_MAGIC_LINK";
  const userEmail = isEmailLogin ? session?.email || "" : "";
  const form = useForm6({
    initialValues: {
      name: "",
      email: userEmail,
      subject: "",
      message: ""
    },
    validate: {
      email: (value) => !isEmailLogin && !/^\S+@\S+$/.test(value) ? t("forms.invalidUrl") : null,
      subject: (value) => value.trim().length > 0 ? null : "Subject is required",
      message: (value) => value.trim().length > 0 ? null : "Message is required"
    }
  });
  useEffect14(() => {
    if (isEmailLogin && userEmail) {
      form.setFieldValue("email", userEmail);
    }
  }, [userEmail, isEmailLogin]);
  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("email", isEmailLogin ? userEmail : values.email);
      formData.append("subject", values.subject);
      formData.append("message", values.message);
      formData.append("login_method", loginMethod || "unknown");
      formData.append("user_identifier", publicKey || "unknown");
      const response = await fetch(formSubmissionEndpoint, {
        method: "POST",
        body: formData,
        redirect: "manual"
      });
      if (response.ok) {
        form.reset();
        setShowSuccessAlert(true);
        setTimeout(() => {
          setShowSuccessAlert(false);
          setIsSubmitted(true);
        }, 3e3);
      } else {
        throw new Error("Failed to submit form");
      }
    } catch (error) {
      console.error("Failed to submit support request:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSubmitted) {
    return /* @__PURE__ */ jsxs27(Fragment17, { children: [
      /* @__PURE__ */ jsx35(Title14, { order: 2, mb: "md", children: t("support.title") }),
      /* @__PURE__ */ jsx35(Stack20, { children: /* @__PURE__ */ jsx35(Card9, { shadow: "xs", padding: "lg", radius: "md", withBorder: true, children: /* @__PURE__ */ jsxs27(Stack20, { align: "center", gap: "md", children: [
        /* @__PURE__ */ jsx35(IconCheck2, { size: 48, color: "green" }),
        /* @__PURE__ */ jsx35(Title14, { order: 2, ta: "center", children: t("support.thankYou") }),
        /* @__PURE__ */ jsx35(Text28, { ta: "center", size: "lg", children: t("support.successMessage") }),
        /* @__PURE__ */ jsx35(Button15, { onClick: () => setIsSubmitted(false), variant: "light", children: t("support.submitAnother") })
      ] }) }) })
    ] });
  }
  return /* @__PURE__ */ jsxs27(Fragment17, { children: [
    /* @__PURE__ */ jsxs27(Stack20, { gap: 4, mb: "md", children: [
      /* @__PURE__ */ jsx35(Title14, { order: 1, size: "h2", fw: 600, style: { letterSpacing: "-0.01em" }, children: t("support.getHelp", { serviceName }) }),
      /* @__PURE__ */ jsx35(Text28, { size: "sm", c: "dimmed", lh: 1.5, children: t("support.description") })
    ] }),
    /* @__PURE__ */ jsx35(Stack20, { children: /* @__PURE__ */ jsxs27(Card9, { shadow: "xs", padding: "lg", radius: "md", withBorder: true, children: [
      showSuccessAlert && /* @__PURE__ */ jsxs27(Alert6, { color: "green", variant: "light", icon: /* @__PURE__ */ jsx35(IconCheck2, { size: 16 }), mb: "md", children: [
        /* @__PURE__ */ jsx35(Text28, { fw: 500, children: t("support.successAlert") }),
        /* @__PURE__ */ jsx35(Text28, { size: "sm", children: t("support.successAlertMessage") })
      ] }),
      /* @__PURE__ */ jsx35("form", { onSubmit: form.onSubmit(handleSubmit), children: /* @__PURE__ */ jsxs27(Stack20, { gap: "md", children: [
        /* @__PURE__ */ jsxs27(Group13, { grow: true, children: [
          /* @__PURE__ */ jsx35(
            TextInput6,
            {
              label: t("support.nameLabel"),
              placeholder: t("support.namePlaceholder"),
              ...form.getInputProps("name")
            }
          ),
          !isEmailLogin && /* @__PURE__ */ jsx35(
            TextInput6,
            {
              label: t("support.emailLabel"),
              placeholder: t("support.emailPlaceholder"),
              type: "email",
              required: true,
              ...form.getInputProps("email")
            }
          ),
          isEmailLogin && /* @__PURE__ */ jsx35(
            TextInput6,
            {
              label: t("support.emailLabel"),
              value: userEmail,
              readOnly: true,
              description: t("support.loggedInEmail")
            }
          )
        ] }),
        /* @__PURE__ */ jsx35(
          Select4,
          {
            label: t("support.subjectLabel"),
            placeholder: t("support.subjectPlaceholder"),
            data: SUBJECT_OPTIONS,
            required: true,
            ...form.getInputProps("subject")
          }
        ),
        /* @__PURE__ */ jsx35(
          Textarea2,
          {
            label: t("support.messageLabel"),
            placeholder: t("support.messagePlaceholder"),
            minRows: 4,
            required: true,
            ...form.getInputProps("message")
          }
        ),
        /* @__PURE__ */ jsx35(Alert6, { color: "blue", variant: "light", children: /* @__PURE__ */ jsxs27(Text28, { size: "sm", children: [
          t("support.formInfo"),
          loginMethod || "unknown",
          t("support.formInfoEnd")
        ] }) }),
        /* @__PURE__ */ jsx35(Group13, { justify: "flex-end", children: /* @__PURE__ */ jsx35(Button15, { type: "submit", loading: isSubmitting, disabled: isSubmitting, children: isSubmitting ? t("support.submitting") : t("support.submitButton") }) })
      ] }) })
    ] }) })
  ] });
}

// src/pages/NewsPage.tsx
import { Container } from "@mantine/core";
import { Fragment as Fragment18, jsx as jsx36, jsxs as jsxs28 } from "react/jsx-runtime";
function NewsPage(props) {
  const { blogBaseUrl, maxItems, summaryLength, footer } = props;
  return /* @__PURE__ */ jsxs28(Fragment18, { children: [
    /* @__PURE__ */ jsx36(Container, { mt: "xl", mb: "xl", children: /* @__PURE__ */ jsx36(News, { blogBaseUrl, maxItems, summaryLength }) }),
    footer
  ] });
}

// src/pages/PrivacyPage.tsx
import { Box as Box7, Center as Center9, Container as Container2, Stack as Stack21, Text as Text29 } from "@mantine/core";
import { useEffect as useEffect15, useState as useState22 } from "react";
import { useTranslation as useTranslation24 } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { Fragment as Fragment19, jsx as jsx37, jsxs as jsxs29 } from "react/jsx-runtime";
function PrivacyPage(props) {
  const { i18n: i18n2 } = useTranslation24();
  const { supportEmail, footer } = props;
  const [content, setContent] = useState22("");
  useEffect15(() => {
    const loadContent = async () => {
      try {
        const response = await fetch(`/content/privacy/${i18n2.language}.md`);
        if (response.ok) {
          const text = await response.text();
          setContent(text);
        } else {
          throw new Error("Language-specific content not found");
        }
      } catch {
        try {
          const response = await fetch("/content/privacy/en.md");
          const text = await response.text();
          setContent(text);
        } catch (error) {
          console.error("Failed to load privacy content:", error);
          setContent("# Privacy Policy\n\nContent could not be loaded.");
        }
      }
    };
    loadContent();
  }, [i18n2.language]);
  const processedContent = content.replace("our support address", supportEmail);
  return /* @__PURE__ */ jsxs29(Fragment19, { children: [
    /* @__PURE__ */ jsx37(Container2, { mt: "xl", mb: "xl", children: /* @__PURE__ */ jsx37(Center9, { children: /* @__PURE__ */ jsx37(Stack21, { maw: 680, children: /* @__PURE__ */ jsx37(
      Box7,
      {
        style: {
          fontSize: "16px",
          lineHeight: "1.6"
        },
        children: /* @__PURE__ */ jsx37(
          ReactMarkdown,
          {
            components: {
              h1: ({ children }) => /* @__PURE__ */ jsx37(Text29, { size: "xl", fw: 700, mb: "md", mt: 0, children }),
              h2: ({ children }) => /* @__PURE__ */ jsx37(Text29, { size: "lg", fw: 600, mb: "sm", mt: "xl", children }),
              h3: ({ children }) => /* @__PURE__ */ jsx37(Text29, { size: "md", fw: 600, mb: "xs", mt: "lg", children }),
              p: ({ children }) => /* @__PURE__ */ jsx37(Text29, { mb: "md", mt: 0, children }),
              ul: ({ children }) => /* @__PURE__ */ jsx37(Box7, { component: "ul", mb: "md", mt: "xs", pl: "xl", children }),
              li: ({ children }) => /* @__PURE__ */ jsx37(Box7, { component: "li", mb: 4, children })
            },
            children: processedContent
          }
        )
      }
    ) }) }) }),
    footer
  ] });
}

// src/pages/SecurityLogPage.tsx
import { Stack as Stack22, Text as Text30, Title as Title15 } from "@mantine/core";
import { useTranslation as useTranslation25 } from "react-i18next";
import { Fragment as Fragment20, jsx as jsx38, jsxs as jsxs30 } from "react/jsx-runtime";
function SecurityLogPage({ banner, ...props }) {
  const { t } = useTranslation25();
  return /* @__PURE__ */ jsxs30(Fragment20, { children: [
    /* @__PURE__ */ jsxs30(Stack22, { gap: 4, mb: "lg", children: [
      /* @__PURE__ */ jsx38(Title15, { order: 1, size: "h2", fw: 600, style: { letterSpacing: "-0.01em" }, children: t("securityLog.title", "Security log") }),
      /* @__PURE__ */ jsx38(Text30, { size: "sm", c: "dimmed", lh: 1.5, children: t("securityLog.description") })
    ] }),
    banner,
    /* @__PURE__ */ jsx38(SecurityLog, { ...props })
  ] });
}

// src/pages/TermsPage.tsx
import { Box as Box8, Center as Center10, Container as Container3, Stack as Stack23, Text as Text31 } from "@mantine/core";
import { useEffect as useEffect16, useState as useState23 } from "react";
import { useTranslation as useTranslation26 } from "react-i18next";
import ReactMarkdown2 from "react-markdown";
import { Fragment as Fragment21, jsx as jsx39, jsxs as jsxs31 } from "react/jsx-runtime";
function TermsPage(props) {
  const { i18n: i18n2 } = useTranslation26();
  const { supportEmail, footer } = props;
  const [content, setContent] = useState23("");
  useEffect16(() => {
    const loadContent = async () => {
      try {
        const response = await fetch(`/content/terms/${i18n2.language}.md`);
        if (response.ok) {
          const text = await response.text();
          setContent(text);
        } else {
          throw new Error("Language-specific content not found");
        }
      } catch {
        try {
          const response = await fetch("/content/terms/en.md");
          const text = await response.text();
          setContent(text);
        } catch (error) {
          console.error("Failed to load terms content:", error);
          setContent("# Terms of Service\n\nContent could not be loaded.");
        }
      }
    };
    loadContent();
  }, [i18n2.language]);
  const processedContent = content.replace("our support email", supportEmail);
  return /* @__PURE__ */ jsxs31(Fragment21, { children: [
    /* @__PURE__ */ jsx39(Container3, { mt: "xl", mb: "xl", children: /* @__PURE__ */ jsx39(Center10, { children: /* @__PURE__ */ jsx39(Stack23, { maw: 680, children: /* @__PURE__ */ jsx39(
      Box8,
      {
        style: {
          fontSize: "16px",
          lineHeight: "1.6"
        },
        children: /* @__PURE__ */ jsx39(
          ReactMarkdown2,
          {
            components: {
              h1: ({ children }) => /* @__PURE__ */ jsx39(Text31, { size: "xl", fw: 700, mb: "md", mt: 0, children }),
              h2: ({ children }) => /* @__PURE__ */ jsx39(Text31, { size: "lg", fw: 600, mb: "sm", mt: "xl", children }),
              h3: ({ children }) => /* @__PURE__ */ jsx39(Text31, { size: "md", fw: 600, mb: "xs", mt: "lg", children }),
              p: ({ children }) => /* @__PURE__ */ jsx39(Text31, { mb: "md", mt: 0, children }),
              ul: ({ children }) => /* @__PURE__ */ jsx39(Box8, { component: "ul", mb: "md", mt: "xs", pl: "xl", children }),
              li: ({ children }) => /* @__PURE__ */ jsx39(Box8, { component: "li", mb: 4, children })
            },
            children: processedContent
          }
        )
      }
    ) }) }) }),
    footer
  ] });
}

// src/pages/index.ts
var LoginPage2 = withLibraryI18n(LoginPage);
var LoginCallbackPage2 = withLibraryI18n(LoginCallbackPage);
var Login2FAPage2 = withLibraryI18n(Login2FAPage);
var MagicLinkLoginPage2 = withLibraryI18n(MagicLinkLoginPage);
var SignupPage2 = withLibraryI18n(SignupPage);
var SupportPage2 = withLibraryI18n(SupportPage);

// src/utils/account-movements.ts
import { formatCurrency } from "@gofranz/common";
function formatAccountMovementAmount(row) {
  return formatCurrency([row.currency, Math.abs(row.amount)], { maxFractionDigits: 6 });
}
function formatAccountMovementFeatures(features) {
  if (!features) return "";
  if (!features.startsWith("tokens:")) return features;
  const parsed = {};
  for (const part of features.substring("tokens:".length).split(",")) {
    const eq = part.indexOf("=");
    if (eq <= 0) continue;
    parsed[part.substring(0, eq)] = part.substring(eq + 1);
  }
  const bucket = parsed.tier ? `Tier: ${parsed.tier}` : parsed.model ? `Model: ${parsed.model}` : null;
  if (bucket && parsed.input && parsed.output) {
    return `${bucket}, Input: ${parsed.input}, Output: ${parsed.output}`;
  }
  return features;
}

// src/utils/notification-handlers.ts
import { getErrorTitle as getErrorTitle2 } from "@gofranz/common";

// src/utils/notifications.ts
function parseApiError(error) {
  if (!error) {
    return {
      title: "Unknown Error",
      message: "An unknown error occurred. Please try again."
    };
  }
  if (typeof error === "string") {
    return {
      title: "Error",
      message: error
    };
  }
  if (error.response?.data) {
    if (error.response.data.error === "validation_error") {
      return parseApiFieldValidationError(error);
    }
    return parseApiErrorData(error.response.data);
  }
  if (error.error && error.message) {
    return parseApiErrorData(error);
  }
  if (error.message) {
    return {
      title: "Error",
      message: error.message
    };
  }
  return {
    title: "Error",
    message: "An unexpected error occurred. Please try again."
  };
}
function parseApiErrorData(data) {
  if (typeof data === "string") {
    return {
      title: "Error",
      message: data
    };
  }
  if (data.error && data.message) {
    return {
      title: getErrorTitle(data.error),
      message: data.message,
      error: data.error,
      action: data.action
    };
  }
  const message = data.message || data.error || data.detail || "An error occurred";
  return {
    title: "Error",
    message: typeof message === "string" ? message : "An error occurred"
  };
}
function parseApiFieldValidationError(error) {
  const responseData = error.response?.data;
  if (responseData.error !== "validation_error") {
    return {
      title: "Validation Error",
      message: "There was a validation error with your request."
    };
  }
  if (!responseData.errors || !Array.isArray(responseData.errors)) {
    return {
      title: "Validation Error",
      message: responseData.message || "There was a validation error with your request."
    };
  }
  const messages = responseData.errors.map((fieldError) => {
    return `${fieldError.field}: ${fieldError.message}`;
  }).join(", ");
  return {
    title: responseData.message,
    message: messages
  };
}
function getErrorTitle(errorCode) {
  const errorTitles = {
    validation_error: "Validation Error",
    authentication_error: "Authentication Error",
    authorization_error: "Permission Denied",
    subscription_error: "Subscription Error",
    payment_error: "Payment Error",
    rate_limit_error: "Rate Limit Exceeded",
    server_error: "Server Error",
    not_found: "Not Found",
    conflict: "Conflict",
    bad_request: "Bad Request",
    expired_token: "Session Expired",
    invalid_token: "Invalid Token",
    invalid_session: "Invalid Session",
    oauth_error: "Authentication Failed",
    invalid_redirect_uri: "Invalid Redirect",
    session_not_found: "Session Not Found",
    email_not_found: "Email Not Found",
    email_already_in_use: "Email In Use",
    email_error: "Email Error",
    invalid_email: "Invalid Email",
    invalid_input: "Invalid Input",
    database_error: "Server Error",
    cache_error: "Server Error",
    // Session / auth errors
    google_email_not_verified: "Email Not Verified",
    invalid_auth_method_google: "Wrong Login Method",
    invalid_auth_method_github: "Wrong Login Method",
    invalid_auth_method_microsoft: "Wrong Login Method",
    invalid_auth_method_nostr: "Wrong Login Method",
    invalid_auth_method: "Wrong Login Method",
    invalid_challenge: "Invalid Login Link",
    challenge_mismatch: "Invalid Login Link",
    invalid_nostr_signature: "Invalid Signature",
    invalid_nostr_challenge: "Invalid Challenge",
    nostr_key_mismatch: "Key Mismatch",
    email_not_primary: "Not Primary Email",
    too_many_attempts: "Too Many Attempts",
    invalid_authorization_code: "Invalid Code",
    expired_or_used_auth_code: "Code Expired",
    invalid_2fa_code: "Invalid Code",
    // 2FA errors
    "2fa_not_enabled": "2FA Not Enabled",
    "2fa_already_enabled": "2FA Already Enabled",
    invalid_code: "Invalid Code",
    code_expired: "Code Expired",
    no_recovery_codes: "No Recovery Codes",
    invalid_method_type: "Invalid Method",
    forbidden: "Permission Denied",
    // Token errors
    token_not_valid_yet: "Token Not Valid Yet",
    // Session errors
    invalid_challenge_type: "Invalid Challenge",
    invalid_nostr_event: "Invalid Authentication",
    invalid_request: "Invalid Request",
    // Email delivery errors
    email_already_verified: "Already Verified",
    email_not_verified: "Email Not Verified",
    cannot_delete_account_email: "Cannot Delete Email",
    inactive_recipient: "Email Undeliverable",
    invalid_address: "Invalid Address",
    spam_complaint: "Email Rejected",
    hard_bounce: "Email Undeliverable",
    service_unavailable: "Service Unavailable",
    rate_limit_exceeded: "Rate Limit Exceeded",
    temporary_failure: "Temporary Error",
    // Payment errors
    payment_exists: "Payment Exists",
    checkout_session_failed: "Checkout Failed",
    invalid_currency: "Invalid Currency",
    payment_gateway_not_found: "Gateway Not Found",
    invalid_payment_gateway_owner: "Permission Denied",
    order_not_found: "Order Not Found",
    payment_not_found: "Payment Not Found",
    customer_already_exists: "Customer Exists",
    webhook_error: "Processing Error",
    // Checkoutbay validation
    invalid_country_code: "Invalid Country",
    invalid_region_code: "Invalid Region",
    unexpected_region_code: "Unexpected Region",
    expected_shop_address: "Invalid Address Type",
    expected_customer_address: "Invalid Address Type",
    warehouse_not_found: "Warehouse Not Found",
    warehouse_address_not_found: "Address Not Found",
    shipping_address_not_found: "Address Not Found",
    billing_address_not_found: "Address Not Found",
    product_not_found: "Product Not Found",
    product_stock_not_found: "Out of Stock",
    shop_owner_email_not_found: "Email Not Found",
    shipping_rate_not_found: "Shipping Rate Not Found",
    invalid_warehouse_owner: "Permission Denied",
    invalid_product_owner: "Permission Denied",
    invalid_billing_address_owner: "Permission Denied",
    invalid_shipping_address_owner: "Permission Denied",
    insufficient_stock: "Insufficient Stock",
    products_not_in_same_warehouse: "Warehouse Mismatch",
    invalid_address_kind: "Invalid Address",
    voucher_usage_limit_exceeded: "Voucher Limit Exceeded",
    has_associated_payments: "Has Active Payments",
    // Payment gateway
    encryption_error: "Server Error",
    // GPT-Muse
    rate_limited: "Rate Limited",
    model_not_found: "Model Not Found",
    free_tier_restriction: "Free Tier Limit",
    unknown_model: "Unknown Model",
    insufficient_balance: "Insufficient Balance",
    content_moderation: "Content Blocked",
    timeout: "Request Timeout",
    request_timeout: "Request Timeout",
    network_error: "Network Error",
    service_overloaded: "Service Overloaded",
    model_down: "Model Unavailable",
    no_providers: "No Providers",
    queue_full: "Service Busy",
    no_providers_available: "No Providers",
    no_providers_in_region: "No Regional Providers",
    internal_error: "Server Error"
  };
  return errorTitles[errorCode] || "Error";
}
function showApiErrorNotification(error, notifications3, defaultTitle = "Error") {
  const parsed = parseApiError(error);
  let actionSuffix = "";
  if (parsed.action) {
    switch (parsed.action) {
      case "retry_later":
        actionSuffix = " Please try again later.";
        break;
      case "add_credits":
        actionSuffix = " Please add credits to your account.";
        break;
      case "contact_support":
        actionSuffix = " Please contact support for assistance.";
        break;
      case "cancel_payments_first":
        actionSuffix = " Cancel associated payments first.";
        break;
      case "request_new_code":
        actionSuffix = " Please request a new code.";
        break;
    }
  }
  const message = parsed.message;
  const finalMessage = actionSuffix && !message.toLowerCase().includes(actionSuffix.trim().toLowerCase().slice(0, 20)) ? `${message}${actionSuffix}` : message;
  notifications3.show({
    title: parsed.title || defaultTitle,
    message: finalMessage,
    color: "red",
    autoClose: 1e4
  });
}
function showSuccessNotification(title, message, notifications3) {
  notifications3.show({
    title,
    message,
    color: "green",
    autoClose: 5e3
  });
}
function showInfoNotification(title, message, notifications3) {
  notifications3.show({
    title,
    message,
    color: "blue",
    autoClose: 6e3
  });
}
function showWarningNotification(title, message, notifications3) {
  notifications3.show({
    title,
    message,
    color: "orange",
    autoClose: 7e3
  });
}

// src/utils/notification-handlers.ts
function createErrorHandler(options) {
  const { notifications: notifications3, onError } = options;
  return (error) => {
    if (error.code === "ECONNABORTED" || error.message === "Network Error") {
      notifications3.show({
        title: "Connection Error",
        message: "Unable to reach the server. Please check your connection and try again.",
        color: "red",
        autoClose: 1e4
      });
      return;
    }
    if (onError) {
      onError(error);
    }
    const config = error.config;
    const customNotification = config?.notification;
    if (customNotification && "onError" in customNotification && customNotification.onError) {
      showApiErrorNotification(
        error,
        notifications3,
        customNotification.onError.title || getErrorTitle2(error)
      );
    } else {
      const title = getErrorTitle2(error);
      showApiErrorNotification(error, notifications3, title);
    }
  };
}
function createSuccessHandler(options) {
  const { notifications: notifications3, onSuccess, ignoredPaths = [] } = options;
  return (response) => {
    const accepted = ["post", "put", "patch"];
    if (!accepted.includes(response.config.method || "")) {
      return;
    }
    if (ignoredPaths.some((path) => response.config.url?.includes(path))) {
      return;
    }
    if (onSuccess) {
      onSuccess(response);
    }
    const config = response.config;
    const customNotification = config?.notification;
    if (customNotification) {
      if ("onSuccess" in customNotification) {
        if (customNotification.onSuccess !== false && customNotification.onSuccess) {
          const successNotif = customNotification.onSuccess;
          showSuccessNotification(successNotif.title, successNotif.message, notifications3);
        }
      } else {
        const simpleNotif = customNotification;
        showSuccessNotification(simpleNotif.title, simpleNotif.message, notifications3);
      }
    } else {
      showSuccessNotification(
        `Request Successful`,
        `Your ${response.config.method?.toUpperCase()} request to ${response.config.url} was successful.`,
        notifications3
      );
    }
  };
}

// src/index.ts
var commonResources = {
  en: { ...en_default, ...integrations_default },
  de: { ...de_default, ...integrations_default },
  es: { ...es_default, ...integrations_default },
  fr: { ...fr_default, ...integrations_default },
  pt: { ...pt_default, ...integrations_default },
  zh: { ...zh_default, ...integrations_default },
  th: { ...th_default, ...integrations_default },
  ar: { ...ar_default, ...integrations_default }
};
export {
  ADAPTER_REGISTRY,
  AdapterConfigFields,
  AdapterPickerGrid,
  ColorSchemeToggle,
  ConnectedAccounts,
  ConnectedAccountsLanding,
  ConnectedAccountsPage,
  CreateVerifiedEmail,
  Entity,
  EntityFormCreate,
  EntityFormEdit,
  EventSubscriptionCheckboxGrid,
  FORMSHIVE_EVENT_TYPES,
  GeneralizedCreatePage,
  GeneralizedViewPage,
  HmacSecretReveal,
  IdpHandoffButton,
  IntegrationDeliveryHistory,
  IntegrationForm,
  IntegrationsList,
  IsVerified,
  LargeTitle,
  LargeTitleWithText,
  LibraryI18nProvider,
  Login2 as Login,
  Login2FAPage2 as Login2FAPage,
  LoginCallback2 as LoginCallback,
  LoginCallbackPage2 as LoginCallbackPage,
  LoginPage2 as LoginPage,
  MagicLinkLoginPage2 as MagicLinkLoginPage,
  News2 as News,
  NewsPage,
  OidcSecurityLogBanner,
  OidcSyncFromIdpButton,
  OidcTwoFactorRedirect,
  PrivacyPage,
  SecurityLog,
  SecurityLogPage,
  SignupPage2 as SignupPage,
  SupportPage2 as SupportPage,
  TermsPage,
  VerifiedEmailComponent2 as VerifiedEmailComponent,
  VerifiedEmailDetail,
  ViewPageMode,
  commonResources,
  createErrorHandler,
  createSuccessHandler,
  detectAndSetLanguage,
  formatAccountMovementAmount,
  formatAccountMovementFeatures,
  getAdapterMeta,
  i18n_default as libraryI18n,
  listAdapters,
  parseApiError,
  showApiErrorNotification,
  showInfoNotification,
  showSuccessNotification,
  showWarningNotification,
  storeLanguage,
  updateHashWithLanguage,
  useConnectedAccountsFlow,
  useIdpHandoff,
  useLanguageAwareRouting,
  useOidcRefreshOnReturn,
  usePagination,
  useSessionCheck
};
